/* ⛔ SPEED RULES FIRST (Ryan 2026-10-24). Before adding a card, a request, a data source or a field to this page
   (or to an endpoint it calls), read Team-Portal-Bot/tools/Marketing-Ops/SPEED-RULES.md. The page waits for its
   SLOWEST request, a slowdown is invisible on a warm reload, and a new card must not become the slowest one cold. */
(function() {
  'use strict';

  // Marketing-worker base. Distinct from rfq-worker — this worker holds the
  // spend records + dashboard read endpoints.
  var MW_BASE = 'https://marketing-worker.ryan-29b.workers.dev';

  var state = {
    rangeKey: 'month',          // 'year'|'quarter'|'month'|'week'|'lastyear'|'lastquarter'|'lastmonth'|'lastweek'|'last30'|'last90'|'last180'|'all'|'custom'
    customFrom: null,           // ISO date string when rangeKey='custom'
    customTo: null,
    rangeDays: 28,              // legacy — used by burn-pace etc. that haven't migrated to rangeKey
    platform: '',
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — attribution model for the contribution-margin
    // surfaces (Attributed Revenue / ROAS / channel + campaign breakdowns). Default
    // first-paid-touch (audit F-006: last-click under-credits BSF's long cycles ~2x).
    // Recomputed server-side from stored journeys; persisted across sessions.
    attribution: (function () { try { return localStorage.getItem('moph_attribution') || 'firstPaid'; } catch (_) { return 'firstPaid'; } })(),
    metric: 'cm',           // Daily Performance default tab — hero metric (#2). 'inquiries' (Leads tab) renders stacked source bars; the rest are lines.
    spendView: 'total',     // CUSTOM_MARKETINGOPS_BY_CLAUDE — Ad Spend tab sub-view: 'total' (single line) or 'channel' (stacked area by Google/Meta/Microsoft).
    series: [],
    kpis: null,
    channels: null,
    campaigns: null,
    health: null,
    sort: { key: 'spend', dir: 'desc' },
    showPaused: false,  // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-06) — Campaigns "Paused" switch; off = paused rows hidden.
    isStale: false,
    showPlan: false,    // toolbar toggle — adds Target + Variance columns
    planYear: null,     // { year, poolConfig, campaigns: [...] } from /budget/year
    marginPct: 35,      // Fixed no-sold-orders CM estimate assumption (Margin % dropdown removed N23). True per-order CM is used whenever orders exist.
    cm: null,           // Phase 1.8.5 — { totals, byChannel, isContributionMargin } from /dashboard/contribution-margin, OR null on upstream fail (falls back to marginPct estimate)
    orders: null,       // Phase 6 — { orders: [...], totalMatched, returned } from /dashboard/sold-orders
    ordersSort: { key: 'dateSold', dir: 'desc' },  // Recent Sales table — independent from Campaigns table sort.
    ordersShown: 20,    // N14 — Recent Sales rows currently visible (Show 20 more / less / all). Resets to 20 each load.
    ordersFilter: { channels: [], sources: [] },  // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-24) — Sales table Channel + Source multi-select filter (empty = all). Client-side over o.channel / o.sourceGroup. Resets each load.
    insights: null,     // Phase 6 v2 — { repeatCustomerRate, bySourceMetrics, topVerticals } from /dashboard/insights
    reinvest: null,     // Phase 4.1 v1 — { recs: [...], campaignsEvaluated, rulesEvaluated } from /dashboard/reinvestment-memo
    aliases: {},        // #9 — { campaignId: alias } from /dashboard/campaign-aliases (user-friendly campaign display names)
    inquiries: null,    // Inquiry Flow — { byDate:[{date,bySource,total}], sources, totals, excluded } from /dashboard/inquiries, OR null on upstream fail
    leads: null,        // Leads table — { leads:[...], total, kpi:{total,adAttributed,quoted,won,lost} } from /dashboard/leads, OR null on upstream fail
    leadsShown: 20,     // Leads rows currently visible (Show 20 more / less / all). Resets to 20 each load.
    leadsSort: { key: 'date', dir: 'desc' },  // Leads table sort — independent from the Sales table.
  };

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-03) — "Include non-marketing revenue" (referral +
     rep-produced). Defaults OFF so an admin opening the page sees exactly what Malt sees; the
     agency never gets the control at all, and marketing-worker refuses the flag for role=agency
     regardless, so this is a convenience switch and NOT the enforcement point.
     Persisted per-browser: an admin who turns it on to look at the whole business shouldn't have
     to re-flip it on every page load. */
  var NONMKT_LS_KEY = 'moph.includeNonMarketing';
  function nonMktOn() {
    try { return localStorage.getItem(NONMKT_LS_KEY) === '1'; } catch (_) { return false; }
  }
  function setNonMktOn(on) {
    try { localStorage.setItem(NONMKT_LS_KEY, on ? '1' : '0'); } catch (_) {}
  }
  /* ⚠️ The BROWSER SESSION and the JWT disagree about admin, by design — this cost a shipped
     invisible toggle on 2026-08-03. rfq-worker mints the token with
     `role: rep.admin ? 'admin' : (rep.role || 'rep')` (admin collapsed INTO role), but hands the
     browser `{ admin: rep.admin, role: rep.role || 'rep' }` (kept separate). So an admin's session
     usually reads role:'rep', admin:true. Checking role alone hides the control from every admin.
     custom-rep-portal.js states the real rule: `var isAdmin = !!session.admin`. Match it. */
  function isAdminSession() {
    try {
      var s = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession();
      return !!s && (s.admin === true || (s.role || '') === 'admin');
    } catch (_) { return false; }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — endpoints that provably never read the flag.

     Appending `includeNonMarketing=1` changes the URL, and the URL is the cache key at BOTH layers
     (the browser's `private, max-age=60`, and lib/mo-cache.js server-side). So sending it to an
     endpoint that ignores it buys nothing and guarantees a cold miss on every toggle — part of why
     flipping All Sales costs a full reload rather than a filter.

     ⚠️ DENY-LIST, NOT AN ALLOW-LIST, AND THAT DIRECTION IS THE POINT. Forgetting to add a new
     flag-CONSUMING endpoint to an allow-list would silently drop the filter and print numbers that
     disagree with the rest of the page. Forgetting to add a new flag-IGNORING one here costs a
     cache miss. Wrong-and-fast is unacceptable; right-and-slow is survivable, so a new endpoint
     defaults to receiving the param.

     Verified against marketing-worker on 2026-08-19 — the five that DO consume it are
     contribution-margin, sold-orders, leads, insights and reinvestment-memo (the memo feeds it into
     fetchFinanceAggregate, so it must keep receiving it). /dashboard/inquiries already hard-codes
     `false` into its own cache key for the same reason. */
  var NONMKT_IGNORING_PATHS = [
    '/dashboard/kpis', '/dashboard/channels', '/dashboard/campaigns', '/dashboard/timeseries',
    '/dashboard/campaign-aliases', '/dashboard/geo-spend', '/dashboard/inquiries',
  ];
  function pathIgnoresNonMkt(path) {
    var base = String(path || '').split('?')[0];
    return NONMKT_IGNORING_PATHS.indexOf(base) !== -1;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — force-fresh flag for the Refresh button.

     Set for the duration of the fan-out's CONSTRUCTION, not its completion. Every mwFetch() in
     loadAll() is invoked synchronously by the array literal, and mwFetch appends to `path` before
     its first await, so a flag raised immediately before the literal and lowered immediately after
     is seen by all of them and by nothing else.

     ⚠️ That is only true while the array stays synchronously constructed. If an `await` is ever
     added between raising the flag and the literal, the later fetches lose the param and refresh
     silently degrades to a normal load. Thread it explicitly rather than moving this. */
  var _forceFresh = false;

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — run a fan-out AS THE OTHER TOGGLE STATE.
     null = follow the live toggle. Set only around the admin prefetch's array construction, under
     the same synchronous-literal rule as _forceFresh above.
     ⚠️ It overrides the toggle, never the ADMIN test: `isAdminSession()` is still ANDed below, so
     this cannot make a non-admin session ask for non-marketing rows. The real enforcement is
     server-side anyway (resolveIncludeNonMarketing returns false for any non-admin whatever the URL
     says) — this just means the client never even asks. */
  var _nonMktOverride = null;

  /** Fetch helper — auto-attaches the same Bearer token RepPortal uses. */
  async function mwFetch(path) {
    /* `_fresh=1` tells the worker to skip its cache READ for this request while still writing the
       result back under the normal key — so one person pressing Refresh re-warms the entry for
       everyone else in the meeting rather than carving out a private copy. mo-cache strips the
       param from the key for exactly that reason. */
    if (_forceFresh) {
      path += (path.indexOf('?') === -1 ? '?' : '&') + '_fresh=1';
    }
    /* Appended HERE, once, rather than at each of the seven call sites — the filter has to reach
       every surface (sales, leads, insights, memo, contribution margin) or the page shows numbers
       that disagree with each other. One choke point can't drift. */
    var _nmWanted = (_nonMktOverride === null) ? nonMktOn() : _nonMktOverride;
    if (_nmWanted && isAdminSession() && !pathIgnoresNonMkt(path)) {
      path += (path.indexOf('?') === -1 ? '?' : '&') + 'includeNonMarketing=1';
    }
    var token = await window.RepPortal.Auth.getAccessToken();
    var resp = await fetch(MW_BASE + path, {
      headers: token ? { 'Authorization': 'Bearer ' + token } : {},
      credentials: 'include',
    });
    if (!resp.ok) {
      var err = await resp.json().catch(function() { return { error: 'Request failed' }; });
      throw new Error(err.error || ('Request failed (' + resp.status + ')'));
    }
    return resp.json();
  }

  /** POST helper (#9) — JSON body, same bearer token as mwFetch. */
  async function mwPost(path, payload) {
    var token = await window.RepPortal.Auth.getAccessToken();
    var resp = await fetch(MW_BASE + path, {
      method: 'POST',
      headers: Object.assign({ 'Content-Type': 'application/json' }, token ? { 'Authorization': 'Bearer ' + token } : {}),
      credentials: 'include',
      body: JSON.stringify(payload || {}),
    });
    if (!resp.ok) {
      var err = await resp.json().catch(function() { return { error: 'Request failed' }; });
      throw new Error(err.error || ('Request failed (' + resp.status + ')'));
    }
    return resp.json();
  }

  // Quote-Dashboard-style date-range presets. Returns { from, to } as
  // ISO YYYY-MM-DD strings. Week starts Monday (ISO 8601). 'all' floors
  // to MARKETING_OPS launch (2026-05-01) — pre-launch data is excluded
  // server-side too, this just keeps the visible range honest.
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (F-023, 2026-05-31) — compute "now" in the
     BSFS Google Ads account timezone (America/Denver), NOT UTC. Spend is dated
     in account-TZ, and the old UTC math blanked Today / This-Month in the last
     ~6h of each period for US users (worse for Malt in AU — they'd open a fresh
     month to $0). Returns { y, m0 (0-indexed month), d, iso }. */
  var ACCOUNT_TZ = 'America/Denver';
  function accountNowParts() {
    var p = {};
    try {
      new Intl.DateTimeFormat('en-CA', { timeZone: ACCOUNT_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(new Date()).forEach(function(x){ if (x.type !== 'literal') p[x.type] = x.value; });
    } catch (e) { /* Intl/TZ unavailable — fall through to UTC */ }
    var y = parseInt(p.year, 10), m1 = parseInt(p.month, 10), d = parseInt(p.day, 10);
    if (!y || !m1 || !d) { var u = new Date(); y = u.getUTCFullYear(); m1 = u.getUTCMonth() + 1; d = u.getUTCDate(); }
    return { y: y, m0: m1 - 1, d: d, iso: String(y) + '-' + ('0' + m1).slice(-2) + '-' + ('0' + d).slice(-2) };
  }
  function dateRange() {
    var now = accountNowParts();
    var year = now.y;
    var month = now.m0;
    var endStr = now.iso;
    // Represent the account-TZ calendar day as a UTC-midnight Date so all the
    // existing Date.UTC + getUTC* arithmetic below stays internally correct.
    var today = new Date(Date.UTC(year, month, now.d));
    function iso(d) { return d.toISOString().slice(0, 10); }
    function startOfWeek(d) {
      var copy = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      var day = copy.getUTCDay(); // 0=Sun, 1=Mon
      var diff = day === 0 ? -6 : (1 - day); // Mon-start
      copy.setUTCDate(copy.getUTCDate() + diff);
      return copy;
    }
    var start, end = endStr;
    switch (state.rangeKey) {
      case 'today':      start = today; break; // CUSTOM_DATERANGES_BY_CLAUDE (Ryan 2026-10-10): the account-TZ day
      case 'year':       start = new Date(Date.UTC(year, 0, 1)); break;
      case 'quarter':    start = new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1)); break;
      case 'month':      start = new Date(Date.UTC(year, month, 1)); break;
      case 'week':       start = startOfWeek(today); break;
      case 'lastyear':   start = new Date(Date.UTC(year - 1, 0, 1));
                         end   = iso(new Date(Date.UTC(year - 1, 11, 31))); break;
      case 'lastquarter': {
        var lastQStart = Math.floor(month / 3) * 3 - 3;
        var lqYear = year, lqMonth = lastQStart;
        if (lqMonth < 0) { lqMonth += 12; lqYear -= 1; }
        start = new Date(Date.UTC(lqYear, lqMonth, 1));
        end   = iso(new Date(Date.UTC(lqYear, lqMonth + 3, 0)));
        break;
      }
      case 'lastmonth': {
        var lmYear = year, lmMonth = month - 1;
        if (lmMonth < 0) { lmMonth = 11; lmYear -= 1; }
        start = new Date(Date.UTC(lmYear, lmMonth, 1));
        end   = iso(new Date(Date.UTC(lmYear, lmMonth + 1, 0)));
        break;
      }
      case 'lastweek': {
        var thisWkStart = startOfWeek(today);
        start = new Date(thisWkStart.getTime() - 7 * 86400000);
        end   = iso(new Date(thisWkStart.getTime() - 86400000));
        break;
      }
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-07-03) — rolling trailing-N-day
         windows, inclusive of today (end stays the account-TZ 'today'). 'today'
         is UTC-midnight of the account-TZ date, so subtracting whole days keeps
         the start on a clean date boundary. N-1 offset makes the window N days
         inclusive (e.g. last30 = today and the 29 days before it). */
      case 'last30':     start = new Date(today.getTime() - 29 * 86400000); break;
      case 'last90':     start = new Date(today.getTime() - 89 * 86400000); break;
      case 'last180':    start = new Date(today.getTime() - 179 * 86400000); break;
      case 'all':        start = new Date(Date.UTC(2026, 4, 1)); break; // marketing-ops launch floor
      case 'custom':
        return {
          from: state.customFrom || iso(new Date(Date.UTC(year, month, 1))),
          to: state.customTo || endStr,
        };
      default:           start = new Date(Date.UTC(year, month, 1)); break;
    }
    return { from: iso(start), to: end };
  }

  // ─── Show Your Work (Phase 2 wedge feature) ────────────────────────────
  // Per-KPI explainer drawer. Each key returns { title, body } where body
  // is an HTML fragment composed from formula + sources + exclusions +
  // platform-delta sections. The wedge feature per VOC report — every
  // metric defends its number without the agency needing to ask.
  function explainerFor(key) {
    var range = dateRange();
    var rangeStr = '<strong>' + escapeHtml(rangeLabel()) + '</strong> (' + range.from + ' to ' + range.to + ')';
    var asOf = state.kpis && state.kpis.fetchedAt
      ? new Date(state.kpis.fetchedAt).toLocaleString()
      : 'unknown';
    var t = (state.kpis && state.kpis.totals) || {};
    var cm = state.cm;
    var marginPct = Number(state.marginPct) || 35;
    var useTrueCm = cm && cm.totals && cm.totals.orderCount > 0;
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — efficiency-tile inputs (CPC/CPM/CPA
    // explainers). Mirrors renderKpis exactly: clicks + impressions summed from
    // the (already platform-filtered) campaigns list; spend + Ad Conversions
    // from the KPI totals.
    var _effCamps = (state.campaigns && state.campaigns.campaigns) || [];
    var _effClicks = 0, _effImpr = 0;
    _effCamps.forEach(function (c) { _effClicks += Number(c.clicks) || 0; _effImpr += Number(c.impressions) || 0; });
    var _effSpend = Number(t.spend) || 0, _effConv = Number(t.conversions) || 0;

    function section(title, html) {
      return '<div class="moph-explain__section"><div class="moph-explain__section-title">' + escapeHtml(title) + '</div>' + html + '</div>';
    }
    function kv(rows) {
      return '<div class="moph-explain__kv">' + rows.map(function(r) {
        return '<div class="moph-explain__kv-key">' + escapeHtml(r[0]) + '</div><div class="moph-explain__kv-val">' + r[1] + '</div>';
      }).join('') + '</div>';
    }
    function footer() {
      return section('As of', '<p class="moph-explain__sub">' + escapeHtml(asOf) + ' · range ' + rangeStr + '</p>');
    }

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.14) — how the agency performance fee was
       built, order by order. Every clause here is a term of the actual agreement:
       what counts, what is carved out, what basis it is measured on, and the fact
       that the number is not final until the month is invoiced. */
    function agencyFeeProse(ac) {
      var pf = ac.performanceFee;
      // Ruling a deal into the fee base moves money → admin only (server enforces it too).
      var canRuleComp = !!(cm && cm.viewerIsAdmin);
      var paidCount = pf.lines.direct.count + pf.lines.propagatedHard.count + pf.lines.propagatedSoftOverride.count;
      var money = function(n) { return '$' + Math.round(Number(n) || 0).toLocaleString('en-US'); };
      var html = '<p><strong>Agency performance fee</strong> is computed <em>per order</em>, not from a blended rate: every ad-sourced sold order in range contributes '
        + '<strong>3%</strong> of its net merchandise if it closed online (Shopify self-checkout) or <strong>1%</strong> if it was lead/rep-closed. '
        + 'Net merchandise = product revenue net of refunds, excluding shipping, tax, freight/install and the actual per-order card fee. '
        + 'Attribution is this dashboard\'s <strong>last-click</strong> measure — deliberately not each ad platform\'s own reporting, which runs hot on view-through and long click windows. '
        + paidCount + ' order' + (paidCount === 1 ? '' : 's') + ' paid, on ' + money(pf.base.total) + ' of net merchandise.</p>';

      if (pf.lines.propagatedHard.count || pf.lines.propagatedSoftOverride.count) {
        html += '<p><strong>Propagated credit — paid above the written agreement, on purpose.</strong> '
          + (pf.lines.propagatedHard.count ? pf.lines.propagatedHard.count + ' repeat order' + (pf.lines.propagatedHard.count === 1 ? '' : 's') + ' matched an earlier ad click by the same person (email or phone) and paid ' + money(pf.lines.propagatedHard.fee) + '. ' : '')
          + (pf.lines.propagatedSoftOverride.count ? pf.lines.propagatedSoftOverride.count + ' company-level match' + (pf.lines.propagatedSoftOverride.count === 1 ? ' was' : 'es were') + ' ruled in at invoice review and paid ' + money(pf.lines.propagatedSoftOverride.fee) + '. ' : '')
          + 'The agreement excludes repeat orders without a fresh ad click; these are paid anyway to keep the agency chasing large accounts — so they are shown as their own line rather than buried in the total.</p>';
      }
      if (pf.excluded.propagatedSoft.count) {
        html += '<p><strong>' + pf.excluded.propagatedSoft.count + ' company-level match' + (pf.excluded.propagatedSoft.count === 1 ? '' : 'es') + ' excluded by default</strong> ('
          + money(pf.excluded.propagatedSoft.netMerch) + ' of net merchandise, ' + money(pf.excluded.propagatedSoft.fee) + ' of fee). '
          + 'A shared corporate domain or company name is not proof the same buyer came back — two unrelated departments of one school district share a domain. These are ruled in or out deal by deal at invoice review.</p>';
        // Invoice review: name every candidate and what ruling it in would cost.
        html += '<ul>' + pf.softCandidates.map(function(c) {
          return '<li>' + escapeHtml(c.ref) + ' &middot; ' + escapeHtml(c.dateSold) + ' &middot; matched on ' + escapeHtml(c.rung)
            + ' &middot; ' + (c.online ? 'online' : 'lead-closed') + ' &middot; ' + money(c.netMerch) + ' → would add <strong>' + money(c.feeIfIncluded) + '</strong>'
            + (canRuleComp ? ' <button type="button" class="moph-inline-btn" data-comp-override="' + escapeHtml(c.ref) + '" data-include="true" data-amount="' + money(c.feeIfIncluded) + '">Include</button>' : '')
            + '</li>';
        }).join('') + '</ul>';
      }
      // Deals an admin has already ruled ON, so a ruling can be undone from the same place.
      var ruledIn = (pf.orders || []).filter(function(o) { return o.line === 'propagatedSoftOverride'; });
      if (canRuleComp && ruledIn.length) {
        html += '<p><strong>Ruled in at invoice review:</strong></p><ul  >' + ruledIn.map(function(o) {
          return '<li>' + escapeHtml(o.ref) + ' &middot; ' + escapeHtml(o.dateSold) + ' &middot; ' + money(o.fee)
            + ' <button type="button" class="moph-inline-btn" data-comp-override="' + escapeHtml(o.ref) + '" data-include="false" data-amount="' + money(o.fee) + '">Remove</button></li>';
        }).join('') + '</ul>';
      }
      if (pf.excluded.brand.count) {
        html += '<p><strong>Brand carve-out:</strong> ' + pf.excluded.brand.count + ' order' + (pf.excluded.brand.count === 1 ? '' : 's') + ' on Blue Sky brand campaigns ('
          + money(pf.excluded.brand.netMerch) + ') earned no fee (' + money(pf.excluded.brand.fee) + ' not billed) — a click on our own brand listing harvests demand the brand already created. Manufacturer brands (ECORE, Spirit) are payable.</p>';
      }
      if (pf.excluded.preEffective.count) {
        html += '<p>' + pf.excluded.preEffective.count + ' ad-sourced order' + (pf.excluded.preEffective.count === 1 ? '' : 's') + ' in this range predate the 2026-07-01 effective date ('
          + money(pf.excluded.preEffective.netMerch) + ') and earn no fee — the performance model does not apply retroactively.</p>';
      }
      if (ac.provisional && ac.provisional.isProvisional) {
        html += '<p><strong>Provisional.</strong> ' + escapeHtml(ac.provisional.note) + '</p>';
      }
      return html;
    }

    switch (key) {
      case 'contributionMargin': {
        var oh = useTrueCm && cm.totals.overhead && cm.totals.overhead.amount > 0 ? cm.totals.overhead : null;
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.14) — the agency performance fee is a
           SEPARATE line from the retainer and from rep commission. Retainer = fixed
           monthly; performance fee = 3% of ad-sourced online + 1% of ad-produced
           lead-closed net merchandise; commission = the REP fee (already inside GP). */
        var agencyComp = useTrueCm ? (cm.totals.agencyComp || null) : null;
        var perfFee = useTrueCm ? (Number(cm.totals.agencyPerformanceFee) || 0) : 0;
        var commissionTotal = useTrueCm ? (Number(cm.totals.commission) || 0) : 0;
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-17) — these were ONE number.
           totals.cardFee carried the offline customer surcharge AND the online Shopify
           cut, and this panel rendered the sum as "− Shopify fees", so money coming in
           was displayed as money going out. rfq-worker now returns them separately;
           the || cardFee fallback keeps the row honest against a worker that hasn't
           been redeployed yet. */
        var shopifyFeeTotal = useTrueCm ? (Number(cm.totals.shopifyFees) || Number(cm.totals.cardFee) || 0) : 0;
        var cardFeeTotal = useTrueCm ? (Number(cm.totals.cardFee) || 0) : 0;
        var hasSplitFees = useTrueCm && cm.totals.shopifyFees !== undefined;
        var freightChargedTotal = useTrueCm ? (Number(cm.totals.freightCharged) || 0) : 0;
        var freightCostTotal = useTrueCm ? (Number(cm.totals.freightCost) || 0) : 0;
        var freightMarginTotal = freightChargedTotal - freightCostTotal;
        var directTotal = useTrueCm ? (Number(cm.totals.directTotal) || 0) : 0;
        var costTotal = useTrueCm ? (Number(cm.totals.costTotal) || 0) : 0;
        var goodsGpTotal = directTotal - costTotal;
        var formula = useTrueCm
          ? ((oh || perfFee > 0)
              ? 'contributionMargin = (goodsGP + freightMargin + cardFee − shopifyFees − repCommission) − adSpend − agencyRetainer − agencyPerformanceFee'
              : 'contributionMargin = (goodsGP + freightMargin + cardFee − shopifyFees − repCommission) − adSpend')
          : 'contributionMargin = (revenue × marginPct/100) − adSpend';
        var srcText = useTrueCm
          ? '<p>Pulled from <strong>rfq-worker</strong> — walks every <code>lifecycle:</code> record with <code>status=\'sold\'</code> AND <code>dateSold</code> in range, plus every Shopify direct order (no rfq tag). Per-order grossProfit nets goods margin (Revenue − COGS), freight margin (charged − cost), Shopify fees, and rep commission so the final number is the cash that\'s actually available to the business after every variable per-order cost.</p>'
            + (shopifyFeeTotal > 0 ? '<p><strong>Shopify fees</strong> (~2.7% × order total + $0.30 per transaction) are a real per-order business cost. They\'re excluded from the per-order Product GP column on the Orders table (which shows GP of Goods — goods − COGS — matching the Deal Dashboard\'s GP of Goods / GP of Product) but folded in here, along with freight margin, so CM reflects true takehome.</p>' : '')
            + (Math.abs(freightMarginTotal) > 0.5 ? '<p><strong>Freight margin</strong> = what customer paid for shipping − what we paid the carrier. Usually positive (we mark up freight) but occasional supplier-quote overruns flip it negative. Either way it\'s real CM-relevant cash.</p>' : '')
            + (commissionTotal > 0 ? '<p><strong>Rep commission</strong> (variable per-order cost, set on the workstation Commission card for offline sold orders) is netted out of grossProfit upstream. Online orders carry no commission.</p>' : '')
            + (oh ? '<p>Also subtracts the <strong>agency retainer</strong> (' + escapeHtml(oh.label) + ', $' + oh.monthlyAmount + '/mo) pro-rated calendar-aware over the active range. Per-campaign CM stays clean (variable costs only); agency comp nets only at the summary layer for the true bottom-line.'
                + (oh.segments && oh.segments.length > 1 ? ' This range spans a rate change, so each side is billed at its own rate: ' + oh.segments.map(function(s) { return s.from + '→' + s.to + ' at $' + s.monthlyAmount + '/mo'; }).join(', ') + '.' : '')
                + '</p>' : '')
            /* Phase 1.14 — the fee's per-order basis, spelled out. */
            + (agencyComp ? agencyFeeProse(agencyComp) : '')
          : '<p>Estimate: total attributed revenue from Google + Meta multiplied by your configured margin %, then minus total ad spend. Used as a fallback when no sold orders exist in the active range OR when the cross-worker lifecycle aggregate is unreachable.</p>';
        var statsRows;
        if (useTrueCm) {
          // Reconstruct the math step-by-step so the explainer makes every
          // deduction visible. All deductions are already netted into
          // cm.totals.grossProfit upstream — this shows the user where
          // their dollars went on the way to CM.
          statsRows = [];
          statsRows.push(['Goods GP', '$' + Math.round(goodsGpTotal).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(revenue $' + Math.round(directTotal).toLocaleString('en-US') + ' − COGS $' + Math.round(costTotal).toLocaleString('en-US') + ')</span>']);
          if (Math.abs(freightMarginTotal) > 0.5) {
            var sign = freightMarginTotal >= 0 ? '+' : '−';
            /* CUSTOM_MARKETINGOPS_BY_CLAUDE (AIOPS 597c8b1b4638, Ryan 2026-09-10) — the parenthetical
               spelled the aggregate freight COST out in words, on a "?" tooltip with no admin check
               anywhere between it and the click that opens it. The margin itself stays for everyone
               — it is a performance figure, and it is what this row is about; only the two operands
               that reconstruct what we PAY are admin-only. */
            statsRows.push([sign + ' Freight margin', '$' + Math.round(Math.abs(freightMarginTotal)).toLocaleString('en-US')
              + (isAdminSession()
                ? ' <span style="opacity:0.6;font-weight:400">(charged $' + Math.round(freightChargedTotal).toLocaleString('en-US') + ' − cost $' + Math.round(freightCostTotal).toLocaleString('en-US') + ')</span>'
                : '')]);
          }
          /* The customer-paid card surcharge is REVENUE — it exists to offset the
             processing cut on the next row. Shown only when the worker returns the two
             separately; against an older worker the fees row absorbs both, as before. */
          if (hasSplitFees && cardFeeTotal > 0) {
            statsRows.push(['+ Card fee', '$' + Math.round(cardFeeTotal).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(customer-paid surcharge)</span>']);
          }
          if (shopifyFeeTotal > 0) {
            statsRows.push(['− Shopify fees', '$' + Math.round(shopifyFeeTotal).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(payment processing)</span>']);
          }
          if (commissionTotal > 0) {
            statsRows.push(['− Rep commission', '$' + Math.round(commissionTotal).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(offline sold orders only)</span>']);
          }
          statsRows.push(['= Gross profit (net)', '$' + Math.round(cm.totals.grossProfit).toLocaleString('en-US')]);
          statsRows.push(['− Ad spend', '$' + Math.round(cm.totals.adSpend).toLocaleString('en-US')]);
          // When overhead is present, fold it into the final CM row (the
          // headline CM tile shows the post-overhead number — "true cash
          // takehome"). Intermediate pre-overhead value still shown above
          // as "= Gross profit (net) − adSpend" via the running math.
          if (oh || perfFee > 0) {
            statsRows.push(['= CM before agency comp', '$' + Math.round(cm.totals.contributionMargin).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(variable costs only — used for per-channel ROI decisions)</span>']);
            if (oh) {
              /* A range spanning the 2026-07-01 rate change bills each side at its own
                 rate, so quoting one "$X/mo" would misstate it — list the segments. */
              var ohDetail = (oh.segments && oh.segments.length > 1)
                ? oh.segments.map(function(s) { return '$' + s.monthlyAmount + '/mo ' + s.from + '→' + s.to; }).join(' + ')
                : oh.daysInRange + ' days × $' + oh.monthlyAmount + '/mo pro-rated';
              statsRows.push(['− Agency retainer', '$' + Math.round(oh.amount).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(' + escapeHtml(ohDetail) + ')</span>']);
            }
            /* Phase 1.14 — the performance fee, and (when any exists) the portion that
               comes from Phase-G identity propagation. That portion pays MORE than the
               written agreement, deliberately, so it is never folded into one number. */
            if (agencyComp && perfFee > 0) {
              var pf = agencyComp.performanceFee;
              statsRows.push(['− Agency performance fee', '$' + Math.round(perfFee).toLocaleString('en-US')
                + ' <span style="opacity:0.6;font-weight:400">(3% × $' + Math.round(pf.base.online).toLocaleString('en-US') + ' online + 1% × $' + Math.round(pf.base.lead).toLocaleString('en-US') + ' lead-closed, net merchandise)</span>']);
              var propFee = (pf.lines.propagatedHard.fee || 0) + (pf.lines.propagatedSoftOverride.fee || 0);
              if (propFee > 0) {
                statsRows.push(['&nbsp;&nbsp;↳ of which propagated', '$' + Math.round(propFee).toLocaleString('en-US')
                  + ' <span style="opacity:0.6;font-weight:400">(' + (pf.lines.propagatedHard.count + pf.lines.propagatedSoftOverride.count) + ' repeat order'
                  + ((pf.lines.propagatedHard.count + pf.lines.propagatedSoftOverride.count) === 1 ? '' : 's') + ' matched to an earlier ad click by the same customer)</span>']);
              }
            } else if (perfFee > 0) {
              statsRows.push(['− Agency performance fee', '$' + Math.round(perfFee).toLocaleString('en-US')]);
            }
            statsRows.push(['= CM', '<strong>$' + Math.round(cm.totals.contributionMarginAfterOverhead).toLocaleString('en-US') + '</strong> <span style="opacity:0.6;font-weight:400">(what you can reinvest)</span>']);
          } else {
            statsRows.push(['= CM', '<strong>$' + Math.round(cm.totals.contributionMargin).toLocaleString('en-US') + '</strong>']);
          }
        } else {
          statsRows = [
            ['Revenue',     '$' + Math.round(t.revenue || 0).toLocaleString('en-US')],
            ['× ' + marginPct + '%', '$' + Math.round((t.revenue || 0) * marginPct / 100).toLocaleString('en-US')],
            ['− Spend',     '$' + Math.round(t.spend || 0).toLocaleString('en-US')],
            ['= CM (est.)', '$' + Math.round(((t.revenue || 0) * marginPct / 100) - (t.spend || 0)).toLocaleString('en-US')],
          ];
        }
        // Overhead is now folded into the useTrueCm rows above (CM is the
        // post-overhead "takehome" number). For the estimate-only path
        // (no sold orders in range), append overhead breakdown if present
        // so the fallback view still shows a bottom-line.
        if (oh && !useTrueCm) {
          statsRows.push(['− ' + oh.label, '$' + Math.round(oh.amount).toLocaleString('en-US') + ' <span style="opacity:0.6;font-weight:400">(' + oh.daysInRange + ' days × $' + oh.monthlyAmount + '/mo pro-rated)</span>']);
        }
        if (useTrueCm) {
          statsRows.push(['Orders', String(cm.totals.orderCount) + ' (' + cm.totals.attributedCount + ' paid-attributed)']);
        }
        var stats = kv(statsRows);
        var excluded = '<ul>'
          + '<li>$0 orders (free sample-request "purchases")</li>'
          + '<li>Cancelled orders</li>'
          + '<li>Orders before 2026-05-01 (pre-launch floor)</li>'
          + '<li>Balance/upsell child orders (rolled up into their parent\'s revenue)</li>'
          + '</ul>';
        return {
          title: useTrueCm ? 'Contribution Margin' : 'Contribution Margin (estimate)',
          body:
            section('Formula', '<div class="moph-explain__formula">' + escapeHtml(formula) + '</div>') +
            section('How it\'s computed', srcText) +
            section('This range', stats) +
            section('Excluded', excluded) +
            section('Versus ad-platform claim',
              '<p class="moph-explain__sub">Side-by-side comparison to Meta Ads Manager / Google Ads UI claims is on the roadmap. For now, the rfq-worker lifecycle aggregate is the source of truth because every Shopify order has a real grossProfit number tied to it.</p>') +
            footer(),
        };
      }
      case 'spend': {
        return {
          title: 'Total Spend',
          body:
            section('Formula', '<div class="moph-explain__formula">spend = sum of daily ad spend across linked platforms</div>') +
            section('How it\'s computed',
              '<p>Pulled nightly from <strong>Google Ads</strong> + <strong>Meta Marketing</strong> APIs (3am EST cron) into KV. Each platform reports daily spend per campaign, which the nightly sync also folds into a pre-aggregated daily rollup keyed by platform + date. The dashboard reads the rollup for snappy date-range scans; the per-campaign records back the Campaigns table + the Reinvestment Memo.</p>') +
            section('This range', kv([
              ['Total spend', '$' + Math.round(t.spend || 0).toLocaleString('en-US')],
              ['Records',     state.kpis && state.kpis.recordCount != null ? String(state.kpis.recordCount) : 'unknown'],
            ])) +
            section('Excluded',
              '<ul><li>Manual / offline ad investments (radio, print) — not tracked in this dashboard</li>'
              + '<li>Pre-launch periods before tokens were provisioned</li></ul>') +
            footer(),
        };
      }
      case 'revenue': {
        var revAttr = useTrueCm ? (Number(cm.totals.attributedCount) || 0) : null;
        var revOrders = useTrueCm ? (Number(cm.totals.orderCount) || 0) : null;
        var revGap = useTrueCm && revOrders > 0 && revAttr === 0;
        // Shopify-attributed revenue = paid (non-organic) channel directTotal.
        var revShop = 0;
        if (useTrueCm && cm.byChannel) {
          Object.keys(cm.byChannel).forEach(function(ck){ if (ck !== 'organic') revShop += Number(cm.byChannel[ck].directTotal) || 0; });
        }
        var revPlatformClaim = Number(t.revenue) || 0;
        var revRows = [];
        if (useTrueCm) {
          revRows.push(['Shopify-attributed revenue', '$' + Math.round(revShop).toLocaleString('en-US')]);
          revRows.push(['Paid-attributed orders', revAttr + ' of ' + revOrders + ' sold']);
          revRows.push(['Meta/Google CLAIM (conv. value)', '$' + Math.round(revPlatformClaim).toLocaleString('en-US')]);
          revRows.push(['Variance (truth − claim)', '$' + Math.round(revShop - revPlatformClaim).toLocaleString('en-US')]);
        } else {
          revRows.push(['Revenue (platform-claimed)', '$' + Math.round(revPlatformClaim).toLocaleString('en-US')]);
        }
        return {
          title: 'Ad Revenue',
          body:
            section('Formula', '<div class="moph-explain__formula">' + (useTrueCm
              ? 'ad revenue = Σ Shopify order revenue (directTotal) for sold orders whose paid touch maps to a paid channel'
              : 'revenue = Σ conversionValue from ad-platform reporting (fallback — no sold orders in range)') + '</div>') +
            section('How it\'s computed', useTrueCm
              ? '<p>Sums actual <strong>Shopify order revenue</strong> (goods subtotal) for every sold order in range whose ' + (state.attribution === 'firstPaid' ? 'first' : 'last') + ' paid touch — from the lead\'s UTM journey — maps to a paid channel (Google, Meta, …). This is your Shopify Admin\'s revenue, not a platform claim.</p>'
                + '<p class="moph-explain__sub">Meta + Google each <em>claim</em> a <code>conversionValue</code> from their own pixels (shown above as the variance line). For BSFS those claims are unreliable — e.g. Google reporting ~$1 per conversion when value tracking isn\'t wired to real order amounts. When the two differ, Shopify wins.</p>'
              : '<p>No sold orders in range, so we fall back to the platforms\' own claimed <code>conversionValue</code> — what Meta + Google say their ads drove, NOT Shopify truth. The number resolves to Shopify-attributed revenue once orders close in range.</p>') +
            section('This range', kv(revRows)) +
            (revGap ? section('Attribution gap', '<p class="moph-explain__sub" style="color:#b45309;"><strong>' + revOrders + ' sold orders, none mapped to a paid touch yet.</strong> Shopify-attributed paid revenue is $0 until reconciliation lands (Shopify customerJourneySummary + UTM journey + click IDs). The platforms still CLAIM $' + Math.round(revPlatformClaim).toLocaleString('en-US') + '.</p>') : '') +
            footer(),
        };
      }
      case 'roas': {
        var roasAttr = useTrueCm ? (Number(cm.totals.attributedCount) || 0) : null;
        var roasOrders = useTrueCm ? (Number(cm.totals.orderCount) || 0) : null;
        var roasGap = useTrueCm && roasOrders > 0 && roasAttr === 0;
        var roasShop = 0;
        if (useTrueCm && cm.byChannel) {
          Object.keys(cm.byChannel).forEach(function(ck){ if (ck !== 'organic') roasShop += Number(cm.byChannel[ck].directTotal) || 0; });
        }
        var roasNumLabel = useTrueCm ? 'Shopify-attributed revenue' : 'Revenue (platform-claimed)';
        var roasNumVal = useTrueCm ? roasShop : (Number(t.revenue) || 0);
        var roasVal = useTrueCm ? ((Number(t.spend) || 0) > 0 ? roasShop / (Number(t.spend) || 0) : 0) : (Number(t.roas) || 0);
        return {
          title: 'Ad ROAS',
          body:
            section('Formula', '<div class="moph-explain__formula">Ad ROAS = ' + (useTrueCm ? 'Shopify-attributed (paid) revenue' : 'platform-claimed revenue') + ' ÷ spend</div>') +
            section('How it\'s computed',
              '<p>Ad ROAS = ' + (useTrueCm ? '<strong>real Shopify revenue attributed to paid touches</strong>' : 'platform-claimed conversion value') + ' divided by total ad spend across the date range. Paid-attributed only — pair it with <strong>Total ROAS</strong> (all sales ÷ spend) to bracket the truth.</p>'
              + '<p class="moph-explain__sub">⚠️ <strong>ROAS hides margin.</strong> A 4× ROAS on a low-margin product (e.g., a $5 freight charge) can still lose money. Always cross-check against Contribution Margin (the hero KPI), which factors in COGS + ad spend together.</p>') +
            section('This range', kv([
              [roasNumLabel, '$' + Math.round(roasNumVal).toLocaleString('en-US')],
              ['÷ Spend', '$' + Math.round(t.spend || 0).toLocaleString('en-US')],
              ['= ROAS', fmtRoas(roasVal)],
            ])) +
            (roasGap ? section('Attribution gap', '<p class="moph-explain__sub" style="color:#b45309;"><strong>Numerator is $0 Shopify-attributed revenue</strong> — ' + roasOrders + ' sold orders, none paid-attributed yet. Platforms CLAIM ' + fmtRoas(t.roas) + '. Real ROAS resolves as reconciliation lands.</p>') : '') +
            footer(),
        };
      }
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-05-31) — Total Revenue / Total ROAS
         drawers, the Sheet's "all sales" counterpart to Ad Revenue / Ad ROAS. */
      case 'totalRevenue': {
        var trTotal = useTrueCm ? (Number(cm.totals.directTotal) || 0) : 0;
        var trAd = 0;
        if (useTrueCm && cm.byChannel) {
          Object.keys(cm.byChannel).forEach(function(ck){ if (ck !== 'organic') trAd += Number(cm.byChannel[ck].directTotal) || 0; });
        }
        var trOther = trTotal - trAd;
        var trShare = trTotal > 0 ? Math.round((trAd / trTotal) * 100) : 0;
        return {
          title: 'Total Revenue',
          body:
            section('Formula', '<div class="moph-explain__formula">total revenue = Σ Shopify order revenue (directTotal) for ALL sold orders in range — paid, organic, direct &amp; quote-driven</div>') +
            section('How it\'s computed', useTrueCm
              ? '<p>Every sold order\'s real Shopify revenue (goods subtotal) in range, regardless of how the customer found us. This is the BSFS Media Plan Sheet\'s <strong>Total Revenue</strong> — it pairs with <strong>Ad Revenue</strong> (paid-attributed only) so you can see what share of sales the ads are credited for.</p>'
                + '<p class="moph-explain__sub">Ad Revenue ÷ Total Revenue = <strong>' + trShare + '%</strong> of this range\'s sales are attributed to paid channels.</p>'
              : '<p>No sold orders in range yet — resolves once orders close.</p>') +
            section('This range', kv([
              ['Total revenue (all orders)', '$' + Math.round(trTotal).toLocaleString('en-US')],
              ['— Ad-attributed (paid)', '$' + Math.round(trAd).toLocaleString('en-US')],
              ['— Organic / direct / quote', '$' + Math.round(trOther).toLocaleString('en-US')],
              ['Sold orders', String(useTrueCm ? (Number(cm.totals.orderCount) || 0) : 0)],
            ])) +
            footer(),
        };
      }
      case 'totalRoas': {
        var trrTotal = useTrueCm ? (Number(cm.totals.directTotal) || 0) : 0;
        var trrSpend = Number(t.spend) || 0;
        var trrVal = trrSpend > 0 ? trrTotal / trrSpend : 0;
        return {
          title: 'Total ROAS',
          body:
            section('Formula', '<div class="moph-explain__formula">Total ROAS = total revenue (all sold orders) ÷ ad spend</div>') +
            section('How it\'s computed',
              '<p>All Shopify revenue in range ÷ total ad spend — the Sheet\'s <strong>Total ROAS</strong>. Unlike <strong>Ad ROAS</strong> (paid-attributed revenue only), this credits the ad budget with every sale, so it reads higher. The truth sits between the two: Ad ROAS <em>under</em>-credits (attribution gaps), Total ROAS <em>over</em>-credits (organic sales the ads didn\'t cause).</p>'
              + '<p class="moph-explain__sub">⚠️ Both hide margin — cross-check Contribution Margin (the hero KPI).</p>') +
            section('This range', kv([
              ['Total revenue (all orders)', '$' + Math.round(trrTotal).toLocaleString('en-US')],
              ['÷ Spend', '$' + Math.round(trrSpend).toLocaleString('en-US')],
              ['= Total ROAS', fmtRoas(trrVal)],
            ])) +
            footer(),
        };
      }
      case 'conversions': {
        return {
          title: 'Ad Conversions',
          body:
            section('Formula', '<div class="moph-explain__formula">conversions = sum of platform-reported conversion events</div>') +
            section('How it\'s computed',
              '<p>Each ad platform reports a count of <strong>conversion events</strong> attributed to its campaigns (purchases for Google/Meta by default; configurable via the conversion-action allowlist in worker secrets).</p>'
              + '<p class="moph-explain__sub">This is the platform\'s <strong>claimed</strong> count — NOT Shopify orders. A single Shopify order may register as 1, multiple, or zero conversions depending on how the platform pixel + your conversion config interpret it, so it both <strong>over- and under-claims</strong> vs real sales and may include <strong>$0-value non-purchase events</strong> (page views, add-to-cart, leads). It also <strong>lags</strong> (same-day it often reads 0 while real sales have already landed). The <strong>Orders</strong> tile + Recent Sales card are the Shopify ground truth.</p>') +
            section('Why it won\'t match Orders 1:1 (attribution window)',
              '<p>Beyond over/under-counting, the two columns run on <strong>different clocks</strong>. Platform conversions use an <strong>attribution window</strong> (commonly <strong>90-day click</strong>): a sale from a click weeks or months ago still gets credited — and booked back to the <strong>click date</strong>, not the sale date. The <strong>Orders</strong> column counts Shopify orders by their <strong>actual sale date</strong> in this range.</p>'
              + '<p class="moph-explain__sub">With a long sales cycle the skew is large — a 90-day window catches deals that close weeks later and books them against the original click month. So even when nothing is wrong, Ad Conversions and Orders won\'t reconcile 1:1.</p>') +
            section('This range', kv([['Conversions', fmtInt(t.conversions)]])) +
            footer(),
        };
      }
      case 'orders': {
        var ordTotal = (useTrueCm && cm.totals) ? (Number(cm.totals.orderCount) || 0) : 0;
        var ordAttrib = (useTrueCm && cm.totals) ? (Number(cm.totals.attributedCount) || 0) : 0;
        return {
          title: 'Orders',
          body:
            section('Formula', '<div class="moph-explain__formula">orders = count of sold Shopify orders in range (every channel)</div>') +
            section('How it\'s computed',
              '<p>Real <strong>Shopify sold orders</strong> with <code>dateSold</code> in the active range — every channel: paid, organic, direct, inbound, AND <strong>rep-sourced</strong> (sales-team deals). This is the Shopify ground truth: the actual count of sales, and the truth behind the platform-claimed <strong>Ad Conversions</strong> tile.</p>'
              + '<p class="moph-explain__sub">Not all of these are ad-driven — a large rep-sourced or organic order counts here without any ad involvement. Of the total, <strong>' + ordAttrib + '</strong> carried a paid touch (that subset is the CAC denominator). Pairs with Ad Conversions, which is the platform pixel count (lags + under-counts vs this).</p>') +
            section('This range', kv([
              ['Sold orders (all channels)', String(ordTotal)],
              ['— of which paid-attributed', String(ordAttrib)],
            ])) +
            footer(),
        };
      }
      case 'cac': {
        var cacOrders = (useTrueCm && cm.totals) ? (Number(cm.totals.attributedCount) || 0) : 0;
        var cacSpend = Number(t.spend) || 0;
        var cacVal = cacOrders > 0 ? cacSpend / cacOrders : null;
        return {
          title: 'Customer Acquisition Cost (CAC)',
          body:
            section('Formula', '<div class="moph-explain__formula">CAC = ad spend ÷ converted orders (paid-attributed)</div>') +
            section('How it\'s computed',
              '<p>Total ad spend divided by the number of <strong>sold Shopify orders attributed to a paid touch</strong> in the range (per the Attribution selector). True cost-to-acquire-a-customer — not cost-per-lead.</p>'
              + '<p class="moph-explain__sub">The denominator is acquired customers (real Shopify orders), NOT platform conversion events. Orders with no paid touch in their journey aren\'t counted, so CAC reflects only ad-driven customers. Shows "—" until at least one paid-attributed order closes in range.</p>') +
            section('This range', kv([
              ['Ad spend',                 '$' + Math.round(cacSpend).toLocaleString('en-US')],
              ['÷ Paid-attributed orders', String(cacOrders)],
              ['= CAC',                    cacVal != null ? fmtMoney(cacVal) : '—'],
            ])) +
            footer(),
        };
      }
      case 'poolTarget': {
        return {
          title: 'Pool Target',
          body:
            section('Formula', '<div class="moph-explain__formula">poolTarget = Σ (channel budget in force that month × overlap_days_with_range / days_in_month)</div>') +
            section('How it\'s computed',
              '<p>The monthly budget set for this channel in Pool settings on the Budget Plan page, pro-rated for the active date range. Each month uses the budget in force at that month&rsquo;s end (every change is recorded in the budget history).</p>'
              + '<p class="moph-explain__sub">Only visible when the toolbar\'s "📋 Show plan" toggle is on. Reads the budget history from <code>/budget/ledger</code>. Campaigns have no target of their own: budgets are set per channel.</p>') +
            section('Excluded',
              '<ul><li>Months before budgets were first recorded (May 2026) show no target</li></ul>') +
            footer(),
        };
      }
      case 'variance': {
        return {
          title: 'Variance vs Plan',
          body:
            section('Formula', '<div class="moph-explain__formula">variance% = (actual_spend − pool_target) / pool_target × 100</div>') +
            section('How it\'s computed',
              '<p>How much over (positive) or under (negative) you\'re spending versus the planned pool target for this channel + date range.</p>'
              + '<ul><li><strong>Green</strong>: you\'re spending less than planned by &gt;5% (under-pacing)</li>'
              + '<li><strong>Red</strong>: you\'re spending more than planned by &gt;5% (over-pacing)</li>'
              + '<li><strong>Gray</strong>: within ±5% of plan (on pace)</li></ul>'
              + '<p class="moph-explain__sub">Mid-month, expect variance to track time-passed % — being at 50% variance halfway through the month is on-pace, not under-pacing.</p>') +
            footer(),
        };
      }
      case 'net': {
        return {
          title: 'Net (Revenue − Spend)',
          body:
            section('Formula', '<div class="moph-explain__formula">net = revenue − spend</div>') +
            section('How it\'s computed',
              '<p>Straight subtraction of channel ad spend from channel attributed revenue. <strong>Doesn\'t include COGS</strong> — so a $1000 net on low-margin SKUs may still lose money once cost of goods is factored.</p>'
              + '<p class="moph-explain__sub">For the truth-anchored profitability view, see <strong>Contribution Margin</strong> (the hero KPI) — that one nets COGS + freight + fees + ad spend together.</p>') +
            footer(),
        };
      }
      case 'impressions': {
        return {
          title: 'Impressions',
          body:
            section('Formula', '<div class="moph-explain__formula">impressions = sum of platform-reported ad views</div>') +
            section('How it\'s computed',
              '<p>The count of times your ads were rendered to a user (whether or not they clicked). Reported by each platform\'s API and summed across the date range.</p>'
              + '<p class="moph-explain__sub">Useful for awareness-stage diagnostics (high impressions but no clicks → wrong audience or weak creative). Not a conversion metric on its own.</p>') +
            footer(),
        };
      }
      case 'cpc': {
        var cpcVal = _effClicks > 0 ? _effSpend / _effClicks : null;
        return {
          title: 'Cost Per Click (CPC)',
          body:
            section('Formula', '<div class="moph-explain__formula">CPC = total ad spend ÷ total clicks</div>') +
            section('How it\'s computed',
              '<p>Total ad spend divided by total clicks (Google + Meta), summed over the date range — the price you pay for each visit to the site.</p>'
              + '<p class="moph-explain__sub">Both spend and clicks are platform-reported, so this reconciles directly against the Google Ads / Meta Ads Manager UIs — the least-ambiguous efficiency metric. Respects the platform filter.</p>') +
            section('This range', kv([
              ['Ad spend', '$' + Math.round(_effSpend).toLocaleString('en-US')],
              ['÷ Clicks', _effClicks.toLocaleString('en-US')],
              ['= CPC',    cpcVal != null ? fmtMoney(cpcVal) : '—'],
            ])) +
            footer(),
        };
      }
      case 'cpm': {
        var cpmVal = _effImpr > 0 ? _effSpend / _effImpr * 1000 : null;
        return {
          title: 'Cost Per Mille (CPM)',
          body:
            section('Formula', '<div class="moph-explain__formula">CPM = total ad spend ÷ impressions × 1,000</div>') +
            section('How it\'s computed',
              '<p>What it costs to show your ads 1,000 times — total spend ÷ impressions, ×1,000. The core awareness/efficiency metric, especially on Meta.</p>'
              + '<p class="moph-explain__sub">Impressions are platform-reported ad views (whether or not clicked). Rising CPM at steady targeting usually means more auction competition or audience fatigue. Respects the platform filter.</p>') +
            section('This range', kv([
              ['Ad spend',        '$' + Math.round(_effSpend).toLocaleString('en-US')],
              ['÷ Impressions',   _effImpr.toLocaleString('en-US')],
              ['= CPM (× 1,000)', cpmVal != null ? fmtMoney(cpmVal) : '—'],
            ])) +
            footer(),
        };
      }
      case 'cpa': {
        var cpaVal = _effConv > 0 ? _effSpend / _effConv : null;
        return {
          title: 'Cost Per Acquisition (CPA)',
          body:
            section('Formula', '<div class="moph-explain__formula">CPA = total ad spend ÷ Ad Conversions (platform pixel)</div>') +
            section('How it\'s computed',
              '<p>Total ad spend divided by <strong>platform-claimed Ad Conversions</strong> (Google + Meta pixel events) over the range — the platforms\' own cost-per-conversion, which reconciles against their UIs.</p>'
              + '<p class="moph-explain__sub">⚠ The denominator is the platform pixel count, NOT real Shopify orders — platforms over/under-count and the pixel includes non-purchase events. For cost per REAL acquired customer (spend ÷ Shopify paid-attributed orders), use <strong>CAC</strong> on the Channel + Campaign tables.</p>') +
            section('This range', kv([
              ['Ad spend',         '$' + Math.round(_effSpend).toLocaleString('en-US')],
              ['÷ Ad Conversions', _effConv.toLocaleString('en-US')],
              ['= CPA',            cpaVal != null ? fmtMoney(cpaVal) : '—'],
            ])) +
            footer(),
        };
      }
      case 'hoursSaved': {
        var h = computeHoursSaved();
        var rangeWords = (function() {
          switch (state.rangeKey) {
            case 'today': return 'today';
            case 'year': return 'this year';
            case 'quarter': return 'this quarter';
            case 'month': return 'this month';
            case 'week': return 'this week';
            case 'lastyear': return 'last year';
            case 'lastquarter': return 'last quarter';
            case 'lastmonth': return 'last month';
            case 'lastweek': return 'last week';
            case 'last30': return 'the last 30 days';
            case 'last90': return 'the last 90 days';
            case 'last180': return 'the last 180 days';
            case 'all': return 'since launch (May 1)';
            default: return 'this range';
          }
        })();
        var totalDisplay = h.hours < 1
          ? Math.round(h.minutes) + ' minutes'
          : (h.hours < 10 ? h.hours.toFixed(1).replace(/\.0$/, '') + ' hours' : Math.round(h.hours).toLocaleString('en-US') + ' hours');

        // Per-category breakdown rows. Skip rows where units = 0 so we don't
        // padding the breakdown with 0-min line items that look like noise.
        var rowsHtml = h.rows.filter(function(r) { return r.units > 0 && r.minutes > 0; }).map(function(r) {
          var hrs = r.minutes / 60;
          var hoursDisplay = hrs < 1 ? Math.round(r.minutes) + ' min' : (hrs.toFixed(1).replace(/\.0$/, '') + ' hrs');
          var unitDisplay  = r.unitName === 'wk'
            ? r.units.toFixed(1).replace(/\.0$/, '') + ' wks × ' + r.perUnit + ' min'
            : Math.round(r.units).toLocaleString('en-US') + ' ' + r.unitName + (r.units === 1 ? '' : 's') + ' × ' + r.perUnit + ' min';
          return '<div class="moph-explain__hours-row">' +
            '<div class="moph-explain__hours-row-head">' +
              '<span class="moph-explain__hours-row-label">' + escapeHtml(r.label) + '</span>' +
              '<span class="moph-explain__hours-row-value">' + escapeHtml(hoursDisplay) + '</span>' +
            '</div>' +
            '<div class="moph-explain__hours-row-math">' + escapeHtml(unitDisplay) + '</div>' +
            '<div class="moph-explain__hours-row-why">' + escapeHtml(r.why) + '</div>' +
          '</div>';
        }).join('');

        return {
          title: 'Hours saved ' + rangeWords,
          body:
            section('What this measures',
              '<p>An estimate of the manual work the Paid Ads dashboard returns to you ' + escapeHtml(rangeWords) + ' versus the pre-tool baseline. Each line below maps to a workflow the agency genuinely did by hand before this dashboard existed — not a vanity metric.</p>') +
            section('Total this range',
              '<div class="moph-explain__formula" style="font-size:18px;font-weight:700;color:var(--moph-blue);text-align:center;">~ ' + escapeHtml(totalDisplay) + '</div>') +
            section('Breakdown', rowsHtml || '<p class="moph-explain__sub">No automatable activity yet in this range — try a wider date filter.</p>') +
            section('What\'s NOT counted yet',
              '<ul>'
              + '<li><strong>Cross-channel attribution debugging</strong> — when a customer touched 4 ads across 2 platforms, the manual untangle. On the roadmap once the journey explorer ships.</li>'
              + '<li><strong>Campaign creative iteration</strong> — the Paid Ads dashboard surfaces the data; the creative call is still human work.</li>'
              + '<li><strong>Stakeholder explanations</strong> — every "why is ROAS down?" Slack reply now has a Show-Your-Work drawer to point at, but the conversation itself isn\'t timed here.</li>'
              + '</ul>') +
            footer(),
        };
      }
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE — chart-only metrics (CTR / CVR / Leads /
         Total Conversions) get their own Show-Your-Work panes so the chart's
         universal "?" opens a drawer for every tab. */
      case 'ctr': {
        var ctrVal = _effImpr > 0 ? _effClicks / _effImpr : null;
        return {
          title: 'Click-Through Rate (CTR)',
          body:
            section('Formula', '<div class="moph-explain__formula">CTR = clicks ÷ impressions</div>') +
            section('How it\'s computed',
              '<p>The share of ad views that earned a click — platform-reported clicks ÷ impressions. On the chart it\'s a true per-day line; the figure below is the blended rate across the range.</p>'
              + '<p class="moph-explain__sub">Low CTR = a creative / targeting problem — the ad isn\'t compelling enough to earn the click. It\'s an upper-funnel signal; pair it with CVR (does the click convert?) to separate a creative problem from a landing-page / offer one. Respects the platform filter.</p>') +
            section('This range', kv([
              ['Clicks', _effClicks.toLocaleString('en-US')],
              ['÷ Impressions', _effImpr.toLocaleString('en-US')],
              ['= CTR', ctrVal != null ? (ctrVal * 100).toFixed(2) + '%' : '—'],
            ])) +
            footer(),
        };
      }
      case 'cvr': {
        var _cvrOrders = (useTrueCm && cm.totals) ? (Number(cm.totals.attributedCount) || 0) : 0;
        var cvrVal = _effClicks > 0 ? _cvrOrders / _effClicks : null;
        return {
          title: 'Click → Sale Conversion Rate (CVR)',
          body:
            section('Formula', '<div class="moph-explain__formula">CVR = Shopify paid-attributed orders ÷ ad clicks (cumulative)</div>') +
            section('How it\'s computed',
              '<p>Of the people who clicked an ad, the share who ultimately bought — real Shopify <strong>paid-attributed orders</strong> ÷ ad clicks. The chart plots this <strong>cumulatively</strong>: a sale today came from a click weeks ago (BSF\'s cycle runs long), so a same-day CVR would be noise. The last chart point equals the period\'s true rate.</p>'
              + '<p class="moph-explain__sub">Healthy CTR but low CVR = the click lands but doesn\'t convert → a landing-page / offer / audience-quality problem, not a creative one. The numerator is Shopify truth, so it\'s subject to the ~46% attribution gap — read it as a lower bound.</p>') +
            section('This range', kv([
              ['Paid-attributed orders', _cvrOrders.toLocaleString('en-US')],
              ['÷ Clicks', _effClicks.toLocaleString('en-US')],
              ['= CVR', cvrVal != null ? (cvrVal * 100).toFixed(2) + '%' : '—'],
            ])) +
            footer(),
        };
      }
      case 'inquiries': {
        var iq = state.inquiries;
        var iqTot = (iq && iq.totals && iq.totals.total) || 0;
        var ex = (iq && iq.excluded) || {};
        var bySrc = (iq && iq.totals && iq.totals.bySource) || {};
        var srcRows = Object.keys(bySrc).sort(function(a, b) { return bySrc[b] - bySrc[a]; })
          .map(function(s) { return [(iq && iq.sourceLabels && iq.sourceLabels[s]) || s, String(bySrc[s])]; });
        return {
          title: 'Leads (inbound inquiries)',
          body:
            section('Formula', '<div class="moph-explain__formula">Leads = inbound inquiries counted on the day they arrived, by source</div>') +
            section('What this measures',
              '<p>Every inbound lead counted on the day it <em>arrived</em> across the range, whether or not it has converted to a sale yet. This is the <strong>leading indicator</strong>: lead volume moves weeks before the sales it drives, and (unlike attributed revenue) it isn\'t subject to the ~46% order-attribution gap. Bars stack by source; the line overlays sold orders (right axis).</p>') +
            section('In range, by source', srcRows.length ? kv([['Total leads', String(iqTot)]].concat(srcRows)) : '<p class="moph-explain__sub">No leads in this range.</p>') +
            section('What\'s excluded',
              '<p class="moph-explain__sub">CFA outbound prospecting (' + (ex.cfaProspects || 0) + ') and rep-entered outbound (' + (ex.outboundManualRep || 0) + ') — not inbound demand. Online checkouts that never filed an inquiry create no lead record, so they\'re absent by construction. <strong>Dead leads ARE counted</strong> — captured at intake, so a past day\'s bar never shrinks as leads later die. Source is the original capture-time source; bucketed on the account-local day (' + escapeHtml((iq && iq.tz) || 'America/Denver') + ') so the bars line up with the sold-order line.</p>') +
            footer(),
        };
      }
      case 'totalConversions': {
        var _tcOrders = (useTrueCm && cm.totals) ? (Number(cm.totals.orderCount) || 0) : 0;
        return {
          title: 'Total Conversions (Shopify orders)',
          body:
            section('Formula', '<div class="moph-explain__formula">Total Conversions = all Shopify sold orders per day (every channel)</div>') +
            section('How it\'s computed',
              '<p>The real count of Shopify sold orders per day — <strong>every channel</strong> (paid, organic, direct, inbound, rep-sourced), straight from the lifecycle records. This is the truth counterpart to the platform-claimed <strong>Ad Conv.</strong> tab.</p>'
              + '<p class="moph-explain__sub">Unlike Ad Conv. (Google/Meta pixel events, which over/under-count and include non-purchase events), this counts actual orders. It\'s broader than paid — it includes sales the ads didn\'t drive — so pair it with Ad Conv. to bracket how much of demand is ad-driven.</p>') +
            section('This range', kv([
              ['Shopify sold orders', _tcOrders.toLocaleString('en-US')],
            ])) +
            footer(),
        };
      }
      default:
        return { title: 'Metric', body: '<p>No explainer configured for this metric yet.</p>' };
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-09-29) — the Ad ROAS break-even + Goal for the range.
     The worker works the break-even out ONCE (marketing-worker lib/break-even.js, returned on
     /dashboard/contribution-margin as `breakEven`): revenue ÷ gross profit over AD-DRIVEN sales,
     all sales only when the range has none — the same figure the Budget Plan judges against.
     ⛔ Do not recompute it here from state.cm.totals: that was the all-sales margin, which held the
     ads to a margin they never earned (rep-discounted deals mostly are not ad-driven).
     Goal = break-even × the Theme setting's multiple (default 2). { be: 0, goal: 0 } hides both. */
  function breakEvenLines() {
    var b = state.cm && state.cm.breakEven;
    var be = b && Number(b.breakEvenRoas) > 0 ? Number(b.breakEvenRoas) : 0;
    var mult = (window.MOPH_CONFIG && Number(window.MOPH_CONFIG.targetRoasMultiple)) || 2;
    var goal = be * mult;
    if (!(goal > 0) || goal > 50) return { be: 0, goal: 0, basis: null }; // sanity guard
    return { be: be, goal: goal, basis: b.basis || null };
  }

  function openExplainerDrawer(key) {
    var data = explainerFor(key);
    document.getElementById('mophExplainTitle').textContent = data.title;
    document.getElementById('mophExplainBody').innerHTML = data.body;
    document.getElementById('mophExplain').hidden = false;
    bindCompOverrideButtons();
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.14) — invoice review, in place. Phase-G
     company-level matches are excluded from the agency fee by default; ruling one in
     is a money decision, so it's admin-only and each click states the dollars it adds.
     Bound after every drawer render because the body is replaced wholesale. */
  function bindCompOverrideButtons() {
    document.querySelectorAll('#mophExplainBody [data-comp-override]').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        var ref = btn.getAttribute('data-comp-override');
        var include = btn.getAttribute('data-include') === 'true';
        var amount = btn.getAttribute('data-amount') || '';
        if (!window.confirm((include ? 'Pay the agency on ' : 'Remove agency credit for ')
          + ref + (amount ? ' (' + amount + ')' : '') + '?\n\nThis changes what is invoiced.')) return;
        btn.disabled = true;
        try {
          await mwPost('/dashboard/agency-comp-override', { ref: ref, include: include, note: 'ruled at invoice review' });
          // Re-pull just the CM aggregate so the fee, the tile and the drawer all
          // move together — a stale drawer beside a new total is how disputes start.
          var _r = dateRange();
          state.cm = await mwFetch('/dashboard/contribution-margin?from=' + _r.from + '&to=' + _r.to
            + '&attribution=' + (state.attribution || 'firstPaid'));
          renderKpis();
          renderNonMktNote();
          openExplainerDrawer('contributionMargin'); // re-render with the new ruling
        } catch (err) {
          btn.disabled = false;
          window.alert('Could not save the ruling: ' + (err && err.message ? err.message : 'unknown error'));
        }
      });
    });
  }
  function closeExplainerDrawer() {
    document.getElementById('mophExplain').hidden = true;
  }

  // Shopify shopping-bag SVG — same inline icon Quote Dashboard uses on the
  // Online Orders tab's REF column (dashboard-main.js #2960). Prepended to
  // the 'online' source badge so Shopify-direct orders visually match the
  // existing rep-portal convention.
  function shopifyLogoSvg() {
    return '<svg width="11" height="13" viewBox="0 0 23 26" xmlns="http://www.w3.org/2000/svg" style="vertical-align:-2px;margin-right:4px;" aria-label="Shopify online order">'
      + '<path fill="#95BF47" d="M19.486 4.667c-.012-.092-.087-.142-.15-.142-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149c-.112-.111-.337-.077-.424-.052-.013.004-.23.071-.591.183-.358-1.027-.987-1.97-2.094-1.97-.03 0-.062.001-.094.003C13.291 1.097 12.91.95 12.578.95c-2.55 0-3.768 3.179-4.151 4.795-.991.306-1.696.524-1.785.553-.553.173-.572.19-.644.711-.054.394-1.5 11.541-1.5 11.541l11.262 2.108L21.69 19.34s-2.193-14.583-2.204-14.673z"/>'
      + '<path fill="#5E8E3E" d="M19.337 4.525c-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149a.262.262 0 0 0-.149-.07v18.95l5.027-1.083s-2.193-14.583-2.204-14.673c-.012-.092-.087-.142-.15-.142z"/>'
      + '<path fill="#FFFFFF" d="M13.482 8.247l-.652 1.927c-.385-.205-.836-.327-1.342-.327-1.082 0-1.143.677-1.143.847 0 .929 2.422 1.286 2.422 3.461 0 1.711-1.085 2.812-2.546 2.812-1.751 0-2.647-1.092-2.647-1.092l.469-1.553s.921.79 1.7.79c.508 0 .714-.4.714-.694 0-1.211-1.987-1.265-1.987-3.255 0-1.674 1.205-3.295 3.633-3.295.937.001 1.379.32 1.379.32z"/>'
      + '</svg>';
  }

  // Human-readable label for the active state.rangeKey — used in hint
  // strings like "16 orders sold in this month".
  function rangeLabel() {
    switch (state.rangeKey) {
      case 'today':       return 'today';
      case 'year':        return 'this year';
      case 'quarter':     return 'this quarter';
      case 'month':       return 'this month';
      case 'week':        return 'this week';
      case 'lastyear':    return 'last year';
      case 'lastquarter': return 'last quarter';
      case 'lastmonth':   return 'last month';
      case 'lastweek':    return 'last week';
      case 'last30':      return 'the last 30 days';
      case 'last90':      return 'the last 90 days';
      case 'last180':     return 'the last 180 days';
      case 'all':         return 'all time';
      case 'custom':
        return state.customFrom && state.customTo
          ? (state.customFrom + ' to ' + state.customTo)
          : 'custom range';
      default:            return 'the selected range';
    }
  }

  function fmtMoney(n, opts) {
    if (n == null || isNaN(n)) return '—';
    var abs = Math.abs(n);
    // Compact = whole-dollar, no cents, regardless of magnitude.
    // Tables + KPI tiles always use compact so $600 and $5,660 both render
    // consistently (previously $600 would show as $600.00 due to a >=1000
    // threshold). Non-compact callers (drawer detail, formulae) keep cents.
    if (opts && opts.compact) return (n < 0 ? '-' : '') + '$' + Math.round(abs).toLocaleString('en-US');
    return (n < 0 ? '-' : '') + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function fmtInt(n) { return n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString('en-US'); }
  function fmtRoas(n) { return n == null || isNaN(n) || !isFinite(n) ? '—' : n.toFixed(2) + '×'; }

  // ─── Plan overlay helpers ──────────────────────────────────────────────
  // When the agency toggles "Show plan" on, we lazy-load /budget/year for
  // the year of the date range. The plan target gets pro-rated across
  // months that overlap the active range — partial months count by their
  // overlap-day-count ratio. Pool-level totals (channel breakdown) work
  // day 1; per-campaign Target requires the plan campaignId to match the
  // platform campaignId (linking gap — not all rows will have a target).
  async function loadPlanIfNeeded() {
    if (!state.showPlan) return;
    var range = dateRange();
    var year = range.from.slice(0, 4);
    if (state.planYear && state.planYear.year === year) return;
    try {
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.16, 2026-09-27) — the plan is now the CHANNEL
         budget in force each month (the Budget Plan's ledger). Per-campaign targets were
         retired with the tiers; the old /budget/year records are frozen and would read stale. */
      var led = await mwFetch('/budget/ledger');
      state.planYear = { year: year, ledger: (led && led.entries) || [] };
    } catch (err) {
      console.warn('[marketing-ops] plan load failed', err);
      state.planYear = null;
    }
  }

  function daysOverlapWithMonth(fromStr, toStr, year, month1Indexed) {
    var from = new Date(fromStr + 'T00:00:00Z');
    var to = new Date(toStr + 'T00:00:00Z');
    var monthStart = new Date(Date.UTC(year, month1Indexed - 1, 1));
    var monthEnd = new Date(Date.UTC(year, month1Indexed, 0)); // last day of month
    var oStart = monthStart > from ? monthStart : from;
    var oEnd   = monthEnd < to ? monthEnd : to;
    if (oStart > oEnd) return 0;
    return Math.round((oEnd - oStart) / 86400000) + 1;
  }
  function daysInMonth(year, month1Indexed) {
    return new Date(Date.UTC(year, month1Indexed, 0)).getUTCDate();
  }

  // Sum planCampaign.months[mm].targetMonthly weighted by month overlap with range.
  function targetForCampaignInRange(planCampaign, fromStr, toStr) {
    if (!planCampaign || !planCampaign.months) return null;
    var year = parseInt(fromStr.slice(0, 4), 10);
    var total = 0;
    for (var m = 1; m <= 12; m++) {
      var mm = String(m).padStart(2, '0');
      var rec = planCampaign.months[mm];
      if (!rec) continue;
      if (rec.paused) continue;
      var ratio = daysOverlapWithMonth(fromStr, toStr, year, m) / daysInMonth(year, m);
      total += (rec.targetMonthly || 0) * ratio;
    }
    return total;
  }

  // Aggregate plan target for a channel (pool-level), independent of any
  // per-campaign linking — sums every plan campaign's prorated target where
  // channel matches. 'google-ads' platform → 'google' plan channel.
  function planChannelKey(platform) {
    if (platform === 'google-ads') return 'google';
    if (platform === 'meta') return 'meta';
    if (platform === 'microsoft') return 'microsoft';
    return platform;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — sticky channel color + label, one source of
     truth so adding a platform (Microsoft 2026-06-09; TikTok next) is one edit
     here, not N inline ternaries. Replaces the old binary google/meta ternaries
     that mislabeled any third platform as "Meta". */
  function platformDotClass(platform) {
    if (platform === 'google-ads') return 'moph-platform-dot--google';
    if (platform === 'meta') return 'moph-platform-dot--meta';
    if (platform === 'microsoft') return 'moph-platform-dot--microsoft';
    if (platform === 'openai') return 'moph-platform-dot--openai';
    return '';
  }
  function platformDisplayLabel(platform) {
    if (platform === 'google-ads') return 'Google';
    if (platform === 'meta') return 'Meta';
    if (platform === 'microsoft') return 'Microsoft';
    if (platform === 'openai') return 'OpenAI';
    return platform;
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — channel fill colors for the stacked-area spend
     view, matching the .moph-platform-dot swatches exactly (Google blue / Meta
     magenta / Microsoft gold / OpenAI purple). One edit when a platform is added. */
  function channelColorHex(platform) {
    if (platform === 'google-ads') return '#4285F4';
    if (platform === 'meta') return '#E1306C';
    if (platform === 'microsoft') return '#EAB308';
    if (platform === 'openai') return '#8B5CF6'; // medium purple (Ryan 2026-09-28; was near-black)
    return '#9ca3af';
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-07-08) — channel-key → colored dot +
     display label, the single source of truth for the Sales + Leads tables and the
     Sales channel filter. Keyed by the utm CHANNEL the order/lead rows carry
     ('google'/'meta'/'bing'/'microsoft'/'openai'), so EVERY paid channel gets its
     swatch — Google blue, Meta magenta, Microsoft/Bing gold, OpenAI near-black —
     instead of only Google + Meta (everything else fell through to gray). cls===''
     means organic/unknown, so the caller keeps its gray "Organic"/capitalized text.
     Colors are defined once in .moph-platform-dot--* (rep-paid-ads.liquid). */
  function channelDot(channelKey) {
    var k = String(channelKey || '').toLowerCase();
    if (k === 'google') return { cls: 'moph-platform-dot--google', label: 'Google' };
    if (k === 'meta') return { cls: 'moph-platform-dot--meta', label: 'Meta' };
    if (k === 'bing' || k === 'microsoft') return { cls: 'moph-platform-dot--microsoft', label: 'Microsoft' }; // display 'bing' as "Microsoft" for consistency (Ryan 2026-07-08)
    if (k === 'openai' || k === 'chatgpt') return { cls: 'moph-platform-dot--openai', label: 'OpenAI' };
    return { cls: '', label: '' };
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.16) — channel budget for a date range: each
     overlapping month's budget (the ledger entry in force at that month's end, or now for
     the current month) pro-rated by overlap days. Null before budgets were recorded. */
  function ledgerBudgetAt(iso) {
    var found = null, led = (state.planYear && state.planYear.ledger) || [];
    for (var i = 0; i < led.length; i++) { if (led[i].at <= iso) found = led[i].after; }
    return found;
  }
  function totalChannelPlanForRange(platform, fromStr, toStr) {
    if (!state.planYear || !Array.isArray(state.planYear.ledger)) return null;
    var key = planChannelKey(platform);
    var y0 = parseInt(fromStr.slice(0, 4), 10), y1 = parseInt(toStr.slice(0, 4), 10);
    var nowIso = new Date().toISOString(), total = 0, any = false;
    for (var y = y0; y <= y1; y++) {
      for (var m = 1; m <= 12; m++) {
        var overlap = daysOverlapWithMonth(fromStr, toStr, y, m);
        if (!overlap) continue;
        var endIso = new Date(Date.UTC(y, m, 0, 23, 59, 59)).toISOString();
        var b = ledgerBudgetAt(endIso < nowIso ? endIso : nowIso);
        if (!b) continue;
        any = true;
        total += (Number(b[key]) || 0) * overlap / daysInMonth(y, m);
      }
    }
    return any ? total : null;
  }
  function findPlanCampaign(platformCampaignId) {
    // Campaign-level targets were retired 2026-09-27 (budgets are per channel): planYear no
    // longer carries campaigns, so this returns null and the Target column reads "—".
    if (!state.planYear || !Array.isArray(state.planYear.campaigns)) return null;
    var id = String(platformCampaignId);
    // Phase 4 linking: prefer linkedCampaignIds (explicit mapping the agency
    // set in Plan View). Fall back to string-equality on the plan campaignId
    // for the case where the agency named their plan record after the
    // platform ID directly.
    for (var i = 0; i < state.planYear.campaigns.length; i++) {
      var pc = state.planYear.campaigns[i];
      if (Array.isArray(pc.linkedCampaignIds) && pc.linkedCampaignIds.indexOf(id) >= 0) return pc;
    }
    for (var j = 0; j < state.planYear.campaigns.length; j++) {
      if (String(state.planYear.campaigns[j].campaignId) === id) return state.planYear.campaigns[j];
    }
    return null;
  }
  function variancePct(actual, target) {
    if (!target || target === 0) return null;
    return ((actual - target) / target) * 100;
  }
  function fmtVariance(pct) {
    if (pct == null || isNaN(pct) || !isFinite(pct)) return '<span class="moph-variance--none">—</span>';
    var sign = pct > 0 ? '+' : '';
    var cls = pct > 5 ? 'moph-variance--over' : (pct < -5 ? 'moph-variance--under' : 'moph-variance--none');
    return '<span class="' + cls + '">' + sign + pct.toFixed(1) + '%</span>';
  }

  // ─── Data Health strip ──────────────────────────────────────────────────
  async function loadHealth() {
    var el = document.getElementById('mophHealth');
    try {
      var data = await mwFetch('/dashboard/data-health');
      state.health = data;
      renderHealth(data);
    } catch (err) {
      el.innerHTML = '<div class="moph-health__row"><span class="moph-health__dot moph-health__dot--red"></span><span class="moph-health__label">Data health check failed: ' + escapeHtml(err.message) + '</span></div>';
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-09) — health dot uses the channel color
     when the sync is HEALTHY (green) so the strip reads like the channel legend;
     amber/red still take over on warning/break so the "turns red when broken"
     alarm survives. Monday Memo has no channel → hollow, theme-matched. */
  /* (Ryan 2026-10-07) A healthy sync wears the channel's logo (.moph-platform-dot--*), the same mark
     as the tables; amber / red still replace it on a warning or break so the alarm survives. Was a
     coloured dot (OpenAI a hollow ring). */
  function healthDotClass(status, platform) {
    return status.color === 'green' ? 'moph-platform-dot moph-platform-dot--' + platform : 'moph-health__dot--' + status.color;
  }
  function renderHealth(data) {
    var rows = [];
    var googleStatus = statusFor(data.secretsConfigured.googleAds, data.lastNightlySync, 'Google');
    rows.push('<div class="moph-health__row"><span class="moph-health__dot ' + healthDotClass(googleStatus, 'google') + '"></span><span class="moph-health__label">Google:</span><span class="moph-health__sub">' + escapeHtml(googleStatus.text) + '</span></div>');
    var metaStatus = statusFor(data.secretsConfigured.meta, data.lastNightlySync, 'Meta');
    rows.push('<div class="moph-health__row"><span class="moph-health__dot ' + healthDotClass(metaStatus, 'meta') + '"></span><span class="moph-health__label">Meta:</span><span class="moph-health__sub">' + escapeHtml(metaStatus.text) + '</span></div>');
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-09) — Microsoft Ads (Bing) sync status */
    var microsoftStatus = statusFor(data.secretsConfigured.microsoft, data.lastNightlySync, 'Microsoft');
    rows.push('<div class="moph-health__row"><span class="moph-health__dot ' + healthDotClass(microsoftStatus, 'microsoft') + '"></span><span class="moph-health__label">Microsoft:</span><span class="moph-health__sub">' + escapeHtml(microsoftStatus.text) + '</span></div>');
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-24) — OpenAI / ChatGPT Ads sync
       status takes over the old Monday Memo slot's neutral "hollow" theme-matched dot.
       Keyed to the worker's secretsConfigured.openai (set once the OpenAI adapter +
       credentials land); reads "Token not yet provisioned" until the feed is connected,
       then green sync status like the other channels. (digestStatusFor kept for the
       weekly-digest data, just no longer surfaced in this strip.) */
    var openaiStatus = statusFor(data.secretsConfigured.openai, data.lastNightlySync, 'OpenAI');
    rows.push('<div class="moph-health__row"><span class="moph-health__dot ' + healthDotClass(openaiStatus, 'openai') + '"></span><span class="moph-health__label">OpenAI:</span><span class="moph-health__sub">' + escapeHtml(openaiStatus.text) + '</span></div>');
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — Attribution coverage row
       removed from the health banner (per Ryan). The per-order "How this was
       credited" detail now lives in the Recent Sales drawer; the banner stays
       focused on the sync/health statuses. */
    document.getElementById('mophHealth').innerHTML = rows.join('');
  }

  function statusFor(secretsSet, lastNightlySync, label) {
    if (!secretsSet) return { color: 'amber', text: 'Token not yet provisioned (Pre-Launch Checklist)' };
    if (!lastNightlySync) return { color: 'amber', text: 'No sync run yet' };
    var hours = (Date.now() - new Date(lastNightlySync.finishedAt).getTime()) / 3600000;
    if (hours > 30) return { color: 'red', text: 'Last sync ' + Math.round(hours) + 'h ago — stale' };
    return { color: 'green', text: 'Last sync ' + Math.round(hours) + 'h ago' };
  }

  function digestStatusFor(lastDigest, resendSet) {
    if (!resendSet) return { color: 'amber', text: 'Resend not configured' };
    if (!lastDigest) return { color: 'amber', text: 'No digest sent yet' };
    var days = (Date.now() - new Date(lastDigest.finishedAt).getTime()) / 86400000;
    if (days > 8) return { color: 'red', text: 'Last digest ' + Math.round(days) + 'd ago' };
    if (lastDigest.sent === false) return { color: 'amber', text: 'Skipped last run: ' + (lastDigest.reason || 'no data') };
    return { color: 'green', text: 'Last sent ' + Math.round(days) + 'd ago' };
  }

  // ─── Dashboard data load ────────────────────────────────────────────────
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — VIEW BUNDLE MEMO.

     loadAll() fires 13 requests and Promise.all waits for the slowest, and the browser kept
     NOTHING: flipping Last-30 → Last-90 → Last-30 paid the full cold cost all three times. The
     server-side layers can't cover that gap on their own — the shared response cache is minutes,
     and the order-walk memo under it is module-scope state inside a Worker isolate, so whether a
     second visit hits it is a coin flip. The data the user was looking at four seconds ago is the
     one thing guaranteed to be available, and it was being thrown away.

     Keyed on EVERY input that changes a payload — the dates, the platform filter, the attribution
     model and the resolved non-marketing flag. Miss one and the page shows another view's numbers,
     which is worse than being slow, so the signature is built from the same values the query
     strings are.

     ⚠️ TTL'd, and the page still tells the truth about age: the "As of" line reads
     `state.kpis.fetchedAt` off the payload itself, so a replayed bundle reports when it was
     actually fetched rather than claiming to be fresh.

     Safe to replay because every loadAll() caller is a VIEW change (range, custom dates, platform,
     attribution, the All-Sales toggle, first load) — no mutation path refreshes through here. If
     one is ever added, call loadAll({ force: true }). */
  var _bundleCache = new Map();
  /* 60 minutes, sized to the actual use case (Ryan 2026-08-19): an hour-long marketing meeting
     where people take turns driving, everyone sets the same view to follow along, and each of them
     was paying a fresh ~20s Shopify walk to see what the last person was already looking at. The
     matching bump on the shared edge cache is what makes the SECOND and THIRD viewer instant; this
     one makes going back to a view YOU already had instant.

     Safe to hold this long because the page reports its own age — the "As of" line reads
     `kpis.fetchedAt` off the payload — and because the Refresh button forces every layer cold. A
     long cache with no way out would be the wrong trade; a long cache with an escape hatch is just
     a meeting that doesn't stall. */
  var BUNDLE_TTL_MS = 60 * 60 * 1000;
  /* 12, not 8: admins now hold TWO bundles per range (the view plus its prefetched All-Sales twin),
     so the old cap held only four ranges and evicting the twin would have undone the prefetch it
     was stored for. Each bundle is ~200 KB at the top end, so this is single-digit MB in the worst
     case — cheap next to re-walking Shopify. */
  var BUNDLE_MAX = 12;

  /** `nmOn` defaults to the live toggle state; pass it explicitly to address the OTHER variant's
   *  bundle (the admin prefetch below stores under the state the user is not currently in). */
  function bundleKey(range, nmOn) {
    var nm = (nmOn === undefined) ? (nonMktOn() && isAdminSession()) : !!nmOn;
    return [
      range.from, range.to,
      state.platform || '',
      state.attribution || 'firstPaid',
      nm ? 'nm1' : 'nm0',
    ].join('|');
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — THE FAN-OUT, IN ONE PLACE.

     Extracted so the admin prefetch below can issue the IDENTICAL 13 requests as the opposite
     All-Sales state. Duplicating the list would have been the obvious way and the wrong one: the
     two copies drift the first time an endpoint is added, and the symptom is a bundle that is
     complete on one side of the toggle and quietly missing a card on the other.

     ⚠️ Every mwFetch() here is invoked SYNCHRONOUSLY by the array literal, and that is load-bearing:
     _forceFresh and _nonMktOverride are raised around the call to this function and lowered the
     instant it returns. Introduce an `await` inside and the later calls silently lose the flag. */
  function buildDashboardFetches(range) {
    var pf = state.platform ? ('&platform=' + state.platform) : '';
    var qs = 'from=' + range.from + '&to=' + range.to + pf;

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#4) — Recommendations run over a FIXED
       ~90-day look-back, decoupled from the page date picker. The rules express
       every estimate "/mo" and gate on monthly-ish dollar floors, so a stable
       ~quarter of signal (worker floors the cost side to launch + normalizes the
       money figures to a 30-day month) is the right basis — not whatever range
       the user happens to have selected. Platform filter still applies. */
    var _memoNow = accountNowParts();
    var _memoTo = _memoNow.iso;
    var _memoFrom = new Date(Date.UTC(_memoNow.y, _memoNow.m0, _memoNow.d) - 90 * 86400000).toISOString().slice(0, 10);
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — pass the SAME Target-ROAS multiple the chart
       uses so the memo's profit-floor gate + over-performer scale rules derive
       the identical break-even / Target ROAS as the goal line. */
    var _memoMult = (window.MOPH_CONFIG && Number(window.MOPH_CONFIG.targetRoasMultiple)) || 2;
    var memoQs = 'from=' + _memoFrom + '&to=' + _memoTo + pf + '&targetMultiple=' + _memoMult + '&attribution=' + (state.attribution || 'firstPaid');
    var cmRange = 'from=' + range.from + '&to=' + range.to + '&attribution=' + (state.attribution || 'firstPaid');
    var ordersQs = cmRange + '&limit=200'; // CUSTOM_MARKETINGOPS_BY_CLAUDE (audit F-001) — was 30, which hid 3 of 6 Google orders + made the table un-reconcilable with the footer totals. 200 covers a normal month's order volume.

    return [
        mwFetch('/dashboard/kpis?' + qs),
        mwFetch('/dashboard/channels?' + qs),
        mwFetch('/dashboard/campaigns?' + qs),
        mwFetch('/dashboard/timeseries?' + qs),
        loadPlanIfNeeded(),
        mwFetch('/dashboard/contribution-margin?' + cmRange).catch(function(err) {
          console.warn('[marketing-ops] contribution-margin upstream failed (falling back to margin% estimate)', err);
          return null;
        }),
        mwFetch('/dashboard/sold-orders?' + ordersQs).catch(function(err) {
          console.warn('[marketing-ops] sold-orders upstream failed', err);
          return null;
        }),
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 6 v2: insights bundle
           (repeat customer rate + per-source metrics + top verticals).
           Non-blocking — if the upstream fails, the Insights section
           hides itself instead of breaking the dashboard. */
        mwFetch('/dashboard/insights?' + cmRange).catch(function(err) {
          console.warn('[marketing-ops] insights upstream failed', err);
          return null;
        }),
        /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 4.1 v1 Reinvestment Memo.
           Forward-looking top-5 recs. Non-blocking — card hides itself if
           upstream errors or no rules fire. */
        mwFetch('/dashboard/reinvestment-memo?' + memoQs).catch(function(err) {
          console.warn('[marketing-ops] reinvestment-memo upstream failed', err);
          return null;
        }),
        /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */
        /* #9 — campaign display-name aliases. Range-independent (aliases apply
           across all periods); non-blocking — table shows raw names if it fails. */
        mwFetch('/dashboard/campaign-aliases').catch(function(err) {
          console.warn('[marketing-ops] campaign-aliases upstream failed', err);
          return null;
        }),
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE — per-state Google ad spend (on-demand)
           for the Geography card's Ad Spend + ROAS columns. Non-blocking — those
           two columns render '—' if it fails or returns nothing. */
        mwFetch('/dashboard/geo-spend?from=' + range.from + '&to=' + range.to).catch(function(err) {
          console.warn('[marketing-ops] geo-spend upstream failed', err);
          return null;
        }),
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Inquiry Flow) — daily inbound inquiry
           counts by source for the Daily Performance chart's Inquiries / Both
           views. Non-blocking — the Inquiries toggle shows "unavailable" if it
           fails; the default Sales view is unaffected. */
        mwFetch('/dashboard/inquiries?from=' + range.from + '&to=' + range.to).catch(function(err) {
          console.warn('[marketing-ops] inquiries upstream failed', err);
          return null;
        }),
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Leads table) — per-lead rows + KPI strip for
           the Leads card. Non-blocking — the card shows an error row if it fails. */
        mwFetch('/dashboard/leads?from=' + range.from + '&to=' + range.to + '&limit=500').catch(function(err) {
          console.warn('[marketing-ops] leads upstream failed', err);
          return null;
        }),
      ];
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — PREFETCH THE OTHER ALL-SALES STATE. ADMINS ONLY.

     Perf item 4. The bundle memo already makes toggling BACK instant, because the state you came
     from is still in memory. What stayed slow was the FIRST toggle on a given range — the one that
     has to go and get a view nobody has asked for yet. So fetch it before it is asked for, on idle,
     and the toggle becomes a memory read in both directions.

     ⚠️ ADMIN-ONLY, AND NOT FOR THE REASON IT LOOKS LIKE. It is not a permissions nicety bolted onto
     a perf change:
       • The agency never sees the All-Sales control at all, so there is no toggle of theirs to make
         fast — the work would be pure waste on their page loads.
       • The bucket this reveals is `referral` + `manual-rep`: revenue a partner or a relationship
         produced, which is precisely what must not sit in the numbers Malt is judged on.
     The server is still the enforcement point either way — resolveIncludeNonMarketing returns false
     for any non-admin whatever the URL says — so the worst a bug here could do is waste a request,
     not leak a row. Ryan confirmed admin-only on 2026-08-19.

     This is deliberately a PREFETCH rather than the "send one superset and filter in the browser"
     shape originally sketched. Same outcome, far less blast radius: contribution-margin, insights
     and the memo return AGGREGATES, not rows, so a client-side filter would have meant teaching
     five differently-shaped money endpoints to emit both variants. Prefetching reuses the exact
     request path that is already proven, and touches no server aggregate at all. */
  var _altPrefetchKey = null;   // in-flight guard; a second loadAll must not start a duplicate
  async function prefetchAltNonMkt(range, fresh) {
    if (!isAdminSession()) return;
    var want = !nonMktOn();
    var key = bundleKey(range, want);
    /* `fresh` carries the Refresh press through to the TWIN. Without it the button would refresh
       the view you are looking at and leave its opposite up to an hour old, so toggling straight
       after a refresh would show stale numbers — which makes the button's whole promise untrue in
       the one moment someone is actually checking. */
    if (fresh) _bundleCache.delete(key);
    if (_bundleCache.has(key) || _altPrefetchKey === key) return;
    _altPrefetchKey = key;
    try {
      _nonMktOverride = want;
      _forceFresh = !!fresh;
      var fetches = buildDashboardFetches(range); // synchronous literal — see the note in mwFetch
      _nonMktOverride = null;
      _forceFresh = false;
      var res = await Promise.all(fetches);
      _bundleCache.set(key, { ts: Date.now(), results: res });
      while (_bundleCache.size > BUNDLE_MAX) _bundleCache.delete(_bundleCache.keys().next().value);
    } catch (err) {
      // Non-blocking by design: the toggle still works, it just pays the normal load.
      console.warn('[marketing-ops] All-Sales prefetch failed; the toggle will load normally', err);
    } finally {
      // Belt and braces — a throw mid-construction must not strand either flag raised, or every
      // later fetch on the page inherits it.
      _nonMktOverride = null;
      _forceFresh = false;
      _altPrefetchKey = null;
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-23, Marketing Ops speed step 4) — one bundle onto the page.
     Pulled out of loadAll so the SAVED COPY (below) and the fresh answer draw through the same lines;
     two copies of this list would drift the first time a card is added. Only state + renders live here:
     the one-shot month fallback and the background warms run on the FRESH answer alone. */
  function applyBundle(results) {
    state.kpis = results[0];
    state.channels = results[1];
    state.campaigns = results[2];
    state.series = results[3].series || [];
    state.cm = results[5];
    state.orders = results[6];
    state.ordersLoadFailed = !results[6]; // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-06) — sold-orders' catch returns null; say so instead of "Loading…" forever
    state.ordersShown = 20; // N14 — reset Recent Sales to the first 20 on every (re)load
    state.ordersFilter = { channels: [], sources: [] }; // CUSTOM_MARKETINGOPS_BY_CLAUDE — reset Sales filters on every (re)load (new range = fresh filter)
    state.insights = results[7];
    state.reinvest = results[8];
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Budget Plan 1.17) — once an alias has changed in this tab (here,
       on the Budget Plan page, or via a focus refetch), keep that live copy: the view-bundle memo can
       replay an hour-old aliases payload on a range switch and would put the old name back. */
    state.aliases = _liveAliases || (results[9] && results[9].aliases) || {};
    state.geoSpend = (results[10] && results[10].byState) || {}; // { 'CA': { spend, conversions, conversionValue }, ... }
    state.inquiries = results[11] || null; // Inquiry Flow — daily inbound inquiry counts by source
    state.leads = results[12] || null; // Leads table — per-lead rows + KPI strip
    state.leadsShown = 20; // reset Leads pagination on every (re)load
    renderKpis();
    renderNonMktNote();
    renderChannels();
    renderCampaigns();
    renderChart();
    renderSalesFilters();
    renderOrders();
    renderLeads();
    renderInsights();
    renderMix();
    renderGeography();
    renderReinvestmentMemo();
    publishExplainContext(); // CUSTOM_MARKETINGOPS_BY_CLAUDE — refresh the bot's live-number snapshot
    if (state.health) renderHealth(state.health); /* CUSTOM_MARKETINGOPS_BY_CLAUDE (PM-52) — re-render the health strip now that cm.totals is loaded so the Attribution coverage row appears */
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-23, Marketing Ops speed step 4) — THE SAVED COPY.
     The in-tab bundle memo above dies with the tab, so every visit to this page started blank and waited on
     the slowest of 13 requests. The last fresh answer for each view is now also kept in IndexedDB
     (RepPortal.API.savedCopyGet/Put, keyed per signed-in user + the same bundleKey), painted the moment it
     is read, and replaced when the fresh answer lands. The toolbar says so in words ("Saved copy from …
     · updating"), so an old number is never shown as a current one. 24 h for an exact match. */
  var SAVED_BUNDLE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  function savedBundleApi() {
    var api = window.RepPortal && window.RepPortal.API;
    return (api && typeof api.savedCopyGet === 'function') ? api : null;
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-24) — THE FIRST LOAD OF THE DAY. The key holds the dates, and
     This Month (Last 30, This Year …) ends today, so the day's first load never found a copy. Each fresh save
     also writes a small pointer, "the newest copy of this preset" (same platform, attribution and All Sales
     setting; the user is in RepPortal's own key), and a miss paints that copy, labelled with its own date.
     ⛔ Only when it covers the SAME period: a period-to-date preset whose start moved (the 1st of the month,
     a new quarter, year or week, or Today) was a different period yesterday, so it waits for the fresh answer.
     The rolling windows (Last 30/90/180) are the same question a day on, so they paint. */
  var SAVED_FALLBACK_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  var SAVED_ROLLING = { last30: 1, last90: 1, last180: 1 };
  function savedPresetKey(range, rangeKey) {
    if (rangeKey === 'custom') return null;
    return 'moph:latest:' + [rangeKey].concat(bundleKey(range).split('|').slice(2)).join('|');
  }
  /** This view's saved copy, else the newest copy of the same preset when it may stand in (see above). */
  function savedBundleGet(api, range, rangeKey, bKey) {
    return api.savedCopyGet('moph:' + bKey, SAVED_BUNDLE_MAX_AGE_MS).then(function(exact) {
      var pk = exact ? null : savedPresetKey(range, rangeKey);
      if (!pk) return exact;
      return api.savedCopyGet(pk, SAVED_FALLBACK_MAX_AGE_MS).then(function(p) {
        var ptr = p && p.data;
        if (!ptr || !ptr.key || ptr.key === 'moph:' + bKey) return null;
        if (!SAVED_ROLLING[rangeKey] && ptr.from !== range.from) return null;
        return api.savedCopyGet(ptr.key, SAVED_FALLBACK_MAX_AGE_MS);
      });
    });
  }
  function savedNote(text, failed) {
    var el = document.getElementById('mophSaved');
    if (!el) return;
    el.hidden = !text;
    el.classList.toggle('moph-saved--failed', !!failed);
    var lab = el.querySelector('.moph-saved__label');
    if (lab) lab.textContent = text || '';
  }
  function savedAtLabel(at) {
    var d = new Date(at);
    var t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (new Date().toDateString() === d.toDateString()) ? t : (d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + t);
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-24) — the page-load line in SPEED-RULES.md reads these marks:
     mo:painted (saved copy on screen), mo:fresh (fresh numbers drawn). The FIRST of each only, so a later range
     switch never moves them. */
  function markOnce(name) {
    try { if (!performance.getEntriesByName(name).length) performance.mark(name); } catch (_) {}
  }

  async function loadAll(opts) {
    // Race guard — if the range is changed while a load is in flight, a later
    // selection must win. Each load tags itself; only the latest applies results.
    var mySeq = (state._loadSeq = (state._loadSeq || 0) + 1);
    var range = dateRange();
    var _rk = state.rangeKey;
    var _bKey = bundleKey(range);
    var _forced = !!(opts && opts.force);
    if (_forced) _bundleCache.delete(_bKey);
    // N16 — flag when the range reaches before launch (cost side is floored to May 1 server-side).
    var _ln = document.getElementById('mophLaunchNote');
    if (_ln) _ln.hidden = !(range.from < '2026-05-01');

    var _cachedBundle = null;
    var _paintedSavedAt = 0; // speed step 4: set when this load drew the saved copy first
    savedNote(''); // a previous view's "Saved copy from …" note never sits over this one
    if (!_forced) {
      var _hit = _bundleCache.get(_bKey);
      if (_hit && (Date.now() - _hit.ts) < BUNDLE_TTL_MS) _cachedBundle = _hit.results;
    }

    // #24 — show the in-progress state until all data renders. Skipped on a replay: the render is
    // synchronous from here, and flashing the blur + spinner for one frame reads as a stutter.
    setLoading(!_cachedBundle);
    try {
      // Plan data is loaded in parallel only when the toggle is on, and
      // only if the year has changed since last load (in-memory cache).
      // Phase 1.8.5: also fetch true Contribution Margin from the cross-
      // worker lifecycle aggregate. Failure is non-blocking — falls back
      // to the configurable marginPct estimate (Phase 1.8 v1).
      var results;
      if (_cachedBundle) {
        results = _cachedBundle;
        /* results[4] is loadPlanIfNeeded(), which is called for its SIDE EFFECT (state.planYear)
           and whose slot in the array is never read — so a replay would skip it. It is keyed on
           YEAR, not on the range, so 2026 → a 2025 custom range → back to 2026 would replay with
           state.planYear still holding 2025 and draw the wrong plan overlay. Re-running it is free:
           it returns immediately unless the plan overlay is on AND the year actually moved. */
        await loadPlanIfNeeded();
      } else {
      _forceFresh = _forced; // raised only across the synchronous array literal below — see mwFetch
      var fetches = buildDashboardFetches(range);
      _forceFresh = false; // lowered the instant construction ends, before any await
      /* Speed step 4 — the requests are already in flight; while they run, paint this view's saved copy
         if there is one (not on Refresh: that asked for fresh numbers on purpose). */
      var _all = Promise.all(fetches);
      var _freshIn = false;
      _all.then(function() { _freshIn = true; }, function() {});
      var _api = _forced ? null : savedBundleApi();
      /* Not awaited: the fresh answer never waits on the saved copy. It paints when it arrives, unless the
         fresh answer beat it (the flag above is set before this load's own `await _all` resumes). */
      if (_api) {
        savedBundleGet(_api, range, _rk, _bKey).then(function(_saved) {
          if (!_saved || _freshIn || mySeq !== state._loadSeq) return;
          applyBundle(_saved.data);
          _paintedSavedAt = _saved.at;
          setLoading(false);
          savedNote('Saved copy from ' + savedAtLabel(_saved.at) + ' · updating…');
          markOnce('mo:painted');
        });
      }
      results = await _all;
      if (_api) {
        _api.savedCopyPut('moph:' + _bKey, results);
        var _pk = savedPresetKey(range, _rk);
        if (_pk) _api.savedCopyPut(_pk, { key: 'moph:' + _bKey, from: range.from });
      }
      /* Stored even when this load has been superseded — the payloads are keyed on the view they
         describe, not on which load won the race, so they are still the right answer for _bKey.
         ⚠️ Stored BY REFERENCE: the render path sorts state.orders / state.leads in place, which
         mutates the bundle. That is intentional (it is the same view, and a sort the user applied
         survives a flip away and back), but it means nothing here may be handed to a DIFFERENT
         cache key. */
      _bundleCache.set(_bKey, { ts: Date.now(), results: results });
      while (_bundleCache.size > BUNDLE_MAX) _bundleCache.delete(_bundleCache.keys().next().value);
      }
      if (mySeq !== state._loadSeq) return; // a newer range change superseded this load — discard
      applyBundle(results);
      if (mySeq === state._loadSeq) savedNote('');
      markOnce('mo:fresh');
      maybeFallbackToLastMonth();
      // Lazy — fetch the prior period in the background, then re-draw with the
      // dotted overlay. Non-blocking so the dashboard render isn't held up.
      loadPrevPeriod();
      // Lazy — detect "record high/low" outcome metrics vs prior comparable
      // periods-to-date and flame the cards. Non-blocking; flames fade in after.
      loadRecords();
      // Warm the per-campaign detail cache in the background (idle) so expanding a
      // row is instant. Deferred so it never competes with the initial render.
      (window.requestIdleCallback || function(f) { return setTimeout(f, 400); })(function() { prefetchCampaignDetails(); });
      /* Lowest priority on the page: queued behind the campaign-detail warm so it never competes
         with the render or with a card the user can actually see yet. Runs on the replay path too —
         a bundle served from memory can still be missing its opposite twin. */
      (window.requestIdleCallback || function(f) { return setTimeout(f, 1200); })(function() {
        // A range change while we were idle supersedes this — prefetching the twin of a view the
        // user has already left is pure waste, and the newer load queues its own.
        if (mySeq !== state._loadSeq) return;
        prefetchAltNonMkt(range, _forced);
      });
    } catch (err) {
      console.error('[marketing-ops] load failed', err);
      // The saved copy is still on screen: say it is old rather than let it pass for today's numbers.
      if (_paintedSavedAt && mySeq === state._loadSeq) savedNote('Saved copy from ' + savedAtLabel(_paintedSavedAt) + ' · could not update (' + (err && err.message || 'error') + ')', true);
    } finally {
      if (mySeq === state._loadSeq) setLoading(false); // only the latest load clears the spinner
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — month-rollover guard. "This Month" is the
     default range, but on the 1st/2nd of a month it's empty (no spend synced +
     no orders yet) → reads as a scary "everything's $0". If the INITIAL This-
     Month view has no data at all, silently fall back to Last Month (the
     dropdown updates to match). One-shot: never overrides a manual pick, and the
     reload it triggers won't re-fire (rangeKey is then 'lastmonth'). Replicates
     setRange('lastmonth') inline since setRange is scoped inside bind(). */
  var _autoFallbackChecked = false;
  function maybeFallbackToLastMonth() {
    if (_autoFallbackChecked) return;
    _autoFallbackChecked = true;
    if (state.rangeKey !== 'month') return; // only the initial This-Month default
    var noSpend  = !state.kpis || !state.kpis.totals || (Number(state.kpis.totals.spend) || 0) === 0;
    var noOrders = !state.cm || !state.cm.totals || (Number(state.cm.totals.orderCount) || 0) === 0;
    if (noSpend && noOrders) {
      state.rangeKey = 'lastmonth';
      var ms = document.getElementById('mophRange'); if (ms) ms.value = 'lastmonth';
      var os = document.getElementById('mophOrdersRange'); if (os) os.value = 'lastmonth';
      var custom = document.getElementById('mophCustomRange'); if (custom) custom.hidden = true;
      loadAll();
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-03) — say what the filter removed. A reporting
     change that silently shrinks the numbers is indistinguishable from a bad month, so the page
     states the count and the dollars rather than making anyone diff it against memory. Shown to
     admins only (the agency isn't told what it isn't seeing) and only when something was actually
     excluded. */
  function renderNonMktNote() {
    var el = document.getElementById('mophNonMktNote');
    if (!el) return;
    var ex = state.cm && state.cm.nonMarketingExcluded;
    var on = nonMktOn() && isAdminSession();
    if (!isAdminSession() || on || !ex || !ex.count) { el.hidden = true; return; }
    var money = '$' + Math.round(ex.revenue || 0).toLocaleString();
    var parts = [];
    if (ex.byGroup && ex.byGroup.referral) parts.push(ex.byGroup.referral + ' referral');
    if (ex.byGroup && ex.byGroup['manual-rep']) parts.push(ex.byGroup['manual-rep'] + ' rep-produced');
    var detail = parts.length ? ' (' + parts.join(', ') + ')' : '';
    el.innerHTML = '🚫 Excluding <strong>' + ex.count + '</strong> non-marketing sale'
      + (ex.count === 1 ? '' : 's') + detail + ' worth <strong>' + money
      + '</strong> — neither ad-produced nor organic. Turn on <em>All sales</em> to include them.';
    el.hidden = false;
  }

  function renderKpis() {
    if (!state.kpis) return;
    var t = state.kpis.totals;
    var marginPct = Number(state.marginPct) || 35;

    // Phase 1.8.5 — prefer the true CM from /dashboard/contribution-margin
    // (cross-worker lifecycle aggregate). Fall back to the marginPct estimate
    // if the upstream call failed OR if there are simply no sold orders in
    // range (orderCount === 0 → estimate is more useful than $0).
    var useTrueCm = state.cm && state.cm.totals && state.cm.totals.orderCount > 0;
    var contributionMargin, heroLabelEl, heroLabelText, heroNoteHtml;
    if (useTrueCm) {
      // Headline CM = post-overhead, the true "what I can pay myself / reinvest"
      // number Ryan uses for business decisions. When no overhead is configured
      // (MARKETING_MONTHLY_OVERHEAD = 0), the pre-overhead value is the only
      // number that exists and we display that directly. Per-channel CM in the
      // Channel Breakdown table stays pre-overhead (variable-cost-only) since
      // allocating fixed overhead across channels isn't methodologically clean.
      var oh = state.cm.totals.overhead;
      var preOverheadCm = state.cm.totals.contributionMargin;
      var postOverheadCm = state.cm.totals.contributionMarginAfterOverhead;
      /* Phase 1.14 — agency comp is now retainer + performance fee, so the headline
         nets BOTH. Testing the retainer alone would have shown a pre-fee number the
         moment a range had a fee but no retainer. */
      var perfFee = Number(state.cm.totals.agencyPerformanceFee) || 0;
      var hasOverhead = (oh && oh.amount > 0) || perfFee > 0;
      contributionMargin = hasOverhead ? postOverheadCm : preOverheadCm;
      heroLabelText = 'Contribution Margin';
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Hero sub-text trimmed per Ryan.
         The attributed-count, pre-overhead breakdown, and overhead-source
         attribution are all viewable in the (?) Show-Your-Work drawer +
         elsewhere on the page, so the hero stays minimal. */
      heroNoteHtml = 'Real per-order gross profit - ad spend' + (hasOverhead ? ' - agency comp' : '') + ', based on <strong>' + state.cm.totals.orderCount + '</strong> sold order' + (state.cm.totals.orderCount === 1 ? '' : 's') + '.'
        + (perfFee > 0 && state.cm.totals.agencyComp && state.cm.totals.agencyComp.provisional && state.cm.totals.agencyComp.provisional.isProvisional
            ? ' <span style="opacity:0.7">Agency fee provisional until invoiced.</span>' : '');
      /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */
    } else {
      contributionMargin = (Number(t.revenue) || 0) * (marginPct / 100) - (Number(t.spend) || 0);
      heroLabelText = 'Contribution Margin (est.)';
      var reason = (state.cm && state.cm.totals && state.cm.totals.orderCount === 0)
        ? 'No sold orders in range yet — estimate active until orders close.'
        : 'Lifecycle aggregate unavailable — using estimate.';
      heroNoteHtml = 'revenue × <span data-margin-display>' + marginPct + '</span>% − spend. ' + reason;
    }

    setKpi('contributionMargin', fmtMoney(contributionMargin, { compact: true }));
    heroLabelEl = document.querySelector('.moph-kpi--hero .moph-kpi__label');
    if (heroLabelEl) heroLabelEl.textContent = heroLabelText;

    setKpi('spend', fmtMoney(t.spend, { compact: true }));

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.12) — anchor Attributed Revenue +
       Blended ROAS on Shopify truth, not the platform conversionValue claim.
       Before this, both tiles showed the platforms' own reported conversionValue
       (e.g. Google's "$17 on 17 conversions" — value tracking isn't wired to real
       order amounts, so it's meaningless). When the lifecycle aggregate is live
       (useTrueCm), sum real per-channel Shopify revenue (directTotal) across PAID
       (non-organic) channels and use THAT for the revenue tile; ROAS = that ÷ spend.
       The platform conversionValue stays in each tile's Show-Your-Work drawer as the
       labeled "platform claim" for the variance story (the wedge feature). Falls
       back to the platform number only when the aggregate is unavailable (no sold
       orders in range / upstream fail). */
    var shopifyAttrRevenue = null, shopifyBlendedRoas = null;
    if (useTrueCm && state.cm && state.cm.byChannel) {
      shopifyAttrRevenue = 0;
      Object.keys(state.cm.byChannel).forEach(function(chKey) {
        if (chKey === 'organic') return;
        shopifyAttrRevenue += Number(state.cm.byChannel[chKey].directTotal) || 0;
      });
      shopifyBlendedRoas = (Number(t.spend) || 0) > 0 ? shopifyAttrRevenue / (Number(t.spend) || 0) : 0;
    }
    setKpi('revenue', fmtMoney(shopifyAttrRevenue != null ? shopifyAttrRevenue : t.revenue, { compact: true }));
    setKpi('roas', fmtRoas(shopifyBlendedRoas != null ? shopifyBlendedRoas : t.roas));
    setKpi('conversions', fmtInt(t.conversions));
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — removed the persistent "platform claim" chip
       on the Ad Conversions tile (Ryan): it's redundant with the tile's own (?)
       drawer, which now carries the full platform-claim / lag / over-under-claim /
       non-purchase-event caveat. (revenue/roas still use setKpiGapBadge for the
       conditional attribution-gap badge — a different, transient case.) */
    // Real Shopify sold orders (truth) — pairs with the platform-claimed Ad Conversions.
    setKpi('orders', fmtInt((state.cm && state.cm.totals && state.cm.totals.orderCount) || 0));
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#8) — CAC (Customer Acquisition Cost) =
       ad spend ÷ paid-attributed sold orders (Shopify truth), NOT spend ÷
       platform conversions. '—' until at least one paid-attributed order
       closes in range. */
    var cacOrders = (useTrueCm && state.cm.totals) ? (Number(state.cm.totals.attributedCount) || 0) : 0;
    setKpi('cac', cacOrders > 0 ? fmtMoney((Number(t.spend) || 0) / cacOrders) : '—');

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-05-31) — Total Revenue / Total ROAS
       beside Ad Revenue / Ad ROAS, mirroring the BSFS Media Plan Sheet. Ad* =
       paid-attributed Shopify revenue only (computed above). Total* = ALL
       sold-order revenue in range (directTotal — incl. organic / direct / quote-
       driven) ÷ spend. The two bracket the truth: Ad under-credits (attribution
       gaps), Total over-credits (organic sales the ads didn't cause). Both fall
       back to '—' when no lifecycle aggregate is available (no sold orders). */
    var totalRevenue = useTrueCm ? (Number(state.cm.totals.directTotal) || 0) : null;
    var totalRoas = (totalRevenue != null && (Number(t.spend) || 0) > 0) ? totalRevenue / (Number(t.spend) || 0) : null;
    setKpi('totalRevenue', totalRevenue != null ? fmtMoney(totalRevenue, { compact: true }) : '—');
    setKpi('totalRoas', totalRoas != null ? fmtRoas(totalRoas) : '—');

    // Attribution-gap honest disclosure. With Attributed Revenue + ROAS now
    // anchored on Shopify-attributed revenue (above), the gap case is "real sold
    // orders exist but none are mapped to a paid touch yet" → the tiles correctly
    // read $0 / 0×. Surface an amber badge explaining why (and what the platforms
    // CLAIM) so the empty number reads as honest-pending, not broken.
    var hasAttributionGap = useTrueCm
      && state.cm.totals.orderCount > 0
      && (Number(state.cm.totals.attributedCount) || 0) === 0
      && (Number(t.spend) || 0) > 0;
    setKpiGapBadge('revenue', hasAttributionGap, 'No paid-touch attribution yet — ' + (state.cm && state.cm.totals && state.cm.totals.orderCount || 0) + ' sold orders this range, none mapped to a paid channel (reconciliation in progress). Meta/Google CLAIM $' + Math.round(Number(t.revenue) || 0).toLocaleString('en-US') + ' via their own pixels — see the (?) drawer for the variance. Contribution Margin is the truth anchor.');
    setKpiGapBadge('roas', hasAttributionGap, 'No paid-touch attribution yet — numerator is $0 Shopify-attributed revenue. Platform-claimed ROAS is ' + fmtRoas(t.roas) + ' (see the (?) drawer). Real ROAS resolves as reconciliation lands.');

    var marginDisplays = document.querySelectorAll('[data-margin-display]');
    marginDisplays.forEach(function(el) { el.textContent = String(marginPct); });
    var hero = document.getElementById('mophHeroNote');
    if (state.kpis.recordCount === 0 && !useTrueCm) {
      hero.textContent = 'No spend data yet. Once Google + Meta tokens are provisioned, nightly sync will populate this dashboard within 24 hours.';
    } else {
      hero.innerHTML = heroNoteHtml;
    }

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#4) — efficiency metrics (account-level):
       CPC = spend ÷ clicks, CPM = spend ÷ impr × 1000, CPA = spend ÷ Ad
       Conversions. Clicks + impressions summed from the campaigns list (it's
       already platform-filtered). These are the plain-language numbers a marketer
       reconciles against the Google/Meta UIs. */
    var _camps = (state.campaigns && state.campaigns.campaigns) || [];
    var _tClicks = 0, _tImpr = 0;
    _camps.forEach(function (c) { _tClicks += Number(c.clicks) || 0; _tImpr += Number(c.impressions) || 0; });
    var _spend = Number(t.spend) || 0, _conv = Number(t.conversions) || 0;
    var _cpc = _tClicks > 0 ? _spend / _tClicks : null;
    var _cpm = _tImpr > 0 ? _spend / _tImpr * 1000 : null;
    var _cpa = _conv > 0 ? _spend / _conv : null;
    setKpi('cpc', _cpc != null ? fmtMoney(_cpc) : '—');
    setKpi('cpm', _cpm != null ? fmtMoney(_cpm) : '—');
    setKpi('cpa', _cpa != null ? fmtMoney(_cpa) : '—');

    /* #11 — Total Revenue / Total ROAS folded into sub-notes on the Ad tiles
       (the standalone tiles were removed; the numbers are preserved here). */
    setSubnote('totalRevenue', totalRevenue != null ? ('Total: ' + fmtMoney(totalRevenue, { compact: true })) : '');
    // (Total ROAS sub-note removed from the Ad ROAS tile per Ryan — too cramped;
    // keep only B/E + Goal. Total ROAS still has its own metric tab + explainer.)
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Break-even + Goal ROAS as sub-lines on the Ad ROAS
       tile. Same numbers as the chart's reference lines (breakEvenLines()). Blank (→ hidden)
       when the range has no sold orders. */
    var _beLines = useTrueCm ? breakEvenLines() : { be: 0, goal: 0 };
    setSubnote('beRoas', _beLines.be > 0 ? ('B/E: ' + fmtRoas(_beLines.be)) : '');
    setSubnote('minRoas', _beLines.goal > 0 ? ('Goal: ' + fmtRoas(_beLines.goal)) : '');

    /* #5 — vs-prior-period deltas. */
    var _curRevenue = (shopifyAttrRevenue != null ? shopifyAttrRevenue : Number(t.revenue) || 0);
    var _curRoas = (shopifyBlendedRoas != null ? shopifyBlendedRoas : Number(t.roas) || 0);
    var _curCac = cacOrders > 0 ? _spend / cacOrders : null;
    renderKpiDeltas({
      contributionMargin: contributionMargin, spend: _spend, revenue: _curRevenue,
      roas: _curRoas, conversions: _conv,
      orders: (state.cm && state.cm.totals && state.cm.totals.orderCount) || 0, cac: _curCac,
    });

    renderHoursSavedChip();
  }

  // #11 — secondary sub-note (Total Revenue/ROAS) under a KPI tile.
  function setSubnote(name, text) {
    var el = document.querySelector('[data-subnote="' + name + '"]');
    if (el) el.textContent = text || '';
  }

  /* #5 — render a vs-prior-period delta chip on a KPI tile. opts.invert = lower
     is better (CAC); opts.neutral = no good/bad color (Spend). Blank when either
     value is missing or prior is 0. */
  function setKpiDelta(name, cur, prev, opts) {
    var el = document.querySelector('[data-delta="' + name + '"]');
    if (!el) return;
    opts = opts || {};
    if (cur == null || prev == null || !isFinite(cur) || !isFinite(prev) || prev === 0) {
      el.textContent = ''; el.className = 'moph-kpi__delta'; el.removeAttribute('title'); return;
    }
    var pct = (cur - prev) / Math.abs(prev) * 100;
    var up = pct >= 0;
    var good = opts.neutral ? null : (opts.invert ? !up : up);
    el.textContent = (up ? '▲ +' : '▼ ') + Math.round(pct) + '%';
    el.className = 'moph-kpi__delta' + (good === true ? ' moph-kpi__delta--good' : good === false ? ' moph-kpi__delta--bad' : ' moph-kpi__delta--neutral');
    el.title = 'vs prior period (' + prevPeriodLabel() + ')';
  }

  function renderKpiDeltas(cur) {
    var pk = state.prevKpis, pcm = state.prevCm;
    var names = ['contributionMargin', 'spend', 'revenue', 'roas', 'conversions', 'orders', 'cac', 'cpc', 'cpm', 'cpa'];
    if (!pk && !pcm) { names.forEach(function (n) { setKpiDelta(n, null, null); }); return; }
    var pt = (pk && pk.totals) || {};
    var pSpend = Number(pt.spend) || 0, pConv = Number(pt.conversions) || 0;
    var pCm = null, pOrders = null, pCac = null, pAdRev = null, pAdRoas = null;
    if (pcm && pcm.totals) {
      if (pcm.totals.orderCount > 0 && pcm.byChannel) {
        pAdRev = 0;
        Object.keys(pcm.byChannel).forEach(function (k) { if (k !== 'organic') pAdRev += Number(pcm.byChannel[k].directTotal) || 0; });
        pAdRoas = pSpend > 0 ? pAdRev / pSpend : 0;
      }
      var pOh = pcm.totals.overhead;
      /* Phase 1.14 — mirror the hero's test (retainer OR performance fee), so the
         prior-period comparison is like-for-like with the number above it. */
      pCm = ((pOh && pOh.amount > 0) || (Number(pcm.totals.agencyPerformanceFee) || 0) > 0)
        ? pcm.totals.contributionMarginAfterOverhead : pcm.totals.contributionMargin;
      pOrders = Number(pcm.totals.orderCount) || 0;
      var pAttr = Number(pcm.totals.attributedCount) || 0;
      pCac = pAttr > 0 ? pSpend / pAttr : null;
    }
    if (pAdRev == null) pAdRev = pk ? (Number(pt.revenue) || 0) : null;
    if (pAdRoas == null) pAdRoas = pk ? (Number(pt.roas) || 0) : null;
    setKpiDelta('contributionMargin', cur.contributionMargin, pCm);
    setKpiDelta('spend', cur.spend, pSpend, { neutral: true });
    setKpiDelta('revenue', cur.revenue, pAdRev);
    setKpiDelta('roas', cur.roas, pAdRoas);
    setKpiDelta('conversions', cur.conversions, pConv);
    setKpiDelta('orders', cur.orders, pOrders);
    setKpiDelta('cac', cur.cac, pCac, { invert: true });
    // CPC/CPM/CPA deltas need prior-period clicks/impr (not fetched) — left blank.
  }

  function setKpi(name, value) {
    var el = document.querySelector('[data-kpi="' + name + '"]');
    if (el) el.textContent = value;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-24) — publish a compact, PII-free
     snapshot of what's on screen (active range, attribution model, the KPI values as
     rendered, channel + top-campaign aggregates) to window.MOPH_EXPLAIN_CONTEXT. The
     portal chatbot reads this when an admin/agency user asks the Marketing Ops bot to
     explain a number, so it can answer with their actual figures. Reads KPI text from
     the DOM (exactly what they see); channels/campaigns from state. Raw order rows /
     customer names are deliberately excluded. */
  function publishExplainContext() {
    try {
      var range = {}; try { range = dateRange() || {}; } catch (_) {}
      var kpis = {};
      document.querySelectorAll('.moph-kpi [data-kpi]').forEach(function(el) {
        var tile = el.closest('.moph-kpi');
        var labelEl = tile && tile.querySelector('.moph-kpi__label');
        var label = labelEl ? labelEl.textContent.trim() : el.getAttribute('data-kpi');
        var val = (el.textContent || '').trim();
        if (label && val && val !== '—') kpis[label] = val;
      });
      var byCh = (state.cm && state.cm.byChannel) || {};
      var channels = [];
      if (state.channels && Array.isArray(state.channels.channels)) {
        state.channels.channels.slice(0, 8).forEach(function(c) {
          var ch = c.channel || c.platform || '';
          channels.push({
            channel: ch,
            spend: Math.round(Number(c.spend) || 0),
            revenue: Math.round(Number(c.revenue) || 0),
            roas: c.roas != null ? Math.round(Number(c.roas) * 100) / 100 : null,
            orders: (byCh[ch] && Number(byCh[ch].orderCount)) || Number(c.orders) || 0,
          });
        });
      }
      var topCampaigns = [];
      if (state.campaigns && Array.isArray(state.campaigns.campaigns)) {
        state.campaigns.campaigns.slice()
          .sort(function(a, b) { return (Number(b.spend) || 0) - (Number(a.spend) || 0); })
          .slice(0, 5).forEach(function(c) {
            var nm = displayCampaignName(c.campaignId == null ? '' : String(c.campaignId), c.name || c.campaignId || '');
            topCampaigns.push({
              name: nm,
              spend: Math.round(Number(c.spend) || 0),
              revenue: Math.round(Number(c.revenue) || 0),
              roas: c.roas != null ? Math.round(Number(c.roas) * 100) / 100 : null,
            });
          });
      }
      window.MOPH_EXPLAIN_CONTEXT = {
        page: 'Paid Ads (Marketing Ops)',
        rangeLabel: (function () { try { return rangeLabel(); } catch (_) { return ''; } })(),
        from: range.from || null,
        to: range.to || null,
        attribution: state.attribution || null,
        kpis: kpis,
        channels: channels,
        topCampaigns: topCampaigns,
        updatedAt: new Date().toISOString(),
      };
      document.dispatchEvent(new CustomEvent('moph:explain-context-updated'));
    } catch (e) { /* non-fatal — snapshot is best-effort context for the bot */ }
  }

  /* #24 — loading state for date-range / filter changes. Shows an "Updating…"
     spinner in the toolbar and dims the data sections so it's obvious the
     dashboard is fetching the new window (previously nothing visibly happened). */
  function setLoading(on) {
    var el = document.getElementById('mophUpdating');
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-03) — hoist the indicator to <body> once. It is
       centred with `position: fixed`, and a fixed element anchors to the nearest ancestor carrying
       a transform / filter / backdrop-filter rather than to the viewport. The portal shell wrapping
       this section is outside this file, so rather than audit it (and re-audit it every time it
       changes) the node is simply moved out of reach of any such ancestor. */
    if (el && el.parentNode !== document.body) document.body.appendChild(el);
    /* Mirror the portal's dark-mode state onto the hoisted node. Living on <body> puts it outside
       `.rep-quote-portal-wrap`, where --rq-* is defined, so it can't inherit the theme any more —
       without this the dark-mode light blue silently reverts to the light-mode navy. Re-checked on
       every call rather than once at init, so switching theme mid-session keeps up. */
    if (el) {
      var _wrap = document.querySelector('.rep-quote-portal-wrap');
      el.classList.toggle('is-dark', !!(_wrap && _wrap.classList.contains('rep-quote-dark')));
    }
    if (el) el.hidden = !on;
    var content = document.querySelector('.rep-quote-portal-content');
    if (content) content.classList.toggle('moph-is-loading', !!on);
  }

  // Toggle a small "platform claim" warning chip next to a KPI value.
  // Lives in the same tile as the value; hover for the gap explanation.
  // Idempotent — adds the chip once, updates the tooltip if it already exists,
  // removes it when `show` flips false.
  function setKpiGapBadge(name, show, tooltip) {
    var valueEl = document.querySelector('[data-kpi="' + name + '"]');
    if (!valueEl) return;
    var tile = valueEl.closest('.moph-kpi');
    if (!tile) return;
    var existing = tile.querySelector('.moph-kpi__gap-chip');
    if (!show) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      existing.setAttribute('title', tooltip);
      return;
    }
    var chip = document.createElement('span');
    chip.className = 'moph-kpi__gap-chip';
    chip.textContent = 'platform claim';
    chip.setAttribute('title', tooltip);
    valueEl.parentNode.insertBefore(chip, valueEl.nextSibling);
  }

  // ─── Phase 6.5 (F) Delight Layer — Hours Saved chip ────────────────────
  // Quantifies the time the Paid Ads dashboard returns vs. the pre-tool baseline
  // (manual spend pulls into Sheet, manual pacing recon, weekly digest
  // assembly, per-inquiry source classification, per-order attribution
  // lookup). Numbers chosen to be defensible — each category maps 1:1 to
  // a workflow the agency genuinely did by hand before. Click the chip
  // to open the standard Show Your Work drawer with the breakdown.
  function computeHoursSaved() {
    var range = dateRange();
    var fromMs = new Date(range.from + 'T00:00:00Z').getTime();
    var toMs = new Date(range.to + 'T00:00:00Z').getTime();
    var days = Math.max(1, Math.round((toMs - fromMs) / 86400000) + 1);
    var weeks = days / 7;
    var conversions = (state.kpis && state.kpis.totals && Number(state.kpis.totals.conversions)) || 0;
    var orderCount = (state.cm && state.cm.totals && state.cm.totals.orderCount) || 0;
    var paidCount  = (state.cm && state.cm.totals && state.cm.totals.attributedCount) || 0;

    // Each entry: { label, perUnit (min), units, minutes, why }
    var rows = [
      { key: 'spendAgg',     label: 'Cross-platform spend aggregation', perUnit: 30, units: weeks,  unitName: 'wk',  minutes: weeks * 30, why: 'Used to be a 30-min Monday pull from Google Ads UI + Meta Ads Manager into the budget Sheet. Now the nightly cron does it.' },
      { key: 'pacing',       label: 'Budget pacing reconciliation',    perUnit: 20, units: weeks,  unitName: 'wk',  minutes: weeks * 20, why: '20 min/wk reconciling actual vs. planned per channel. Replaced by the burn-pace strip + variance columns on the Budget page.' },
      { key: 'weeklyDigest', label: 'Weekly performance digest',       perUnit: 45, units: weeks,  unitName: 'wk',  minutes: weeks * 45, why: 'Sun-night digest assembly (45 min/wk) — what spent, what converted, what to flag. Now auto-emailed via the Monday Memo or read live off the dashboard.' },
      { key: 'sourceClass',  label: 'Per-inquiry source classification', perUnit: 5,  units: conversions, unitName: 'inquiry', minutes: conversions * 5, why: 'Each inquiry took ~5 min to look up in GA4 / Meta and tag a real source. Now classified server-side from the captured UTM journey on submit.' },
      { key: 'orderAttrib',  label: 'Per-order attribution lookup',     perUnit: 10, units: orderCount,  unitName: 'order',   minutes: orderCount * 10, why: 'For every sold order, ~10 min digging through ad-platform UIs to figure out if a paid touch contributed. Now stored on the order via note_attributes.bsfs_utm_journey + rendered in the Orders drawer.' },
    ];
    var totalMinutes = rows.reduce(function(s, r) { return s + (r.minutes || 0); }, 0);
    return {
      hours: totalMinutes / 60,
      minutes: totalMinutes,
      days: days,
      weeks: weeks,
      conversions: conversions,
      orderCount: orderCount,
      paidCount: paidCount,
      rows: rows,
    };
  }

  function fmtHoursLabel(hours) {
    if (hours < 1) return '~' + Math.round(hours * 60) + ' min saved';
    if (hours < 10) return '~' + hours.toFixed(1).replace(/\.0$/, '') + ' hrs saved (est.)';
    return '~' + Math.round(hours).toLocaleString('en-US') + ' hrs saved (est.)';
  }

  function renderHoursSavedChip() {
    var chip = document.getElementById('mophHoursValue');
    if (!chip) return;
    var h = computeHoursSaved();
    var rangeSuffix;
    switch (state.rangeKey) {
      case 'today': rangeSuffix = ' today'; break;
      case 'year': rangeSuffix = ' this year'; break;
      case 'quarter': rangeSuffix = ' this quarter'; break;
      case 'month': rangeSuffix = ' this month'; break;
      case 'week': rangeSuffix = ' this week'; break;
      case 'lastyear': rangeSuffix = ' last year'; break;
      case 'lastquarter': rangeSuffix = ' last quarter'; break;
      case 'lastmonth': rangeSuffix = ' last month'; break;
      case 'lastweek': rangeSuffix = ' last week'; break;
      case 'last30': rangeSuffix = ' last 30 days'; break;
      case 'last90': rangeSuffix = ' last 90 days'; break;
      case 'last180': rangeSuffix = ' last 180 days'; break;
      case 'all': rangeSuffix = ' all-time'; break;
      default: rangeSuffix = '';
    }
    chip.textContent = fmtHoursLabel(h.hours) + rangeSuffix;
  }

  function renderChannels() {
    var tbody = document.querySelector('#mophChannels tbody');
    var range = dateRange();
    if (!state.channels || !state.channels.channels || state.channels.channels.length === 0) {
      tbody.innerHTML = '<tr><td colspan="11" class="moph-empty">Loading…</td></tr>';
      return;
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Phase 1.12) — overlay Shopify-attributed
       revenue / net / ROAS onto each channel row from the lifecycle aggregate
       (cm.byChannel, keyed by platform), matching the KPI tiles. Platform
       conversionValue is unreliable for BSFS, so when the aggregate is live
       (sold orders in range) we show Shopify truth — $0 for a channel with no
       paid-attributed orders, NOT the platform claim. Spend / conversions /
       impressions stay platform-sourced (legit ad-platform metrics); CAC =
       channel spend ÷ paid-attributed orders (Shopify truth).
       Falls back to the platform number only when the aggregate is unavailable. */
    var useTrueCmChannels = state.cm && state.cm.totals && state.cm.totals.orderCount > 0;
    var cmByPlatform = {};
    if (state.cm && state.cm.byChannel) {
      Object.keys(state.cm.byChannel).forEach(function(ck) {
        var e = state.cm.byChannel[ck];
        if (e && e.platform) cmByPlatform[e.platform] = e;
      });
    }
    tbody.innerHTML = state.channels.channels.map(function(c) {
      var dotCls = platformDotClass(c.platform);
      var label = platformDisplayLabel(c.platform);
      // Pool-level plan target — works without per-campaign linking.
      var planTarget = state.showPlan ? totalChannelPlanForRange(c.platform, range.from, range.to) : null;
      var variance = (planTarget != null && planTarget > 0) ? variancePct(c.spend, planTarget) : null;
      // Revenue / Net / ROAS from Shopify truth when the aggregate is live.
      var cmEntry = cmByPlatform[c.platform] || null;
      var rowRevenue, rowNet, rowRoas;
      if (useTrueCmChannels) {
        var chRev = cmEntry ? (Number(cmEntry.directTotal) || 0) : 0;
        rowRevenue = chRev;
        rowNet = chRev - (Number(c.spend) || 0);
        rowRoas = (Number(c.spend) || 0) > 0 ? chRev / (Number(c.spend) || 0) : 0;
      } else {
        rowRevenue = c.revenue; rowNet = c.net; rowRoas = c.roas;
      }
      // Phase 4.4 #2/#3 — flag platform conversions that don't reflect real
      // sales: (#2) divergence — claimed conversions but 0 Shopify orders this
      // period; (#3) $0-value — the platform itself reported $0 conv value, so
      // they're likely non-purchase pixel events. Mute the count + add a ⚠.
      var convN = Number(c.conversions) || 0;
      var convVal = Number(c.conversionValue != null ? c.conversionValue : c.revenue) || 0;
      var ordersN = (useTrueCmChannels && cmEntry) ? (Number(cmEntry.orderCount) || 0) : null;
      var convFlag = '', convCls = '';
      if (ordersN != null) {
        if (convN > 0 && (ordersN === 0 || convVal === 0)) {
          // SUSPECT (amber ⚠, muted) — claimed but 0 real orders and/or $0 value.
          var _r = [];
          if (ordersN === 0) _r.push('0 tied to a Shopify order this period');
          if (convVal === 0) _r.push('platform reported $0 value (likely non-purchase pixel events — page views, add-to-cart, leads — not sales)');
          convFlag = ' <span class="moph-conv-flag" title="' + escapeHtml('Platform over-claim — ' + _r.join('; ') + '. Trust the Orders column for real sales.') + '">&#9888;</span>';
          convCls = ' moph-conv--suspect';
        } else if (convN !== ordersN) {
          // MISMATCH (blue ⓘ) — count simply differs from real orders; platforms
          // over/under-report. Informational (esp. under-count = more real sales).
          var _dir = convN < ordersN ? 'under-counts' : 'over-counts';
          convFlag = ' <span class="moph-conv-flag" title="' + escapeHtml('Platform ' + _dir + ' vs Shopify — the pixel logged ' + convN + ', but ' + ordersN + ' real Shopify orders are attributed to this channel (the Orders column). Platforms routinely over/under-report; trust Orders.') + '">&#9888;</span>';
        }
      }
      return '<tr>' +
        '<td><span class="moph-platform-dot ' + dotCls + '"></span>' + escapeHtml(label) + '</td>' +
        '<td class="moph-table__num">' + fmtMoney(c.spend, { compact: true }) + '</td>' +
        '<td class="moph-table__num">' + fmtInt(c.impressions) + '</td>' +
        '<td class="moph-table__num moph-col--plan">' + (planTarget && planTarget > 0 ? fmtMoney(planTarget, { compact: true }) : '<span class="moph-variance--none">—</span>') + '</td>' +
        '<td class="moph-table__num moph-col--plan">' + fmtVariance(variance) + '</td>' +
        '<td class="moph-table__num">' + fmtMoney(rowRevenue, { compact: true }) + '</td>' +
        '<td class="moph-table__num">' + fmtMoney(rowNet, { compact: true }) + '</td>' +
        '<td class="moph-table__num">' + fmtRoas(rowRoas) + '</td>' +
        '<td class="moph-table__num' + convCls + '">' + fmtInt(convN) + convFlag + '</td>' +
        '<td class="moph-table__num">' + (useTrueCmChannels ? fmtInt(cmEntry ? (Number(cmEntry.orderCount) || 0) : 0) : '<span style="color:var(--moph-ink-2);">—</span>') + '</td>' +
        '<td class="moph-table__num">' + ((useTrueCmChannels && cmEntry && (Number(cmEntry.orderCount) || 0) > 0) ? fmtMoney((Number(c.spend) || 0) / Number(cmEntry.orderCount)) : '—') + '</td>' +
      '</tr>';
    }).join('');
  }

  function updateCampaignSortIndicators() {
    document.querySelectorAll('#mophCampaigns thead th[data-sort]').forEach(function(th) {
      var k = th.getAttribute('data-sort');
      if (k === state.sort.key) th.setAttribute('data-sort-active', state.sort.dir);
      else th.removeAttribute('data-sort-active');
    });
  }

  /* #9 — campaign display-name aliases. displayCampaignName resolves the
     friendly alias when set, else the original. The inline rename swaps the
     name cell for an editor; save persists to the marketing-worker (keyed on
     campaign ID) and optimistically updates everywhere the campaign shows. */
  function displayCampaignName(campaignId, originalName) {
    var id = (campaignId == null) ? '' : String(campaignId);
    return (id && state.aliases && state.aliases[id]) ? state.aliases[id] : originalName;
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — tier-aware campaign-name sort key. Pulls a
     LOW/MED(IUM)/HIGH token out of the name so siblings sharing a base ("Saunas
     LOW/MED/HIGH") order LOW→MED→HIGH instead of alphabetical (HIGH,LOW,MED).
     base = name minus the tier token (case/space/underscore-normalized); rank =
     LOW 0, MED 1, HIGH 2, none -1 (untiered names sort by base = plain alpha).
     \b boundaries avoid false hits inside words (Flow, Highlands, Medallion). */
  function campaignNameSortKey(displayName) {
    var n = String(displayName || '');
    var m = n.match(/\b(low|medium|med|high)\b/i);
    var rank = -1;
    if (m) {
      var t = m[1].toLowerCase();
      rank = (t === 'low') ? 0 : (t === 'high') ? 2 : 1;
    }
    var base = (m ? (n.slice(0, m.index) + n.slice(m.index + m[0].length)) : n)
      .replace(/[\s_]+/g, ' ').trim().toLowerCase();
    return { base: base, rank: rank, full: n.toLowerCase() };
  }
  function startCampaignRename(btn) {
    var td = btn.closest('td');
    if (!td) return;
    var cid = btn.getAttribute('data-cid') || '';
    var orig = btn.getAttribute('data-orig') || '';
    var current = (cid && state.aliases[cid]) || '';
    td.innerHTML =
      '<div class="moph-camp-rename">'
      + '<input type="text" class="moph-camp-rename__input" maxlength="80" value="' + escapeHtml(current) + '" placeholder="' + escapeHtml(orig) + '">'
      + '<button type="button" class="moph-camp-rename__save">Save</button>'
      + '<button type="button" class="moph-camp-rename__cancel">Cancel</button>'
      + '<div class="moph-camp-rename__orig" title="' + escapeHtml(orig) + '">Original: ' + escapeHtml(orig) + '</div>'
      + '</div>';
    var input = td.querySelector('.moph-camp-rename__input');
    input.focus(); input.select();
    function commit() { saveCampaignAlias(cid, input.value); }
    input.addEventListener('keydown', function(ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
      else if (ev.key === 'Escape') { ev.preventDefault(); renderCampaigns(); }
    });
    td.querySelector('.moph-camp-rename__save').addEventListener('click', commit);
    td.querySelector('.moph-camp-rename__cancel').addEventListener('click', function() { renderCampaigns(); });
  }
  function saveCampaignAlias(cid, value) {
    if (!cid) { renderCampaigns(); return; }
    var alias = (value || '').trim();
    if (alias) state.aliases[cid] = alias; else delete state.aliases[cid];
    _liveAliases = state.aliases;
    // Optimistic — re-render every surface the campaign name appears on.
    rerenderAliasSurfaces();
    announceAliases();
    mwPost('/dashboard/campaign-alias', { campaignId: cid, alias: alias }).catch(function(err) {
      console.error('[marketing-ops] alias save failed', err);
    });
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Budget Plan 1.17, Ryan 2026-09-28) — the Budget Plan page edits the
     SAME alias record. An edit on either page reaches the other at once in the same browser
     (BroadcastChannel 'mo-campaign-aliases', full map), and someone else's edit arrives when this
     tab is looked at again (refetch on focus; /dashboard/campaign-aliases is not edge-cached). */
  var _liveAliases = null;
  var _aliasBus = null;
  function rerenderAliasSurfaces() {
    renderCampaigns();
    renderOrders();
    renderReinvestmentMemo();
  }
  function applyAliases(next) {
    if (!next || typeof next !== 'object') return;
    if (JSON.stringify(next) === JSON.stringify(state.aliases || {})) return;
    state.aliases = next;
    _liveAliases = next;
    try { rerenderAliasSurfaces(); } catch (e) { console.warn('[marketing-ops] alias re-render failed', e); }
  }
  try {
    _aliasBus = new BroadcastChannel('mo-campaign-aliases');
    _aliasBus.onmessage = function(ev) {
      var d = ev && ev.data;
      if (d && d.type === 'aliases') applyAliases(Object.assign({}, d.aliases || {}));
    };
  } catch (_) {}
  function announceAliases() {
    try { if (_aliasBus) _aliasBus.postMessage({ type: 'aliases', aliases: state.aliases || {} }); } catch (_) {}
  }
  var _aliasFetchedAt = 0;
  function refreshAliasesOnFocus() {
    if (!state.campaigns || Date.now() - _aliasFetchedAt < 5000) return; // page not loaded yet / throttle
    _aliasFetchedAt = Date.now();
    mwFetch('/dashboard/campaign-aliases').then(function(d) { applyAliases((d && d.aliases) || {}); })
      .catch(function(err) { console.warn('[marketing-ops] alias refresh failed', err); });
  }
  document.addEventListener('visibilitychange', function() { if (document.visibilityState === 'visible') refreshAliasesOnFocus(); });
  window.addEventListener('focus', refreshAliasesOnFocus);

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-06) — the statuses that wear the orange
     PAUSED / SUSPENDED pill (same list as the status normalisation in renderCampaigns).
     Ended campaigns are not "paused" and stay visible. */
  function isPausedCampaign(c) {
    var s = String((c && c.status) || '').toUpperCase();
    return s === 'PAUSED' || s === 'BUDGETPAUSED' || s === 'BUDGETANDMANUALPAUSED' || s === 'SUSPENDED';
  }
  function renderCampaigns() {
    _unpinCampaignCols();
    _watchCampaignHeaders();
    updateCampaignSortIndicators();
    var tbody = document.querySelector('#mophCampaigns tbody');
    var range = dateRange();
    var rows = (state.campaigns && state.campaigns.campaigns) || [];
    if (rows.length === 0) {
      tbody.innerHTML = '<tr><td colspan="18" class="moph-empty">Loading campaigns…</td></tr>';
      var _cc0 = document.getElementById('mophCampaignsCount'); if (_cc0) _cc0.textContent = '';
      var _cf = document.getElementById('mophCampaignsFoot'); if (_cf) _cf.hidden = true;
      return;
    }
    // Decorate rows with planTarget + variance for sort-aware columns.
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (per-campaign Shopify attribution) — override
       revenue/net/roas with REAL Shopify revenue from the lifecycle aggregate
       (cm.byCampaign, keyed by utm_campaign = Google campaign ID; name fallback)
       so the campaign table shows actual sales, not the $1 platform conversionValue.
       $0 = no attributed orders this period (honest — last-click + UTM gaps leave
       most campaigns unmatched). Platform conversionValue kept as platformConvValue
       for the tooltip. Falls back to the platform value only if the aggregate is
       unavailable (no sold orders in range / upstream fail). Doing it here (not in
       the render map) keeps column sorting consistent with what's displayed. */
    var _cmCmp = (state.cm && state.cm.byCampaign) || null;
    var _useTrueCmp = state.cm && state.cm.totals && state.cm.totals.orderCount > 0;
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-07-08) — per-campaign LEAD counts for
       the Leads column. Aggregate the loaded leads list by campaignId (name fallback),
       mirroring the cm.byCampaign join, so each row shows the leads attributed to it. */
    var _leadRows = (state.leads && state.leads.leads) || [];
    var _leadsById = {}, _leadsByName = {};
    _leadRows.forEach(function(l) {
      var lid = (l.campaignId != null) ? String(l.campaignId) : '';
      if (lid) _leadsById[lid] = (_leadsById[lid] || 0) + 1;
      var lnm = String(l.campaignName || '').trim().toLowerCase();
      if (lnm) _leadsByName[lnm] = (_leadsByName[lnm] || 0) + 1;
    });
    function _leadCountFor(c) {
      var byId = _leadsById[String(c.campaignId)];
      if (byId != null) return byId;
      return _leadsByName[String(c.name || '').trim().toLowerCase()] || 0;
    }
    var decorated = rows.map(function(c) {
      var planRec = state.showPlan ? findPlanCampaign(c.campaignId) : null;
      var planTarget = planRec ? targetForCampaignInRange(planRec, range.from, range.to) : null;
      var variance = (planTarget != null && planTarget > 0) ? variancePct(c.spend, planTarget) : null;
      var _shop = _cmCmp ? (_cmCmp[c.campaignId] || _cmCmp[String(c.name || '').trim()] || null) : null;
      var _platformClaim = Number(c.revenue) || 0;
      var _spend = Number(c.spend) || 0;
      var _shopRev = _useTrueCmp ? (_shop ? (Number(_shop.directTotal) || 0) : 0) : _platformClaim;
      // #3 — per-campaign attributed contribution margin (GP − spend), for the
      // scale-badge profit gate. null when GP isn't available for this campaign.
      var _shopGp = (_shop && _shop.grossProfit != null) ? Number(_shop.grossProfit) : null;
      return Object.assign({}, c, {
        planTarget: planTarget,
        variance: variance,
        revenue: _shopRev,
        net: _shopRev - _spend,
        contributionMargin: _shopGp == null ? null : (_shopGp - _spend),
        roas: _spend > 0 ? _shopRev / _spend : 0,
        platformConvValue: _platformClaim,
        attributedOrders: _shop ? (Number(_shop.orderCount) || 0) : 0,
        // CAC (#8) — campaign spend ÷ attributed Shopify orders; null until one closes.
        cac: (_useTrueCmp && _shop && (Number(_shop.orderCount) || 0) > 0) ? (_spend / Number(_shop.orderCount)) : null,
        // #6 — CTR (clicks ÷ impr) + CVR (conversions ÷ clicks). Derived fields so
        // the columns both render AND sort. null when the denominator is 0.
        ctr: (Number(c.impressions) || 0) > 0 ? (Number(c.clicks) || 0) / Number(c.impressions) : null,
        cvr: (Number(c.clicks) || 0) > 0 ? (Number(c.conversions) || 0) / Number(c.clicks) : null,
        leads: _leadCountFor(c),
      });
    });
    // Paused switch off → drop paused rows here, so the rows, the count AND the TOTAL
    // row all describe the same visible set.
    var hiddenPaused = 0;
    var pausedInRange = decorated.filter(isPausedCampaign).length;
    if (!state.showPaused) {
      decorated = decorated.filter(function(c) {
        if (isPausedCampaign(c)) { hiddenPaused++; return false; }
        return true;
      });
    }
    // The table only lists campaigns that SPENT in the range, so a short range often holds no
    // paused campaign at all. Put the count on the switch so "nothing to show" reads as such
    // instead of as a switch that does nothing (Ryan 2026-10-07).
    var _pausedLbl = document.querySelector('#mophPausedToggle .moph-switch__label');
    if (_pausedLbl) _pausedLbl.textContent = 'Paused (' + pausedInRange + ')';
    var sorted = decorated.slice().sort(function(a, b) {
      var k = state.sort.key, dir = state.sort.dir === 'asc' ? 1 : -1;
      if (k === 'name') {
        // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-06) — group by CHANNEL first (Google,
        // Meta, Microsoft, OpenAI: the display label, alphabetical), then by name, so an
        // alias no longer needs a "Meta …" prefix to land with its channel. Two campaigns
        // both aliased "Colorado Saunas" sort into their own channel's block. Desc
        // reverses both levels.
        var cPlat = String(platformDisplayLabel(a.platform) || '').localeCompare(String(platformDisplayLabel(b.platform) || ''));
        if (cPlat !== 0) return cPlat * dir;
        // Sort by the DISPLAYED name (alias when set). Tier-aware: within the
        // same base name, order LOW → MED → HIGH instead of alphabetical
        // (which would give HIGH, LOW, MED). Everything else stays alphabetical.
        var ka = campaignNameSortKey(displayCampaignName(a.campaignId, a.name));
        var kb = campaignNameSortKey(displayCampaignName(b.campaignId, b.name));
        var cBase = ka.base.localeCompare(kb.base);
        if (cBase !== 0) return cBase * dir;
        if (ka.rank !== kb.rank) return (ka.rank - kb.rank) * dir; // LOW(0) < MED(1) < HIGH(2)
        return ka.full.localeCompare(kb.full) * dir;
      }
      if (k === 'platform' || k === 'status') {
        return (String(a[k] || '').localeCompare(String(b[k] || ''))) * dir;
      }
      var av = a[k] == null ? -Infinity : a[k];
      var bv = b[k] == null ? -Infinity : b[k];
      return (av - bv) * dir;
    });
    tbody.innerHTML = sorted.map(function(c) {
      var dotCls = platformDotClass(c.platform);
      var platformLabel = platformDisplayLabel(c.platform);
      var fullName = c.name || c.campaignId;
      // Impression Share + losses — null on PMax/Display/Meta. Render as
      // % with one decimal when present; em-dash + muted otherwise.
      var fmtIS = function(v) {
        if (v == null) return '<span style="color:var(--moph-ink-2);">—</span>';
        return Math.round(v * 100) + '%'; // whole-number % (narrower IS / Lost columns, Ryan 2026-07-08)
      };
      // #6 — CVR rate cell: 2-decimal % so single-digit rates read precisely.
      var fmtRate = function(v) {
        if (v == null || !isFinite(v)) return '<span style="color:var(--moph-ink-2);">—</span>';
        return (v * 100).toFixed(2) + '%';
      };
      // CTR uses ONE decimal (narrower column per Ryan 2026-07-08); CVR keeps two.
      var fmtRate1 = function(v) {
        if (v == null || !isFinite(v)) return '<span style="color:var(--moph-ink-2);">—</span>';
        return (v * 100).toFixed(1) + '%';
      };
      // #9 — compact impressions (777 / 25.9k / 130k) to keep the column narrow.
      var fmtImpr = function(n) {
        n = Number(n) || 0;
        if (n >= 10000) return Math.round(n / 1000) + 'k';
        if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
        return String(n);
      };
      // Normalize platform-specific status strings to a small set of
      // canonical labels — Google reports ENABLED / PAUSED / REMOVED,
      // Meta reports ACTIVE / PAUSED / DELETED, Microsoft reports ACTIVE /
      // PAUSED / BUDGETPAUSED / BUDGETANDMANUALPAUSED / SUSPENDED / DELETED.
      // All map cleanly into Active / Paused / Ended.
      var rawStatus = String(c.status || '').toUpperCase();
      var statusLabel, statusCls;
      if (rawStatus === 'ENABLED' || rawStatus === 'ACTIVE') {
        statusLabel = 'Active'; statusCls = 'moph-campaign-status--active';
      } else if (rawStatus === 'PAUSED' || rawStatus === 'BUDGETPAUSED' || rawStatus === 'BUDGETANDMANUALPAUSED' || rawStatus === 'SUSPENDED') {
        statusLabel = (rawStatus === 'SUSPENDED') ? 'Suspended' : 'Paused';
        statusCls = 'moph-campaign-status--paused';
      } else if (rawStatus === 'REMOVED' || rawStatus === 'DELETED' || rawStatus === 'ARCHIVED') {
        statusLabel = 'Ended'; statusCls = 'moph-campaign-status--ended';
      } else {
        statusLabel = c.status || '—'; statusCls = 'moph-campaign-status--unknown';
      }
      // Recommendation badge — color-coded per tier. Hover for the
      // reasoning derived from (net × IS) signals.
      var rec = c.recommendation || { tier: 'no-data', label: '—', why: '' };
      /* #3 — the server "Scale / Budget-capped" badge is computed from impression
         share ONLY (no profit gate — it predates per-campaign Shopify CM). Don't
         tell a marketer to scale a campaign whose attributed contribution margin
         is negative. When CM is known < 0, downgrade Scale → "Verify" (red) with
         the loss called out. Attribution is a lower bound, so frame as verify. */
      if (rec.tier === 'scale' && c.contributionMargin != null && c.contributionMargin < 0) {
        rec = { tier: 'verify', label: 'Verify', why: 'Budget-capped on impression share, BUT attributed contribution margin is −$' + Math.abs(Math.round(c.contributionMargin)) + ' (gross profit − spend) — scaling would grow a loss. Confirm true profit (attributed CM is a lower bound) before adding budget.' };
      }
      var recColors = {
        scale:        { bg: 'rgba(22,163,74,0.12)',  fg: '#16a34a' }, // green
        optimize:     { bg: 'rgba(0,107,204,0.12)',  fg: '#006BCC' }, // blue
        hold:         { bg: 'rgba(102,112,133,0.12)',fg: 'var(--moph-ink-2)' }, // gray — token so it lifts in dark mode (Ryan 2026-09-28)
        fix:          { bg: 'rgba(220,38,38,0.12)',  fg: '#dc2626' }, // red
        verify:       { bg: 'rgba(217,119,6,0.14)',  fg: '#b45309' }, // amber — #3 profit-gate override
        insufficient: { bg: 'rgba(102,112,133,0.06)',fg: '#9ca3af' }, // muted
        'no-data':    { bg: 'rgba(102,112,133,0.06)',fg: '#9ca3af' }, // muted
      };
      var col = recColors[rec.tier] || recColors['no-data'];
      // N17 — compact ACTION label (the recommendation itself) so the pill fits
      // the REC column without clipping; the full symptom/reasoning ("losing X%
      // of impressions to budget", etc.) stays on hover via rec.why.
      var recShort = { scale: 'Scale', optimize: 'Optimize', hold: 'Hold', fix: 'Fix', verify: 'Verify', insufficient: '—', 'no-data': '—' };
      var recLabel = recShort[rec.tier] || rec.label || '—';
      var recBadge = '<span class="moph-rec-badge" style="display:inline-block;box-sizing:border-box;text-align:center;padding:2px 9px;font-size:11px;font-weight:600;border-radius:50px;white-space:nowrap;background:' + col.bg + ';color:' + col.fg + ';" title="' + escapeHtml(rec.why || '') + '">' + escapeHtml(recLabel) + '</span>';
      // Platform dot prefixes the campaign name (Channel column dropped to
      // free horizontal space). title= attribute shows full name + platform
      // on hover for ellipsis-truncated rows.
      // #9 — show the user alias when set; original Google Ads name on hover.
      var cidStr = (c.campaignId == null) ? '' : String(c.campaignId);
      var aliasName = displayCampaignName(cidStr, fullName);
      var isAliased = aliasName !== fullName;
      var nameTitle = (isAliased ? ('“' + aliasName + '” — original: ' + fullName) : fullName) + ' · ' + platformLabel;
      // #11 — Status column removed; show an inline tag after the name ONLY when
      // a campaign is NOT live (active campaigns show nothing — no redundant pill).
      var inlineStatusTag = (statusCls === 'moph-campaign-status--active')
        ? ''
        : '<span class="moph-campaign-status moph-campaign-status--inline ' + statusCls + '" title="Campaign status: ' + escapeHtml(statusLabel) + '">' + escapeHtml(statusLabel) + '</span>';
      var nameCell = '<td class="moph-camp-name" title="' + escapeHtml(nameTitle) + '">'
        + '<div class="moph-camp-name__inner">'
        + '<span class="moph-platform-dot ' + dotCls + '"></span>'
        + '<span class="moph-camp-name__txt">' + escapeHtml(aliasName) + '</span>'
        + inlineStatusTag
        + '<button type="button" class="moph-camp-edit" title="Rename for display" data-cid="' + escapeHtml(cidStr) + '" data-orig="' + escapeHtml(fullName) + '">✎</button>'
        + '</div>'
        + '</td>';
      return '<tr class="moph-camp-row" data-cid="' + escapeHtml(cidStr) + '" data-name="' + escapeHtml(fullName) + '" data-platform="' + escapeHtml(c.platform || '') + '">' +
        nameCell +
        '<td class="moph-table__num">' + fmtMoney(c.spend, { compact: true }) + '</td>' +
        '<td class="moph-table__num" title="' + (Number(c.impressions) || 0).toLocaleString() + ' impressions">' + fmtImpr(c.impressions) + '</td>' +
        '<td class="moph-table__num" title="' + (Number(c.clicks) || 0).toLocaleString() + ' clicks">' + fmtImpr(c.clicks) + '</td>' +
        '<td class="moph-table__num" title="Click-through rate — clicks ÷ impressions (' + (Number(c.clicks) || 0).toLocaleString() + ' clicks). How well the ad earns the click.">' + fmtRate1(c.ctr) + '</td>' +
        '<td class="moph-table__num" title="' + (c.leads > 0 ? (c.leads + ' lead' + (c.leads === 1 ? '' : 's') + ' attributed to this campaign in range') : 'No leads attributed to this campaign in range') + '">' + fmtInt(c.leads) + '</td>' +
        '<td class="moph-table__num moph-col--plan">' + (c.planTarget != null && c.planTarget > 0 ? fmtMoney(c.planTarget, { compact: true }) : '<span class="moph-variance--none">—</span>') + '</td>' +
        '<td class="moph-table__num moph-col--plan">' + fmtVariance(c.variance) + '</td>' +
        '<td class="moph-table__num" title="' + (c.attributedOrders || 0) + ' Shopify order' + (c.attributedOrders === 1 ? '' : 's') + ' attributed to this campaign (' + (state.attribution === 'firstPaid' ? 'first-paid' : 'last-paid') + ' touch). Platform claimed conv. value: ' + fmtMoney(c.platformConvValue) + '.">' + fmtMoney(c.revenue, { compact: true }) + '</td>' +
        '<td class="moph-table__num">' + fmtMoney(c.net, { compact: true }) + '</td>' +
        '<td class="moph-table__num">' + fmtRoas(c.roas) + '</td>' +
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-09-06) — Conv. is the PLATFORM's count. When Google
           puts $0.00 of value on every one of them the count is almost certainly free sample checkouts
           ($0 Shopify orders that fired the purchase pixel) — the ECORE Flooring text campaign showed
           "6 conversions, $620" in August and all six were samples. Flag it so the row explains itself.
           The pixel stopped firing purchase on $0 checkouts on 2026-09-06, so this should fade out. */
        (function () {
          var _cn = Number(c.conversions) || 0, _cv = Number(c.platformConvValue) || 0;
          if (_cn > 0 && _cv === 0) {
            return '<td class="moph-table__num moph-conv--suspect" title="' + escapeHtml('The ad platform reported $0.00 value on all ' + fmtInt(_cn) + ' of these conversions — most likely free sample checkouts ($0 orders that fired the purchase pixel), not sales. Since 6 Sep 2026 a $0 checkout no longer fires the purchase conversion. Trust the Rev. and Sales columns for real revenue.') + '">' + fmtInt(_cn) + ' <span class="moph-conv-flag">&#9888;</span></td>';
          }
          return '<td class="moph-table__num">' + fmtInt(c.conversions) + '</td>';
        })() +
        '<td class="moph-table__num" title="Conversion rate — conversions ÷ clicks. How well clicks turn into conversions.">' + fmtRate(c.cvr) + '</td>' +
        '<td class="moph-table__num" title="' + (c.cac != null ? ((c.attributedOrders || 0) + ' attributed order' + (c.attributedOrders === 1 ? '' : 's') + ' — spend ÷ orders') : 'No paid-attributed orders this period') + '">' + (c.cac != null ? fmtMoney(c.cac, { compact: true }) : '—') + '</td>' +
        '<td class="moph-table__num">' + fmtIS(c.searchImpressionShare) + '</td>' +
        '<td class="moph-table__num">' + fmtIS(c.searchBudgetLostIS) + '</td>' +
        '<td class="moph-table__num">' + fmtIS(c.searchRankLostIS) + '</td>' +
        '<td>' + recBadge + '</td>' +
      '</tr>';
    }).join('');
    if (!sorted.length && hiddenPaused) {
      tbody.innerHTML = '<tr><td colspan="18" class="moph-empty">All ' + hiddenPaused + ' campaigns in this range are paused. Turn on the Paused switch to see them.</td></tr>';
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — equalize REC pill widths:
       set every pill (Scale / Optimize / Hold / Fix / Verify) to the widest one
       (usually "Optimize") so the column reads as a clean, aligned stack. Measured
       after render (box-sizing:border-box on the badge makes width = total width);
       re-runs on every re-render. Mirrors the SOURCE-pill equalize pass. */
    var _recPills = tbody.querySelectorAll('.moph-rec-badge');
    if (_recPills.length) {
      var _maxRecW = 0;
      _recPills.forEach(function(p) { p.style.width = ''; if (p.offsetWidth > _maxRecW) _maxRecW = p.offsetWidth; });
      if (_maxRecW > 0) _recPills.forEach(function(p) { p.style.width = _maxRecW + 'px'; });
    }
    var unmatched = state.showPlan ? decorated.filter(function(c) { return c.planTarget == null; }).length : 0;
    var _cCount = document.getElementById('mophCampaignsCount');
    if (_cCount) {
      _cCount.textContent = '(' + sorted.length + ')';
      _cCount.title = [
        hiddenPaused ? (hiddenPaused + ' paused campaign' + (hiddenPaused === 1 ? '' : 's') + ' hidden. Turn on the Paused switch to show ' + (hiddenPaused === 1 ? 'it' : 'them') + '.') : '',
        (state.showPlan && unmatched > 0) ? 'Budgets are set per channel on the Budget Plan page; campaigns carry no target of their own' : ''
      ].filter(Boolean).join(' ');
    }

    // Totals row — sums for Spend/Revenue/Net/Conv; BLENDED ROAS + CAC (total ÷
    // total, not column sums). Computed over the full filtered set (decorated),
    // so it's sort-independent. Mirrors the Recent Sales footer.
    var foot = document.getElementById('mophCampaignsFoot');
    if (foot) {
      var tSpend = 0, tRev = 0, tConv = 0, tOrders = 0, tTarget = 0, tImpr = 0, tClicks = 0, tLeads = 0, anyTarget = false;
      decorated.forEach(function(c) {
        tSpend += Number(c.spend) || 0;
        tRev += Number(c.revenue) || 0;
        tConv += Number(c.conversions) || 0;
        tOrders += Number(c.attributedOrders) || 0;
        tImpr += Number(c.impressions) || 0;
        tClicks += Number(c.clicks) || 0;
        tLeads += Number(c.leads) || 0;
        if (c.planTarget != null && c.planTarget > 0) { tTarget += c.planTarget; anyTarget = true; }
      });
      foot.hidden = false;
      document.getElementById('mophCampaignsFootRange').textContent = '(' + rangeLabel() + ')';
      document.getElementById('mophCampFootSpend').textContent = fmtMoney(tSpend, { compact: true });
      var _fImpr = document.getElementById('mophCampFootImpr');
      if (_fImpr) _fImpr.textContent = tImpr >= 10000 ? Math.round(tImpr / 1000) + 'k' : (tImpr >= 1000 ? (tImpr / 1000).toFixed(1) + 'k' : String(tImpr));
      var _fClicks = document.getElementById('mophCampFootClicks');
      if (_fClicks) _fClicks.textContent = tClicks >= 10000 ? Math.round(tClicks / 1000) + 'k' : (tClicks >= 1000 ? (tClicks / 1000).toFixed(1) + 'k' : String(tClicks));
      // #6 — blended CTR (total clicks ÷ total impr, 1-decimal) + CVR (total conv ÷ total clicks).
      var _fCtr = document.getElementById('mophCampFootCtr');
      if (_fCtr) _fCtr.textContent = tImpr > 0 ? (tClicks / tImpr * 100).toFixed(1) + '%' : '—';
      var _fLeads = document.getElementById('mophCampFootLeads');
      if (_fLeads) _fLeads.textContent = fmtInt(tLeads);
      var _fCvr = document.getElementById('mophCampFootCvr');
      if (_fCvr) _fCvr.textContent = tClicks > 0 ? (tConv / tClicks * 100).toFixed(2) + '%' : '—';
      document.getElementById('mophCampFootRevenue').textContent = fmtMoney(tRev, { compact: true });
      document.getElementById('mophCampFootNet').textContent = fmtMoney(tRev - tSpend, { compact: true });
      document.getElementById('mophCampFootRoas').textContent = fmtRoas(tSpend > 0 ? tRev / tSpend : 0);
      document.getElementById('mophCampFootConv').textContent = fmtInt(tConv);
      document.getElementById('mophCampFootCac').textContent = tOrders > 0 ? fmtMoney(tSpend / tOrders, { compact: true }) : '—';
      document.getElementById('mophCampFootTarget').textContent = anyTarget ? fmtMoney(tTarget, { compact: true }) : '—';
      document.getElementById('mophCampFootVariance').innerHTML = (anyTarget && tTarget > 0) ? fmtVariance(variancePct(tSpend, tTarget)) : '—';
    }
  }

  // ─── Campaign-row expand (CUSTOM_MARKETINGOPS_BY_CLAUDE) ─────────────────
  // DD-style click-to-expand: inject a detail <tr> after the clicked row,
  // lazy-load /dashboard/campaign-detail, render an in-period daily trend +
  // month-over-month history. Insert-on-demand (one panel at a time per row)
  // keeps the DOM clean and survives renderCampaigns() rebuilds.
  // The Campaigns table is table-layout:fixed, but a colspan detail row still
  // makes it redistribute the auto columns (Revenue…Rec shrink ~90→74px while a
  // row is expanded — the hidden plan columns trigger it). Pin the current
  // header widths inline while any panel is open so columns can't reflow; unpin
  // when the last one closes (and on every renderCampaigns rebuild).
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-07) — a header too wide for its column is cut to
     its fitting letters + ONE "." (Campaign → "Campa.") instead of the browser's "…", which costs
     letters. Chrome has no CSS for a custom marker (text-overflow:"." is Firefox-only), so this
     trims the header's own text node, longest prefix that fits, and keeps the full name in title.
     Re-fits whenever a header cell changes size (window, Plan columns, pinned widths); fitting is
     idempotent, so the observer settles after one pass. */
  function _fitCampaignHeaders() {
    var ths = document.querySelectorAll('#mophCampaigns thead th');
    ths.forEach(function(th) {
      var node = null;
      for (var n = th.firstChild; n; n = n.nextSibling) { if (n.nodeType === 3 && n.nodeValue.trim()) { node = n; break; } }
      if (!node) return;
      if (th._fullLabel == null) {
        th._fullLabel = node.nodeValue;
        if (!th.title) th.title = th._fullLabel.trim();
      }
      var full = th._fullLabel;
      node.nodeValue = full;
      if (th.scrollWidth <= th.clientWidth) return;
      var lo = 1, hi = full.trim().length - 1, best = 1;
      while (lo <= hi) {
        var mid = (lo + hi) >> 1;
        node.nodeValue = full.trim().slice(0, mid) + '.';
        if (th.scrollWidth <= th.clientWidth) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      node.nodeValue = full.trim().slice(0, best) + '.';
    });
  }
  var _campHeadRO = null, _campHeadRaf = 0;
  function _watchCampaignHeaders() {
    if (_campHeadRO || typeof ResizeObserver === 'undefined') { if (!_campHeadRO) _fitCampaignHeaders(); return; }
    _campHeadRO = new ResizeObserver(function() {
      if (_campHeadRaf) return;
      _campHeadRaf = requestAnimationFrame(function() { _campHeadRaf = 0; _fitCampaignHeaders(); });
    });
    document.querySelectorAll('#mophCampaigns thead th').forEach(function(th) { _campHeadRO.observe(th); });
  }
  function _pinCampaignCols() {
    var tbl = document.getElementById('mophCampaigns');
    if (!tbl || tbl._colsPinned) return;
    var ths = tbl.querySelectorAll('thead th');
    for (var i = 0; i < ths.length; i++) ths[i].style.width = Math.round(ths[i].getBoundingClientRect().width) + 'px';
    tbl._colsPinned = true;
  }
  function _unpinCampaignCols() {
    var tbl = document.getElementById('mophCampaigns');
    if (!tbl || !tbl._colsPinned) return;
    var ths = tbl.querySelectorAll('thead th');
    for (var i = 0; i < ths.length; i++) ths[i].style.width = '';
    tbl._colsPinned = false;
  }
  // #7 — set a panel's slide height to its measured content (jank-free open/close
  // + nested ad-group expands). `el` is any node inside the panel.
  function _sizeDetailPanel(el) {
    var inner = el && el.closest ? el.closest('.moph-camp-detail__inner') : null;
    if (inner) inner.style.maxHeight = inner.scrollHeight + 'px';
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-06) — the row's +/− caret is gone: the hover
     colour + pointer already say a row opens, so the caret (and its spin) was redundant. */
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — "context header" clone. When a row
     deep in the table is expanded, a copy of the column-header row drops into place just
     above it so you never lose track of which column is which (Excel "freeze top row",
     but perched on the expanded row). Skipped for the top 4 (the real header is still
     close). One per expanded row; reverses on collapse. It's a real <tr> so it auto-aligns
     to the data columns + rides the pinned widths; it stays bright while focus-mode dims
     the un-expanded rows; and it's non-interactive (the real header keeps the sort UI). */
  var _CTX_SKIP_TOP = 4;
  function _addCtxHeader(row) {
    var tbody = row.parentNode; if (!tbody) return;
    var rows = tbody.querySelectorAll('tr.moph-camp-row');
    if (Array.prototype.indexOf.call(rows, row) < _CTX_SKIP_TOP) return; // top 4 → skip
    var prev = row.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('moph-camp-ctxhead')) return; // already present
    var theadTr = document.querySelector('#mophCampaigns thead tr');
    if (!theadTr) return;
    var ctx = theadTr.cloneNode(true);
    ctx.className = 'moph-camp-ctxhead';
    ctx.querySelectorAll('button').forEach(function(b) { b.remove(); });                       // drop "?" explainers
    ctx.querySelectorAll('[data-sort]').forEach(function(th) { th.removeAttribute('data-sort'); });
    ctx.querySelectorAll('[data-sort-active]').forEach(function(th) { th.removeAttribute('data-sort-active'); });
    ctx.querySelectorAll('th').forEach(function(th) { th.innerHTML = '<span class="moph-ctxh__c">' + th.innerHTML + '</span>'; }); // wrap so we can animate (transform on <th> is flaky)
    tbody.insertBefore(ctx, row);
  }
  function _removeCtxHeader(row) {
    var ctx = row.previousElementSibling;
    if (!ctx || !ctx.classList || !ctx.classList.contains('moph-camp-ctxhead')) return;
    ctx.classList.add('is-closing');
    window.setTimeout(function() { if (ctx.parentNode) ctx.parentNode.removeChild(ctx); }, 200);
  }

  function toggleCampaignDetail(row) {
    var next = row.nextElementSibling;
    if (next && next.classList.contains('moph-camp-detail')) {
      var innerC = next.querySelector('.moph-camp-detail__inner');
      if (innerC) innerC.style.maxHeight = '0px'; // animate measured → 0 (no delayed-collapse jank)
      next.classList.remove('is-open');
      row.classList.remove('moph-camp-row--expanded');
      _removeCtxHeader(row); // slide the context header back up + remove
      window.setTimeout(function() {
        if (next.parentNode) next.parentNode.removeChild(next);
        var tbl = document.getElementById('mophCampaigns');
        if (tbl && !tbl.querySelector('.moph-camp-detail')) _unpinCampaignCols();
      }, 420);
      return;
    }
    var cid = row.getAttribute('data-cid') || '';
    var name = row.getAttribute('data-name') || '';
    var platform = row.getAttribute('data-platform') || '';
    _pinCampaignCols();
    _addCtxHeader(row); // drop the cloned column-header in above this row (skips top 4)
    var tr = document.createElement('tr');
    tr.className = 'moph-camp-detail';
    tr.innerHTML = '<td colspan="16" class="moph-camp-detail__cell"><div class="moph-camp-detail__inner">'
      + '<div class="moph-camp-detail__pad"><div class="moph-cd-loading">Loading campaign detail…</div></div></div></td>';
    row.parentNode.insertBefore(tr, row.nextSibling);
    row.classList.add('moph-camp-row--expanded');
    // Force reflow so the max-height transition runs from 0.
    void tr.offsetHeight;
    tr.classList.add('is-open');
    _sizeDetailPanel(tr.querySelector('.moph-camp-detail__pad')); // animate 0 → placeholder height
    loadCampaignDetail(cid, name, platform, tr.querySelector('.moph-camp-detail__pad'));
  }

  var _cdCache = {};
  async function loadCampaignDetail(cid, name, platform, pad) {
    var range = dateRange();
    // Cache by campaign + range + attribution so re-expanding a row you just
    // closed renders instantly (the synchronous cache hit replaces the
    // "Loading…" placeholder in the same tick — no network, no flash).
    var ckey = (cid || name) + '|' + range.from + '|' + range.to + '|' + state.attribution;
    if (_cdCache[ckey]) { pad.innerHTML = renderCampaignDetail(_cdCache[ckey]); _sizeDetailPanel(pad); _cdLoadPrev(_cdCache[ckey], ckey, cid, name, platform, range, pad); return; }
    var qs = '?from=' + range.from + '&to=' + range.to + '&attribution=' + state.attribution;
    if (cid) qs += '&campaignId=' + encodeURIComponent(cid);
    if (name) qs += '&campaignName=' + encodeURIComponent(name);
    if (platform) qs += '&platform=' + encodeURIComponent(platform);
    try {
      var data = await mwFetch('/dashboard/campaign-detail' + qs);
      _cdCache[ckey] = data;
      pad.innerHTML = renderCampaignDetail(data);
      _sizeDetailPanel(pad); // grow the panel to the full loaded content
      _cdLoadPrev(data, ckey, cid, name, platform, range, pad); // lazy prev-period overlay
    } catch (e) {
      pad.innerHTML = '<div class="moph-camp-detail__err">Couldn’t load campaign detail: ' + escapeHtml((e && e.message) || 'error') + '</div>';
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — previous-period overlay, fetched
     ONLY when a campaign is actually expanded (not in the bulk prefetch): if you opened a
     campaign, you might click a mini to zoom, where the prior-period dotted line lives.
     Slim endpoint (just the daily series). On arrival, stash it on the cached payload and
     re-render so the zoom charts pick up the dotted line (minis are unaffected). */
  async function _cdLoadPrev(data, ckey, cid, name, platform, range, pad) {
    if (!data || data._prevLoaded || !(data.daily && data.daily.length)) return;
    data._prevLoaded = true; // fetch once
    var fromMs = Date.parse(range.from), toMs = Date.parse(range.to);
    if (isNaN(fromMs) || isNaN(toMs)) return;
    var lenDays = Math.round((toMs - fromMs) / 86400000) + 1;
    var prevTo = new Date(fromMs - 86400000).toISOString().slice(0, 10);
    var prevFrom = new Date(fromMs - lenDays * 86400000).toISOString().slice(0, 10);
    var qs = '?from=' + prevFrom + '&to=' + prevTo + '&attribution=' + state.attribution + '&slim=1';
    if (cid) qs += '&campaignId=' + encodeURIComponent(cid);
    if (name) qs += '&campaignName=' + encodeURIComponent(name);
    if (platform) qs += '&platform=' + encodeURIComponent(platform);
    try {
      var prev = await mwFetch('/dashboard/campaign-detail' + qs);
      data.dailyPrev = (prev && prev.daily) || [];
      // Re-render only if this campaign's pad is still the one showing (cache match) and
      // the user hasn't opened a zoom/form yet (avoid yanking an in-progress interaction).
      if (_cdCache[ckey] === data && pad && pad.isConnected
        && !pad.querySelector('.moph-cd-spark--active') && !pad.querySelector('.moph-cd-note-form--open')) {
        pad.innerHTML = renderCampaignDetail(data);
        _sizeDetailPanel(pad);
      }
    } catch (e) { /* best-effort — no overlay if the prior period fails */ }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — background cache-warmer. After
     the dashboard loads, quietly prefetch each campaign's detail payload (in table
     order, throttled to 2 concurrent) into _cdCache, keyed IDENTICALLY to the click
     handler — so expanding a row hits the warm cache and renders instantly instead of
     waiting on a round-trip. Best-effort: failures are ignored (the click path retries),
     and a generation token cancels an in-flight wave when the range/attribution changes
     so we never warm a stale window. */
  var _cdPrefetchGen = 0;
  function prefetchCampaignDetails() {
    var rows = document.querySelectorAll('#mophCampaigns tbody tr.moph-camp-row');
    if (!rows.length) return;
    var range = dateRange();
    var attr = state.attribution;
    var gen = ++_cdPrefetchGen;
    var items = Array.prototype.slice.call(rows, 0, 30).map(function(r) {
      return { cid: r.getAttribute('data-cid') || '', name: r.getAttribute('data-name') || '', platform: r.getAttribute('data-platform') || '' };
    });
    var idx = 0, CONC = 2;
    function pump() {
      if (gen !== _cdPrefetchGen) return;        // a newer load superseded this wave
      if (idx >= items.length) return;
      var it = items[idx++];
      var ckey = (it.cid || it.name) + '|' + range.from + '|' + range.to + '|' + attr;
      if (_cdCache[ckey]) { pump(); return; }     // already warm
      var qs = '?from=' + range.from + '&to=' + range.to + '&attribution=' + attr;
      if (it.cid) qs += '&campaignId=' + encodeURIComponent(it.cid);
      if (it.name) qs += '&campaignName=' + encodeURIComponent(it.name);
      if (it.platform) qs += '&platform=' + encodeURIComponent(it.platform);
      mwFetch('/dashboard/campaign-detail' + qs)
        .then(function(data) { if (gen === _cdPrefetchGen) _cdCache[ckey] = data; })
        .catch(function() { /* best-effort */ })
        .then(pump);                              // advance the chain regardless
    }
    for (var k = 0; k < CONC; k++) pump();
  }

  function cdMD(s) { var p = String(s || '').split('-'); return p.length === 3 ? (p[1] + '/' + p[2]) : s; }
  function cdNiceDate(s) {
    var p = String(s || '').split('-');
    if (p.length !== 3) return s;
    var M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return M[(Number(p[1]) || 1) - 1] + ' ' + (Number(p[2]) || '');
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — small-multiples for the campaign
     expand. Replaces the two "average" charts (daily-trend + MoM) with a grid of tiny
     per-metric sparklines: one clean trend line per metric on a shared time axis, each
     with its period headline + a within-period direction arrow. Far more scannable than
     a cramped dual-axis combo, and the canvas the change-annotation feed will draw on. */
  /* Change-annotation markers (Phase C). All 8 sparklines share the same x-axis, so
     renderCampaignDetail computes the marker set once into _cdActiveMarks and every
     cdSpark draws it. Markers are HTML chevrons overlaid on the chart (NOT inside the
     SVG, whose preserveAspectRatio="none" would distort them) — positioned by % so they
     line up with the line below, colored by source (manual=amber / budget=blue), and
     gently bouncing to draw the eye. */
  var _cdActiveMarks = [];
  function cdSpark(vals, color) {
    var n = vals.length;
    var W = 150, H = 34, PL = 2, PR = 2, PT = 4, PB = 4;
    var CHART_PAD = 8, GLYPH = 10; // must match .moph-cd-spark__chart padding-top + chevron height
    var svg, yF = null;
    if (!n) {
      svg = '<svg class="moph-cd-spark__svg" viewBox="0 0 150 34" preserveAspectRatio="none"></svg>';
    } else {
      var min = Math.min.apply(null, vals), max = Math.max.apply(null, vals);
      if (min === max) { min -= 1; max += 1; } // flat series → center the line
      var xs = vals.map(function(v, i) { return PL + (n > 1 ? i / (n - 1) : 0.5) * (W - PL - PR); });
      yF = function(v) { return PT + (1 - (v - min) / (max - min)) * (H - PT - PB); };
      var line = smoothPath(xs, vals.map(yF));
      var area = line + ' L' + xs[n - 1].toFixed(1) + ',' + (H - PB).toFixed(1) + ' L' + xs[0].toFixed(1) + ',' + (H - PB).toFixed(1) + ' Z';
      svg = '<svg class="moph-cd-spark__svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none">'
        + '<path d="' + area + '" fill="' + color + '" opacity="0.10" stroke="none"/>'
        + '<path d="' + line + '" fill="none" stroke="' + color + '" stroke-width="1.6" vector-effect="non-scaling-stroke"/>'
        + '</svg>';
    }
    // Position each chevron's resting Y on THIS metric's line at the change date, so it
    // bounces right off the line (low line → low chevron). tip ≈ line at rest, then bounces up.
    var marks = (_cdActiveMarks || []).map(function(mk) {
      var topStyle = '';
      if (yF && mk.idx != null && mk.idx >= 0 && mk.idx < n) {
        var tp = yF(vals[mk.idx]) + CHART_PAD - GLYPH;
        if (tp < 0) tp = 0;
        topStyle = ';top:' + tp.toFixed(1) + 'px';
      }
      var srcCls = (mk.source === 'budget' || mk.source === 'mixed') ? mk.source : 'manual';
      var badge = (mk.count > 1) ? '<span class="moph-cd-mark__n">+' + (mk.count - 1) + '</span>' : '';
      return '<span class="moph-cd-mark moph-cd-mark--' + srcCls + '"'
        + ' style="left:' + mk.pct.toFixed(1) + '%' + topStyle + '" title="' + escapeHtml(mk.title) + '">&#9660;' + badge + '</span>';
    }).join('');
    return '<div class="moph-cd-spark__chart">' + svg + marks + '</div>';
  }
  // Within-period momentum: 2nd-half vs 1st-half. _sumPct for volume metrics;
  // _ratioPct for rate metrics (CTR/CVR/ROAS) so the rate, not the raw count, trends.
  // A % is only shown when BOTH halves are strictly positive — otherwise the change
  // either has a zero baseline (started at 0 → divide-by-zero / ∞%) or crosses zero
  // (e.g. Net swinging profit→loss → a meaningless "−200%", or a sparse count spiking
  // then returning to 0 → a misleading "−100%"). In those cases we return null and the
  // card simply shows no trend chip; the sparkline still conveys the shape.
  function _cdSum(a, lo, hi) { var s = 0; for (var i = lo; i < hi; i++) s += Number(a[i]) || 0; return s; }
  function _sumPct(a) { if (a.length < 4) return null; var m = Math.floor(a.length / 2); var s1 = _cdSum(a, 0, m), s2 = _cdSum(a, m, a.length); if (s1 <= 0 || s2 <= 0) return null; return (s2 - s1) / s1 * 100; }
  function _ratioPct(num, den) { if (num.length < 4) return null; var m = Math.floor(num.length / 2); var n1 = _cdSum(num, 0, m), n2 = _cdSum(num, m, num.length), d1 = _cdSum(den, 0, m), d2 = _cdSum(den, m, den.length); var r1 = d1 > 0 ? n1 / d1 : 0, r2 = d2 > 0 ? n2 / d2 : 0; if (r1 <= 0 || r2 <= 0) return null; return (r2 - r1) / r1 * 100; }
  function cdMetric(label, headline, vals, color, opts) {
    opts = opts || {};
    var trendHtml = '', tr = opts.trend;
    if (tr != null && isFinite(tr)) {
      var dir = tr > 0.5 ? 1 : (tr < -0.5 ? -1 : 0);
      var arrow = dir === 0 ? '&rarr;' : (dir > 0 ? '&#9650;' : '&#9660;');
      var clr;
      if (opts.neutral || dir === 0) clr = 'var(--moph-ink-2)';
      else { var good = opts.invert ? dir < 0 : dir > 0; clr = good ? 'var(--moph-green)' : 'var(--moph-red)'; }
      // Clamp the magnitude — a tiny first-half baseline can post a real but silly
      // figure (e.g. 1 → 12 orders = +1100%); cap the display at 999%.
      var _mag = Math.abs(Math.round(tr));
      var _magStr = _mag > 999 ? '999+' : String(_mag);
      trendHtml = '<span class="moph-cd-spark__trend" style="color:' + clr + '" title="2nd half vs 1st half of the selected period">' + arrow + ' ' + _magStr + '%</span>';
    }
    return '<div class="moph-cd-spark"' + (opts.key ? ' data-metric="' + opts.key + '"' : '') + '>'
      + '<div class="moph-cd-spark__top"><span class="moph-cd-spark__label">' + label + '</span>' + trendHtml + '</div>'
      + '<div class="moph-cd-spark__val">' + headline + '</div>'
      + cdSpark(vals, color)
      + '</div>';
  }

  // In-period daily trend — Ad Revenue (blue, filled) vs Spend (gray, dashed).
  // Reuses the page's smoothPath(). Date labels render as HTML below the SVG
  // (not <text>) so the preserveAspectRatio="none" x-stretch can't elongate glyphs.
  function cdLineChart(daily) {
    if (!daily || !daily.length) return '<div class="moph-cd-mom-empty">No daily data in this range.</div>';
    var W = 320, H = 120, PL = 6, PR = 6, PT = 10, PB = 8, n = daily.length;
    var xs = daily.map(function(d, i) { return PL + (n > 1 ? i / (n - 1) : 0.5) * (W - PL - PR); });
    var rev = daily.map(function(d) { return Number(d.revenue) || 0; });
    var spend = daily.map(function(d) { return Number(d.spend) || 0; });
    var maxV = Math.max(1, Math.max.apply(null, rev), Math.max.apply(null, spend));
    function yF(v) { return PT + (1 - v / maxV) * (H - PT - PB); }
    var revPath = smoothPath(xs, rev.map(yF));
    var spPath = smoothPath(xs, spend.map(yF));
    var area = revPath + ' L' + xs[n - 1].toFixed(1) + ',' + yF(0).toFixed(1) + ' L' + xs[0].toFixed(1) + ',' + yF(0).toFixed(1) + ' Z';
    return '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="none" style="display:block;width:100%;height:100%;">'
      + '<path d="' + area + '" fill="var(--moph-blue-glow)" stroke="none"/>'
      + '<path d="' + spPath + '" fill="none" stroke="var(--moph-ink-2)" stroke-width="1.5" stroke-dasharray="4,3" vector-effect="non-scaling-stroke"/>'
      + '<path d="' + revPath + '" fill="none" stroke="var(--moph-blue)" stroke-width="2" vector-effect="non-scaling-stroke"/>'
      + '</svg>';
  }

  // Month-over-month — horizontal spend bars (real history) + CM figure where
  // a closed-month snapshot has captured it. Spend always present; CM accrues.
  function cdMomChart(mom) {
    if (!mom || !mom.length) return '<div class="moph-cd-mom-empty">Month-over-month history will appear as months close.</div>';
    var rows = mom.slice(-12);
    var maxSpend = Math.max(1, Math.max.apply(null, rows.map(function(m) { return Number(m.spend) || 0; })));
    var anyCm = rows.some(function(m) { return m.cm != null; });
    var bars = rows.map(function(m) {
      var pct = (Number(m.spend) || 0) / maxSpend * 100;
      var cmCell = (m.cm != null) ? '<span class="moph-momrow__cm">CM ' + fmtMoney(m.cm, { compact: true }) + '</span>' : '';
      return '<div class="moph-momrow" title="' + escapeHtml(m.month) + '">'
        + '<span class="moph-momrow__m">' + escapeHtml(m.month.slice(2)) + '</span>'
        + '<span class="moph-momrow__track"><span class="moph-momrow__bar" style="width:' + pct.toFixed(1) + '%"></span></span>'
        + '<span class="moph-momrow__v">' + fmtMoney(m.spend, { compact: true }) + '</span>'
        + cmCell
        + '</div>';
    }).join('');
    var note = anyCm ? '' : '<div class="moph-cd-note">Bars = monthly ad spend. CM history builds as each month closes.</div>';
    return '<div class="moph-mom">' + bars + '</div>' + note;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#7) — ad-group → ad breakdown (Google only).
     Each ad group is an expandable <details> (the +/− spins via the shared
     capture-phase toggle handler); expanding reveals its ads, giving the full
     campaign → ad group → ad hierarchy. Empty for Meta / PMax (no rows). */
  function cdNum(n) {
    n = Number(n) || 0;
    if (n >= 10000) return Math.round(n / 1000) + 'k';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
    return String(n);
  }
  function cdPct(v) { return (v != null && isFinite(v)) ? (v * 100).toFixed(2) + '%' : '—'; }
  function cdAdLabel(a) {
    if (a.adName) return a.adName;
    var ty = a.adType ? String(a.adType).replace(/_/g, ' ').toLowerCase() : 'ad';
    return ty + ' · ' + (a.adId || '');
  }
  function cdAdGroups(adGroups) {
    if (!adGroups || !adGroups.length) return '';
    // Summary (group totals), the column header, and each ad row all share the SAME
    // grid (.moph-cd-ag__row) so the group totals sit directly over their column headers
    // and the ad values below. Groups render auto-expanded (open) so the ads show without
    // a click; "Ad" is left-aligned, the four metrics right-aligned over their columns.
    var groups = adGroups.map(function(g) {
      var ads = (g.ads || []).map(function(a) {
        var lbl = cdAdLabel(a);
        var aCvr = (Number(a.clicks) || 0) > 0 ? (Number(a.conversions) || 0) / a.clicks : null;
        return '<div class="moph-cd-ag__row moph-cd-ag__adrow">'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--name"><span class="moph-cd-ag__nametext" title="' + escapeHtml(lbl) + '">' + escapeHtml(lbl) + '</span></span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + fmtMoney(a.spend, { compact: true }) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + cdNum(a.impressions) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + cdNum(a.clicks) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + cdPct(a.ctr) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + cdNum(a.conversions) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n">' + cdPct(aCvr) + '</span>'
          + '</div>';
      }).join('');
      var adCount = (g.ads || []).length;
      var gname = escapeHtml(g.adGroupName || '(unnamed ad group)');
      return '<details class="moph-cd-ag" open>'
        + '<summary class="moph-cd-ag__sum moph-cd-ag__row">'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--name">'
            + '<span class="moph-cd-ag__caret" aria-hidden="true"></span>'
            + '<span class="moph-cd-ag__nametext" title="' + gname + '">' + gname + '</span>'
            + '<span class="moph-cd-ag__count">' + adCount + ' ad' + (adCount === 1 ? '' : 's') + '</span>'
          + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + fmtMoney(g.spend, { compact: true }) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + cdNum(g.impressions) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + cdNum(g.clicks) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + cdPct(g.ctr) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + cdNum(g.conversions) + '</span>'
          + '<span class="moph-cd-ag__c moph-cd-ag__c--n moph-cd-ag__total">' + cdPct((Number(g.clicks) || 0) > 0 ? (Number(g.conversions) || 0) / g.clicks : null) + '</span>'
        + '</summary>'
        + '<div class="moph-cd-ag__body">'
          + '<div class="moph-cd-ag__row moph-cd-ag__head">'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--name">Ad</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n">Spend</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n">Impr</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n">Clicks</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n">CTR</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n" title="Platform-reported conversions (Google’s own count, not Shopify orders)">Conv</span>'
            + '<span class="moph-cd-ag__c moph-cd-ag__c--n" title="Conversions ÷ clicks (platform conversions)">CVR</span>'
          + '</div>'
          + ads
        + '</div>'
        + '</details>';
    }).join('');
    return '<div class="moph-cd-card moph-cd-card--full">'
      + '<div class="moph-cd-card__h">Ad groups &rarr; ads <span class="moph-cd-ag__src">Google · group totals over each column</span></div>'
      + groups + '</div>';
  }

  /* Per-metric daily series from a daily array — shared by the current AND the prior
     period (the dotted zoom overlay). Volume metrics use daily values; ROAS & CVR are
     CUMULATIVE (running rev÷spend, running orders÷clicks — a daily-point ratio explodes
     since a sale lags its click); CTR is a same-day signal. */
  function _cdMetricSeries(d) {
    d = d || [];
    var spend = d.map(function(x) { return Number(x.spend) || 0; });
    var rev = d.map(function(x) { return Number(x.revenue) || 0; });
    var net = d.map(function(x) { return Number(x.cm) || 0; });
    var impr = d.map(function(x) { return Number(x.impressions) || 0; });
    var clicks = d.map(function(x) { return Number(x.clicks) || 0; });
    var orders = d.map(function(x) { return Number(x.orders) || 0; });
    var ctr = d.map(function(x) { var i = Number(x.impressions) || 0; return i > 0 ? (Number(x.clicks) || 0) / i : 0; });
    var roasC = [], cvrC = [], cRev = 0, cSpend = 0, cOrders = 0, cClicks = 0;
    d.forEach(function(x) {
      cRev += Number(x.revenue) || 0; cSpend += Number(x.spend) || 0;
      cOrders += Number(x.orders) || 0; cClicks += Number(x.clicks) || 0;
      roasC.push(cSpend > 0 ? cRev / cSpend : 0);
      cvrC.push(cClicks > 0 ? cOrders / cClicks : 0);
    });
    return { spend: spend, rev: rev, net: net, impr: impr, clicks: clicks, orders: orders, ctr: ctr, roasC: roasC, cvrC: cvrC };
  }

  function renderCampaignDetail(data) {
    var t = data.totals || {};
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08, fix d) — this campaign's OWN
       break-even ROAS (attributed revenue ÷ gross profit = 1/margin), so you can
       judge it against its own product-mix margin instead of the blunt account-wide
       break-even line. Colors the ROAS green/red vs that break-even. */
    var _be = (t.breakEvenRoas != null) ? Number(t.breakEvenRoas) : null;
    var _roasOk = (_be != null && t.roas != null) ? (Number(t.roas) >= _be) : null;
    var _roasClr = (_roasOk === null) ? '' : (' style="color:' + (_roasOk ? 'var(--moph-green)' : 'var(--moph-red)') + '"');
    var head = '<div class="moph-cd-head">'
      + '<span class="moph-cd-title">' + escapeHtml(data.name || '') + '</span>'
      + '<span class="moph-cd-stat">Spend <b>' + fmtMoney(t.spend) + '</b></span>'
      + '<span class="moph-cd-stat">Ad Revenue <b>' + fmtMoney(t.revenue) + '</b></span>'
      + '<span class="moph-cd-stat">CM <b>' + fmtMoney(t.cm) + '</b></span>'
      + '<span class="moph-cd-stat">ROAS <b' + _roasClr + '>' + fmtRoas(t.roas) + '</b></span>'
      + (_be != null
          ? '<span class="moph-cd-stat" title="This campaign’s own break-even ROAS = its attributed revenue ÷ gross profit (1 ÷ margin). At/above it the campaign is profitable after goods cost; below it, each ad dollar loses money on this campaign’s product mix. Independent of the account-wide break-even line.">B/E <b>' + fmtRoas(_be) + '</b>'
              + (_roasOk !== null ? ' <span style="font-weight:700;color:' + (_roasOk ? 'var(--moph-green)' : 'var(--moph-red)') + '">' + (_roasOk ? '✓' : '⚠') + '</span>' : '')
              + '</span>'
          : '')
      + '<span class="moph-cd-stat">Orders <b>' + (t.orders || 0) + '</b></span>'
      + '</div>';
    var daily = data.daily || [];
    if (!daily.length) {
      _cdActiveMarks = [];
      return head + '<div class="moph-cd-mom-empty">No daily data in this range.</div>' + cdChanges(data) + cdAdGroups(data.adGroups);
    }
    /* Phase C — change-annotation markers across the shared x-axis. Only notes within
       the visible window are drawn (the rest still list in the Changes section); each is
       snapped to the nearest plotted day so the chevron sits over the right point. Set on
       the module-level _cdActiveMarks so every cdSpark below picks it up. */
    var _d0 = daily[0].date, _d1 = daily[daily.length - 1].date;
    var _idxFor = function(dateStr) {
      var tt = Date.parse(dateStr), best = 0, bestDiff = Infinity;
      for (var i = 0; i < daily.length; i++) { var diff = Math.abs(Date.parse(daily[i].date) - tt); if (diff < bestDiff) { bestDiff = diff; best = i; } }
      return best;
    };
    var _cap = function(s) { return s.charAt(0).toUpperCase() + s.slice(1); };
    // Group same-date changes into ONE marker so 2+ on a day don't stack into a mush.
    // The marker shows a "+n" badge and its hover lists every change that day.
    var _byDate = {};
    (data.notes || []).forEach(function(nt) {
      if (!nt || nt.date < _d0 || nt.date > _d1) return;
      (_byDate[nt.date] = _byDate[nt.date] || []).push(nt);
    });
    _cdActiveMarks = Object.keys(_byDate).map(function(dt) {
      var grp = _byDate[dt];
      var best = _idxFor(dt);
      var pct = daily.length > 1 ? (best / (daily.length - 1)) * 100 : 50;
      var srcs = {}; grp.forEach(function(nt) { srcs[nt.source === 'budget' ? 'budget' : 'manual'] = 1; });
      var skeys = Object.keys(srcs);
      var source = skeys.length === 1 ? skeys[0] : 'mixed'; // both agency + budget that day → "mixed"
      var title;
      if (grp.length === 1) {
        var one = grp[0];
        var cat1 = (one.category && one.category.toLowerCase() !== 'other') ? (_cap(one.category) + ' · ') : '';
        var who1 = one.source === 'budget' ? 'Budget change' : (one.authorName ? 'Logged by ' + one.authorName : 'Agency');
        title = cdNiceDate(dt) + ' — ' + cat1 + (one.text || '') + ' · ' + who1;
      } else {
        title = cdNiceDate(dt) + ' — ' + grp.length + ' changes:\n' + grp.map(function(nt) {
          var cat = (nt.category && nt.category.toLowerCase() !== 'other') ? (_cap(nt.category) + ' · ') : '';
          var who = nt.source === 'budget' ? 'Budget' : (nt.authorName || 'Agency');
          return '• ' + cat + (nt.text || '') + ' (' + who + ')';
        }).join('\n');
      }
      return { pct: pct, idx: best, source: source, count: grp.length, title: title };
    });
    // Per-metric daily series (via _cdMetricSeries). The prior-period series, when it's
    // been lazily loaded, drives the zoom charts' dotted overlay.
    var _ser = _cdMetricSeries(daily);
    var spend = _ser.spend, rev = _ser.rev, net = _ser.net, impr = _ser.impr, clicks = _ser.clicks, orders = _ser.orders, ctr = _ser.ctr, roasC = _ser.roasC, cvrC = _ser.cvrC;
    var _serP = (data.dailyPrev && data.dailyPrev.length) ? _cdMetricSeries(data.dailyPrev) : null;
    var totImpr = _cdSum(impr, 0, impr.length), totClicks = _cdSum(clicks, 0, clicks.length);
    var totOrders = (t.orders != null) ? Number(t.orders) : _cdSum(orders, 0, orders.length);
    var netClr = (Number(t.cm) >= 0) ? 'var(--moph-green)' : 'var(--moph-red)';
    // 8 metrics in 2 logical rows of 4. Row 1 = spend + the funnel rates (cost → reach
    // → click-rate → conversion-rate); Row 2 = the results (orders → revenue → profit →
    // efficiency). Volume inputs (Spend, Impressions) are neutral-colored; rates &
    // outcomes go green/red by direction.
    // One spec per metric → drives BOTH the mini grid AND the click-to-zoom big charts
    // (no duplicated metric definitions). `key` ties a mini to its zoom panel.
    var _specs = [
      { key: 'spend', label: 'Spend', vals: spend, prevVals: _serP && _serP.spend, color: 'var(--moph-ink-2)', headline: fmtMoney(t.spend), trend: _sumPct(spend), neutral: true, fmt: 'money' },
      { key: 'impr', label: 'Impressions', vals: impr, prevVals: _serP && _serP.impr, color: '#6366f1', headline: cdNum(totImpr), trend: _sumPct(impr), neutral: true, fmt: 'num' },
      { key: 'ctr', label: 'CTR', vals: ctr, prevVals: _serP && _serP.ctr, color: 'var(--moph-amber)', headline: cdPct(totImpr > 0 ? totClicks / totImpr : 0), trend: _ratioPct(clicks, impr), fmt: 'pct' },
      { key: 'cvr', label: 'CVR', vals: cvrC, prevVals: _serP && _serP.cvrC, color: '#db2777', headline: cdPct(totClicks > 0 ? totOrders / totClicks : 0), trend: _ratioPct(orders, clicks), fmt: 'pct' },
      { key: 'orders', label: 'Orders', vals: orders, prevVals: _serP && _serP.orders, color: '#0891b2', headline: fmtInt(totOrders), trend: _sumPct(orders), fmt: 'num' },
      { key: 'revenue', label: 'Ad Revenue', vals: rev, prevVals: _serP && _serP.rev, color: 'var(--moph-blue)', headline: fmtMoney(t.revenue), trend: _sumPct(rev), fmt: 'money' },
      { key: 'net', label: 'Net (CM)', vals: net, prevVals: _serP && _serP.net, color: netClr, headline: fmtMoney(t.cm), trend: _sumPct(net), fmt: 'money' },
      { key: 'roas', label: 'ROAS', vals: roasC, prevVals: _serP && _serP.roasC, color: '#7c3aed', headline: fmtRoas(t.roas), trend: _ratioPct(rev, spend), fmt: 'roas' }
    ];
    var grid = _specs.map(function(s) { return cdMetric(s.label, s.headline, s.vals, s.color, { trend: s.trend, neutral: s.neutral, key: s.key }); }).join('');
    // Pre-render every metric's big chart (hidden) so a click just reveals one — no
    // data plumbing, and _cdActiveMarks is correct here (synchronous, this panel's marks).
    var zoom = '<div class="moph-cd-zoom" hidden>' + _specs.map(function(s) { return cdBigChart(s, daily, _cdActiveMarks); }).join('') + '</div>';
    var d0 = daily[0].date, d1 = daily[daily.length - 1].date;
    var spanDays = Math.round((Date.parse(d1) - Date.parse(d0)) / 86400000) + 1;
    var dateHead = '<div class="moph-cd-sparkhead">'
      + '<span class="moph-cd-sparkhead__t">Daily trend</span>'
      + '<span class="moph-cd-sparkhead__r">' + cdNiceDate(d0) + ' &ndash; ' + cdNiceDate(d1)
        + '<span class="moph-cd-sparkhead__d"> · ' + spanDays + ' day' + (spanDays === 1 ? '' : 's') + '</span></span>'
      + '</div>';
    var foot = '<div class="moph-cd-sparkfoot">Click a graph to zoom · ROAS &amp; CVR shown cumulative · arrow = 2nd half vs 1st half</div>';
    return head + dateHead + '<div class="moph-cd-sparkgrid">' + grid + '</div>' + zoom + foot + cdChanges(data) + cdAdGroups(data.adGroups);
  }

  /* Phase C zoom — click a mini → a big full-width chart of that metric accordions open
     (real y-axis range + date labels + the same change markers). Pre-rendered hidden in
     the zoom container; the click handler reveals one and resizes the slide panel. */
  var ZOOM_H = 228; // chart height (px + viewBox) — kept in sync with .moph-cd-zoom CSS (taller = more y-axis resolution)
  function cdBigChart(spec, daily, marks) {
    var vals = spec.vals || [], n = vals.length;
    var prev = (spec.prevVals && spec.prevVals.length) ? spec.prevVals.slice(0, n) : null; // day-of-period aligned
    var W = 600, H = ZOOM_H, PL = 4, PR = 4, PT = 10, PB = 10;
    // Combined y-scale so current (solid) + previous (dotted) are directly comparable.
    var allV = prev ? vals.concat(prev) : vals;
    var min = allV.length ? Math.min.apply(null, allV) : 0, max = allV.length ? Math.max.apply(null, allV) : 1;
    if (min === max) { min -= 1; max += 1; }
    var xs = vals.map(function(v, i) { return PL + (n > 1 ? i / (n - 1) : 0.5) * (W - PL - PR); });
    var yF = function(v) { return PT + (1 - (v - min) / (max - min)) * (H - PT - PB); };
    var line = n ? smoothPath(xs, vals.map(yF)) : '';
    var area = n ? (line + ' L' + xs[n - 1].toFixed(1) + ',' + (H - PB).toFixed(1) + ' L' + xs[0].toFixed(1) + ',' + (H - PB).toFixed(1) + ' Z') : '';
    var prevPath = prev ? ('<path d="' + smoothPath(xs.slice(0, prev.length), prev.map(yF)) + '" fill="none" stroke="' + spec.color + '" stroke-width="1.5" stroke-dasharray="4,3" opacity="0.5" vector-effect="non-scaling-stroke"/>') : '';
    var svg = '<svg class="moph-cd-zoom__svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none">'
      + (n ? ('<path d="' + area + '" fill="' + spec.color + '" opacity="0.10"/>'
        + prevPath
        + '<path d="' + line + '" fill="none" stroke="' + spec.color + '" stroke-width="2" vector-effect="non-scaling-stroke"/>') : '')
      + '</svg>';
    var marksHtml = (marks || []).map(function(mk) {
      var topStyle = '';
      if (mk.idx != null && mk.idx >= 0 && mk.idx < n) { topStyle = ';top:' + (yF(vals[mk.idx]) - 11).toFixed(1) + 'px'; }
      var src = (mk.source === 'budget' || mk.source === 'mixed') ? mk.source : 'manual';
      var badge = (mk.count > 1) ? '<span class="moph-cd-mark__n">+' + (mk.count - 1) + '</span>' : '';
      return '<span class="moph-cd-mark moph-cd-mark--' + src + '" style="left:' + mk.pct.toFixed(1) + '%' + topStyle + '" title="' + escapeHtml(mk.title) + '">&#9660;' + badge + '</span>';
    }).join('');
    var _fmt = function(v) { return spec.fmt === 'money' ? fmtMoney(v, { compact: true }) : spec.fmt === 'roas' ? fmtRoas(v) : spec.fmt === 'pct' ? cdPct(v) : cdNum(v); };
    var d0 = daily.length ? cdNiceDate(daily[0].date) : '';
    var d1 = daily.length ? cdNiceDate(daily[daily.length - 1].date) : '';
    var dMid = daily.length > 2 ? cdNiceDate(daily[Math.floor(daily.length / 2)].date) : '';
    // Embed the series so the hover (dot + frosted tooltip + dotted guide) is self-contained.
    var _valsR = vals.map(function(v) { return Math.round(v * 1000) / 1000; });
    var _dataAttrs = ' data-vals="' + escapeHtml(JSON.stringify(_valsR)) + '" data-dates="' + escapeHtml(JSON.stringify(daily.map(function(d) { return d.date; }))) + '"'
      + ' data-min="' + min + '" data-max="' + max + '" data-fmt="' + spec.fmt + '" data-color="' + escapeHtml(spec.color) + '" data-label="' + escapeHtml(spec.label) + '"'
      + (prev ? ' data-prevvals="' + escapeHtml(JSON.stringify(prev.map(function(v) { return Math.round(v * 1000) / 1000; }))) + '"' : '');
    var prevNote = prev ? '<span class="moph-cd-zoom__prevnote">– – previous period</span>' : '';
    return '<div class="moph-cd-zoom__panel" data-metric="' + spec.key + '">'
      + '<div class="moph-cd-zoom__head"><span class="moph-cd-zoom__title" style="color:' + spec.color + '">' + spec.label + '</span><span class="moph-cd-zoom__cur">' + spec.headline + '</span>' + prevNote + '</div>'
      + '<div class="moph-cd-zoom__plot">'
        + '<div class="moph-cd-zoom__yax"><span>' + _fmt(max) + '</span><span>' + _fmt(min) + '</span></div>'
        + '<div class="moph-cd-zoom__chart"' + _dataAttrs + '>' + svg + marksHtml + '</div>'
      + '</div>'
      + '<div class="moph-cd-zoom__xax"><span>' + d0 + '</span><span>' + dMid + '</span><span>' + d1 + '</span></div>'
      + '</div>';
  }
  /* Phase C zoom hover — mirror the Daily Performance chart: nearest-day dot + a dashed
     vertical guide + the frosted tooltip (reuses .moph-chart__tip). Self-contained from
     the chart's data-* (no extra fetch). Attached when a zoom panel is revealed. */
  function _setupZoomHover(chartEl) {
    if (!chartEl || chartEl._zoomHoverBound) return;
    chartEl._zoomHoverBound = true;
    chartEl.addEventListener('mousemove', _zoomHoverMove);
    chartEl.addEventListener('mouseleave', function() { var ov = chartEl.querySelector('.moph-cd-zoom__hover'); if (ov) ov.style.display = 'none'; });
  }
  function _zoomHoverMove(e) {
    var chartEl = e.currentTarget, vals, dates;
    try { vals = JSON.parse(chartEl.getAttribute('data-vals') || '[]'); dates = JSON.parse(chartEl.getAttribute('data-dates') || '[]'); } catch (_) { return; }
    var n = vals.length; if (!n) return;
    var rect = chartEl.getBoundingClientRect();
    var fx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    var idx = Math.round(fx * (n - 1)); if (idx < 0) idx = 0; if (idx > n - 1) idx = n - 1;
    var min = Number(chartEl.getAttribute('data-min')), max = Number(chartEl.getAttribute('data-max'));
    var fmt = chartEl.getAttribute('data-fmt'), color = chartEl.getAttribute('data-color'), label = chartEl.getAttribute('data-label');
    var xPct = n > 1 ? (idx / (n - 1)) * 100 : 50;
    var PT = 10, PB = 10;
    var yPx = PT + (1 - (vals[idx] - min) / ((max - min) || 1)) * (ZOOM_H - PT - PB);
    var ov = chartEl.querySelector('.moph-cd-zoom__hover');
    if (!ov) {
      ov = document.createElement('div'); ov.className = 'moph-cd-zoom__hover';
      ov.innerHTML = '<div class="moph-cd-zoom__vline"></div><div class="moph-cd-zoom__dot"></div><div class="moph-chart__tip moph-cd-zoom__tip"></div>';
      chartEl.appendChild(ov);
    }
    ov.style.display = 'block';
    var vline = ov.querySelector('.moph-cd-zoom__vline'), dot = ov.querySelector('.moph-cd-zoom__dot'), tip = ov.querySelector('.moph-cd-zoom__tip');
    vline.style.left = xPct + '%';
    dot.style.left = xPct + '%'; dot.style.top = yPx + 'px'; dot.style.background = color;
    var _fv = function(v) { return fmt === 'money' ? fmtMoney(v, { compact: true }) : fmt === 'roas' ? fmtRoas(v) : fmt === 'pct' ? cdPct(v) : cdNum(v); };
    var prevvals = null; try { var pv = chartEl.getAttribute('data-prevvals'); if (pv) prevvals = JSON.parse(pv); } catch (_) {}
    var prevLine = (prevvals && idx < prevvals.length) ? ('<div class="moph-chart__tip-sub" style="opacity:0.7">prev: ' + _fv(prevvals[idx]) + '</div>') : '';
    // No metric-name sub-line: the card header already says which chart this is, so it'd
    // be redundant. Just date · value · (prev-period value).
    tip.innerHTML = '<div class="moph-chart__tip-date">' + escapeHtml(cdNiceDate(dates[idx])) + '</div>'
      + '<div class="moph-chart__tip-val" style="color:' + color + '">' + _fv(vals[idx]) + '</div>'
      + prevLine;
    tip.style.left = xPct + '%'; tip.style.top = yPx + 'px';
  }

  /* Phase C — the before-vs-after impact of one change, computed by the worker
     (computeImpact, the same primitive the recs engine will use). Shown when you click a
     change row. Honest by construction: a % only when both sides are positive, a bare
     arrow otherwise; "too soon" until enough after-data; a confound warning when another
     change overlaps the window. */
  function cdImpactStrip(impact) {
    if (!impact) return '';
    if (impact.tooSoon) {
      var d = impact.daysAfter || 0;
      return '<div class="moph-cd-impact moph-cd-impact--soon">Logged ' + d + ' day' + (d === 1 ? '' : 's')
        + ' ago — too soon to read impact (revenue &amp; orders lag the click ~weeks).</div>';
    }
    var m = impact.metrics || {};
    var defs = [
      { k: 'spend', label: 'Spend', fmt: 'money', neutral: true },
      { k: 'impressions', label: 'Impr', fmt: 'num', neutral: true },
      { k: 'ctr', label: 'CTR', fmt: 'pct' },
      { k: 'cvr', label: 'CVR', fmt: 'pct' },
      { k: 'orders', label: 'Orders', fmt: 'num' },
      { k: 'revenue', label: 'Revenue', fmt: 'money' },
      { k: 'net', label: 'Net', fmt: 'money' },
      { k: 'roas', label: 'ROAS', fmt: 'roas' }
    ];
    // Percent with trailing zeros stripped: 11.00% → 11%, but 0.61% stays 0.61%.
    function _pctTrim(v) { return String(Math.round((Number(v) || 0) * 10000) / 100) + '%'; }
    function _f(v, kind) { return kind === 'money' ? fmtMoney(v, { compact: true }) : kind === 'roas' ? fmtRoas(v) : kind === 'pct' ? _pctTrim(v) : cdNum(v); }
    // Absolute delta in the metric's own units — used when a relative % isn't honest
    // (zero/negative baseline, e.g. Net -$31 → -$25 = +$6) so the change always has a number.
    function _absDelta(mm, kind) {
      var a = Math.abs(mm.after - mm.before);
      return kind === 'money' ? fmtMoney(a, { compact: true }) : kind === 'roas' ? (a.toFixed(1) + '×') : kind === 'pct' ? _pctTrim(a) : cdNum(a);
    }
    var FLAT = 3; // |Δ| < 3% (or no direction) → "no meaningful change" → a dash, gray
    // One metric → three cells: right-aligned LABEL · left-aligned before→after · delta.
    function _trio(def) {
      var mm = def && m[def.k];
      if (!mm) return '<span class="moph-cd-impact__label"></span><span class="moph-cd-impact__ba"></span><span class="moph-cd-impact__delta"></span>';
      var dir = mm.dir;
      var flat = dir === 0 || (mm.deltaPct != null && Math.abs(mm.deltaPct) < FLAT);
      var deltaHtml, clr;
      if (flat) {
        deltaHtml = '&ndash;'; clr = 'var(--moph-ink-2)'; // no meaningful change → dash
      } else {
        var arrow = dir > 0 ? '&#9650;' : '&#9660;';
        var num = (mm.deltaPct != null) ? (Math.abs(Math.round(mm.deltaPct)) + '%') : _absDelta(mm, def.fmt); // always a number
        deltaHtml = arrow + ' ' + num;
        clr = def.neutral ? 'var(--moph-ink-2)' : (dir > 0 ? 'var(--moph-green)' : 'var(--moph-red)');
      }
      return '<span class="moph-cd-impact__label">' + def.label + '</span>'
        + '<span class="moph-cd-impact__ba">' + _f(mm.before, def.fmt) + ' &rarr; ' + _f(mm.after, def.fmt) + '</span>'
        + '<span class="moph-cd-impact__delta" style="color:' + clr + '">' + deltaHtml + '</span>';
    }
    // Two tight metric-columns: render in pairs (left=defs[i], right=defs[i+1]) with a
    // spacer between → label/value/delta clusters that align down each column.
    var cells = '';
    for (var pi = 0; pi < defs.length; pi += 2) {
      cells += _trio(defs[pi]) + '<span class="moph-cd-impact__gap"></span>' + _trio(defs[pi + 1]);
    }
    var warn = impact.confounded
      ? '<div class="moph-cd-impact__warn">&#9888; Another change overlaps this window — the movement can&rsquo;t be cleanly credited to just this one.</div>'
      : '';
    var foot = '<div class="moph-cd-impact__foot">' + impact.daysBefore + 'd before vs ' + impact.daysAfter + 'd after · directional, not proof</div>';
    return '<div class="moph-cd-impact">' + warn + '<div class="moph-cd-impact__grid">' + cells + '</div>' + foot + '</div>';
  }

  /* Phase C — the "Changes" card: log creative/budget/bid/targeting changes to a
     campaign and see them as chevron markers on the sparklines above. Manual notes
     (agency) carry the author; budget notes are auto-created by the Budget tool and
     show as "Budget". The card stamps cid/name/platform as data-attrs so the delegated
     add/save/delete handlers can post + reload this campaign's detail. */
  function cdChanges(data) {
    var cid = data.campaignId == null ? '' : String(data.campaignId);
    var name = data.name || '';
    var platform = data.platform || '';
    var today = new Date().toISOString().slice(0, 10);
    // Categories mirror the recommendation-card lever pills exactly (1:1 with cooldown).
    var cats = ['Creative', 'Targeting & quality', 'Budget & scaling', 'Landing & conversion', 'Other'];
    var opts = '<option value="" disabled selected hidden>Select…</option>'
      + cats.map(function(c) { return '<option value="' + escapeHtml(c) + '">' + escapeHtml(c) + '</option>'; }).join('');
    var notes = (data.notes || []).slice().sort(function(a, b) { return String(b.date).localeCompare(String(a.date)); });
    var canDelete = !!data.viewerIsAdmin; // deleting a change is admin-only (audit log)
    // Show the 5 most recent inline; the rest collapse behind a "Show N more" button.
    var rowsHtml = notes.map(function(nt, i) {
      var src = nt.source === 'budget' ? 'budget' : 'manual';
      var catLabel = nt.category ? (nt.category.charAt(0).toUpperCase() + nt.category.slice(1)) : 'Other';
      var by = src === 'budget' ? 'Budget' : (nt.authorName || 'Agency');
      var hasImpact = !!nt.impact;
      var extraCls = (i >= 5) ? ' moph-cd-note--extra' : '';
      return '<div class="moph-cd-note moph-cd-note--' + src + (hasImpact ? ' moph-cd-note--has-impact' : '') + extraCls + '">'
        + '<div class="moph-cd-note__row">'
          + '<span class="moph-cd-note__date">' + escapeHtml(cdNiceDate(nt.date)) + '</span>'
          + '<span class="moph-cd-note__cat">' + escapeHtml(catLabel) + '</span>'
          + '<span class="moph-cd-note__text">' + escapeHtml(nt.text || '') + '</span>'
          + '<span class="moph-cd-note__by">' + escapeHtml(by) + '</span>'
          + (hasImpact ? '<span class="moph-cd-note__caret" aria-hidden="true" title="Show this change\'s impact">&#9656;</span>' : '')
          + (canDelete ? '<button type="button" class="moph-cd-note__del" data-id="' + escapeHtml(nt.id || '') + '" title="Delete this change (admin)">&times;</button>' : '')
        + '</div>'
        + (hasImpact ? '<div class="moph-cd-note__impact">' + cdImpactStrip(nt.impact) + '</div>' : '')
        + '</div>';
    }).join('');
    var _extraN = Math.max(0, notes.length - 5);
    var showMore = _extraN ? '<button type="button" class="moph-cd-note-showall" data-extra="' + _extraN + '" aria-expanded="false">Show ' + _extraN + ' more change' + (_extraN === 1 ? '' : 's') + '</button>' : '';
    var rows = notes.length ? (rowsHtml + showMore) : '<div class="moph-cd-note-empty">No changes logged yet.</div>';
    return '<div class="moph-cd-card moph-cd-card--full moph-cd-changes" data-cid="' + escapeHtml(cid) + '" data-name="' + escapeHtml(name) + '" data-platform="' + escapeHtml(platform) + '">'
      + '<div class="moph-cd-card__h">Changes '
        + '<span class="moph-cd-ag__src">'
          + '<span class="moph-cd-leg moph-cd-leg--manual">&#9660; agency</span> '
          + '<span class="moph-cd-leg moph-cd-leg--budget">&#9660; budget</span></span>'
        + '<button type="button" class="moph-cd-note-add">+ Log a change</button>'
      + '</div>'
      + '<div class="moph-cd-note-form">'
        + '<input type="date" class="moph-cd-note-date" value="' + today + '" max="' + today + '">'
        + '<select class="moph-cd-note-cat" required>' + opts + '</select>'
        + '<input type="text" class="moph-cd-note-text" maxlength="280" placeholder="What changed? (e.g. refreshed RSA creative, added 3 headlines)">'
        + '<button type="button" class="moph-cd-note-save" disabled>Save</button>'
        + '<button type="button" class="moph-cd-note-cancel">Cancel</button>'
        + '<span class="moph-cd-note-err" hidden></span>'
      + '</div>'
      + '<div class="moph-cd-note-list">' + rows + '</div>'
      + '</div>';
  }

  /* Phase C — note actions. A note only changes the `notes` array of an already-cached
     payload, so we patch the cache IN PLACE and re-render from it — instant, no slow
     campaign-detail re-fetch. (Only if the cache entry is somehow gone do we fall back
     to a fresh load.) renderCampaignDetail rebuilds the list AND the sparkline markers. */
  function _cdPatchNotes(card, mutate) {
    var cid = card.getAttribute('data-cid') || '', name = card.getAttribute('data-name') || '', platform = card.getAttribute('data-platform') || '';
    var range = dateRange();
    var ckey = (cid || name) + '|' + range.from + '|' + range.to + '|' + state.attribution;
    var cached = _cdCache[ckey];
    var detail = card.closest('.moph-camp-detail');
    var pad = detail && detail.querySelector('.moph-camp-detail__pad');
    if (cached && pad) {
      cached.notes = Array.isArray(cached.notes) ? cached.notes : [];
      mutate(cached.notes);
      pad.innerHTML = renderCampaignDetail(cached);
      _sizeDetailPanel(pad);
    } else if (pad) {
      delete _cdCache[ckey];
      loadCampaignDetail(cid, name, platform, pad);
    }
  }
  /* Enable Save only once a category is chosen AND a description is typed (you can't log
     a change with nothing selected). Driven by input/change events on the form. */
  function _cdSyncSaveBtn(form) {
    if (!form) return;
    var catEl = form.querySelector('.moph-cd-note-cat'), textEl = form.querySelector('.moph-cd-note-text'), saveBtn = form.querySelector('.moph-cd-note-save');
    if (!saveBtn) return;
    saveBtn.disabled = !((catEl && catEl.value) && (textEl && (textEl.value || '').trim()));
  }
  async function _cdSaveNote(btn) {
    var card = btn.closest('.moph-cd-changes'); if (!card) return;
    var dateEl = card.querySelector('.moph-cd-note-date'), catEl = card.querySelector('.moph-cd-note-cat'),
        textEl = card.querySelector('.moph-cd-note-text'), errEl = card.querySelector('.moph-cd-note-err');
    var date = dateEl && dateEl.value, category = catEl && catEl.value, text = (textEl && textEl.value || '').trim();
    if (errEl) errEl.hidden = true;
    if (!date || !category || !text) { if (textEl && !text) textEl.focus(); return; } // guard (button also disabled)
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      var resp = await mwPost('/dashboard/campaign-note', {
        campaignId: card.getAttribute('data-cid') || '', campaignName: card.getAttribute('data-name') || '',
        date: date, category: category, text: text,
      });
      _cdPatchNotes(card, function(notes) { if (resp && resp.note) notes.push(resp.note); });
    } catch (e) {
      btn.disabled = false; btn.textContent = 'Save';
      if (errEl) { errEl.textContent = (e && e.message) || 'Failed to save'; errEl.hidden = false; }
    }
  }
  async function _cdDeleteNote(btn) {
    var card = btn.closest('.moph-cd-changes'); if (!card) return;
    var id = btn.getAttribute('data-id'); if (!id) return;
    btn.disabled = true;
    try {
      await mwPost('/dashboard/campaign-note-delete', {
        campaignId: card.getAttribute('data-cid') || '', campaignName: card.getAttribute('data-name') || '', id: id,
      });
      _cdPatchNotes(card, function(notes) { for (var i = 0; i < notes.length; i++) { if (notes[i] && notes[i].id === id) { notes.splice(i, 1); break; } } });
    } catch (e) { btn.disabled = false; }
  }

  /* Smoothly grow/shrink a nested element (the change form, an impact strip) via a
     max-height transition, while UNCAPPING the campaign slide-panel so it follows along
     (then re-capping it to a pixel height afterward so the campaign's own collapse still
     animates). `el` must be CSS-collapsed by default (max-height:0; overflow:hidden). */
  function _cdNestedAccordion(el, open) {
    if (!el) return;
    var inner = el.closest('.moph-camp-detail__inner');
    if (inner) inner.style.maxHeight = 'none'; // let the panel follow the inner animation
    var done = false;
    function recap(ev) {
      if (ev && ev.propertyName && ev.propertyName !== 'max-height') return;
      if (done) return; done = true;
      el.removeEventListener('transitionend', recap);
      if (open) el.style.maxHeight = 'none'; // release so inner content can change later
      if (inner) inner.style.maxHeight = inner.scrollHeight + 'px'; // re-cap for the campaign collapse anim
    }
    el.addEventListener('transitionend', recap);
    window.setTimeout(recap, 360); // fallback if transitionend doesn't fire
    if (open) { el.style.maxHeight = el.scrollHeight + 'px'; }
    else { el.style.maxHeight = el.scrollHeight + 'px'; void el.offsetHeight; el.style.maxHeight = '0'; }
  }

  // ─── Recent Sales (Phase 6) ─────────────────────────────────────────────
  function updateOrdersSortIndicators() {
    document.querySelectorAll('#mophOrders thead th[data-sort]').forEach(function(th) {
      var k = th.getAttribute('data-sort');
      if (k === state.ordersSort.key) th.setAttribute('data-sort-active', state.ordersSort.dir);
      else th.removeAttribute('data-sort-active');
    });
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — the per-order channel-CAC
     allocation (and the CAC / True Profit columns it fed) was removed: a blended
     per-order ad cost was misleading. Account-level ad-adjusted profit = the
     Contribution Margin KPI; per-campaign efficiency = the Campaigns table +
     per-campaign break-even. */

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — Leads table renderer. Mirrors
     renderOrders (same Source pill + Channel + Touches treatment) with lead-specific
     columns (Status pill, Value) + a KPI strip (Leads / Ad-attributed / Quoted / Won). */
  function leadStatusPill(status) {
    var s = String(status || 'Open');
    var cls = s === 'Won' ? 'won' : s === 'Lost' ? 'lost' : s === 'Quoted' ? 'quoted' : s === 'Dead' ? 'dead' : 'open';
    return '<span class="moph-lead-status moph-lead-status--' + cls + '">' + escapeHtml(s) + '</span>';
  }
  function updateLeadsSortIndicators() {
    var ths = document.querySelectorAll('#mophLeads thead th[data-sort]');
    for (var i = 0; i < ths.length; i++) {
      var th = ths[i], k = th.getAttribute('data-sort');
      if (k === state.leadsSort.key) th.setAttribute('data-sort-active', state.leadsSort.dir);
      else th.removeAttribute('data-sort-active');
    }
  }
  function renderLeads() {
    var tbody = document.querySelector('#mophLeads tbody');
    if (!tbody) return;
    var hint = document.getElementById('mophLeadsCount');
    var pager = document.getElementById('mophLeadsPager');
    var foot = document.getElementById('mophLeadsFoot');
    if (!state.leads) {
      tbody.innerHTML = '<tr><td colspan="6" class="moph-empty">Leads unavailable.</td></tr>';
      if (hint) hint.textContent = '';
      if (pager) pager.hidden = true; if (foot) foot.hidden = true;
      return;
    }
    var leads = (state.leads.leads || []).slice();
    var kpi = state.leads.kpi || { won: 0 };
    if (leads.length === 0) {
      tbody.innerHTML = '<tr><td colspan="6" class="moph-empty">No leads in this date range</td></tr>';
      if (hint) hint.textContent = '';
      if (pager) pager.hidden = true; if (foot) foot.hidden = true;
      return;
    }
    updateLeadsSortIndicators();
    var sortKey = state.leadsSort.key, dir = state.leadsSort.dir === 'asc' ? 1 : -1;
    var stringKeys = { date: 1, customer: 1, sourceGroup: 1, channel: 1, status: 1 };
    leads.sort(function(a, b) {
      if (sortKey === 'date') { var at = a.soldTs || a.date || '', bt = b.soldTs || b.date || ''; return String(at).localeCompare(String(bt)) * dir; }
      var av = a[sortKey], bv = b[sortKey];
      if (stringKeys[sortKey]) return String(av || '').localeCompare(String(bv || '')) * dir;
      return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
    });
    var totalRows = leads.length;
    var shownCount = Math.min(state.leadsShown || 20, totalRows);
    tbody.innerHTML = leads.slice(0, shownCount).map(function(l) {
      var _sg = l.sourceGroup || 'unknown';
      var _scolor = SOURCE_COLORS[_sg] || '#9ca3af';
      var srcLabel = SOURCE_LABELS[_sg] || _sg.replace(/-/g, ' ');
      var _lchd = channelDot(l.channel);
      var channelHtml;
      if (_lchd.cls) channelHtml = '<span class="moph-platform-dot ' + _lchd.cls + '"></span>' + escapeHtml(_lchd.label);
      else if (l.channel && l.channel !== 'organic') channelHtml = '<span style="color:var(--moph-ink-2);text-transform:capitalize;">' + escapeHtml(l.channel) + '</span>';
      else channelHtml = '<span style="color:var(--moph-ink-2);" title="No paid ad click on this lead’s captured journey.">Organic</span>';
      var campTip = l.attributed
        ? ('Ad-attributed' + (l.campaignName ? ' · ' + l.campaignName : (l.campaignId ? ' · ' + l.campaignId : '')) + ' (' + (l.channel || '') + ')')
        : 'No paid touch in the captured journey';
      var valHtml = (Number(l.value) > 0) ? fmtMoney(l.value, { compact: true }) : '<span style="color:var(--moph-ink-2);">—</span>';
      return '<tr>' +
        '<td>' + escapeHtml((l.date || '').slice(0, 10)) + '</td>' +
        '<td>' + escapeHtml(l.customer || '(unknown)') + '</td>' +
        '<td><span class="moph-source" style="background:' + (SOURCE_BG[_sg] || (_scolor + '22')) + ';color:' + _scolor + ';">' + escapeHtml(srcLabel) + '</span></td>' +
        '<td title="' + escapeHtml(campTip) + '">' + channelHtml + '</td>' +
        '<td>' + leadStatusPill(l.status) + '</td>' +
        '<td>' + valHtml + '</td>' +
      '</tr>';
    }).join('');
    if (hint) hint.textContent = '(' + totalRows + ')';
    if (foot) {
      foot.hidden = false;
      var wonVal = 0; leads.forEach(function(l) { if (l.status === 'Won') wonVal += Number(l.value) || 0; });
      document.getElementById('mophLeadsFootValue').textContent = wonVal > 0 ? fmtMoney(wonVal, { compact: true }) : '—';
      var fr = document.getElementById('mophLeadsFootRange'); if (fr) fr.textContent = '(' + totalRows + ' leads · ' + (kpi.won || 0) + ' won)';
    }
    if (pager) {
      pager.hidden = totalRows <= 20;
      document.getElementById('mophLeadsLess').hidden = shownCount <= 20;
      document.getElementById('mophLeadsMore').hidden = shownCount >= totalRows;
      document.getElementById('mophLeadsAll').hidden = shownCount >= totalRows;
      document.getElementById('mophLeadsMore').textContent = 'Show ' + Math.min(20, totalRows - shownCount) + ' more';
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-24) — Sales table Channel + Source
     filters. Every order row already carries o.channel + o.sourceGroup (the two visible
     columns), so this is a pure client-side filter. channelKeyOf collapses empty/undefined
     channel into 'organic' so it groups with the "Organic / direct" rows the table shows. */
  function channelKeyOf(o) {
    var c = o && o.channel ? String(o.channel) : '';
    return (!c || c === 'organic') ? 'organic' : c;
  }
  /* Apply the active Channel + Source filters. Empty filter = pass-through (returns a
     fresh slice so callers can sort in place). Used by renderOrders (rows, footer, count)
     AND _ordersTotal (pager math) so all three stay consistent. */
  function getFilteredOrders() {
    var all = (state.orders && state.orders.orders) || [];
    var f = state.ordersFilter || (state.ordersFilter = { channels: [], sources: [] });
    var chSel = f.channels || [], srcSel = f.sources || [];
    if (!chSel.length && !srcSel.length) return all.slice();
    return all.filter(function(o) {
      if (chSel.length && chSel.indexOf(channelKeyOf(o)) === -1) return false;
      if (srcSel.length && srcSel.indexOf(o.sourceGroup || 'unknown') === -1) return false;
      return true;
    });
  }
  function ordersFilterActive() {
    var f = state.ordersFilter || {};
    return !!((f.channels && f.channels.length) || (f.sources && f.sources.length));
  }
  function _channelOptLabel(key) {
    if (key === 'organic') return 'Organic';
    return channelDot(key).label || (key.charAt(0).toUpperCase() + key.slice(1));
  }
  /* Build the two filter dropdowns from the values present in the current range
     (so the agency never sees an option that returns zero rows). Source options are
     grouped by display label, so 'inbound' + 'unknown' (both "Unknown") collapse into
     one option carrying both underlying keys. Called once per load; checkbox state is
     mutated in place by the delegated handlers (wireSalesFilters), not rebuilt per toggle. */
  function renderSalesFilters() {
    var bar = document.getElementById('mophSalesFilters');
    if (!bar) return;
    var orders = (state.orders && state.orders.orders) || [];
    if (orders.length === 0) { bar.hidden = true; return; }
    bar.hidden = false;

    // Channel options — paid channels first (by frequency), Organic last.
    var chCount = {};
    orders.forEach(function(o) { var k = channelKeyOf(o); chCount[k] = (chCount[k] || 0) + 1; });
    var chOpts = Object.keys(chCount).sort(function(a, b) {
      if (a === 'organic') return 1; if (b === 'organic') return -1;
      return chCount[b] - chCount[a];
    }).map(function(k) {
      return { keys: [k], label: _channelOptLabel(k), count: chCount[k],
        dot: channelDot(k).cls,
        color: SOURCE_COLORS[k] || (k === 'organic' ? '#94a3b8' : '#9ca3af') };
    });

    // Source options — grouped by shared display label (collapses inbound+unknown).
    var byLabel = {};
    orders.forEach(function(o) {
      var sg = o.sourceGroup || 'unknown';
      var label = SOURCE_LABELS[sg] || sg.replace(/-/g, ' ');
      var e = byLabel[label] || (byLabel[label] = { label: label, keys: {}, count: 0, color: SOURCE_COLORS[sg] || '#9ca3af' });
      e.keys[sg] = 1; e.count++;
    });
    var srcOpts = Object.keys(byLabel).map(function(l) {
      var e = byLabel[l];
      return { keys: Object.keys(e.keys), label: e.label, count: e.count, color: e.color };
    }).sort(function(a, b) { return b.count - a.count; });

    _buildMselMenu(bar.querySelector('[data-filter="channel"]'), 'channels', chOpts, true);
    _buildMselMenu(bar.querySelector('[data-filter="source"]'),  'sources',  srcOpts, false);
  }
  /* Populate one dropdown's option rows. isChannel options show the platform dot
     (Meta/Google) or a gray swatch; source options show the SOURCE_COLORS swatch so
     they read identically to the table's Source pills + the donut legend. */
  function _buildMselMenu(root, stateKey, opts, isChannel) {
    if (!root) return;
    var optsEl = root.querySelector('.moph-msel__opts');
    var sel = state.ordersFilter[stateKey] || (state.ordersFilter[stateKey] = []);
    optsEl.innerHTML = opts.map(function(opt) {
      var checked = opt.keys.every(function(k) { return sel.indexOf(k) !== -1; });
      var mark = (isChannel && opt.dot)
        ? '<span class="moph-platform-dot ' + opt.dot + '"></span>'
        : '<span class="moph-msel__swatch" style="background:' + opt.color + '"></span>';
      return '<label class="moph-msel__opt">' +
        '<input type="checkbox" data-keys="' + escapeHtml(opt.keys.join(',')) + '"' + (checked ? ' checked' : '') + '>' +
        mark + '<span class="moph-msel__opt-label">' + escapeHtml(opt.label) + '</span>' +
        '<span class="moph-msel__opt-count">' + opt.count + '</span>' +
      '</label>';
    }).join('');
    _refreshMselVal(root);
  }
  /* Update a dropdown button's value text from its checkboxes' live state. */
  function _refreshMselVal(root) {
    var valEl = root.querySelector('.moph-msel__val');
    if (!valEl) return;
    var boxes = root.querySelectorAll('.moph-msel__opts input[type="checkbox"]');
    var checked = [].filter.call(boxes, function(b) { return b.checked; });
    if (!checked.length) valEl.textContent = 'All';
    else if (checked.length === 1) {
      var lbl = checked[0].parentNode.querySelector('.moph-msel__opt-label');
      valEl.textContent = lbl ? lbl.textContent : '1 selected';
    } else valEl.textContent = checked.length + ' selected';
  }

  function renderOrders() {
    var tbody = document.querySelector('#mophOrders tbody');
    var hint = document.getElementById('mophOrdersCount');
    if (!state.orders) {
      tbody.innerHTML = state.ordersLoadFailed
        ? '<tr><td colspan="8" class="moph-empty">Sales couldn’t be loaded for this date range. Press Refresh to try again.</td></tr>'
        : '<tr><td colspan="8" class="moph-empty">Loading recent sales…</td></tr>';
      hint.textContent = '';
      return;
    }
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — rows come from the filtered set (Channel + Source).
    var orders = getFilteredOrders();
    if (orders.length === 0) {
      var hasAny = ((state.orders.orders || []).length > 0);
      tbody.innerHTML = '<tr><td colspan="8" class="moph-empty">' +
        (hasAny ? 'No sold orders match the current filter' : 'No sold orders in this date range') + '</td></tr>';
      hint.textContent = '';
      var _ef = document.getElementById('mophOrdersFoot'); if (_ef) _ef.hidden = true;
      var _ep = document.getElementById('mophOrdersPager'); if (_ep) _ep.hidden = true;
      return;
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — per-order CAC / True Profit
       removed. CAC is a campaign/channel AVERAGE; pushing it down onto individual
       orders made cheap-but-efficient orders look like losses (a single blended cost
       can't represent what one sale actually cost to acquire, and it blends ticket
       sizes). The Sales table is now honest per-order goods economics; ad-cost
       profitability lives where it's accurate — the Contribution Margin KPI (account)
       and the Campaigns table + per-campaign break-even. */
    // Client-side sort over the returned set (matches Orders page convention).
    updateOrdersSortIndicators();
    var sortKey = state.ordersSort.key;
    var dir = state.ordersSort.dir === 'asc' ? 1 : -1;
    var stringKeys = { dateSold: 1, ref: 1, customer: 1, sourceGroup: 1, channel: 1 };
    orders.sort(function(a, b) {
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (#4) — the Date column sorts on the precise
      // soldTs (full ISO) so same-day orders order by time-of-day, newest first;
      // falls back to the date-only dateSold for any record without a timestamp.
      if (sortKey === 'dateSold') {
        var at = a.soldTs || a.dateSold || '', bt = b.soldTs || b.dateSold || '';
        return String(at).localeCompare(String(bt)) * dir;
      }
      var av = a[sortKey], bv = b[sortKey];
      if (stringKeys[sortKey]) {
        return String(av || '').localeCompare(String(bv || '')) * dir;
      }
      return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
    });
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (provenance) — compact provenance bits for a
       Recent Sales row: a hover tooltip (method · campaign · basis) + the
       campaign name as a visible sub-line under Channel. Mirrors the orders-page
       "How this was credited" drawer in compact form (full detail one click away
       via "See all orders →"). Reads o.provenance stamped by the worker. */
    function provBitsForRow(o) {
      var p = o.provenance || null;
      var methodLabel = {
        'landing_site': 'Entry URL (landing_site)', 'landing_site-quote': 'Quote — entry URL (landing_site)',
        'shopify-journey': 'Shopify journey',
        'lead-utm-journey': 'Lead UTM journey', 'organic': 'Organic / direct',
        'organic-journey': 'Organic / direct', 'no-data': 'No attribution data'
      };
      var method = (p && p.method) || (o.channel && o.channel !== 'organic' ? 'shopify-journey' : 'organic');
      var label = methodLabel[method] || 'Organic / direct';
      var campName = (p && p.campaignId) ? displayCampaignName(p.campaignId, (p.campaignName || String(p.campaignId))) : '';
      var campFull = (p && p.campaignId) ? (p.campaignName ? (p.campaignName + ' (' + p.campaignId + ')') : String(p.campaignId)) : '';
      var basisParts = [];
      if (p && p.source) basisParts.push('source=' + p.source);
      if (p && p.medium) basisParts.push('medium=' + p.medium);
      if (p && p.campaignId) basisParts.push('campaign=' + p.campaignId);
      var basis = basisParts.join(' · ');
      var tip = 'Credited via ' + label + (campFull ? (' · ' + campFull) : '') + (basis ? (' · ' + basis) : '');
      return { tip: tip, campName: campName };
    }
    // N14 — render only the first `ordersShown` rows (default 20); the Show
    // more / less / all controls below adjust it. Footer totals + the hint
    // still reflect the FULL range, not just the rows rendered.
    var totalRows = orders.length;
    var shownCount = Math.min(state.ordersShown || 20, totalRows);
    tbody.innerHTML = orders.slice(0, shownCount).map(function(o) {
      var _chd = channelDot(o.channel);
      var dotCls = _chd.cls;
      var channelLabel = _chd.label || (o.channel || 'organic');
      // Until attribution reconciliation lands (Shopify customerJourneySummary
      // + identity stitching), orders without any paid touch are bucketed as
      // "organic" upstream — visually misleading because they may simply lack
      // a UTM-tagged journey. When the touch count is zero AND the channel is
      // the default "organic", render "(no UTM)" with a tooltip so the agency
      // doesn't read this as "definitely organic traffic."
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — non-paid orders read
         "Organic / direct" instead of a bare "—". The dash dated from before GA4
         reconciliation (when an untagged order MIGHT have been a missed paid touch);
         now that landing_site + GA4 catch the paid clicks, an order with no paid touch
         is genuinely organic/direct, so we label it rather than leaving a confusing dash. */
      var channelHtml;
      if (dotCls) {
        channelHtml = '<span class="moph-platform-dot ' + dotCls + '"></span>' + escapeHtml(channelLabel);
      } else if (o.channel && o.channel !== 'organic') {
        channelHtml = '<span style="color:var(--moph-ink-2);text-transform:capitalize;">' + escapeHtml(o.channel) + '</span>';
      } else {
        channelHtml = '<span style="color:var(--moph-ink-2);" title="No paid ad click found for this order (no Google / Meta / Bing touch). Counts as organic search, direct, or referral traffic.">Organic</span>';
      }
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan) — source labels via the shared module
         SOURCE_LABELS map (also used by the donut legend). 'inbound' shows as
         'Unknown' (coarse "they reached us" tag, no channel detail); 'rfq' → 'RFQ'. */
      var _sg = o.sourceGroup || 'unknown';
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — color the source pill from
      // the shared SOURCE_COLORS palette (tint bg + solid text) so it matches the
      // Lead Source Mix donut exactly. '22' = ~13% alpha (8-digit hex).
      var _scolor = SOURCE_COLORS[_sg] || '#9ca3af';
      var srcLabel = SOURCE_LABELS[_sg] || _sg.replace(/-/g, ' ');
      var srcPrefix = _sg === 'online' ? shopifyLogoSvg() : ''; // Shopify-direct visual marker
      // #12 — touch count from the journey (online: Shopify momentCount; lead:
      // captured journey length). Falls back across both so the column isn't blank.
      var _tc = (o.attribution && (Number(o.attribution.touchCount) || Number(o.attribution.journeyLength))) || 0;
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — assisted now shows a compact
      // "*" (e.g. "2*") instead of "(assisted)"; the cell title explains the asterisk.
      var _assisted = _tc > 0 && !!(o.attribution && o.attribution.assistedConversion);
      var touchInfo = _tc > 0 ? (_tc + (_assisted ? '*' : '')) : '—';
      var grossPct = o.grossMargin ? ' <span style="color:var(--moph-ink-2);font-size:11px;">(' + Math.round(o.grossMargin) + '%)</span>' : '';
      var pb = provBitsForRow(o);
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — campaign name is no longer
         rendered inline next to the channel; it stays in the cell's hover tooltip
         (pb.tip already includes the campaign) so the Channel column reads cleanly
         as just the channel. */
      return '<tr class="moph-orow" data-ref="' + escapeHtml(o.ref) + '" title="Click for the full per-order economics + attribution journey">' +
        '<td>' + escapeHtml((o.dateSold || '').slice(0,10)) + '</td>' +
        '<td>' + escapeHtml(o.ref) + '</td>' +
        '<td>' + escapeHtml(o.customer || '(unknown)') + '</td>' +
        '<td>' + fmtMoney(o.revenue, { compact: true }) + '</td>' +
        '<td>' + fmtMoney(o.grossProfit, { compact: true }) + grossPct + '</td>' +
        '<td><span class="moph-source" style="background:' + (SOURCE_BG[_sg] || (_scolor + '22')) + ';color:' + _scolor + ';">' + srcPrefix + escapeHtml(srcLabel) + '</span></td>' +
        '<td title="' + escapeHtml(pb.tip) + '">' + channelHtml + '</td>' +
        '<td' + (_assisted ? ' title="* assisted — a paid ad was in the journey but not the last (converting) click"' : '') + '>' + escapeHtml(touchInfo) + '</td>' +
      '</tr>';
    }).join('');
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — equalize SOURCE pill widths:
       set every pill to the widest one (usually Online, which carries the Shopify
       logo) so the column reads as a clean, aligned stack. Measured after render
       (the labels + logo vary in width); re-runs on every re-render. */
    var _srcPills = tbody.querySelectorAll('.moph-source');
    if (_srcPills.length) {
      var _maxPillW = 0;
      _srcPills.forEach(function(p) { p.style.width = ''; if (p.offsetWidth > _maxPillW) _maxPillW = p.offsetWidth; });
      if (_maxPillW > 0) _srcPills.forEach(function(p) { p.style.width = _maxPillW + 'px'; });
    }
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — when a filter is active, the count + footer reflect
    // the filtered set ("X of Y"); with no filter, the worker's full-range totals are used
    // verbatim so the default view is unchanged.
    var _filterOn = ordersFilterActive();
    var _matched = state.orders.totalMatched;
    var n = _filterOn ? totalRows : _matched;
    if (hint) {
      if (_filterOn) {
        hint.textContent = '(' + totalRows + ' of ' + _matched + ')';
        hint.title = 'Filtered to ' + totalRows + ' of ' + _matched + ' order' + (_matched === 1 ? '' : 's') + ' sold ' + rangeLabel() + (shownCount < totalRows ? (' · showing ' + shownCount) : '');
      } else {
        hint.textContent = '(' + n + ')';
        hint.title = (shownCount < n ? ('Showing ' + shownCount + ' of ' + n + ' · ') : '') + n + ' order' + (n === 1 ? '' : 's') + ' sold ' + rangeLabel();
      }
    }
    // N14 — Show more / less / all controls; only when there's more than the default 20.
    var pager = document.getElementById('mophOrdersPager');
    if (pager) {
      if (totalRows <= 20) {
        pager.hidden = true;
      } else {
        pager.hidden = false;
        document.getElementById('mophOrdersLess').hidden = shownCount <= 20;
        document.getElementById('mophOrdersMore').hidden = shownCount >= totalRows;
        document.getElementById('mophOrdersAll').hidden = shownCount >= totalRows;
        document.getElementById('mophOrdersMore').textContent = 'Show ' + Math.min(20, totalRows - shownCount) + ' more';
      }
    }

    // Footer totals — accumulated across the FULL range by the worker
    // (not just the 30 displayed). Empty range → hide tfoot.
    var foot = document.getElementById('mophOrdersFoot');
    if (n > 0) {
      foot.hidden = false;
      document.getElementById('mophOrdersFootRange').textContent = '(' + rangeLabel() + ')';
      // Total GP cell shows $ amount AND blended GP% (dollar-weighted, not
      // simple average — same convention as the Orders page Avg margin tile).
      // CUSTOM_MARKETINGOPS_BY_CLAUDE — filter active → sum the filtered rows so the
      // TOTAL matches what's visible; otherwise use the worker's full-range totals.
      var totalRev, totalGp;
      if (_filterOn) {
        totalRev = orders.reduce(function(s, o) { return s + (Number(o.revenue) || 0); }, 0);
        totalGp  = orders.reduce(function(s, o) { return s + (Number(o.grossProfit) || 0); }, 0);
      } else {
        totalRev = Number(state.orders.totalRevenue) || 0;
        totalGp  = Number(state.orders.totalGrossProfit) || 0;
      }
      document.getElementById('mophOrdersFootRevenue').textContent = fmtMoney(totalRev, { compact: true });
      var gpPct = totalRev > 0 ? (totalGp / totalRev) * 100 : 0;
      var gpHtml = fmtMoney(totalGp, { compact: true });
      if (totalRev > 0) {
        // Slightly smaller than the 13px base, still readable, still bold.
        gpHtml += ' <span style="color:var(--moph-ink-2);font-weight:700;font-size:12px;">(' + gpPct.toFixed(1) + '%)</span>';
      }
      document.getElementById('mophOrdersFootGP').innerHTML = gpHtml;
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-07-09) — simple order-count total in the
      // Channel column (uses n, so it matches the footer's full-range / filtered scope).
      var _fc = document.getElementById('mophOrdersFootChannel');
      if (_fc) _fc.textContent = n + (n === 1 ? ' order' : ' orders');
    } else {
      foot.hidden = true;
    }
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 4.1 v1 Reinvestment Memo render.
     Top-3 forward-looking recs. The card stays hidden unless ≥1 rule fired;
     this is to avoid a "no recommendations" placeholder eating real estate
     when the underlying data is too thin for the rules to be confident. */
  function renderReinvestmentMemo() {
    var card = document.getElementById('mophReinvestCard');
    var listEl = document.getElementById('mophReinvestList');
    var metaEl = document.getElementById('mophReinvestRulesMeta');
    if (!card || !listEl || !metaEl) return;

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — once-only delegated
       handler for the "Show N more" expander. Attached to the stable list
       element so it survives re-renders; toggles the reveal class + button copy. */
    if (!listEl._mophShowAllWired) {
      listEl._mophShowAllWired = true;
      listEl.addEventListener('click', function(e) {
        var btn = e.target.closest && e.target.closest('.moph-reinvest-showall');
        if (!btn || !listEl.contains(btn)) return;
        var expanded = listEl.classList.toggle('moph-reinvest-list--all');
        var n = btn.getAttribute('data-extra') || '';
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded
          ? 'Show fewer recommendations'
          : 'Show ' + n + ' more recommendation' + (n === '1' ? '' : 's');
      });
    }

    var _rCount = document.getElementById('mophReinvestCount');
    var data = state.reinvest;
    if (!data || !Array.isArray(data.recs)) {
      if (_rCount) _rCount.textContent = '';
      card.hidden = true;
      return;
    }
    // Count badge after the "Recommendations" title, e.g. "(4)" — mirrors the
    // Campaigns card. Reflects how many recs are shown (capped at 5).
    if (_rCount) _rCount.textContent = '(' + data.recs.length + ')';

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#4) — make the decoupling explicit: this
       card ignores the page date picker and reads a fixed look-back, with every
       dollar figure normalized to a 30-day month so "/mo" is honest regardless
       of how many days of data are actually in the window. */
    var capEl = document.getElementById('mophReinvestCaption');
    if (capEl) {
      var _wd = data.windowDays || null;
      var _basis = data.window && data.window.from
        ? ('the last ' + (_wd ? _wd + ' days' : '~90 days') + ' of data (since ' + data.window.from + ')')
        : '~90 days of data';
      capEl.innerHTML = 'Independent of the date filter above — based on <strong>' + _basis + '</strong>. '
        + 'Dollar figures are normalized per month' + (data.normalizedToMonth === false ? '' : ' (30-day basis)') + '.';
      capEl.hidden = false;
    }
    if (data.recs.length === 0) {
      // Empty-state card — keeps the surface visible so the agency knows
      // the rule engine ran (and which rules) without burying the signal
      // in console. Mirror the meta-line format of the populated state so
      // the design language is consistent.
      card.hidden = false;
      var rulesTotalE = (data.rulesEvaluated || []).length;
      var enabledE = (data.rulesEvaluated || []).filter(function(r) { return r.enabled; });
      var pendingE = (data.rulesEvaluated || []).filter(function(r) { return !r.enabled; });
      listEl.innerHTML = '<div class="moph-reinvest-empty">'
        + '<strong>No recommendations this period.</strong> '
        + 'Evaluated <strong>' + (data.campaignsEvaluated || 0) + '</strong> campaigns across <strong>' + enabledE.length + '</strong> live rule' + (enabledE.length === 1 ? '' : 's') + ' (' + pendingE.length + ' pending data prereqs). '
        + 'This memo now diagnoses across signals — budget-strangled winners, rank-vs-budget (don\'t throw money at a rank problem), unprofitable-after-ad-spend, weak or fatiguing creative, landing-page leaks (high CTR &rarr; low CVR), and channel mix — and prescribes the matching lever. '
        + 'A clean read means none cleared their thresholds on this window; because ~46% of orders are still unattributed, it isn\'t a guarantee nothing\'s actionable.'
        + '</div>';
      var pendingText = pendingE.length
        ? '<br><span style="font-size:11px;">Rules pending (data prereqs not yet met): ' + pendingE.map(function(r) { return escapeHtml(r.label); }).join(', ') + '</span>'
        : '';
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (audit F-007) — meta line now uses the SAME
      // enabled-rule count as the body (was rulesTotalE=3 vs body's enabled=1,
      // a visible contradiction in one card).
      metaEl.innerHTML = 'Evaluated <strong>' + (data.campaignsEvaluated || 0) + '</strong> campaigns across <strong>' + enabledE.length + '</strong> live rule' + (enabledE.length === 1 ? '' : 's') + '. <strong>0</strong> fired.' + pendingText;
      console.info('[marketing-ops] Reinvestment Memo: no rules fired this window. Eval state:', data.rulesEvaluated);
      return;
    }

    card.hidden = false;
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — category-diverse memo.
       The worker now returns up to 12 recs, round-robined across categories so a
       strong-but-low-$ idea (e.g. sauna creative CTR) isn't crowded out of the
       headline by high-$ budget moves. We show the first _HEADLINE inline and
       tuck the rest behind a "Show N more" expander. Each meta row carries a
       category chip. */
    var _HEADLINE = 6;
    listEl.innerHTML = data.recs.map(function(rec, i) {
      var _extraCls = (i >= _HEADLINE) ? ' moph-reinvest-row--extra' : '';
      var _catTag = rec.category
        ? '<span class="moph-reinvest-tag moph-reinvest-tag--cat">' + escapeHtml(rec.category) + '</span>'
        : '';
      // Secondary-lever rec — a winning campaign's lower-priority note (e.g. weak
      // creative) that rides alongside its primary rec. Marked so it doesn't read
      // as a duplicate of the campaign's headline rec. When the worker flags it as
      // REINFORCING the primary (a complementary pair), show the shared play label
      // (e.g. "click-starved winner") instead of a bare "secondary".
      var _secTag = '';
      if (rec.relation) {
        _secTag = '<span class="moph-reinvest-tag moph-reinvest-tag--secondary" title="Reinforces this campaign\'s primary recommendation above — treat the two as one play.">+ ' + escapeHtml(rec.relation) + '</span>';
      } else if (rec.secondary) {
        _secTag = '<span class="moph-reinvest-tag moph-reinvest-tag--secondary" title="A secondary opportunity on a campaign that already has a primary recommendation above.">secondary</span>';
      }
      var confChipCls = 'moph-reinvest-row__chip moph-reinvest-row__chip--' + (rec.confidence || 'medium');
      var math = rec.math || {};
      var workLines = (rec.showYourWork || []).map(function(l) { return '<li>' + escapeHtml(l) + '</li>'; }).join('');
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-05) — show the campaign ALIAS
         in the memo headline when one is set (mirrors the Campaigns table), with
         the real Google Ads name on hover. The worker builds the headline with the
         raw name, so swap it for the alias + a title tooltip. Channel-level recs
         (no campaign) and un-aliased campaigns render unchanged. */
      var _rawName = rec.campaign && rec.campaign.name;
      var _cid = rec.campaign && rec.campaign.id;
      var _alias = (rec.campaign && _rawName) ? displayCampaignName(String(_cid || ''), _rawName) : null;
      var headlineHtml;
      if (rec.campaign && _alias && _alias !== _rawName && rec.headline && rec.headline.indexOf(_rawName) !== -1) {
        headlineHtml = rec.headline.split(_rawName).map(escapeHtml).join(
          '<span class="moph-reinvest-alias" style="border-bottom:1px dotted currentColor; cursor:help;" title="Original: ' + escapeHtml(_rawName) + '">' + escapeHtml(_alias) + '</span>'
        );
      } else {
        headlineHtml = escapeHtml(rec.headline);
      }
      return '<div class="moph-reinvest-row' + _extraCls + '">'
        + '<span class="moph-reinvest-row__rank">' + (i + 1) + '</span>'
        + '<div class="moph-reinvest-row__body">'
          + '<div class="moph-reinvest-row__headline">' + headlineHtml + '</div>'
          + '<div class="moph-reinvest-row__meta">'
            + '<span class="' + confChipCls + '">' + escapeHtml(rec.confidence || 'medium') + ' confidence</span>'
            + _catTag
            + _secTag
            + '<span>' + escapeHtml(rec.ruleLabel || rec.rule) + '</span>'
            + (rec.campaign ? '<span>·</span><span>' + escapeHtml(rec.campaign.platform || '') + '</span>' : '')
          + '</div>'
          + '<details>'
            + '<summary>Show your work</summary>'
            + '<div class="moph-reinvest-row__work">'
              + '<ol>' + workLines + '</ol>'
              + '<div style="margin-top:10px; padding-top:10px; border-top:1px solid var(--moph-line); display:grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap:8px; font-size:11px; color:var(--moph-ink-2);">'
                + '<div><strong>Current spend:</strong> ' + fmtMoney(math.currentSpend, { compact: true }) + '</div>'
                + '<div><strong>Current revenue:</strong> ' + fmtMoney(math.currentRevenue, { compact: true }) + '</div>'
                + '<div><strong>Current ROAS:</strong> ' + (math.currentRoas != null ? math.currentRoas.toFixed(1) + '×' : '—') + '</div>'
                + (math.searchBudgetLostIS != null ? '<div><strong>IS Lost (Budget):</strong> ' + (math.searchBudgetLostIS * 100).toFixed(0) + '%</div>' : '')
                + (math.estimatedExtraSpend != null ? '<div><strong>Suggested extra spend:</strong> ' + fmtMoney(math.estimatedExtraSpend, { compact: true }) + '</div>' : '')
                + (math.estimatedExtraNet != null ? '<div><strong>Estimated extra net:</strong> ' + fmtMoney(math.estimatedExtraNet, { compact: true }) + '</div>' : '')
              + '</div>'
            + '</div>'
          + '</details>'
        + '</div>'
      + '</div>';
    }).join('');

    // Show-all expander — only when there's a tail beyond the headline set.
    var _extraCount = data.recs.length - _HEADLINE;
    if (_extraCount > 0) {
      listEl.innerHTML += '<button type="button" class="moph-reinvest-showall" data-extra="' + _extraCount + '" aria-expanded="false">'
        + 'Show ' + _extraCount + ' more recommendation' + (_extraCount === 1 ? '' : 's') + '</button>';
    }

    var rulesTotal = (data.rulesEvaluated || []).length;
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — change-cooldown transparency.
       Recs held because a matching change is still maturing aren't shown (we don't nag
       you to redo what you just did), but we say how many + what, so nothing's silently
       hidden. Hover lists them. */
    var _held = data.cooldownHeld || [];
    var _heldNote = '';
    if (_held.length) {
      var _heldList = _held.map(function(h) { return '• ' + h.campaign + ' — ' + h.category + ' change ' + h.days + 'd ago (maturing)'; }).join('\n');
      _heldNote = ' · <span title="' + escapeHtml('Held — a matching change was logged recently, so these are paused to let the change settle:\n' + _heldList) + '" style="cursor:help; border-bottom:1px dotted currentColor;"><strong>' + _held.length + '</strong> held</span>';
    }
    metaEl.innerHTML = 'Evaluated <strong>' + (data.campaignsEvaluated || 0) + '</strong> campaigns across <strong>' + rulesTotal + '</strong> rules · <strong>' + data.recs.length + '</strong> recommendation' + (data.recs.length === 1 ? '' : 's') + _heldNote + '.';
  }
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 2 Export Everywhere:
     generic CSV helper. Takes a header array, a rows array, and a
     filename → triggers a download. CSV-safe escaping (quotes any
     value containing comma / quote / newline; doubles inner quotes
     per RFC 4180). Pure client-side; no network. */
  function downloadCsv(headers, rows, filename) {
    function escape(s) {
      if (s == null) return '';
      var v = String(s);
      return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
    }
    var lines = [headers.map(escape).join(',')];
    rows.forEach(function (r) { lines.push(r.map(escape).join(',')); });
    var blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 0);
  }

  function downloadCampaignsCsv() {
    var rows = (state.campaigns && state.campaigns.campaigns) || [];
    if (!rows.length) return;
    var range = dateRange();
    var fname = 'paid-ads-campaigns_' + range.from + '_to_' + range.to + '.csv';
    var headers = [
      'Campaign', 'Channel', 'Status', 'Spend', 'Impressions', 'CTR %', 'Plan Target', 'Variance',
      'Revenue', 'Net', 'ROAS', 'Conversions', 'CVR %', 'CAC',
      'Search IS %', 'IS Lost (Budget) %', 'IS Lost (Rank) %',
      'Recommendation'
    ];
    var _cmCmp = (state.cm && state.cm.byCampaign) || null;
    var _useTrueCmp = state.cm && state.cm.totals && state.cm.totals.orderCount > 0;
    var data = rows.map(function (c) {
      var _shop = _cmCmp ? (_cmCmp[c.campaignId] || _cmCmp[String(c.name || '').trim()] || null) : null;
      var _ord = (_useTrueCmp && _shop) ? (Number(_shop.orderCount) || 0) : 0;
      var _cac = _ord > 0 ? (Number(c.spend) || 0) / _ord : null;
      return [
        c.name || c.campaignName || '',
        c.platform || c.channel || '',
        c.status || '',
        Number(c.spend) || 0,
        Number(c.impressions) || 0,
        (Number(c.impressions) || 0) > 0 ? ((Number(c.clicks) || 0) / Number(c.impressions) * 100).toFixed(2) : '',
        Number(c.planTarget) || 0,
        Number(c.variance) || 0,
        Number(c.revenue) || 0,
        Number(c.net) || 0,
        c.roas != null ? Number(c.roas).toFixed(2) : '',
        Number(c.conversions) || 0,
        (Number(c.clicks) || 0) > 0 ? ((Number(c.conversions) || 0) / Number(c.clicks) * 100).toFixed(2) : '',
        _cac != null ? _cac.toFixed(2) : '',
        c.searchImpressionShare != null ? (Number(c.searchImpressionShare) * 100).toFixed(1) : '',
        c.searchBudgetLostIS != null ? (Number(c.searchBudgetLostIS) * 100).toFixed(1) : '',
        c.searchRankLostIS != null ? (Number(c.searchRankLostIS) * 100).toFixed(1) : '',
        c.recommendation || ''
      ];
    });
    downloadCsv(headers, data, fname);
  }

  function downloadRecentSalesCsv() {
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — export the filtered set (what you see is what
    // you download); with no filter active this is the full range.
    var orders = getFilteredOrders();
    if (!orders.length) return;
    var range = dateRange();
    var fname = 'paid-ads-recent-sales_' + range.from + '_to_' + range.to + '.csv';
    var headers = [
      'Date', 'Ref', 'Customer', 'Vertical',
      'Product Revenue', 'Product GP', 'Margin %',
      'Source Group', 'Channel', 'Touches', 'Paid Touches'
    ];
    var data = orders.map(function (o) {
      var att = o.attribution || {};
      return [
        (o.dateSold || '').slice(0, 10),
        o.ref || '',
        o.customer || '',
        o.vertical || '',
        Number(o.revenue) || 0,
        Number(o.grossProfit) || 0,
        o.grossMargin != null ? Math.round(o.grossMargin) : '',
        o.sourceGroup || '',
        o.channel || '',
        att.touchCount || 0,
        att.paidTouchCount || 0,
      ];
    });
    downloadCsv(headers, data, fname);
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Leads table) — Leads CSV export. */
  function downloadLeadsCsv() {
    var leads = (state.leads && state.leads.leads) || [];
    if (!leads.length) return;
    var range = dateRange();
    var fname = 'paid-ads-leads_' + range.from + '_to_' + range.to + '.csv';
    var headers = ['Date', 'Name', 'Source', 'Channel', 'Ad Attributed', 'Campaign', 'Status', 'Value', 'Touches'];
    var data = leads.map(function (l) {
      return [
        (l.date || '').slice(0, 10),
        l.customer || '',
        l.sourceGroup || '',
        l.channel || '',
        l.attributed ? 'Yes' : 'No',
        l.campaignName || l.campaignId || '',
        l.status || '',
        Number(l.value) || 0,
        Number(l.touches) || 0,
      ];
    });
    downloadCsv(headers, data, fname);
  }

  // Channel breakdown CSV — mirrors the rendered table (Shopify-truth rev/net/
  // roas/CAC when the aggregate is live, else platform numbers).
  function downloadChannelsCsv() {
    var rows = (state.channels && state.channels.channels) || [];
    if (!rows.length) return;
    var range = dateRange();
    var fname = 'paid-ads-channels_' + range.from + '_to_' + range.to + '.csv';
    var useTrueCmChannels = state.cm && state.cm.totals && state.cm.totals.orderCount > 0;
    var cmByPlatform = {};
    if (state.cm && state.cm.byChannel) {
      Object.keys(state.cm.byChannel).forEach(function(ck) { var e = state.cm.byChannel[ck]; if (e && e.platform) cmByPlatform[e.platform] = e; });
    }
    var headers = ['Channel', 'Spend', 'Plan Target', 'Variance %', 'Revenue', 'Net', 'ROAS', 'Conversions', 'CAC', 'Impressions'];
    var data = rows.map(function (c) {
      var label = c.platform === 'google-ads' ? 'Google Ads' : (c.platform === 'meta' ? 'Meta' : c.platform);
      var planTarget = state.showPlan ? totalChannelPlanForRange(c.platform, range.from, range.to) : null;
      var variance = (planTarget != null && planTarget > 0) ? variancePct(c.spend, planTarget) : null;
      var cmEntry = cmByPlatform[c.platform] || null;
      var rev, net, roas, cac = '';
      if (useTrueCmChannels) {
        var chRev = cmEntry ? (Number(cmEntry.directTotal) || 0) : 0;
        rev = chRev; net = chRev - (Number(c.spend) || 0);
        roas = (Number(c.spend) || 0) > 0 ? chRev / (Number(c.spend) || 0) : 0;
        if (cmEntry && (Number(cmEntry.orderCount) || 0) > 0) cac = ((Number(c.spend) || 0) / Number(cmEntry.orderCount)).toFixed(2);
      } else { rev = c.revenue; net = c.net; roas = c.roas; }
      return [
        label, Number(c.spend) || 0,
        planTarget != null ? Number(planTarget) : '', variance != null ? Number(variance) : '',
        Number(rev) || 0, Number(net) || 0, roas != null ? Number(roas).toFixed(2) : '',
        Number(c.conversions) || 0, cac, Number(c.impressions) || 0
      ];
    });
    downloadCsv(headers, data, fname);
  }

  // Daily Performance series CSV — the full per-day series behind the chart
  // (every tab's metric in one file), so the agency can chart it their own way.
  function downloadSeriesCsv() {
    var s = chartSeries();
    if (!s.length) return;
    var range = dateRange();
    var fname = 'paid-ads-daily-performance_' + range.from + '_to_' + range.to + '.csv';
    var headers = [
      'Date', 'Ad Spend', 'Impressions', 'Clicks', 'CTR %', 'Ad Conversions',
      'Total Conversions (orders)', 'Paid Orders', 'Ad Revenue', 'Total Revenue',
      'Gross Profit', 'Contribution Margin', 'Ad ROAS (cum)', 'Total ROAS (cum)', 'Click->Sale CVR % (cum)'
    ];
    var data = s.map(function (m) {
      return [
        m.date, Number(m.spend) || 0, Number(m.impressions) || 0, Number(m.clicks) || 0,
        ((Number(m.ctr) || 0) * 100).toFixed(2), Number(m.conversions) || 0,
        Number(m.totalConversions) || 0, Number(m.paidOrders) || 0,
        Number(m.adRevenue) || 0, Number(m.totalRevenue) || 0, Number(m.grossProfit) || 0,
        Number(m.cm) || 0, (Number(m.adRoas) || 0).toFixed(2), (Number(m.totalRoas) || 0).toFixed(2),
        ((Number(m.cvr) || 0) * 100).toFixed(2)
      ];
    });
    downloadCsv(headers, data, fname);
  }
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — Lead Source Mix donut +
     per-order economics drawer, ported from the retired standalone Orders page so
     Recent Sales is a full drill-down surface (no separate page to load). Both
     read the already-loaded state.orders.orders (same /dashboard/sold-orders
     payload). Class/var names remapped to the .moph-odrawer / .moph-mix / --moph-*
     namespaces; reuses the existing fmtMoney / escapeHtml / shopifyLogoSvg. */
  /* Distinct categorical palette spread across the hue wheel so each KNOWN source
     reads at a glance (rfq/email/phone/referral were all defaulting to gray). The
     genuinely-unknown buckets (inbound/unknown/other) stay muted on purpose. */
  var SOURCE_COLORS = {
    'online':     '#16a34a', // green
    'rfq':        '#2563eb', // blue
    'form':       '#06b6d4', // cyan
    'chatbot':    '#f59e0b', // amber
    'sample':     '#8b5cf6', // purple
    'email':      '#f472b6', // bright/light pink
    'phone':      '#ff3030', // bright fire-truck red — pops on its near-black pill bg (see SOURCE_BG); distinct from Email's pink
    'referral':   '#0abab5', // Tiffany blue — distinct from Chatbot's amber (was yellow #eab308, too close to amber; Ryan 2026-07-09)
    'manual-rep': '#64748b', // slate
    'inbound':    '#94a3b8', // muted (coarse "they reached us")
    'unknown':    '#94a3b8', // muted
    'other':      '#9ca3af', // gray
    // CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-26) — WEIGHTS: leads that came from the Weight
    // Designer. Green pill body + bright YELLOW label (see SOURCE_BG below), Ryan's pick. Yellow
    // rather than the mustard also floated: this same value is the donut's solid fill, where mustard
    // would land on top of Chatbot's amber — the adjacency that retired #eab308 for Referral.
    'blog':       '#d946ef', // fuchsia — CUSTOM_BLOG_CTA_BY_CLAUDE fix (Ryan 2026-07-26). blog-cta has
                             // grouped to 'blog' since it shipped, but this map never got the key, so blog
                             // leads drew the same fallback gray as 'other'.
    'weights':    '#FFE01A', // bright yellow on a deep-green pill
  };
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — per-source pill BACKGROUND
     overrides for the SALES table SOURCE column. Default bg is the source color at
     ~13% alpha (`_scolor + '22'`); a source listed here uses an explicit bg instead.
     Phone gets a near-black (black + ~10% red) so its bright fire-truck-red label
     reads with maximum punch. The donut (solid fills) ignores this — bg is pill-only. */
  var SOURCE_BG = {
    'phone': '#1a0606', // near-black with a hint of red
    'weights': '#14532d', // deep green body — the yellow label reads hard against it
  };
  /* Display labels per source group, shared by the Recent Sales table + the donut
     legend so they read identically (e.g. "RFQ", "Rep entered", not "Rfq"). */
  var SOURCE_LABELS = {
    online: 'Online', rfq: 'RFQ', form: 'Form', chatbot: 'Chatbot', sample: 'Sample',
    email: 'Email', phone: 'Phone', referral: 'Referral', 'manual-rep': 'Rep entered',
    inbound: 'Unknown', unknown: 'Unknown', other: 'Other',
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-26) — "Weights", not "WEIGHTS". The key stays
       lowercase `weights`; only the DISPLAY changes. Every reader is `MAP[key] || fallback`, so
       nothing compares against the label. */
    weights: 'Weights', blog: 'Blog'
  };
  /* Build one donut + legend into the given elements. segs = [{key,count,color,
     label,prefix,sub}]; sized by ORDER COUNT out of `total` (not revenue, so one
     big order can't dominate). `sub` is the secondary stat shown in the legend
     (revenue $). data-mix-key on each arc + row drives the hover cross-highlight. */
  function _buildMixDonut(donutEl, legendEl, segs, total) {
    if (!donutEl || !legendEl) return;
    var size = 132, cx = size / 2, cy = size / 2, r = 51, strokeW = 22;
    var circ = 2 * Math.PI * r;
    var offset = 0;
    var arcs = segs.map(function(s) {
      var pct = total > 0 ? s.count / total : 0;
      var dash = (pct * circ) + ' ' + circ;
      var rot = offset * 360; offset += pct;
      return '<circle data-mix-key="' + escapeHtml(s.key) + '" cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="none" stroke="' + s.color + '" stroke-width="' + strokeW + '" stroke-dasharray="' + dash + '" stroke-dashoffset="0" transform="rotate(' + (rot - 90) + ' ' + cx + ' ' + cy + ')" />';
    }).join('');
    donutEl.innerHTML = '<svg width="' + size + '" height="' + size + '" viewBox="0 0 ' + size + ' ' + size + '">' + arcs + '<text x="' + cx + '" y="' + (cy - 2) + '" text-anchor="middle" font-size="20" font-weight="700" fill="currentColor">' + total + '</text><text x="' + cx + '" y="' + (cy + 14) + '" text-anchor="middle" font-size="11" fill="currentColor" opacity="0.55">orders</text></svg>';
    legendEl.innerHTML = segs.map(function(s) {
      var pct = total > 0 ? (s.count / total) * 100 : 0;
      // count · revenue · order-% — count is the slice basis; revenue is context.
      return '<div class="moph-mix__legend-row" data-mix-key="' + escapeHtml(s.key) + '" title="' + s.count + ' order' + (s.count === 1 ? '' : 's') + ' · ' + s.sub + ' · ' + pct.toFixed(1) + '% of orders">' +
        '<span class="moph-mix__legend-dot" style="background:' + s.color + '"></span>' +
        '<span class="moph-mix__legend-label">' + (s.prefix || '') + escapeHtml(s.label) + '</span>' +
        '<span class="moph-mix__legend-pct">' + s.count + ' · ' + s.sub + ' · ' + pct.toFixed(1) + '%</span>' +
      '</div>';
    }).join('');
  }
  /* Hover cross-highlight, scoped to one .moph-mix group: dim the non-matching
     arcs + activate the matching legend row (and vice-versa). key=null clears. */
  function _mixHighlight(mixEl, key) {
    if (!mixEl) return;
    mixEl.querySelectorAll('[data-mix-key]').forEach(function(el) {
      var match = (key == null) || el.getAttribute('data-mix-key') === key;
      if (el.tagName.toLowerCase() === 'circle') {
        el.style.opacity = match ? '1' : '0.22';
      } else {
        el.classList.toggle('moph-mix__legend-row--active', key != null && match);
      }
    });
  }
  function renderMix() {
    var wrap = document.getElementById('mophMixWrap');
    if (!wrap) return;
    var orders = (state.orders && state.orders.orders) || [];
    if (orders.length === 0) { wrap.hidden = true; return; }
    var totalCount = orders.length;
    // Donut 1 — source mix, sized by order count (revenue stays in the legend).
    var revByGroup = {}, cntByGroup = {};
    orders.forEach(function(o) {
      var g = o.sourceGroup || 'unknown';
      revByGroup[g] = (revByGroup[g] || 0) + (Number(o.revenue) || 0);
      cntByGroup[g] = (cntByGroup[g] || 0) + 1;
    });
    var srcSegs = Object.keys(cntByGroup).map(function(k) {
      return { key: k, count: cntByGroup[k], color: SOURCE_COLORS[k] || '#9ca3af',
        label: SOURCE_LABELS[k] || k.replace('-', ' '), prefix: k === 'online' ? shopifyLogoSvg() : '',
        sub: fmtMoney(revByGroup[k] || 0, { compact: true }) };
    }).sort(function(a, b) { return b.count - a.count; });
    _buildMixDonut(document.getElementById('mophMixDonut'), document.getElementById('mophMixLegend'), srcSegs, totalCount);
    // Donut 2 — commercial vs residential. An order is commercial when it carried
    // an org/company name (o.organization, set by the worker); else residential.
    var bizCnt = 0, bizRev = 0, resCnt = 0, resRev = 0;
    orders.forEach(function(o) {
      var rev = Number(o.revenue) || 0;
      if (o.organization && String(o.organization).trim()) { bizCnt++; bizRev += rev; }
      else { resCnt++; resRev += rev; }
    });
    var crSegs = [];
    if (bizCnt > 0) crSegs.push({ key: 'commercial', count: bizCnt, color: '#0d9488', label: 'Commercial', prefix: '', sub: fmtMoney(bizRev, { compact: true }) });
    if (resCnt > 0) crSegs.push({ key: 'residential', count: resCnt, color: '#d97706', label: 'Residential', prefix: '', sub: fmtMoney(resRev, { compact: true }) });
    _buildMixDonut(document.getElementById('mophMixDonut2'), document.getElementById('mophMixLegend2'), crSegs, totalCount);
    // Donut 3 — channel: paid vs organic, by last-touch channel (matches the table's
    // Channel column: any non-organic channel = paid).
    var paidCnt = 0, paidRev = 0, orgCnt = 0, orgRev = 0;
    orders.forEach(function(o) {
      var ch = (o.channel || 'organic').toString().toLowerCase();
      var rev = Number(o.revenue) || 0;
      if (ch && ch !== 'organic') { paidCnt++; paidRev += rev; }
      else { orgCnt++; orgRev += rev; }
    });
    var poSegs = [];
    if (paidCnt > 0) poSegs.push({ key: 'paid', count: paidCnt, color: '#006BCC', label: 'Paid', prefix: '', sub: fmtMoney(paidRev, { compact: true }) });
    if (orgCnt > 0) poSegs.push({ key: 'organic', count: orgCnt, color: '#65a30d', label: 'Organic', prefix: '', sub: fmtMoney(orgRev, { compact: true }) });
    _buildMixDonut(document.getElementById('mophMixDonut3'), document.getElementById('mophMixLegend3'), poSegs, totalCount);
    wrap.hidden = false;
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — channel color matching the
     Channel Breakdown table's platform dots (Google #4285F4, Meta #E1306C) so the
     drawer's channel reads identically in both the Lead Source and How-credited
     sections instead of two different blues. */
  function _mophChannelColor(ch) {
    ch = (ch || '').toString().toLowerCase();
    return ch === 'google' ? '#4285F4' : (ch === 'meta' ? '#E1306C' : 'var(--moph-ink-2)');
  }
  function _oProvenanceHtml(o) {
    var p = o.provenance || null;
    var channel = o.channel || (p && p.channel) || 'organic';
    var methodMeta = {
      'landing_site':       { label: 'Ad landing page', paid: true },
      'landing_site-quote': { label: 'Ad landing page', paid: true },
      'shopify-journey':    { label: 'Shopify journey', paid: true },
      'lead-utm-journey':   { label: 'Lead journey', paid: true },
      'organic':            { label: 'Organic', paid: false },
      'organic-journey':    { label: 'Organic', paid: false },
      'no-data':            { label: 'No data', paid: false },
      'ga4-bigquery':       { label: 'GA4 recovered', paid: true },
      'ga4-organic':        { label: 'Organic', paid: false },
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-26) — THREE methods the worker has always
         stamped were missing from this table, and an unmapped method falls through to the
         'organic' default. So a row credited by identity propagation read "Method: Organic" and,
         via _oAttributedVia's default branch, the sentence "unpaid search results, NOT AN AD" —
         the exact opposite of why it was credited. RW-1356-A showed Channel: Google beside it.
         ⛔ This is the screen you open to RULE on a soft match, so the one row that needs a
         judgement was the row being actively misdescribed. */
      'identity-propagation': { label: 'Matched to an earlier ad click', paid: true },
      'phone-call':           { label: 'Tracking number', paid: true },
      'paid-referral':        { label: 'Partner referral', paid: false }
    };
    var method = (p && p.method) || (channel !== 'organic' ? 'shopify-journey' : 'organic');
    var meta = methodMeta[method] || methodMeta['organic'];
    var campLabel = '—';
    if (p && p.campaignId) {
      campLabel = p.campaignName
        ? escapeHtml(p.campaignName) + ' (' + escapeHtml(String(p.campaignId)) + ')'
        : escapeHtml(String(p.campaignId));
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — the SYSTEM the data came from
       (concise — just the system name; the Landing page / Campaign rows show the detail). */
    var DATA_SOURCE = {
      'landing_site': 'Shopify',
      'landing_site-quote': 'Shopify',
      'shopify-journey': 'Shopify',
      'lead-utm-journey': 'Our website',
      'ga4-bigquery': 'Google Analytics',
      'ga4-organic': 'Google Analytics',
      'organic': 'Shopify',
      'organic-journey': 'Shopify',
      'no-data': 'None',
      'identity-propagation': 'Same-customer match',
      'phone-call': 'Call tracking',
      'paid-referral': 'Referral partner'
    };
    var dataSource = DATA_SOURCE[method] || 'Shopify';
    return '<div class="moph-odrawer__section">' +
      '<div class="moph-odrawer__section-title">How this was credited</div>' +
      '<div class="moph-odrawer__kv">' +
        '<div class="moph-odrawer__kv-key">Data source</div><div class="moph-odrawer__kv-val">' + escapeHtml(dataSource) + '</div>' +
        '<div class="moph-odrawer__kv-key">Method</div><div class="moph-odrawer__kv-val">' + escapeHtml(meta.label) + '</div>' +
        (p && p.landingPage ? '<div class="moph-odrawer__kv-key">Landing page</div><div class="moph-odrawer__kv-val">' + escapeHtml(p.landingPage) + '</div>' : '') +
        (meta.paid ? '<div class="moph-odrawer__kv-key">Campaign</div><div class="moph-odrawer__kv-val">' + campLabel + '</div>' : '') +
      '</div>' +
    '</div>';
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — the date the ad click
     occurred, surfaced in the drawer's Lead Source section. Lead/quote journeys
     carry the real paid-touch timestamp (can predate the order by weeks); online-
     direct paid orders (landing_site / Shopify journey / GA4) are single-session,
     so the click happened the day of the order. Organic orders had no ad click → ''.
     Model-aware: prefers the touch the active attribution model credits. */
  function _oClickDate(o) {
    var paid = o.attribution || {};
    var ft = (paid.firstPaidTouch && paid.firstPaidTouch.ts) || '';
    var lt = (paid.lastPaidTouch && paid.lastPaidTouch.ts) || '';
    var t = (state.attribution === 'lastPaid') ? (lt || ft) : (ft || lt);
    if (t) return String(t).slice(0, 10);
    var m = (o.provenance && o.provenance.method) || '';
    if (m === 'landing_site' || m === 'landing_site-quote' || m === 'shopify-journey' || m === 'ga4-bigquery') {
      return String(o.soldTs || o.dateSold || '').slice(0, 10);
    }
    return '';
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — plain-English "how we know
     this is ad-attributed", surfaced in the Lead Source section so a paid deal never
     leaves the agency guessing. Specifically resolves the common Phone/Email source
     vs Google/Meta channel mismatch (they contacted us one way but had clicked an ad
     first). Keyed to the provenance method = the actual signal we credited from.
     Returns safe HTML (dynamic bits escaped) or '' when there's no paid signal. */
  function _oAttributedVia(o) {
    var p = o.provenance || {};
    if (!p.method) return '';
    var chRaw = (o.channel || p.channel || 'organic');
    var ch = escapeHtml(chRaw.charAt(0).toUpperCase() + chRaw.slice(1));
    var campRaw = p.campaignName || (p.campaignId ? ('campaign ' + p.campaignId) : '');
    var campBit = campRaw ? ' (<strong>' + escapeHtml(campRaw) + '</strong>)' : '';
    var sgRaw = (o.sourceGroup || '').toLowerCase();
    var via = (sgRaw && sgRaw !== 'online' && sgRaw !== 'organic') ? escapeHtml(sgRaw) : '';
    var page = p.landingPage ? '<code>' + escapeHtml(p.landingPage) + '</code>' : 'the order’s landing page';
    var lead = '<strong>How we know:</strong> ';
    switch (p.method) {
      case 'landing_site':
        return lead + 'the buyer first landed on ' + page + ' from a ' + ch + ' ad click' + campBit + ', so the sale is credited to ' + ch + '.';
      case 'landing_site-quote':
        return lead + 'even though the customer reached out by ' + (via || 'another channel') + ', they first landed on ' + page + ' from a ' + ch + ' ad click' + campBit + ' — they’d clicked the ad first, so the sale is credited to that ' + ch + ' campaign.';
      case 'lead-utm-journey':
        return lead + 'the lead we captured carried ' + ch + ' ad tags' + campBit + ' — the click that brought them in before the quote. They reached out by ' + (via || 'the lead form') + ', but the ad gets the credit.';
      case 'shopify-journey':
        return lead + 'Shopify’s customer-journey record shows a ' + ch + ' ad touch' + campBit + ' for this order.';
      case 'ga4-bigquery':
        return lead + 'pulled from GA4 — the order’s session carried a ' + ch + ' ad click' + campBit + ' that Shopify’s own signals (journey + landing URL) both missed.';
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-26) — the propagated case, stated honestly.
         This order carried NO ad click of its own; it inherited one from a sibling of the same
         customer inside 30 days. The rung is the whole story — email/phone is the same person and
         pays automatically, domain/org is company-level, excluded by default, and waiting on a
         ruling. Naming both, and naming which, is the difference between a decision and a guess. */
      case 'identity-propagation': {
        var RUNG = {
          email: 'the same email address', phone: 'the same phone number',
          domain: 'a shared email domain', org: 'the same company name'
        };
        var how = RUNG[String(p.rung || '').toLowerCase()] || 'the same customer';
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-09-06) — the window is a setting now (worker stamps
           the one in force on the row), and the anchor can be a LEAD: the sample request that carried
           the click. Name it — "the sample they ordered on JW-1251-A" is the whole story of the deal. */
        var _win = Number(p.windowDays) || Number(state.attributionWindowDays) || 30;
        var _an = p.anchor || null;
        var _SRC = { 'sample-request': 'free sample request', 'sample': 'free sample request', 'chatbot': 'chatbot conversation', 'rfq': 'quote request', 'contact-form': 'web form', 'form': 'web form', 'bulk-quote': 'bulk quote request', 'email-lead': 'email enquiry', 'weight-designer': 'Weight Designer design', 'online': 'online order' };
        var _anchorBit = '';
        if (_an && _an.kind === 'lead') {
          _anchorBit = ' — the click that brought in their <strong>' + escapeHtml(_SRC[String(_an.source || '').toLowerCase()] || 'earlier enquiry') + '</strong>' + (_an.ref ? ' (' + escapeHtml(String(_an.ref)) + ')' : '');
        } else if (_an && _an.kind === 'order') {
          _anchorBit = ' — the click behind their <strong>earlier order</strong>' + (_an.ref ? ' (' + escapeHtml(String(_an.ref)) + ')' : '');
        }
        return lead + 'this order carried <strong>no ad click of its own</strong>. It was matched to an earlier '
          + ch + ' ad click within ' + _win + ' days on <strong>' + escapeHtml(how) + '</strong>' + _anchorBit
          + (p.soft
            ? ' — a <strong>company-level</strong> match, which is excluded from the agency fee by default until you rule it in on the Agency Comp page. A shared domain or company name is not proof the same buyer came back.'
            : ' — a same-person match, which pays automatically.');
      }
      case 'phone-call':
        return lead + 'they dialled our ' + ch + ' call-tracking number'
          + (p.dialedNumber ? ' (' + escapeHtml(String(p.dialedNumber)) + ')' : '')
          + ', which is a direct ad signal — no web journey needed.';
      case 'paid-referral':
        return lead + 'a partner sent this deal'
          + (p.referralName ? ' (<strong>' + escapeHtml(String(p.referralName)) + '</strong>)' : '')
          + ' and is paid on it, so the ads are not credited. Any ad touch on the account is deliberately suppressed here.';
      default:
        return lead + _organicSignal(p);
    }
  }
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — plain-English explanation for
     the non-paid methods (organic search / direct / referral / no-data), so an organic
     order reads "found us through Google organic search" instead of the raw
     "source=google · medium=organic" that looks contradictory next to a Channel of
     organic. Names the real source/engine. */
  function _organicSignal(p) {
    var src = (p.source || '').toLowerCase().trim();
    var med = (p.medium || '').toLowerCase().trim();
    var ga4 = (p.method === 'ga4-organic') ? ' (GA4 confirms it was organic, not a missed ad)' : '';
    if (p.method === 'no-data') return 'no browsing history was captured for this order — typically a rep-entered quote with no online journey.';
    if (med === 'referral' || med === 'referrer') {
      return 'they followed a referral link from <strong>' + escapeHtml(src || 'another site') + '</strong> — not a paid ad' + ga4 + '.';
    }
    var isDirect = (!src || src === '(direct)' || src === 'direct') && (!med || med === '(none)' || med === 'none');
    if (isDirect) return 'they came in directly — typed the address or used a bookmark, with no referring link or ad' + ga4 + '.';
    if (!med || med === 'organic') {
      var eng = src.indexOf('google') > -1 ? 'Google' : (src.indexOf('bing') > -1 ? 'Bing' : (src.indexOf('yahoo') > -1 ? 'Yahoo' : (src.indexOf('duckduckgo') > -1 ? 'DuckDuckGo' : (src ? (src.charAt(0).toUpperCase() + src.slice(1)) : 'a search engine'))));
      return 'they found us through <strong>' + escapeHtml(eng) + '</strong> organic search — unpaid search results, not an ad' + ga4 + '.';
    }
    return 'they came from <strong>' + escapeHtml(src || 'an unknown source') + '</strong>' + (med ? ' (' + escapeHtml(med) + ')' : '') + ' — not a paid ad' + ga4 + '.';
  }
  function openOrderDrawer(o) {
    if (!o) return;
    document.getElementById('mophODrawerRef').textContent = o.ref;
    var paid = o.attribution || {};
    var first = paid.firstPaidTouch;
    var last = paid.lastPaidTouch;
    var clickDate = _oClickDate(o);
    var attributedVia = _oAttributedVia(o);
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — humanized source label, and
       Raw source only when it adds info (not for online orders, where the raw value is
       the "shopify-direct" jargon, and not when it just duplicates the group). */
    var _sgKey = (o.sourceGroup || '').toLowerCase().trim();
    var _sgNorm = _sgKey.replace(/-/g, ' ');
    var _rsNorm = (o.source || '').toLowerCase().trim().replace(/-/g, ' ');
    var SG_LABELS = { 'online':'Online store', 'phone':'Phone', 'form':'Web form', 'chatbot':'Chatbot', 'email':'Email', 'sample-request':'Sample request', 'sample':'Sample request', 'bulk-quote':'Bulk quote', 'rfq':'RFQ', 'manual-rep':'Rep-entered', 'manual':'Rep-entered', 'weights':'Weights', 'blog':'Blog', 'blog-cta':'Blog' };
    var sourceLabel = SG_LABELS[_sgKey] || (o.sourceGroup ? o.sourceGroup.replace(/-/g, ' ') : 'Unknown');
    var rawSourceRow = (_rsNorm && _rsNorm !== _sgNorm && _sgKey !== 'online')
      ? '<div class="moph-odrawer__kv-key">Raw source</div><div class="moph-odrawer__kv-val">' + escapeHtml(o.source) + '</div>'
      : '';
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — a paid-channel order has, by
       definition, ≥1 paid touch (the ad click we credited from the landing URL / GA4),
       even when no multi-touch lead journey was captured. Floor the displayed counts so
       "Channel: Google" never sits next to a contradictory "Paid touches: 0". */
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-26) — ⛔ the floor must NOT apply to a
       PROPAGATED row. Its whole premise is that this order had no ad click of its own, so
       printing "Paid touches: 1" next to "No paid touch in the captured journey" is the
       contradiction the floor exists to prevent, produced by the floor itself. Here the real
       count IS the honest answer, and the How-we-know line above carries the credit. */
    var _propagated = ((o.provenance && o.provenance.method) || '') === 'identity-propagation';
    var _isPaidAttr = (o.channel || 'organic') !== 'organic' && !_propagated;
    var dispPaidTouches = paid.paidTouchCount || 0;
    var dispTotalTouches = paid.touchCount || 0;
    if (_isPaidAttr && dispPaidTouches < 1) dispPaidTouches = 1;
    if (dispTotalTouches < dispPaidTouches) dispTotalTouches = dispPaidTouches;
    function touchHtml(t, label) {
      if (!t) return '<div style="color:var(--moph-ink-2);font-style:italic;">No ' + label + ' touch (organic)</div>';
      var bits = [
        t.utm_source ? 'source=' + t.utm_source : '',
        t.utm_medium ? 'medium=' + t.utm_medium : '',
        t.utm_campaign ? 'campaign=' + t.utm_campaign : '',
        t.utm_content ? 'content=' + t.utm_content : '',
        t.utm_term ? 'term=' + t.utm_term : '',
        t.gclid ? 'gclid=' + (t.gclid.slice(0, 8) + '…') : '',
        t.fbclid ? 'fbclid=' + (t.fbclid.slice(0, 8) + '…') : ''
      ].filter(Boolean).join(' · ');
      return '<div class="moph-odrawer__touch moph-odrawer__touch--paid">' +
        '<div class="moph-odrawer__touch-head">' +
          '<span class="moph-odrawer__touch-source">' + escapeHtml(label) + ': ' + escapeHtml(t.utm_source || '?') + '</span>' +
          '<span class="moph-odrawer__touch-ts">' + escapeHtml((t.ts || '').slice(0, 10)) + '</span>' +
        '</div>' +
        '<div class="moph-odrawer__touch-meta">' + escapeHtml(bits) + '</div>' +
        (t.landingPath ? '<div class="moph-odrawer__touch-meta">landing: ' + escapeHtml(t.landingPath) + '</div>' : '') +
      '</div>';
    }
    document.getElementById('mophODrawerBody').innerHTML =
      '<div class="moph-odrawer__section">' +
        '<div class="moph-odrawer__section-title">Order</div>' +
        '<div class="moph-odrawer__kv">' +
          '<div class="moph-odrawer__kv-key">Date sold</div><div class="moph-odrawer__kv-val">' + escapeHtml((o.dateSold || '').slice(0, 10)) + '</div>' +
          '<div class="moph-odrawer__kv-key">Customer</div><div class="moph-odrawer__kv-val">' + escapeHtml(o.customer || '—') + '</div>' +
          (o.vertical ? '<div class="moph-odrawer__kv-key">Vertical</div><div class="moph-odrawer__kv-val">' + escapeHtml(o.vertical) + '</div>' : '') +
          (o.formName ? '<div class="moph-odrawer__kv-key">Form</div><div class="moph-odrawer__kv-val">' + escapeHtml(o.formName) + '</div>' : '') +
        '</div>' +
      '</div>' +
      (function() {
        var lis = Array.isArray(o.lineItems) ? o.lineItems : [];
        if (lis.length === 0) return '';
        var rows = lis.map(function(li) {
          var sub = [li.variantTitle, li.sku].filter(Boolean).join(' · ');
          return '<tr>' +
            '<td>' + escapeHtml(li.title || '(untitled)') +
              (sub ? '<div class="moph-odrawer__line-sub">' + escapeHtml(sub) + '</div>' : '') +
            '</td>' +
            '<td style="text-align:right;">' + (li.qty || 0) + '</td>' +
            '<td style="text-align:right;">' + fmtMoney(li.lineTotal) + '</td>' +
          '</tr>';
        }).join('');
        return '<div class="moph-odrawer__section">' +
          '<div class="moph-odrawer__section-title">Line Items</div>' +
          '<table class="moph-odrawer__lines"><thead><tr>' +
            '<th style="text-align:left;">Product</th>' +
            '<th style="text-align:right;">Qty</th>' +
            '<th style="text-align:right;">Line Total</th>' +
          '</tr></thead><tbody>' + rows + '</tbody></table>' +
        '</div>';
      })() +
      '<div class="moph-odrawer__section">' +
        '<div class="moph-odrawer__section-title">Financials</div>' +
        '<div class="moph-odrawer__kv">' +
          '<div class="moph-odrawer__kv-key">Product revenue</div><div class="moph-odrawer__kv-val">' + fmtMoney(o.revenue) + '</div>' +
          '<div class="moph-odrawer__kv-key">COGS</div><div class="moph-odrawer__kv-val" style="color:var(--moph-red)">−' + fmtMoney(o.cogs || 0) + '</div>' +
          '<div class="moph-odrawer__kv-key" style="font-weight:700">Product GP</div><div class="moph-odrawer__kv-val" style="font-weight:700">' + fmtMoney(o.grossProfit) + '</div>' +
          '<div class="moph-odrawer__kv-key">Margin</div><div class="moph-odrawer__kv-val">' + (o.grossMargin ? Math.round(o.grossMargin) + '%' : '—') + '</div>' +
        '</div>' +
      '</div>' +
      (function() {
        var hasFreight = (o.freightCharged || 0) > 0 || (o.freightCost || 0) > 0;
        var hasFees = (o.shopifyFees || 0) > 0;
        var hasCustom = Array.isArray(o.customCosts) && o.customCosts.length > 0;
        var hasOrderTotal = (o.orderTotal || 0) > 0 && Math.abs((o.orderTotal || 0) - (o.revenue || 0)) > 0.01;
        if (!hasFreight && !hasFees && !hasCustom && !hasOrderTotal) return '';
        var html = '<div class="moph-odrawer__section">' +
          '<div class="moph-odrawer__section-title">Order Economics</div>' +
          '<div class="moph-odrawer__kv">';
        if (hasOrderTotal) {
          html += '<div class="moph-odrawer__kv-key">Order total (paid)</div><div class="moph-odrawer__kv-val">' + fmtMoney(o.orderTotal) + '</div>';
        }
        if (hasFreight) {
          html += '<div class="moph-odrawer__kv-key">Freight charged</div><div class="moph-odrawer__kv-val">' + fmtMoney(o.freightCharged || 0) + '</div>';
          /* CUSTOM_MARKETINGOPS_BY_CLAUDE (AIOPS 597c8b1b4638, Ryan 2026-09-10) — ⛔ COST AND
             MARGIN ARE ADMIN-ONLY; CHARGED IS NOT. Ryan 2026-09-10: reps see what we charge, never
             what we pay. This drawer is on a page ordinary reps load — `isAdminSession()` guards
             only the "include non-marketing revenue" toggle elsewhere in this file, which is the
             tell that everything else here was written as if the audience were admins.
             ⚠️ THE MARGIN ROW GOES TOO, and it is the one that is easy to miss: charged is on
             screen one line above, so a margin is the cost by subtraction. Hiding the cost and
             leaving the arithmetic that reproduces it is not hiding the cost. */
          if (isAdminSession()) {
            html += '<div class="moph-odrawer__kv-key">Freight cost</div><div class="moph-odrawer__kv-val" style="color:var(--moph-red)">−' + fmtMoney(o.freightCost || 0) + '</div>';
            var freightColor = (o.freightMargin || 0) >= 0 ? 'var(--moph-green)' : 'var(--moph-red)';
            html += '<div class="moph-odrawer__kv-key" style="font-weight:700">Freight margin</div><div class="moph-odrawer__kv-val" style="font-weight:700;color:' + freightColor + '">' + fmtMoney(o.freightMargin || 0) + '</div>';
          }
        }
        if (hasFees) {
          html += '<div class="moph-odrawer__kv-key">Shopify fees</div><div class="moph-odrawer__kv-val" style="color:var(--moph-red)">−' + fmtMoney(o.shopifyFees) + '</div>';
        }
        html += '</div>';
        if (hasCustom) {
          html += '<div style="margin-top:10px;font-size:12px;color:var(--moph-ink-2);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Custom costs (rolled into COGS)</div>';
          html += '<div class="moph-odrawer__kv" style="margin-top:6px;">';
          o.customCosts.forEach(function(c) {
            html += '<div class="moph-odrawer__kv-key">' + escapeHtml(c.label || '(unlabeled)') + '</div><div class="moph-odrawer__kv-val" style="color:var(--moph-red)">−' + fmtMoney(c.amount || 0) + '</div>';
          });
          html += '</div>';
        }
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — bottom-line order GP:
           Product GP + freight margin − Shopify fees (custom costs already roll into
           Product GP), and its margin on total order revenue (goods + freight). */
        var _ogp = (Number(o.grossProfit) || 0) + (Number(o.freightMargin) || 0) - (Number(o.shopifyFees) || 0);
        var _orev = (Number(o.revenue) || 0) + (Number(o.freightCharged) || 0);
        var _omar = _orev > 0 ? (_ogp / _orev) * 100 : null;
        html += '<div class="moph-odrawer__kv" style="margin-top:12px;border-top:1px solid var(--moph-line);padding-top:10px;">' +
          '<div class="moph-odrawer__kv-key" style="font-weight:700">Order GP</div><div class="moph-odrawer__kv-val" style="font-weight:700;color:' + (_ogp >= 0 ? 'var(--moph-green)' : 'var(--moph-red)') + '">' + fmtMoney(_ogp) + '</div>' +
          '<div class="moph-odrawer__kv-key">Order margin</div><div class="moph-odrawer__kv-val">' + (_omar == null ? '—' : (Math.round(_omar) + '%')) + '</div>' +
        '</div>';
        html += '</div>';
        return html;
      })() +
      '<div class="moph-odrawer__section">' +
        '<div class="moph-odrawer__section-title">Lead Source</div>' +
        '<div class="moph-odrawer__kv">' +
          '<div class="moph-odrawer__kv-key">Source</div><div class="moph-odrawer__kv-val">' + escapeHtml(sourceLabel) + '</div>' +
          rawSourceRow +
          '<div class="moph-odrawer__kv-key">Channel</div><div class="moph-odrawer__kv-val" style="text-transform:capitalize">' + escapeHtml(channelDot(o.channel).label || (o.channel || 'organic')) + '</div>' +
          (clickDate ? '<div class="moph-odrawer__kv-key">Click date</div><div class="moph-odrawer__kv-val">' + escapeHtml(clickDate) + '</div>' : '') +
        '</div>' +
        (attributedVia ? '<div style="margin-top:10px;font-size:12px;color:var(--moph-ink-2);line-height:1.5">' + attributedVia + '</div>' : '') +
      '</div>' +
      _oProvenanceHtml(o) +
      '<div class="moph-odrawer__section">' +
        '<div class="moph-odrawer__section-title">Attribution Journey</div>' +
        '<div class="moph-odrawer__kv" style="margin-bottom:12px">' +
          '<div class="moph-odrawer__kv-key">Total touches</div><div class="moph-odrawer__kv-val">' + dispTotalTouches + '</div>' +
          '<div class="moph-odrawer__kv-key">Paid touches</div><div class="moph-odrawer__kv-val">' + dispPaidTouches + '</div>' +
          '<div class="moph-odrawer__kv-key">Assisted</div><div class="moph-odrawer__kv-val">' + (paid.assistedConversion ? 'Yes' : 'No') + '</div>' +
        '</div>' +
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-23) — explain the asterisk that
           appears in the Recent Sales Touches column (e.g. 2*), which had no on-card
           explanation and no visible counterpart in this section. */
        (paid.assistedConversion
          ? '<div style="margin-top:-4px;margin-bottom:12px;font-size:12px;color:var(--moph-ink-2);line-height:1.5"><strong>Assisted</strong> means a paid ad was part of the journey but wasn’t the final converting click. In the Recent Sales <strong>Touches</strong> column these are flagged with a <strong>*</strong> after the count (e.g. <strong>2*</strong>).</div>'
          : '') +
        '<div class="moph-odrawer__journey">' +
          ((first || last)
            ? (touchHtml(first, 'First paid') + (last && last !== first ? touchHtml(last, 'Last paid') : ''))
            : ((o.provenance && o.provenance.method === 'landing_site')
                ? '<div style="color:var(--moph-ink-2);font-style:italic">Online direct order — credited from the entry URL (landing_site) above; Shopify captured no multi-touch lead journey.</div>'
              : (o.provenance && o.provenance.method === 'landing_site-quote')
                ? '<div style="color:var(--moph-ink-2);font-style:italic">Quote order — the lead journey had no paid touch, so it was recovered from the Shopify order’s landing_site (the ad click) above.</div>'
              : (o.provenance && o.provenance.method === 'shopify-journey')
                ? '<div style="color:var(--moph-ink-2);font-style:italic">Online direct order — credited from Shopify’s customer-journey summary above; no captured lead journey.</div>'
                : '<div style="color:var(--moph-ink-2);font-style:italic">No paid touch in the captured journey (organic / direct).</div>')) +
        '</div>' +
      '</div>';
    document.getElementById('mophODrawer').hidden = false;
  }
  function closeOrderDrawer() {
    var d = document.getElementById('mophODrawer');
    if (d) d.hidden = true;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 6 v2: Insights renderer.
     Hides the card on upstream failure OR when there's no data in range
     (no orders at all → all three sub-cards would be empty/dashed). */
  function renderInsights() {
    var card = document.getElementById('mophInsightsCard');
    if (!card) return;
    var ins = state.insights;
    if (!ins || !ins.repeatCustomerRate) { card.hidden = true; return; }
    var rc = ins.repeatCustomerRate;
    if ((rc.total || 0) === 0) { card.hidden = true; return; }
    card.hidden = false;

    // Repeat customer rate tile
    var rateEl = document.getElementById('mophInsightRepeatRate');
    var subEl = document.getElementById('mophInsightRepeatSub');
    if (rateEl) rateEl.textContent = (Number(rc.rate) || 0).toFixed(1) + '%';
    if (subEl) {
      // Scope disclosure — repeatCustomerRate's denominator is orders with
      // a lead: record (form / email / chatbot / referral), not direct
      // online checkouts which lack a per-customer lead history we can dedupe.
      // Make the scope explicit so the agency doesn't wonder why this is "9"
      // when the Recent Sales card shows ~29 orders.
      subEl.textContent = (rc.repeats || 0) + ' of ' + (rc.total || 0)
        + ' inquiry-driven order' + ((rc.total === 1) ? '' : 's') + ' from returning customers'
        + ' (online direct checkouts excluded — no lead history)';
    }

    // By-source table
    var bsBody = document.querySelector('#mophInsightBySource tbody');
    if (bsBody) {
      var rows = (ins.bySourceMetrics || []);
      if (rows.length === 0) {
        bsBody.innerHTML = '<tr><td colspan="4" class="moph-empty">No source attribution in range</td></tr>';
      } else {
        bsBody.innerHTML = rows.map(function (r) {
          var days = (r.avgDaysToClose != null)
            ? (Math.round(r.avgDaysToClose * 10) / 10) + 'd'
            : '<span style="color:var(--moph-ink-2);" title="Requires a lead: record per order (lead.createdAt → lc.dateSold). Online direct checkouts have no lead record to derive time-to-close from.">n/a</span>';
          // CUSTOM_MARKETINGOPS_BY_CLAUDE — proper source labels (RFQ all-caps,
          // inbound→Unknown), matching the Recent Sales column.
          var _BS_LBL = { online: 'Online', rfq: 'RFQ', form: 'Form', chatbot: 'Chatbot', weights: 'Weights', blog: 'Blog', sample: 'Sample', email: 'Email', phone: 'Phone', referral: 'Referral', 'manual-rep': 'Rep entered', inbound: 'Unknown', unknown: 'Unknown', other: 'Other' };
          var sg = _BS_LBL[r.sourceGroup] || String(r.sourceGroup || 'unknown').replace(/-/g, ' ');
          return '<tr>'
            + '<td>' + escapeHtml(sg) + '</td>'
            + '<td style="text-align:right;">' + r.count + '</td>'
            + '<td style="text-align:right;">' + fmtMoney(r.avgDealSize, { compact: true }) + '</td>'
            + '<td style="text-align:right;">' + days + '</td>'
            + '</tr>';
        }).join('');
      }
    }

    // Top Verticals card removed per Ryan (worker still derives it for a revisit).
    // #8 — states + ZIPs moved to the dedicated Geography card (renderGeography).
  }
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#8) — Geography card. A US choropleth (revenue
     by state, shaded + hover tooltip) beside the Top ZIP list. Renders
     independently of the Insights card (it can have geo data even when the
     inquiry-driven repeat-rate is 0). Online-direct only (same source as the old
     state/zip tiles). Hides when there's no state AND no ZIP data. */
  function renderGeoMap(byState) {
    var wrap = document.getElementById('mophGeoMap');
    var legend = document.getElementById('mophGeoLegend');
    if (!wrap) return false;
    var states = byState || [];
    if (!window.MOPH_US_STATES || states.length === 0) {
      wrap.innerHTML = '<div class="moph-geo-empty-msg">'
        + (window.MOPH_US_STATES ? 'No online orders with a shipping state in range.' : 'Map data unavailable.')
        + '</div>';
      if (legend) legend.hidden = true;
      return states.length > 0;
    }
    var byCode = {}, maxRev = 0;
    states.forEach(function (s) {
      var code = String(s.state || '').toUpperCase();
      if (!code) return;
      byCode[code] = { rev: Number(s.revenue) || 0, count: Number(s.count) || 0, share: Number(s.share) || 0 };
      if (byCode[code].rev > maxRev) maxRev = byCode[code].rev;
    });
    var paths = Object.keys(window.MOPH_US_STATES).map(function (code) {
      var d = window.MOPH_US_STATES[code];
      var info = byCode[code];
      var style, cls = 'moph-geo-state', data;
      if (info && info.rev > 0) {
        // sqrt eases the scale so mid-revenue states stay distinguishable.
        var t = maxRev > 0 ? Math.sqrt(info.rev / maxRev) : 0;
        var op = (0.16 + 0.84 * t).toFixed(3);
        style = 'fill:var(--moph-blue);fill-opacity:' + op + ';';
        cls += ' moph-geo-state--data';
        data = 'data-rev="' + info.rev + '" data-count="' + info.count + '" data-share="' + info.share + '"';
      } else {
        style = 'fill:var(--moph-geo-empty);';
        data = 'data-empty="1"';
      }
      return '<path class="' + cls + '" data-state="' + code + '" ' + data + ' style="' + style + '" d="' + d + '"></path>';
    }).join('');
    wrap.innerHTML = '<svg class="moph-geo-svg" viewBox="' + (window.MOPH_US_VIEWBOX || '192 9 1028 746')
      + '" preserveAspectRatio="xMidYMid meet" role="img" aria-label="US revenue by state">' + paths + '</svg>';
    if (legend) legend.hidden = false;
    _ensureGeoTip(wrap);   // re-add the tooltip element (innerHTML above wiped it)
    initGeoTooltip();      // bind listeners once
    initGeoZoom();         // pinch-to-zoom on phones; a new map starts un-zoomed
    return true;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-24) — PINCH TO ZOOM on the map (touch only). Two fingers zoom
     (1-8x) around the point between them, one finger drags once zoomed, and "Reset zoom" appears while zoomed.
     Un-zoomed, one finger is the page's scroll as always: we only preventDefault on two fingers or a zoomed drag.
     Zoom = the SVG's viewBox (same aspect, so the box and the tooltip maths never move). Listeners bound once on
     the wrapper; the svg is looked up per event because renderGeoMap replaces it, and each render resets. */
  var _geoZ = null; // { base:{x,y,w,h}, vb:{x,y,w,h} }
  function _geoSvg() { var w = document.getElementById('mophGeoMap'); return w && w.querySelector('.moph-geo-svg'); }
  function _geoApply(svg) {
    var b = _geoZ.base, v = _geoZ.vb;
    v.w = Math.min(b.w, Math.max(b.w / 8, v.w)); v.h = v.w * b.h / b.w;
    v.x = Math.min(b.x + b.w - v.w, Math.max(b.x, v.x)); v.y = Math.min(b.y + b.h - v.h, Math.max(b.y, v.y));
    svg.setAttribute('viewBox', v.x + ' ' + v.y + ' ' + v.w + ' ' + v.h);
    var btn = document.getElementById('mophGeoReset');
    if (btn) btn.hidden = !(v.w < b.w - 0.5);
  }
  function initGeoZoom() {
    var wrap = document.getElementById('mophGeoMap'), svg = _geoSvg();
    if (!wrap || !svg) return;
    var p = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    _geoZ = { base: { x: p[0], y: p[1], w: p[2], h: p[3] }, vb: { x: p[0], y: p[1], w: p[2], h: p[3] } };
    var btn = document.getElementById('mophGeoReset');
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button'; btn.id = 'mophGeoReset'; btn.className = 'moph-geo-reset'; btn.textContent = 'Reset zoom';
      btn.addEventListener('click', function() { var s = _geoSvg(); if (!s || !_geoZ) return; var b = _geoZ.base; _geoZ.vb = { x: b.x, y: b.y, w: b.w, h: b.h }; _geoApply(s); });
    }
    wrap.appendChild(btn); // renderGeoMap's innerHTML dropped it
    btn.hidden = true;
    if (wrap._geoZoomBound) return;
    wrap._geoZoomBound = true;
    var g = null; // the gesture in progress
    function pts(e) { return Array.prototype.slice.call(e.touches, 0, 2).map(function(t) { return { x: t.clientX, y: t.clientY }; }); }
    function start(e) {
      var s = _geoSvg(); if (!s || !_geoZ) { g = null; return; }
      var r = s.getBoundingClientRect(), t = pts(e), v = _geoZ.vb;
      if (t.length === 2) {
        var mx = (t[0].x + t[1].x) / 2, my = (t[0].y + t[1].y) / 2;
        g = { kind: 'pinch', d0: Math.hypot(t[0].x - t[1].x, t[0].y - t[1].y) || 1, w0: v.w,
              px: v.x + (mx - r.left) / r.width * v.w, py: v.y + (my - r.top) / r.height * v.h };
      } else if (t.length === 1 && v.w < _geoZ.base.w - 0.5) {
        g = { kind: 'pan', x: t[0].x, y: t[0].y };
      } else g = null;
    }
    wrap.addEventListener('touchstart', start, { passive: true });
    wrap.addEventListener('touchmove', function(e) {
      var s = _geoSvg(); if (!g || !s || !_geoZ) return;
      e.preventDefault(); // two fingers, or a zoomed drag: the map's, not the page's
      var r = s.getBoundingClientRect(), t = pts(e), v = _geoZ.vb;
      if (g.kind === 'pinch' && t.length === 2) {
        var mx = (t[0].x + t[1].x) / 2, my = (t[0].y + t[1].y) / 2;
        v.w = g.w0 * g.d0 / (Math.hypot(t[0].x - t[1].x, t[0].y - t[1].y) || 1); v.h = v.w * _geoZ.base.h / _geoZ.base.w;
        v.x = g.px - (mx - r.left) / r.width * v.w; v.y = g.py - (my - r.top) / r.height * v.h;
      } else if (g.kind === 'pan' && t.length === 1) {
        v.x -= (t[0].x - g.x) / r.width * v.w; v.y -= (t[0].y - g.y) / r.height * v.h;
        g.x = t[0].x; g.y = t[0].y;
      }
      _geoApply(s);
      var tip = wrap.querySelector('.moph-geo-tip'); if (tip) tip.style.display = 'none';
    }, { passive: false });
    wrap.addEventListener('touchend', function(e) { if (e.touches.length) start(e); else g = null; }, { passive: true });
    wrap.addEventListener('touchcancel', function() { g = null; }, { passive: true });
  }

  // The tooltip lives inside #mophGeoMap, so renderGeoMap's innerHTML reset wipes
  // it — recreate on demand. Listeners are bound once (guarded) and look the tip
  // up fresh each event so they survive re-renders.
  function _ensureGeoTip(wrap) {
    var tip = wrap.querySelector('.moph-geo-tip');
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'moph-geo-tip';
      tip.style.display = 'none';
      wrap.appendChild(tip);
    }
    return tip;
  }
  function initGeoTooltip() {
    var wrap = document.getElementById('mophGeoMap');
    if (!wrap || wrap._geoBound) return;
    wrap._geoBound = true;
    wrap.addEventListener('mousemove', function (e) {
      var tip = _ensureGeoTip(wrap);
      var p = e.target && e.target.closest ? e.target.closest('.moph-geo-state') : null;
      if (!p) { tip.style.display = 'none'; return; }
      var code = p.getAttribute('data-state');
      var nm = (window.MOPH_US_NAMES && window.MOPH_US_NAMES[code]) || code;
      if (p.getAttribute('data-empty')) {
        tip.innerHTML = '<b>' + escapeHtml(nm) + '</b><span>No online orders</span>';
      } else {
        var cnt = Number(p.getAttribute('data-count')) || 0;
        tip.innerHTML = '<b>' + escapeHtml(nm) + '</b>'
          + '<span>' + cnt + ' order' + (cnt === 1 ? '' : 's') + '</span>'
          + '<span>' + fmtMoney(Number(p.getAttribute('data-rev')) || 0) + '</span>'
          + '<span>' + (Number(p.getAttribute('data-share')) || 0).toFixed(1) + '% of revenue</span>';
      }
      tip.style.display = 'flex';
      var r = wrap.getBoundingClientRect();
      var x = e.clientX - r.left, y = e.clientY - r.top;
      var tw = tip.offsetWidth, th = tip.offsetHeight;
      tip.style.left = Math.max(2, Math.min(x + 14, r.width - tw - 4)) + 'px';
      tip.style.top = Math.max(2, y - th - 8) + 'px';
    });
    wrap.addEventListener('mouseleave', function () { var t = wrap.querySelector('.moph-geo-tip'); if (t) t.style.display = 'none'; });
  }

  /* All-States table — sortable. Default Revenue desc; click any header to re-sort
     (same key flips direction; new key starts desc for numbers, asc for State name).
     N/A Ad Spend / ROAS ('—') always sinks to the bottom regardless of direction. */
  function renderStatesTable(byState) {
    var table = document.getElementById('mophInsightStates');
    var stBody = table && table.querySelector('tbody');
    if (!stBody) return;

    if (!byState || byState.length === 0) {
      stBody.innerHTML = '<tr><td colspan="6" class="moph-empty">No online orders with a shipping state in range</td></tr>';
      return;
    }

    // Derive Ad Spend / ROAS once (numeric, or null when there's no Google spend).
    // Revenue column = TOTAL product revenue (online + offline) = demand. ROAS uses
    // only Google-ATTRIBUTED revenue (incl. offline ad-driven deals) ÷ ad spend, so
    // it credits ads for what they drove without crediting organic/untracked sales.
    var rows = byState.map(function (s) {
      var gs = (state.geoSpend && state.geoSpend[s.state]) || null;
      var rev = Number(s.revenue) || 0;
      var attrRev = Number(s.attributedRevenue) || 0;
      var spend = (gs && gs.spend > 0) ? Number(gs.spend) : null;
      return {
        name: (window.MOPH_US_NAMES && window.MOPH_US_NAMES[s.state]) || s.state || '—',
        count: Number(s.count) || 0,
        spend: spend,
        revenue: rev,
        roas: (spend != null) ? attrRev / spend : null,
        share: Number(s.share) || 0
      };
    });

    if (!state.geoSort) state.geoSort = { key: 'revenue', dir: 'desc' };
    var sort = state.geoSort;
    rows.sort(function (a, b) {
      var av = a[sort.key], bv = b[sort.key];
      if (sort.key === 'name') {
        var cmp = String(av).localeCompare(String(bv));
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      var an = (av == null), bn = (bv == null); // N/A always last, either direction
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      return sort.dir === 'asc' ? av - bv : bv - av;
    });

    stBody.innerHTML = rows.map(function (r) {
      var spendCell = (r.spend != null) ? fmtMoney(r.spend, { compact: true }) : '<span class="moph-geo-na">—</span>';
      // ROAS decimals taper as the number grows so the column stays narrow:
      // <10 → 2 dp (4.20×), 10–99.9 → 1 dp (12.5×), 100+ → 0 dp (240×).
      var roasCell;
      if (r.roas == null) { roasCell = '<span class="moph-geo-na">—</span>'; }
      else { var rv = r.roas; roasCell = (rv >= 100 ? rv.toFixed(0) : rv >= 10 ? rv.toFixed(1) : rv.toFixed(2)) + '×'; }
      return '<tr>'
        + '<td>' + escapeHtml(r.name) + '</td>'
        + '<td style="text-align:right;">' + r.count + '</td>'
        + '<td style="text-align:right;">' + spendCell + '</td>'
        + '<td style="text-align:right;">' + fmtMoney(r.revenue, { compact: true }) + '</td>'
        + '<td style="text-align:right;">' + roasCell + '</td>'
        + '<td style="text-align:right;">' + r.share.toFixed(1) + '%</td>'
        + '</tr>';
    }).join('');

    // Blue chevron on the active column, gray on the rest — same mechanism as the
    // Campaigns table (CSS draws the triangles off data-sort-active).
    table.querySelectorAll('thead th[data-sortkey]').forEach(function (th) {
      if (th.getAttribute('data-sortkey') === sort.key) th.setAttribute('data-sort-active', sort.dir);
      else th.removeAttribute('data-sort-active');
    });

    if (!table.dataset.sortWired) {
      table.dataset.sortWired = '1';
      table.querySelector('thead').addEventListener('click', function (e) {
        var th = e.target.closest('th[data-sortkey]');
        if (!th) return;
        var key = th.getAttribute('data-sortkey');
        if (!state.geoSort) state.geoSort = { key: 'revenue', dir: 'desc' };
        if (state.geoSort.key === key) {
          state.geoSort.dir = (state.geoSort.dir === 'asc') ? 'desc' : 'asc';
        } else {
          state.geoSort.key = key;
          state.geoSort.dir = (key === 'name') ? 'asc' : 'desc';
        }
        renderStatesTable((state.insights && state.insights.byState) || []);
      });
    }
  }

  function renderGeography() {
    var card = document.getElementById('mophGeoCard');
    if (!card) return;
    var ins = state.insights || {};
    var byState = ins.byState || [];
    var byCity = ins.byCity || [];
    var byZip = ins.byZip || [];
    if (byState.length === 0 && byCity.length === 0 && byZip.length === 0) { card.hidden = true; return; }
    card.hidden = false;
    renderGeoMap(byState);

    // Primary: All-states table (always shown alongside the map).
    // Sortable — default Revenue desc; click any header to re-sort.
    renderStatesTable(byState);

    /* #6 geo — City + ZIP tables appear only once a location has 2+ orders from
       different customers (worker returns only qualifying rows, top 10). Each
       wrapper + the whole sub-tables block hides when its data is empty, so the
       UI scales from 1 row up to 10 and shows nothing until there's a cluster. */
    var citiesWrap = document.getElementById('mophGeoCitiesWrap');
    var cityBody = document.querySelector('#mophInsightCities tbody');
    if (cityBody) {
      cityBody.innerHTML = byCity.map(function (c) {
        var loc = [c.city, c.state].filter(Boolean).join(', ');
        return '<tr>'
          + '<td>' + escapeHtml(loc || '—') + '</td>'
          + '<td style="text-align:right;">' + (c.distinctCustomers || 0) + '</td>'
          + '<td style="text-align:right;">' + c.count + '</td>'
          + '<td style="text-align:right;">' + fmtMoney(c.revenue, { compact: true }) + '</td>'
          + '<td style="text-align:right;">' + (Number(c.share) || 0).toFixed(1) + '%</td>'
          + '</tr>';
      }).join('');
    }
    if (citiesWrap) citiesWrap.hidden = byCity.length === 0;

    var zipsWrap = document.getElementById('mophGeoZipsWrap');
    var zipBody = document.querySelector('#mophInsightZips tbody');
    if (zipBody) {
      zipBody.innerHTML = byZip.map(function (z) {
        var loc = [z.city, z.state].filter(Boolean).join(', ');
        return '<tr>'
          + '<td>' + escapeHtml(z.zip || '—') + '</td>'
          + '<td style="color:var(--moph-ink-2);">' + escapeHtml(loc || '—') + '</td>'
          + '<td style="text-align:right;">' + (z.distinctCustomers || 0) + '</td>'
          + '<td style="text-align:right;">' + z.count + '</td>'
          + '<td style="text-align:right;">' + fmtMoney(z.revenue, { compact: true }) + '</td>'
          + '<td style="text-align:right;">' + (Number(z.share) || 0).toFixed(1) + '%</td>'
          + '</tr>';
      }).join('');
    }
    if (zipsWrap) zipsWrap.hidden = byZip.length === 0;

    var tables = document.getElementById('mophGeoTables');
    if (tables) tables.hidden = (byCity.length === 0 && byZip.length === 0);
  }
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  // ─── Chart (inline SVG) ─────────────────────────────────────────────────
  var _chart = null; // geometry cached for the hover tooltip

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Daily Performance chart series. The platform
     timeseries (state.series) carries REAL spend but its revenue/net/roas are
     built from platform conversionValue (~$1/conversion → wrong). When the live
     lifecycle aggregate is available, override revenue with REAL per-day Shopify
     attributed revenue (cm.byDate) and recompute net + roas. Union of spend-days
     and sale-days so neither line is truncated. Falls back to the platform
     series only when there are no sold orders in range. */
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — 7-metric Daily Performance series.
     Per-day fields: spend, conversions (platform timeseries) + adRevenue,
     totalRevenue, grossProfit (real Shopify, cm.byDate). Derived: cm =
     grossProfit − spend (pre-overhead, matches the CM tile basis); adRoas /
     totalRoas are CUMULATIVE (running rev ÷ running spend) — daily point ROAS
     explodes because revenue lags the spend that drove it; the last cumulative
     point reconciles with the Ad/Total ROAS tiles. When no sold orders are in
     range (cm.byDate absent) we degrade to platform numbers — already flagged
     by the truth-anchor note. */
  function chartSeries(spendSeriesArg, cmArg, rangeArg) {
    var spendSeries = spendSeriesArg || state.series || [];
    var cm = (cmArg !== undefined) ? cmArg : state.cm;
    var useTrueCm = cm && cm.totals && cm.totals.orderCount > 0;
    var byDate = (useTrueCm && Array.isArray(cm.byDate)) ? cm.byDate : null;
    var map = {};
    spendSeries.forEach(function(p) {
      map[p.date] = { date: p.date, spend: Number(p.spend) || 0, conversions: Number(p.conversions) || 0,
                      impressions: Number(p.impressions) || 0, clicks: Number(p.clicks) || 0,
                      adRevenue: 0, totalRevenue: 0, grossProfit: 0, totalConversions: 0, paidOrders: 0 };
    });
    if (byDate) {
      byDate.forEach(function(r) {
        if (!map[r.date]) map[r.date] = { date: r.date, spend: 0, conversions: 0, impressions: 0, clicks: 0, adRevenue: 0, totalRevenue: 0, grossProfit: 0, totalConversions: 0, paidOrders: 0 };
        map[r.date].adRevenue = Number(r.attributedRevenue) || 0;
        map[r.date].totalRevenue = Number(r.totalRevenue) || 0;
        map[r.date].grossProfit = Number(r.grossProfit) || 0;
        map[r.date].totalConversions = Number(r.orderCount) || 0; // all Shopify sold orders that day
        map[r.date].paidOrders = Number(r.attributedOrderCount) || 0; // paid-attributed orders — CVR numerator
      });
    } else {
      // No sold orders in range — degenerate fallback on platform numbers.
      spendSeries.forEach(function(p) {
        var m = map[p.date]; if (!m) return;
        m.adRevenue = Number(p.revenue) || 0;
        m.totalRevenue = Number(p.revenue) || 0;
        m.grossProfit = (Number(p.revenue) || 0) - (Number(p.spend) || 0);
      });
    }
    var cumAd = 0, cumTotal = 0, cumSpend = 0, cumClicks = 0, cumPaidOrders = 0;
    // Clamp to the selected range. A boundary order's UTC dateSold can bucket to
    // the day AFTER `to` (a late-5/31 account-TZ order → 6/1 UTC), which tacked a
    // phantom near-zero point onto the end of the chart ("Last Month" plotting
    // through 6/1 with a big drop-off). Only plot days within [from, to].
    var _R = rangeArg || dateRange();
    return Object.keys(map).filter(function(d) { return d >= _R.from && d <= _R.to; }).sort().map(function(d) {
      var m = map[d];
      m.cm = m.grossProfit - m.spend;        // per-day contribution margin (pre-overhead)
      cumAd += m.adRevenue; cumTotal += m.totalRevenue; cumSpend += m.spend;
      m.cumAdRevenue = cumAd; m.cumTotalRevenue = cumTotal; m.cumSpend = cumSpend;
      m.adRoas = cumSpend > 0 ? cumAd / cumSpend : 0;
      m.totalRoas = cumSpend > 0 ? cumTotal / cumSpend : 0;
      // Phase 4.2 funnel. CTR = true daily clicks÷impressions (same-day signal).
      // CVR = CUMULATIVE paid orders÷clicks (a sale lags its click ~6 wks, so a
      // daily-point CVR is noise — last point = the period's true click→sale rate).
      m.ctr = m.impressions > 0 ? m.clicks / m.impressions : 0;
      cumClicks += m.clicks; cumPaidOrders += m.paidOrders;
      m.cumClicks = cumClicks; m.cumPaidOrders = cumPaidOrders;
      m.cvr = cumClicks > 0 ? cumPaidOrders / cumClicks : 0;
      return m;
    });
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — previous-period overlay. The Daily
     Performance chart plots the selected period (solid) + the equivalent prior
     period (lighter dotted), aligned day-of-period. The prior period mirrors the
     range TYPE: This Month → last month, This Quarter → last quarter, This Year →
     last year, Last Year → 2 years ago, etc. */
  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — prior-period window for the
     scorecard deltas + chart overlay. CRITICAL: an IN-PROGRESS calendar preset
     (This month/quarter/year) is period-TO-DATE, so its prior period must also be
     period-to-date — NOT the full prior period. Comparing June 1–8 against a full
     May 1–31 cratered every volume delta (Spend −72%, Orders −79%, CM −70%…) purely
     because the windows were different lengths. Map to-date → the SAME point in the
     prior period (June 1–8 → May 1–8). The COMPLETE-period presets (Last month/
     quarter/year) stay full-vs-full. Custom = immediately-preceding equal window. */
  function prevDateRange() {
    var r = dateRange();
    var fromD = new Date(r.from + 'T00:00:00Z');
    var toD = new Date(r.to + 'T00:00:00Z');
    var y = fromD.getUTCFullYear(), m = fromD.getUTCMonth();
    function iso(d){ return d.toISOString().slice(0,10); }
    // Shift a date back by N months, clamping the day to the target month's length
    // (Mar 31 → Feb 28) so a to-date maps cleanly into a shorter prior month.
    function shiftMonths(d, delta) {
      var tot = d.getUTCMonth() + delta;
      var ny = d.getUTCFullYear() + Math.floor(tot / 12);
      var nm = ((tot % 12) + 12) % 12;
      var dim = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
      return new Date(Date.UTC(ny, nm, Math.min(d.getUTCDate(), dim)));
    }
    switch (state.rangeKey) {
      case 'year': // YTD vs prior-year-to-same-date
        return { from: iso(new Date(Date.UTC(y-1,0,1))), to: iso(shiftMonths(toD, -12)) };
      case 'lastyear':
        return { from: iso(new Date(Date.UTC(y-1,0,1))), to: iso(new Date(Date.UTC(y-1,11,31))) };
      case 'quarter': { // QTD vs prior-quarter-to-same-offset
        var qps = new Date(Date.UTC(y, m-3, 1));
        return { from: iso(qps), to: iso(shiftMonths(toD, -3)) };
      }
      case 'lastquarter': {
        var ps = new Date(Date.UTC(y, m-3, 1));
        return { from: iso(ps), to: iso(new Date(Date.UTC(ps.getUTCFullYear(), ps.getUTCMonth()+3, 0))) };
      }
      case 'month': { // MTD vs prior-month-to-same-day (June 1–8 → May 1–8)
        var mps = new Date(Date.UTC(y, m-1, 1));
        return { from: iso(mps), to: iso(shiftMonths(toD, -1)) };
      }
      case 'lastmonth': {
        var pm = new Date(Date.UTC(y, m-1, 1));
        return { from: iso(pm), to: iso(new Date(Date.UTC(pm.getUTCFullYear(), pm.getUTCMonth()+1, 0))) };
      }
      case 'week': case 'lastweek':
        return { from: iso(new Date(fromD.getTime() - 7*86400000)), to: iso(new Date(fromD.getTime() - 86400000)) };
      case 'all': return null;
      default: { // custom — shift back by the same duration (immediately-preceding window)
        var pTo = new Date(fromD.getTime() - 86400000);
        return { from: iso(new Date(pTo.getTime() - (toD - fromD))), to: iso(pTo) };
      }
    }
  }
  // Absolute period label (e.g. "June 2026" / "Q2 2026" / "2025") — shared by
  // both legend entries so the current + prior read absolute, not relative.
  function _periodLabel(fromIso) {
    if (!fromIso) return '';
    var d = new Date(fromIso + 'T00:00:00Z');
    var MON = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    switch (state.rangeKey) {
      case 'year': case 'lastyear': return String(d.getUTCFullYear());
      case 'quarter': case 'lastquarter': return 'Q' + (Math.floor(d.getUTCMonth()/3)+1) + ' ' + d.getUTCFullYear();
      case 'month': case 'lastmonth': return MON[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
      default: return MONS[d.getUTCMonth()] + ' ' + d.getUTCDate(); // week / custom — start date
    }
  }
  function curPeriodLabel() { return _periodLabel(dateRange().from); }
  function prevPeriodLabel() { var pr = state.prevRange || prevDateRange(); return pr ? _periodLabel(pr.from) : ''; }
  function prevChartSeries() { return chartSeries(state.prevSeries, state.prevCm, state.prevRange); }
  // Lazy — fetched AFTER the main render so the dashboard load stays fast, then
  // re-renders the chart with the overlay. Re-run on every range/attribution change.
  async function loadPrevPeriod() {
    var mySeq = state._loadSeq; // tie to the current load generation
    state.prevSeries = null; state.prevCm = null; state.prevRange = null; state.prevKpis = null;
    var pr = prevDateRange();
    if (!pr) { renderChart(); return; }
    var pf = state.platform ? ('&platform=' + state.platform) : '';
    var qs = 'from=' + pr.from + '&to=' + pr.to + pf;
    var cmRange = 'from=' + pr.from + '&to=' + pr.to + '&attribution=' + (state.attribution || 'firstPaid');
    try {
      var res = await Promise.all([
        mwFetch('/dashboard/timeseries?' + qs).catch(function(){ return null; }),
        mwFetch('/dashboard/contribution-margin?' + cmRange).catch(function(){ return null; }),
        // #5 — prior-period KPI totals so the scorecard can show vs-prior deltas.
        mwFetch('/dashboard/kpis?' + qs).catch(function(){ return null; })
      ]);
      if (mySeq !== state._loadSeq) return; // superseded by a newer range change
      state.prevSeries = (res[0] && res[0].series) || [];
      state.prevCm = res[1] || null;
      state.prevKpis = res[2] || null;
      state.prevRange = pr;
    } catch (e) { if (mySeq !== state._loadSeq) return; /* non-blocking */ }
    if (mySeq !== state._loadSeq) return;
    renderChart();
    renderKpis(); // re-render now that prior-period data is in → deltas populate
  }

  /* ─── CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — record-high/low flames ───
     Flame an OUTCOME card (CM, Ad Revenue, Ad ROAS, Orders, Ad Conversions, CAC) when
     the current value is the best EVER for this kind of date range — judged on a
     "record pace" basis: each prior comparable period is truncated to the SAME number
     of days into it (June 1–8 → May 1–8, Apr 1–8 …) so a partial current period is
     compared apples-to-apples, never against a full prior period. CAC flames on a
     record LOW (lower = better). Spend is intentionally never flamed (a record-high
     spend isn't an achievement). Only meaningful for week/month/quarter/year presets
     (custom + all-time have no natural comparable set). Data floors at the 2026-05-01
     launch, so quarter/year have ~no priors yet — flames there simply won't fire.
     Lazy + cached per (range, attribution, to-date); reuses the SAME /kpis + /cm
     endpoints the cards display from, so the flame can't disagree with the number. */
  var RECORD_FLOOR = '2026-05-01';        // marketing-ops data floor (mirror dateRange 'all')
  var RECORD_MAX_PRIORS = 12;             // bound the fetch fan-out (and the "record vs last N" framing)
  // The six flame-eligible metrics + whether lower is better.
  var RECORD_METRICS = [
    { key: 'contributionMargin', low: false }, { key: 'revenue', low: false },
    { key: 'roas', low: false }, { key: 'orders', low: false },
    { key: 'conversions', low: false }, { key: 'cac', low: true },
    // Efficiency-cost metrics — lower = better, so they flame on a record LOW.
    { key: 'cpc', low: true }, { key: 'cpm', low: true }, { key: 'cpa', low: true },
  ];
  function _isoUTC(d) { return d.toISOString().slice(0, 10); }
  function _shiftMonthsClamp(d, delta) {
    var tot = d.getUTCMonth() + delta;
    var ny = d.getUTCFullYear() + Math.floor(tot / 12);
    var nm = ((tot % 12) + 12) % 12;
    var dim = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
    return new Date(Date.UTC(ny, nm, Math.min(d.getUTCDate(), dim)));
  }
  // Prior comparable periods (NOT incl. current), each a period-to-date window with the
  // same offset as the current range. Newest first isn't required; capped to RECORD_MAX_PRIORS.
  function recordComparablePeriods() {
    var rk = state.rangeKey;
    if (rk === 'custom' || rk === 'all' || rk === 'today' || rk === 'last30' || rk === 'last90' || rk === 'last180') return null; // no natural comparable set (rolling windows aren't a calendar period)
    var r = dateRange();
    var fromD = new Date(r.from + 'T00:00:00Z'), toD = new Date(r.to + 'T00:00:00Z');
    var floor = new Date(RECORD_FLOOR + 'T00:00:00Z');
    var out = [];
    if (rk === 'month') {
      for (var k = 1; k <= RECORD_MAX_PRIORS; k++) {
        var mf = new Date(Date.UTC(fromD.getUTCFullYear(), fromD.getUTCMonth() - k, 1));
        if (mf < floor) break;
        out.push({ from: _isoUTC(mf), to: _isoUTC(_shiftMonthsClamp(toD, -k)) });
      }
    } else if (rk === 'lastmonth') {
      // current is a COMPLETE month; priors are complete months before it
      for (var k2 = 1; k2 <= RECORD_MAX_PRIORS; k2++) {
        var lf = new Date(Date.UTC(fromD.getUTCFullYear(), fromD.getUTCMonth() - k2, 1));
        if (lf < floor) break;
        out.push({ from: _isoUTC(lf), to: _isoUTC(new Date(Date.UTC(lf.getUTCFullYear(), lf.getUTCMonth() + 1, 0))) });
      }
    } else if (rk === 'week' || rk === 'lastweek') {
      var span = Math.round((toD - fromD) / 86400000);
      for (var k3 = 1; k3 <= RECORD_MAX_PRIORS; k3++) {
        var wf = new Date(fromD.getTime() - k3 * 7 * 86400000);
        if (wf < floor) break;
        out.push({ from: _isoUTC(wf), to: _isoUTC(new Date(wf.getTime() + span * 86400000)) });
      }
    } else if (rk === 'quarter') {
      var spanQ = Math.round((toD - fromD) / 86400000);
      for (var k4 = 1; k4 <= RECORD_MAX_PRIORS; k4++) {
        var qf = new Date(Date.UTC(fromD.getUTCFullYear(), fromD.getUTCMonth() - 3 * k4, 1));
        if (qf < floor) break;
        out.push({ from: _isoUTC(qf), to: _isoUTC(new Date(qf.getTime() + spanQ * 86400000)) });
      }
    } else if (rk === 'lastquarter') {
      for (var k5 = 1; k5 <= RECORD_MAX_PRIORS; k5++) {
        var lqf = new Date(Date.UTC(fromD.getUTCFullYear(), fromD.getUTCMonth() - 3 * k5, 1));
        if (lqf < floor) break;
        out.push({ from: _isoUTC(lqf), to: _isoUTC(new Date(Date.UTC(lqf.getUTCFullYear(), lqf.getUTCMonth() + 3, 0))) });
      }
    } else if (rk === 'year') {
      for (var k6 = 1; k6 <= RECORD_MAX_PRIORS; k6++) {
        var yf = new Date(Date.UTC(fromD.getUTCFullYear() - k6, 0, 1));
        if (yf < floor) break;
        out.push({ from: _isoUTC(yf), to: _isoUTC(_shiftMonthsClamp(toD, -12 * k6)) });
      }
    } else if (rk === 'lastyear') {
      for (var k7 = 1; k7 <= RECORD_MAX_PRIORS; k7++) {
        var lyf = new Date(Date.UTC(fromD.getUTCFullYear() - k7, 0, 1));
        if (lyf < floor) break;
        out.push({ from: _isoUTC(lyf), to: _isoUTC(new Date(Date.UTC(lyf.getUTCFullYear(), 11, 31))) });
      }
    }
    return out;
  }
  // Ad Revenue = Σ non-organic channel directTotal (mirrors renderKpis).
  function _adRevFromCm(cm) {
    if (!cm || !cm.byChannel) return 0;
    var s = 0; Object.keys(cm.byChannel).forEach(function (k) { if (k !== 'organic') s += Number(cm.byChannel[k].directTotal) || 0; });
    return s;
  }
  // Reduce a (kpis, cm) pair to the raw primitives the metric formulas need.
  function _recordPrimitives(kpisTotals, cm) {
    var t = kpisTotals || {}, ct = (cm && cm.totals) || {};
    return {
      spend: Number(t.spend) || 0, platRevenue: Number(t.revenue) || 0, conversions: Number(t.conversions) || 0,
      clicks: Number(t.clicks) || 0, impressions: Number(t.impressions) || 0,
      orderCount: Number(ct.orderCount) || 0, attributedCount: Number(ct.attributedCount) || 0,
      cmPre: Number(ct.contributionMargin) || 0, cmPost: Number(ct.contributionMarginAfterOverhead) || 0,
      // Phase 1.14 — same test as renderKpis: agency comp = retainer OR performance fee.
      hasOverhead: !!((ct.overhead && ct.overhead.amount > 0) || (Number(ct.agencyPerformanceFee) || 0) > 0),
      agencyPerformanceFee: Number(ct.agencyPerformanceFee) || 0, adRevenue: _adRevFromCm(cm),
    };
  }
  /* The six outcome metrics from primitives — MUST mirror renderKpis's display math so a
     flame never contradicts the shown number. If you change a formula there, change it here. */
  function _recordMetrics(p, marginPct) {
    var useTrue = p.orderCount > 0;
    return {
      contributionMargin: useTrue ? (p.hasOverhead ? p.cmPost : p.cmPre) : (p.platRevenue * (marginPct / 100) - p.spend),
      revenue: useTrue ? p.adRevenue : p.platRevenue,
      roas: p.spend > 0 ? (useTrue ? p.adRevenue : p.platRevenue) / p.spend : 0,
      orders: p.orderCount,
      conversions: p.conversions,
      cac: (useTrue && p.attributedCount > 0) ? p.spend / p.attributedCount : null,
      // CPC/CPM/CPA off the rollup totals — same source for current + historical so the
      // PB judgment is internally consistent (matches the displayed values in practice).
      cpc: p.clicks > 0 ? p.spend / p.clicks : null,
      cpm: p.impressions > 0 ? (p.spend / p.impressions) * 1000 : null,
      cpa: p.conversions > 0 ? p.spend / p.conversions : null,
    };
  }
  function _currentRecordPrimitives() {
    return _recordPrimitives(state.kpis && state.kpis.totals, state.cm);
  }
  async function loadRecords() {
    var mySeq = state._loadSeq;
    var key = state.rangeKey + '|' + (state.attribution || 'firstPaid') + '|' + (state.platform || 'all') + '|' + dateRange().to;
    if (state._recordsKey === key && state.records) { applyRecordFlames(state.records); return; }
    applyRecordFlames(null); // clear stale flames while we recompute
    var periods = recordComparablePeriods();
    if (!periods || !periods.length) { state.records = {}; state._recordsKey = key; return; }
    var pf = state.platform ? ('&platform=' + state.platform) : '';
    var attr = state.attribution || 'firstPaid';
    var bundles;
    try {
      bundles = await Promise.all(periods.map(function (p) {
        return Promise.all([
          mwFetch('/dashboard/kpis?from=' + p.from + '&to=' + p.to + pf).catch(function () { return null; }),
          mwFetch('/dashboard/contribution-margin?from=' + p.from + '&to=' + p.to + '&attribution=' + attr).catch(function () { return null; }),
        ]);
      }));
    } catch (e) { return; }
    if (mySeq !== state._loadSeq) return; // superseded
    var marginPct = Number(state.marginPct) || 35;
    var priorMetrics = bundles.map(function (b) {
      if (!b || !b[0]) return null;
      return _recordMetrics(_recordPrimitives(b[0].totals, b[1]), marginPct);
    }).filter(Boolean);
    var curMetrics = _recordMetrics(_currentRecordPrimitives(), marginPct);
    var recs = {};
    RECORD_METRICS.forEach(function (def) {
      var cv = curMetrics[def.key];
      if (cv == null || !isFinite(cv) || cv <= 0) { recs[def.key] = { isRecord: false }; return; }
      var priors = priorMetrics.map(function (m) { return m[def.key]; }).filter(function (v) { return v != null && isFinite(v) && v > 0; });
      if (priors.length < 1) { recs[def.key] = { isRecord: false }; return; } // need ≥1 real comparison
      var isRec = def.low ? priors.every(function (v) { return cv < v; }) : priors.every(function (v) { return cv > v; });
      recs[def.key] = { isRecord: isRec, direction: def.low ? 'low' : 'high', n: priors.length + 1 };
    });
    state.records = recs; state._recordsKey = key;
    applyRecordFlames(recs);
  }
  function _recordPeriodWord() {
    switch (state.rangeKey) {
      case 'week': case 'lastweek': return 'weeks';
      case 'quarter': case 'lastquarter': return 'quarters';
      case 'year': case 'lastyear': return 'years';
      default: return 'months';
    }
  }
  function applyRecordFlames(map) {
    RECORD_METRICS.forEach(function (def) { setKpiRecord(def.key, map && map[def.key]); });
  }
  function setKpiRecord(name, rec) {
    var el = document.querySelector('[data-record="' + name + '"]');
    if (!el) return;
    var card = el.closest('.moph-kpi');
    if (!rec || !rec.isRecord) { el.hidden = true; if (card) card.removeAttribute('title'); return; }
    // Just light the glow overlay — no badge text/emoji (those wrapped + resized cards).
    el.hidden = false;
    if (card) card.title = (rec.direction === 'low' ? 'Record low' : 'Personal best') + ' — best of ' + rec.n + ' ' + _recordPeriodWord() + ' compared (same days into each period, since launch).';
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — metric-type helpers + gentle line smoothing.
     smoothPath builds a Catmull-Rom→bezier path with low tension (subtle curve,
     not wavy). Control points are clamped to each segment's own y-range so the
     curve never overshoots past the data on sharp jumps. */
  function chartIsRatio() { return state.metric === 'adRoas' || state.metric === 'totalRoas'; }
  function chartIsCount() { return state.metric === 'conversions' || state.metric === 'totalConversions'; }
  function chartIsPercent() { return state.metric === 'ctr' || state.metric === 'cvr'; } // Phase 4.2 funnel
  function smoothPath(xs, ys) {
    var n = xs.length;
    if (n < 3) return xs.map(function(x, i) { return (i ? 'L' : 'M') + x.toFixed(1) + ',' + ys[i].toFixed(1); }).join(' ');
    var t = 0.16; // tension — small = gentle
    var p = 'M' + xs[0].toFixed(1) + ',' + ys[0].toFixed(1);
    for (var i = 0; i < n - 1; i++) {
      var x0 = xs[i > 0 ? i - 1 : 0], y0 = ys[i > 0 ? i - 1 : 0];
      var x1 = xs[i],               y1 = ys[i];
      var x2 = xs[i + 1],           y2 = ys[i + 1];
      var x3 = xs[i < n - 2 ? i + 2 : i + 1], y3 = ys[i < n - 2 ? i + 2 : i + 1];
      var c1x = x1 + (x2 - x0) * t, c1y = y1 + (y2 - y0) * t;
      var c2x = x2 - (x3 - x1) * t, c2y = y2 - (y3 - y1) * t;
      // Clamp control-point y within the segment so curves don't overshoot.
      var loY = Math.min(y1, y2), hiY = Math.max(y1, y2);
      c1y = Math.max(loY, Math.min(hiY, c1y));
      c2y = Math.max(loY, Math.min(hiY, c2y));
      p += ' C' + c1x.toFixed(1) + ',' + c1y.toFixed(1) + ' ' + c2x.toFixed(1) + ',' + c2y.toFixed(1) + ' ' + x2.toFixed(1) + ',' + y2.toFixed(1);
    }
    return p;
  }

  function renderChart() {
    syncSpendViewUI(); // CUSTOM_MARKETINGOPS_BY_CLAUDE — Total/By-channel toggle visible only on the Ad Spend tab
    // CUSTOM_MARKETINGOPS_BY_CLAUDE (Inquiry Flow) — the Leads tab is a stacked
    // source-bar chart, not a line; delegate to its own renderer.
    if (state.metric === 'inquiries') { renderInquiryChart(); return; }
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — Ad Spend "By channel" = stacked area by platform.
    if (state.metric === 'spend' && state.spendView === 'channel') { renderSpendByChannelChart(); return; }
    var content = document.getElementById('mophChartContent');
    var empty = document.getElementById('mophChartEmpty');
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — the below-chart caveat row is retired; every
    // metric's "how it's computed" now lives behind the header (?) (mophChartExplain)
    // which opens the right-side Show-Your-Work pane. Keep the legacy note hidden.
    var _note = document.getElementById('mophChartNote');
    if (_note) { _note.hidden = true; _note.innerHTML = ''; }
    content.innerHTML = '';
    var series = chartSeries();
    if (!series.length) {
      empty.style.display = '';
      _chart = null;
      return;
    }
    empty.style.display = 'none';

    var W = 720, H = 240, PAD_L = 50, PAD_R = 10, PAD_T = 16, PAD_B = 28;
    var values = series.map(function(p) { return Number(p[state.metric] || 0); });
    // Previous-period overlay values (aligned day-of-period); share the y-scale
    // so the two lines are visually comparable.
    var prevSeries = (state.prevSeries && state.prevSeries.length) ? prevChartSeries() : [];
    var prevValues = prevSeries.map(function(p) { return Number(p[state.metric] || 0); });
    var _scaleV = values.concat(prevValues);
    var maxV = Math.max(0, Math.max.apply(null, _scaleV));
    var minV = Math.min(0, Math.min.apply(null, _scaleV));
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (audit Tier-2 #7) — Target ROAS goal line.
       On the ROAS tabs, extend the y-max so the agency-set target stays on-chart,
       then draw a dashed green reference line at it (built after yFor below). */
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#7b v2; ad-driven 2026-09-29) — Goal = your BREAK-EVEN ROAS
       × an "aggressiveness" multiple. The break-even is revenue ÷ gross profit over AD-DRIVEN
       sales (the worker's, see breakEvenLines()). AD ROAS ONLY — a goal on Total ROAS is
       meaningless (it folds in organic / referral / rep-sourced revenue the ads never drove). */
    var _targetRoas = 0, _breakEvenRoas = 0;
    if (state.metric === 'adRoas') {
      var _bl = breakEvenLines();
      _breakEvenRoas = _bl.be; _targetRoas = _bl.goal;
    }
    if (_targetRoas > 0) maxV = Math.max(maxV, _targetRoas * 1.08);
    var range;
    if (chartIsPercent()) {
      // CTR/CVR are 0–1 fractions. The generic `Math.max(…, 1)` floor below
      // would force a full 0–100% axis and crush a ~2% line flat against the
      // bottom. Instead pin the floor at 0 and round the top UP to the nearest
      // 5% above the data max (min 5%), so the ups/downs are actually visible.
      minV = 0;
      maxV = Math.max(0.05, Math.ceil(maxV / 0.05) * 0.05);
      range = maxV;
    } else if (chartIsCount()) {
      // Integer counts (Ad / Total Conversions) — snap the top to a multiple of 4
      // (the number of gridline intervals) so all 5 ticks land on evenly-spaced
      // CLEAN integers. Otherwise a max of 6 splits into 0/1.5/3/4.5/6 → rounds to
      // a lumpy 0,2,3,5,6; a max of 3 → 0,1,2,2,3 (a duplicate). min axis = 0–4.
      minV = 0;
      maxV = Math.max(4, Math.ceil(maxV / 4) * 4);
      range = maxV;
    } else {
      range = Math.max(maxV - minV, 1);
    }
    function yFor(v) { return PAD_T + (1 - (v - minV) / range) * (H - PAD_T - PAD_B); }

    // Offset-based x-axis — position each point by its day-of-period (days since
    // the period start) so current + previous align day-for-day, AND the previous
    // period shows its FULL span even when the current period is only partway
    // through (e.g. early in the month). maxOff spans the longer of the two.
    function _dayOff(start, dateStr) { return Math.round((new Date(dateStr + 'T00:00:00Z') - new Date(start + 'T00:00:00Z')) / 86400000); }
    var _curStart = dateRange().from;
    var _prevStart = (state.prevRange && state.prevRange.from) || _curStart;
    var curOff = series.map(function(p) { return _dayOff(_curStart, p.date); });
    var prevOff = prevSeries.map(function(p) { return _dayOff(_prevStart, p.date); });
    var maxOff = 0;
    curOff.forEach(function(o) { if (o > maxOff) maxOff = o; });
    prevOff.forEach(function(o) { if (o > maxOff) maxOff = o; });
    if (maxOff < 1) maxOff = Math.max(series.length - 1, 1);
    function xForOff(o) { return PAD_L + (o / maxOff) * (W - PAD_L - PAD_R); }
    var xs = curOff.map(xForOff);
    var prevXs = prevOff.map(xForOff);

    // Chart line colors — Ryan-specified + FIXED (not theme-shifted): the current
    // period draws solid in brand blue #006BCC; the prior-period dotted overlay
    // draws in a lighter #94C8FF so the two read distinctly in light + dark mode.
    var solidStroke = '#006BCC';
    var prevStroke = '#94C8FF';
    var darkMode = document.querySelector('.rep-quote-portal-wrap.rep-quote-dark') != null;
    var gridStroke = darkMode ? '#1e2a42' : '#E5E5E5';
    var labelFill = darkMode ? '#B7C1CE' : '#3a4858'; // CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-09-28) — lifted from #8892a0 (hard to read on navy)
    var areaFill = darkMode ? 'rgba(91,168,247,0.18)' : 'rgba(0,107,204,0.12)';

    // Aspect of the stretched SVG (preserveAspectRatio="none"): width stretches
    // ~1.9x but height only ~1.25x, so plain <text> + <circle> render distorted
    // (elongated glyphs, oval dots). Counter it — dots use rx/ry; axis labels get
    // a horizontal scale(axisSX,1) so the font renders at a normal aspect (#4 +
    // axis-font fix). Computed before the gridlines so the tick text can use it.
    var _crect = document.getElementById('mophChart').getBoundingClientRect();
    var sx = _crect.width > 0 ? _crect.width / W : 1.9;
    var sy = _crect.height > 0 ? _crect.height / H : 1.25;
    var dotRx = (2.5 / sx), dotRy = (2.5 / sy);
    var axisSX = (sx > 0 && sy > 0) ? (sy / sx) : 1; // x-compression to undistort axis text

    // Axis / grid (4 horizontal lines). Tick text is positioned via translate()
    // then scale(axisSX,1) so the glyphs aren't horizontally stretched.
    var gridLines = '';
    // Ratio (ROAS) ticks: once the per-step gap is large (≥5×) the decimal is
    // noise — round to whole numbers (0,11,22,33,44 instead of …,43.9×). Decimals
    // are kept only at small scales where they actually carry information.
    var _ratioTickInt = chartIsRatio() && (range / 4) >= 5;
    for (var i = 0; i <= 4; i++) {
      var v = minV + (range * i / 4);
      var y = yFor(v);
      gridLines += '<line x1="' + PAD_L + '" y1="' + y + '" x2="' + (W - PAD_R) + '" y2="' + y + '" stroke="' + gridStroke + '" stroke-width="1" stroke-dasharray="2,3" />';
      gridLines += '<text x="0" y="' + (y + 3) + '" text-anchor="end" font-size="10" fill="' + labelFill + '" transform="translate(' + (PAD_L - 6) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + formatChartTick(v, _ratioTickInt) + '</text>';
    }

    // today-incomplete detection (range runs through today — account-local).
    var _todayIso = (typeof accountNowParts === 'function') ? accountNowParts().iso : '';
    var _lastIsToday = series.length && _todayIso && series[series.length - 1].date === _todayIso;

    // Line path — gentle Catmull-Rom smoothing (#3). When the range runs through
    // TODAY, the today point is incomplete (ad spend/conversions read 0 until the
    // nightly sync; Shopify metrics are only a partial day), so the solid line +
    // area stop at the last COMPLETE day and a DOTTED "in-progress" segment runs
    // to today — flattened to yesterday's level when today reads 0, so it extends
    // off to the right instead of plunging straight to $0.
    var ys = values.map(yFor);
    var _lastI = xs.length - 1;
    var todayIncomplete = _lastIsToday && xs.length >= 2;
    var d, dottedSeg = '', _areaEndI = _lastI, _dotEndY = 0;
    if (todayIncomplete) {
      // The trailing TODAY point is incomplete until the nightly sync lands, so
      // HOLD the in-progress segment FLAT at yesterday's level and run it to the
      // right — never plunge toward $0 (the data just isn't in yet). Once today
      // closes it renders solid and snaps to its real value. Fine dotting (1,2)
      // keeps it visually distinct from the coarser dashed prior-period line (4,3).
      d = smoothPath(xs.slice(0, _lastI), ys.slice(0, _lastI));
      _areaEndI = _lastI - 1;
      _dotEndY = ys[_lastI - 1];
      dottedSeg = '<path d="M' + xs[_lastI - 1].toFixed(1) + ',' + ys[_lastI - 1].toFixed(1) + ' L' + xs[_lastI].toFixed(1) + ',' + _dotEndY.toFixed(1) + '" fill="none" stroke="' + solidStroke + '" stroke-width="2" stroke-dasharray="1,2" />';
    } else {
      d = smoothPath(xs, ys);
    }
    // Area fill underneath. When today is incomplete, EXTEND the fill under the
    // dotted in-progress segment out to today (held flat at yesterday's level) so
    // the current day is shaded exactly like the completed days — just bounded by
    // the dotted line instead of the solid one. Otherwise it ends at the last point.
    var _areaTopD = d, _areaBaseX = xs[_areaEndI];
    if (todayIncomplete) {
      _areaTopD += ' L' + xs[_lastI].toFixed(1) + ',' + _dotEndY.toFixed(1);
      _areaBaseX = xs[_lastI];
    }
    var areaD = _areaTopD + ' L' + _areaBaseX.toFixed(1) + ',' + yFor(0).toFixed(1) + ' L' + xs[0].toFixed(1) + ',' + yFor(0).toFixed(1) + ' Z';
    // Previous-period dotted overlay (drawn UNDER the current line; aligned by index).
    var prevPath = '';
    if (prevValues.length) {
      prevPath = '<path d="' + smoothPath(prevXs, prevValues.map(yFor))
        + '" fill="none" stroke="' + prevStroke + '" stroke-width="1.4" stroke-dasharray="4,3" />';
    }

    // Date labels across the FULL period axis (start, mid, end) so the axis
    // reflects the whole period even when current data is only partway through.
    var _labOffs = (maxOff <= 0) ? [0] : [0, Math.round(maxOff / 2), maxOff];
    var labels = _labOffs.map(function(o, k) {
      var ld = new Date(new Date(_curStart + 'T00:00:00Z').getTime() + o * 86400000).toISOString().slice(5, 10);
      var x = xForOff(o);
      var anchor = (k === 0) ? 'start' : (o === maxOff ? 'end' : 'middle');
      return '<text x="0" y="' + (H - 8) + '" text-anchor="' + anchor + '" font-size="11" fill="' + labelFill + '" transform="translate(' + x.toFixed(1) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + ld + '</text>';
    }).join('');

    // Pulsing "ping" on the last dot when the range runs through TODAY (account-
    // local) — marks where the live current-period line ends so it isn't confused
    // with the dotted prior-period line. A ~3s expanding/fading ring (SMIL).
    var pingDot = '';
    if (_lastIsToday) {
      // Pulse rides the END of the dotted in-progress segment (today's x, held at
      // yesterday's level) — not the last completed day.
      var _lx, _ly;
      if (todayIncomplete) { _lx = xs[_lastI].toFixed(1); _ly = _dotEndY.toFixed(1); }
      else { var _li = xs.length - 1; _lx = xs[_li].toFixed(1); _ly = yFor(values[_li]).toFixed(1); }
      var _r1x = dotRx.toFixed(2), _r1y = dotRy.toFixed(2);
      var _rmx = (dotRx * 10).toFixed(2), _rmy = (dotRy * 10).toFixed(2); // mid
      var _r2x = (dotRx * 20).toFixed(2), _r2y = (dotRy * 20).toFixed(2); // max (~5x the old ring)
      var _sw = (sx > 0 ? (2.5 / sx) : 2.5).toFixed(2);
      // Radar ping — grows from the dot to ~20× while fading out, staying
      // visible through the mid-expansion (0.9→0.55→0), then repeats every 3s.
      pingDot = '<ellipse cx="' + _lx + '" cy="' + _ly + '" rx="' + _r1x + '" ry="' + _r1y + '" fill="none" stroke="' + solidStroke + '" stroke-width="' + _sw + '">'
        + '<animate attributeName="rx" values="' + _r1x + ';' + _rmx + ';' + _r2x + '" dur="3s" repeatCount="indefinite"/>'
        + '<animate attributeName="ry" values="' + _r1y + ';' + _rmy + ';' + _r2y + '" dur="3s" repeatCount="indefinite"/>'
        + '<animate attributeName="opacity" values="0.9;0.55;0" dur="3s" repeatCount="indefinite"/>'
        + '</ellipse>';
    }
    // #7 — Target ROAS goal line (drawn under the data so the line reads on top).
    var goalLine = '';
    if (_targetRoas > 0) {
      // Goal (green) + Break-even (amber) reference lines. Their values
      // are keyed in the LEGEND below the chart (not labeled on the plot) to keep
      // it clean when the prior/current period lines also show.
      var _gy = yFor(_targetRoas);
      goalLine += '<line class="moph-goalline moph-goalline--target" x1="' + PAD_L + '" y1="' + _gy.toFixed(1) + '" x2="' + (W - PAD_R) + '" y2="' + _gy.toFixed(1) + '" stroke="var(--moph-green)" stroke-width="1.6" opacity="1" />';
      if (_breakEvenRoas > 0) {
        var _by = yFor(_breakEvenRoas);
        goalLine += '<line class="moph-goalline moph-goalline--be" x1="' + PAD_L + '" y1="' + _by.toFixed(1) + '" x2="' + (W - PAD_R) + '" y2="' + _by.toFixed(1) + '" stroke="var(--moph-amber)" stroke-width="1.4" opacity="0.92" />';
      }
    }
    content.innerHTML = gridLines + goalLine +
      '<path d="' + areaD + '" fill="' + areaFill + '" stroke="none" />' +
      prevPath +
      '<path d="' + d + '" fill="none" stroke="' + solidStroke + '" stroke-width="2" />' +
      dottedSeg +
      series.map(function(p, i) { if (todayIncomplete && i === _lastI) return ''; return '<ellipse cx="' + xs[i].toFixed(1) + '" cy="' + yFor(values[i]).toFixed(1) + '" rx="' + dotRx.toFixed(2) + '" ry="' + dotRy.toFixed(2) + '" fill="' + solidStroke + '" />'; }).join('') +
      pingDot +
      labels;

    // Legend (bottom-left) — solid = this period, dotted = the prior period.
    var _lg = document.getElementById('mophChartLegend');
    if (_lg) {
      var _lgParts = [];
      if (prevValues.length) {
        _lgParts.push('<span class="moph-chartlg"><i class="moph-chartlg__sw"></i>' + escapeHtml(curPeriodLabel()) + '</span>');
        _lgParts.push('<span class="moph-chartlg"><i class="moph-chartlg__sw moph-chartlg__sw--prev"></i>' + escapeHtml(prevPeriodLabel()) + '</span>');
      }
      // #7 — Goal + Break-even ROAS keyed here (not labeled on-chart).
      if (_targetRoas > 0) {
        var _fmtX = function(v) { return (v % 1 === 0) ? v.toFixed(0) : v.toFixed(1); };
        _lgParts.push('<span class="moph-chartlg"><i class="moph-chartlg__sw moph-chartlg__sw--target"></i>Goal ' + _fmtX(_targetRoas) + '×</span>');
        if (_breakEvenRoas > 0) _lgParts.push('<span class="moph-chartlg"><i class="moph-chartlg__sw moph-chartlg__sw--be"></i>B/E ' + _fmtX(_breakEvenRoas) + '×</span>');
      }
      if (_lgParts.length) { _lg.innerHTML = _lgParts.join(''); _lg.hidden = false; }
      else { _lg.hidden = true; }
    }

    // Cache geometry for the hover tooltip + reset any prior hover state.
    var _prevByOff = {};
    prevOff.forEach(function(o, k) { _prevByOff[o] = prevValues[k]; });
    _chart = { series: series, xs: xs, values: values, yFor: yFor, blueStroke: solidStroke, curOff: curOff, prevByOff: _prevByOff,
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — today's point is incomplete
         (no daily import yet). Expose it so the hover snaps the dot to the dotted
         in-progress end (dotEndY) instead of plunging to $0, and shows a "pending" note. */
      todayIncomplete: todayIncomplete, lastI: _lastI, dotEndY: _dotEndY };
    document.getElementById('mophChartHover').innerHTML = '';
    document.getElementById('mophChartTip').hidden = true;
    setupChartHover();
  }

  /* ─── CUSTOM_MARKETINGOPS_BY_CLAUDE (Inquiry Flow) ──────────────────────────
     The Daily Performance chart has three views, switched by the segmented
     control: Sales (the existing line, unchanged), Inquiries (stacked bars of
     inbound inquiry volume by source — the LEADING indicator), and Both (the
     inquiry bars on the LEFT axis + sold-order count as a line on the RIGHT
     axis). Inquiries are the front of the funnel: volume rises weeks before the
     sales it drives and, unlike attributed revenue, isn't subject to the ~46%
     attribution gap. Bars-vs-line + dual axis signal "two units, two tempos";
     the lag footnote keeps the dual axis honest. Drawn into the SAME #mophChart
     SVG as the sales line — additive, so the tuned sales path is untouched. */

  // Leads-bar colors + labels reuse the EXACT Lead Source Mix donut palette
  // (SOURCE_COLORS / SOURCE_LABELS) so the two surfaces read identically — no
  // separate palette to drift. The feed emits display-source keys ('web-form'),
  // the donut keys by source GROUP ('form'); this map bridges them so a bar and
  // its donut slice are always the same color. Order = stack order (bottom→top).
  var INQUIRY_SRC_ORDER = ['rfq', 'web-form', 'blog', 'chatbot', 'weights', 'sample', 'email', 'phone', 'referral', 'online', 'inbound', 'other'];
  var INQUIRY_SRC_TO_GROUP = {
    rfq: 'rfq', 'web-form': 'form', blog: 'blog', chatbot: 'chatbot', weights: 'weights', sample: 'sample', email: 'email',
    phone: 'phone', referral: 'referral', online: 'online', inbound: 'inbound', other: 'other',
  };
  function inquirySrcColor(s) { return SOURCE_COLORS[INQUIRY_SRC_TO_GROUP[s] || s] || '#9ca3af'; }
  function inquirySrcLabel(s) { var g = INQUIRY_SRC_TO_GROUP[s] || s; return SOURCE_LABELS[g] || s; }
  var _inqChart = null; // geometry cached for the inquiry-view hover

  function renderInquiryChart() {
    var content = document.getElementById('mophChartContent');
    var empty = document.getElementById('mophChartEmpty');
    var note = document.getElementById('mophChartNote');
    var legend = document.getElementById('mophChartLegend');
    content.innerHTML = '';
    _chart = null; // disable the sales hover; inquiry view binds its own
    document.getElementById('mophChartHover').innerHTML = '';
    document.getElementById('mophChartTip').hidden = true;

    var inq = state.inquiries;
    var byDate = (inq && Array.isArray(inq.byDate)) ? inq.byDate : null;
    if (!byDate || !byDate.length) {
      empty.textContent = inq ? 'No inquiries in this range.' : 'Inquiry data unavailable.';
      empty.style.display = '';
      if (legend) legend.hidden = true;
      if (note) note.hidden = true;
      _inqChart = null;
      return;
    }
    empty.style.display = 'none';

    var cntByDate = {};
    byDate.forEach(function (r) { cntByDate[r.date] = r; });

    // Sold-order count per day from the existing merged sales series, so the
    // overlaid line reconciles exactly with the Total Conv. tab.
    var ordByDate = {};
    chartSeries().forEach(function (p) { ordByDate[p.date] = Number(p.totalConversions) || 0; });

    // Full day axis across the selected range (so empty days read as 0 bars).
    var R = dateRange();
    var days = [];
    var dCur = new Date(R.from + 'T00:00:00Z'), dEnd = new Date(R.to + 'T00:00:00Z');
    for (; dCur <= dEnd; dCur.setUTCDate(dCur.getUTCDate() + 1)) days.push(dCur.toISOString().slice(0, 10));
    if (!days.length) days = Object.keys(cntByDate).sort();

    // Roll up to weeks on long ranges so daily bars don't become hairlines.
    // Weeks are CALENDAR weeks starting SUNDAY (Ryan 2026-06-16) — each bucket is
    // labeled by its Sunday, not the arbitrary weekday the date range starts on.
    // Edge weeks can be partial (the range starts/ends mid-week); that's expected.
    var weekly = days.length > 45;
    var buckets = [];
    if (weekly) {
      var _curWk = null, _wkBucket = null;
      days.forEach(function (d) {
        var _dt = new Date(d + 'T00:00:00Z');
        _dt.setUTCDate(_dt.getUTCDate() - _dt.getUTCDay()); // back up to Sunday (getUTCDay: 0=Sun)
        var ws = _dt.toISOString().slice(0, 10);
        if (ws !== _curWk) { _curWk = ws; _wkBucket = { label: ws, from: ws, to: d, days: [] }; buckets.push(_wkBucket); }
        _wkBucket.days.push(d);
        _wkBucket.to = d;
      });
    } else {
      buckets = days.map(function (d) { return { label: d, from: d, to: d, days: [d] }; });
    }

    var presentSrc = {};
    buckets.forEach(function (b) {
      b.bySource = {}; b.total = 0; b.orders = 0; b.attributed = 0;
      b.days.forEach(function (d) {
        var rec = cntByDate[d];
        if (rec) {
          Object.keys(rec.bySource || {}).forEach(function (s) {
            b.bySource[s] = (b.bySource[s] || 0) + rec.bySource[s];
            presentSrc[s] = true;
          });
          b.total += rec.total || 0;
          // CUSTOM_MARKETINGOPS_BY_CLAUDE — how many of the bucket's leads were
          // ad-attributed (journey resolved to a paid channel); rest = organic.
          b.attributed += rec.attributed || 0;
        }
        b.orders += (ordByDate[d] || 0);
      });
    });
    var srcs = INQUIRY_SRC_ORDER.filter(function (s) { return presentSrc[s]; });

    // ── geometry ── (left axis = lead count; right axis = sold-order count)
    var W = 720, H = 240, PAD_L = 40, PAD_R = 40, PAD_T = 16, PAD_B = 28;
    var maxInq = 0; buckets.forEach(function (b) { if (b.total > maxInq) maxInq = b.total; });
    maxInq = Math.max(4, Math.ceil(maxInq / 4) * 4);
    var maxOrd = 4; buckets.forEach(function (b) { if (b.orders > maxOrd) maxOrd = b.orders; });
    maxOrd = Math.max(4, Math.ceil(maxOrd / 4) * 4);
    function yInq(v) { return PAD_T + (1 - v / maxInq) * (H - PAD_T - PAD_B); }
    function yOrd(v) { return PAD_T + (1 - v / maxOrd) * (H - PAD_T - PAD_B); }

    var n = buckets.length;
    var slotW = (W - PAD_L - PAD_R) / Math.max(n, 1);
    var barW = Math.max(1.5, slotW * (weekly ? 0.78 : 0.66));
    function cx(i) { return PAD_L + slotW * (i + 0.5); }

    var darkMode = document.querySelector('.rep-quote-portal-wrap.rep-quote-dark') != null;
    var gridStroke = darkMode ? '#1e2a42' : '#E5E5E5';
    var labelFill = darkMode ? '#B7C1CE' : '#3a4858'; // CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-09-28) — lifted from #8892a0 (hard to read on navy)
    var lineStroke = darkMode ? '#E8EEF5' : '#00121F';

    var _crect = document.getElementById('mophChart').getBoundingClientRect();
    var sx = _crect.width > 0 ? _crect.width / W : 1.9;
    var sy = _crect.height > 0 ? _crect.height / H : 1.25;
    var axisSX = (sx > 0 && sy > 0) ? (sy / sx) : 1;
    var dotRx = (2.3 / sx), dotRy = (2.3 / sy);

    var svg = '';
    // gridlines + lead-count axis ticks (left)
    for (var g = 0; g <= 4; g++) {
      var gv = maxInq * g / 4, gy = yInq(gv);
      svg += '<line x1="' + PAD_L + '" y1="' + gy + '" x2="' + (W - PAD_R) + '" y2="' + gy + '" stroke="' + gridStroke + '" stroke-width="1" stroke-dasharray="2,3" />';
      svg += '<text x="0" y="' + (gy + 3) + '" text-anchor="end" font-size="10" fill="' + labelFill + '" transform="translate(' + (PAD_L - 5) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + Math.round(gv) + '</text>';
    }
    // sold-order count axis ticks (right)
    for (var g2 = 0; g2 <= 4; g2++) {
      var ov = maxOrd * g2 / 4, oy = yOrd(ov);
      svg += '<text x="0" y="' + (oy + 3) + '" text-anchor="start" font-size="10" fill="' + lineStroke + '" transform="translate(' + (W - PAD_R + 5) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + Math.round(ov) + '</text>';
    }

    // stacked bars (bottom→top in canonical source order). data-bk groups each
    // column's segments so the hover can lighten the whole stack (mirrors the
    // Sales Analytics bar hover, which lightens the hovered bar).
    var bars = '';
    buckets.forEach(function (b, i) {
      var x0 = cx(i) - barW / 2, yTop = yInq(0);
      srcs.forEach(function (s) {
        var v = b.bySource[s] || 0; if (!v) return;
        var h = yInq(0) - yInq(v); yTop -= h;
        bars += '<rect class="moph-inqbar" data-bk="' + i + '" x="' + x0.toFixed(1) + '" y="' + yTop.toFixed(1) + '" width="' + barW.toFixed(1) + '" height="' + h.toFixed(1) + '" fill="' + inquirySrcColor(s) + '" />';
      });
    });

    // sold-order line overlay (right axis) — the lagging outcome the lead volume
    // drives. Charcoal/near-white so it reads over any source color.
    var lx = buckets.map(function (b, i) { return cx(i); });
    var ly = buckets.map(function (b) { return yOrd(b.orders); });
    var lineEls = '<path d="' + smoothPath(lx, ly) + '" fill="none" stroke="' + lineStroke + '" stroke-width="2.4" />'
      + buckets.map(function (b, i) { return '<ellipse cx="' + lx[i].toFixed(1) + '" cy="' + ly[i].toFixed(1) + '" rx="' + dotRx.toFixed(2) + '" ry="' + dotRy.toFixed(2) + '" fill="' + lineStroke + '" />'; }).join('');

    // x date labels — one per bar where they physically fit. Estimate each
    // "MM-DD" label's rendered width (≈5 chars, post horizontal axisSX scale) vs
    // the per-bar slot, and step just enough to avoid overlap. On a quarter
    // (≈13 weekly bars) or a roomy month view this lands at every bar; it only
    // thins when the bars genuinely get too tight, so labels never collide.
    var _lblW = 5 * 10 * 0.6 * axisSX; // chars × fontSize × glyph-width factor × x-scale
    var labelEvery = Math.max(1, Math.ceil((_lblW * 1.15) / slotW));
    var labels = buckets.map(function (b, idx) {
      if (idx % labelEvery !== 0 && idx !== n - 1) return '';
      var anchor = (idx === 0) ? 'start' : (idx === n - 1 ? 'end' : 'middle');
      return '<text x="0" y="' + (H - 8) + '" text-anchor="' + anchor + '" font-size="10" fill="' + labelFill + '" transform="translate(' + cx(idx).toFixed(1) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + b.from.slice(5) + '</text>';
    }).join('');

    content.innerHTML = svg + bars + lineEls + labels;

    // legend — source swatches + the sold-order line
    if (legend) {
      var lg = srcs.map(function (s) { return '<span class="moph-srclg"><i class="moph-srclg__sw" style="background:' + inquirySrcColor(s) + '"></i>' + escapeHtml(inquirySrcLabel(s)) + '</span>'; }).join('');
      lg += '<span class="moph-srclg"><i class="moph-srclg__sw moph-srclg__sw--line"></i>Sold orders</span>';
      legend.innerHTML = lg; legend.hidden = false;
    }
    if (note) { note.hidden = true; note.innerHTML = ''; }

    _inqChart = { buckets: buckets, srcs: srcs, cx: cx, yInq: yInq, slotW: slotW, padL: PAD_L, padT: PAD_T, padB: PAD_B, h: H, weekly: weekly, hoverI: -1 };
    setupInquiryHover();
  }

  function setupInquiryHover() {
    var chart = document.getElementById('mophChart');
    if (!chart || chart._inqHoverBound) return;
    chart._inqHoverBound = true;
    chart.addEventListener('mousemove', onInquiryHover);
    chart.addEventListener('mouseleave', hideInquiryHover);
  }
  // Lighten the hovered column's whole stack (mirrors the Sales Analytics bar
  // hover). data-bk groups a column's segments; toggling the class is cheap and
  // we only touch the DOM when the hovered column actually changes.
  function _inqHighlight(i) {
    var content = document.getElementById('mophChartContent');
    if (!content) return;
    content.querySelectorAll('rect.moph-inqbar--hi').forEach(function (r) { r.classList.remove('moph-inqbar--hi'); });
    if (i >= 0) content.querySelectorAll('rect[data-bk="' + i + '"]').forEach(function (r) { r.classList.add('moph-inqbar--hi'); });
  }
  function hideInquiryHover() {
    if (_inqChart) _inqChart.hoverI = -1;
    _inqHighlight(-1);
    var tip = document.getElementById('mophChartTip'); if (tip) tip.hidden = true;
  }
  function onInquiryHover(e) {
    if (state.metric !== 'inquiries' || !_inqChart || !_inqChart.buckets.length) return;
    var chart = document.getElementById('mophChart');
    var rect = chart.getBoundingClientRect();
    var relX = (e.clientX - rect.left) / rect.width * 720;
    var i = Math.round((relX - _inqChart.padL) / _inqChart.slotW - 0.5);
    i = Math.max(0, Math.min(_inqChart.buckets.length - 1, i));
    if (i !== _inqChart.hoverI) { _inqChart.hoverI = i; _inqHighlight(i); }
    var b = _inqChart.buckets[i];
    var cxv = _inqChart.cx(i);
    var rows = _inqChart.srcs.filter(function (s) { return b.bySource[s]; }).map(function (s) {
      return '<div class="moph-chart__tip-sub"><i class="moph-srclg__sw" style="display:inline-block;vertical-align:0;background:' + inquirySrcColor(s) + '"></i> ' + escapeHtml(inquirySrcLabel(s)) + ': <strong>' + b.bySource[s] + '</strong></div>';
    }).join('');
    var head = _inqChart.weekly ? ('Week of ' + b.from) : b.from;
    var tot = '<div class="moph-chart__tip-val">' + b.total + ' lead' + (b.total === 1 ? '' : 's') + '</div>';
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — ad-attributed vs organic split of the bucket's
    // leads (a lead is attributed when its captured journey resolved to a paid
    // channel; phone/email/walk-ins + no-UTM visits are organic).
    var _attr = b.attributed || 0;
    var _org = Math.max(0, b.total - _attr);
    var attrLine = '<div class="moph-chart__tip-sub" style="margin-top:4px;border-top:1px solid rgba(127,127,127,0.25);padding-top:4px;">'
      + '<i class="moph-srclg__sw" style="display:inline-block;vertical-align:0;background:#4285F4"></i> Ad-attributed: <strong>' + _attr + '</strong>'
      + ' &nbsp;·&nbsp; Organic: <strong>' + _org + '</strong></div>';
    var ord = '<div class="moph-chart__tip-sub" style="margin-top:4px;border-top:1px solid rgba(127,127,127,0.25);padding-top:4px;"><i class="moph-srclg__sw moph-srclg__sw--line" style="display:inline-block;"></i> ' + b.orders + ' sold order' + (b.orders === 1 ? '' : 's') + '</div>';
    var tip = document.getElementById('mophChartTip');
    tip.innerHTML = '<div class="moph-chart__tip-date">' + head + '</div>' + tot + rows + attrLine + ord;
    tip.style.left = ((cxv / 720) * rect.width) + 'px';
    // Anchor the tooltip a CONSTANT gap above the TOP of the hovered column —
    // the CSS transform (translate(-50%, calc(-100% - 12px))) keeps the 12px gap
    // identical for every bar, so tall bars float higher, short bars lower.
    tip.style.top = ((_inqChart.yInq(b.total) / _inqChart.h) * rect.height) + 'px';
    tip.hidden = false;
  }

  // ============================================================
  // CUSTOM_MARKETINGOPS_BY_CLAUDE — Ad Spend "By channel" stacked-area view
  // ============================================================
  var _spendChart = null;

  function syncSpendViewUI() {
    var el = document.getElementById('mophSpendView');
    if (!el) return;
    el.hidden = (state.metric !== 'spend');
    el.querySelectorAll('[data-spendview]').forEach(function(b) {
      b.classList.toggle('moph-spendview__btn--active', b.getAttribute('data-spendview') === state.spendView);
    });
  }

  function _niceMaxMoney(v) {
    if (!(v > 0)) return 100;
    var mag = Math.pow(10, Math.floor(Math.log10(v)));
    var n = v / mag;
    var step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * mag;
  }

  function renderSpendByChannelChart() {
    var content = document.getElementById('mophChartContent');
    var empty = document.getElementById('mophChartEmpty');
    var note = document.getElementById('mophChartNote');
    var legend = document.getElementById('mophChartLegend');
    content.innerHTML = '';
    _chart = null; // disable the line hover; this view binds its own
    document.getElementById('mophChartHover').innerHTML = '';
    document.getElementById('mophChartTip').hidden = true;
    if (note) { note.hidden = true; note.innerHTML = ''; }

    var raw = (state.series && Array.isArray(state.series)) ? state.series : [];
    var byDate = {};
    raw.forEach(function(p) { byDate[p.date] = p.spendByChannel || {}; });

    // Full day axis across the range (empty days read as 0).
    var R = dateRange();
    var days = [];
    var dCur = new Date(R.from + 'T00:00:00Z'), dEnd = new Date(R.to + 'T00:00:00Z');
    for (; dCur <= dEnd; dCur.setUTCDate(dCur.getUTCDate() + 1)) days.push(dCur.toISOString().slice(0, 10));
    if (!days.length) days = Object.keys(byDate).sort();

    // Weekly (Sunday-start) rollup on long ranges — mirrors the Leads chart.
    var weekly = days.length > 45;
    var buckets = [];
    if (weekly) {
      var _curWk = null, _wk = null;
      days.forEach(function(d) {
        var _dt = new Date(d + 'T00:00:00Z'); _dt.setUTCDate(_dt.getUTCDate() - _dt.getUTCDay());
        var ws = _dt.toISOString().slice(0, 10);
        if (ws !== _curWk) { _curWk = ws; _wk = { label: ws, from: ws, to: d, days: [] }; buckets.push(_wk); }
        _wk.days.push(d); _wk.to = d;
      });
    } else {
      buckets = days.map(function(d) { return { label: d, from: d, to: d, days: [d] }; });
    }

    var CH_ORDER = ['google-ads', 'meta', 'microsoft', 'openai'];
    var present = {};
    buckets.forEach(function(b) {
      b.byCh = {}; b.total = 0;
      b.days.forEach(function(d) {
        var m = byDate[d] || {};
        Object.keys(m).forEach(function(ch) {
          var v = Number(m[ch]) || 0;
          b.byCh[ch] = (b.byCh[ch] || 0) + v;
          if (v > 0) present[ch] = true;
        });
      });
      Object.keys(b.byCh).forEach(function(ch) { b.total += b.byCh[ch]; });
    });
    var chs = CH_ORDER.filter(function(c) { return present[c]; });
    Object.keys(present).forEach(function(c) { if (chs.indexOf(c) < 0) chs.push(c); });

    if (!buckets.some(function(b) { return b.total > 0; })) {
      empty.textContent = 'No ad spend in this range.';
      empty.style.display = '';
      if (legend) legend.hidden = true;
      _spendChart = null;
      return;
    }
    empty.style.display = 'none';

    var W = 720, H = 240, PAD_L = 50, PAD_R = 10, PAD_T = 16, PAD_B = 28;
    var maxV = 0; buckets.forEach(function(b) { if (b.total > maxV) maxV = b.total; });
    maxV = _niceMaxMoney(maxV);
    function yFor(v) { return PAD_T + (1 - v / maxV) * (H - PAD_T - PAD_B); }
    var n = buckets.length;
    var slotW = (W - PAD_L - PAD_R) / Math.max(n, 1);
    function cx(i) { return PAD_L + slotW * (i + 0.5); }

    var darkMode = document.querySelector('.rep-quote-portal-wrap.rep-quote-dark') != null;
    var gridStroke = darkMode ? '#1e2a42' : '#E5E5E5';
    var labelFill = darkMode ? '#B7C1CE' : '#3a4858'; // CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-09-28) — lifted from #8892a0 (hard to read on navy)

    var _crect = document.getElementById('mophChart').getBoundingClientRect();
    var sx = _crect.width > 0 ? _crect.width / W : 1.9;
    var sy = _crect.height > 0 ? _crect.height / H : 1.25;
    var axisSX = (sx > 0 && sy > 0) ? (sy / sx) : 1;

    var svg = '';
    for (var g = 0; g <= 4; g++) {
      var gv = maxV * g / 4, gy = yFor(gv);
      svg += '<line x1="' + PAD_L + '" y1="' + gy + '" x2="' + (W - PAD_R) + '" y2="' + gy + '" stroke="' + gridStroke + '" stroke-width="1" stroke-dasharray="2,3" />';
      svg += '<text x="0" y="' + (gy + 3) + '" text-anchor="end" font-size="10" fill="' + labelFill + '" transform="translate(' + (PAD_L - 5) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + fmtMoney(gv, { compact: true }) + '</text>';
    }

    // Stacked areas (bottom→top in canonical channel order). Each band is a polygon:
    // top edge L→R at the running cumulative, then bottom edge R→L below it.
    var cum = buckets.map(function() { return 0; });
    var areas = '';
    chs.forEach(function(ch) {
      var topPts = [], botPts = [];
      buckets.forEach(function(b, i) {
        var bottom = cum[i];
        var top = bottom + (b.byCh[ch] || 0);
        botPts.push([cx(i), yFor(bottom)]);
        topPts.push([cx(i), yFor(top)]);
        cum[i] = top;
      });
      var d = 'M' + topPts.map(function(p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' L');
      for (var bi = botPts.length - 1; bi >= 0; bi--) d += ' L' + botPts[bi][0].toFixed(1) + ',' + botPts[bi][1].toFixed(1);
      d += ' Z';
      areas += '<path d="' + d + '" fill="' + channelColorHex(ch) + '" fill-opacity="0.82" stroke="' + channelColorHex(ch) + '" stroke-width="0.75" />';
    });

    var _lblW = 5 * 10 * 0.6 * axisSX;
    var labelEvery = Math.max(1, Math.ceil((_lblW * 1.15) / slotW));
    var labels = buckets.map(function(b, idx) {
      if (idx % labelEvery !== 0 && idx !== n - 1) return '';
      var anchor = (idx === 0) ? 'start' : (idx === n - 1 ? 'end' : 'middle');
      return '<text x="0" y="' + (H - 8) + '" text-anchor="' + anchor + '" font-size="10" fill="' + labelFill + '" transform="translate(' + cx(idx).toFixed(1) + ',0) scale(' + axisSX.toFixed(3) + ',1)">' + b.from.slice(5) + '</text>';
    }).join('');

    content.innerHTML = svg + areas + labels;

    if (legend) {
      legend.innerHTML = chs.map(function(ch) {
        return '<span class="moph-srclg"><i class="moph-srclg__sw" style="background:' + channelColorHex(ch) + '"></i>' + escapeHtml(platformDisplayLabel(ch)) + '</span>';
      }).join('');
      legend.hidden = false;
    }

    _spendChart = { buckets: buckets, chs: chs, cx: cx, yFor: yFor, slotW: slotW, padL: PAD_L, h: H, weekly: weekly, hoverI: -1 };
    setupSpendHover();
  }

  function setupSpendHover() {
    var chart = document.getElementById('mophChart');
    if (!chart || chart._spendHoverBound) return;
    chart._spendHoverBound = true;
    chart.addEventListener('mousemove', onSpendHover);
    chart.addEventListener('mouseleave', hideSpendHover);
  }
  function hideSpendHover() {
    if (_spendChart) _spendChart.hoverI = -1;
    var tip = document.getElementById('mophChartTip'); if (tip) tip.hidden = true;
  }
  function onSpendHover(e) {
    if (!(state.metric === 'spend' && state.spendView === 'channel') || !_spendChart || !_spendChart.buckets.length) return;
    var chart = document.getElementById('mophChart');
    var rect = chart.getBoundingClientRect();
    var relX = (e.clientX - rect.left) / rect.width * 720;
    var i = Math.round((relX - _spendChart.padL) / _spendChart.slotW - 0.5);
    i = Math.max(0, Math.min(_spendChart.buckets.length - 1, i));
    _spendChart.hoverI = i;
    var b = _spendChart.buckets[i];
    var rows = _spendChart.chs.filter(function(ch) { return b.byCh[ch]; }).map(function(ch) {
      return '<div class="moph-chart__tip-sub"><i class="moph-srclg__sw" style="display:inline-block;vertical-align:0;background:' + channelColorHex(ch) + '"></i> ' + escapeHtml(platformDisplayLabel(ch)) + ': <strong>' + fmtMoney(b.byCh[ch], { compact: true }) + '</strong></div>';
    }).join('');
    var head = _spendChart.weekly ? ('Week of ' + b.from) : b.from;
    var tot = '<div class="moph-chart__tip-val">' + fmtMoney(b.total, { compact: true }) + ' spend</div>';
    var tip = document.getElementById('mophChartTip');
    tip.innerHTML = '<div class="moph-chart__tip-date">' + head + '</div>' + tot + rows;
    tip.style.left = ((_spendChart.cx(i) / 720) * rect.width) + 'px';
    tip.style.top = ((_spendChart.yFor(b.total) / _spendChart.h) * rect.height) + 'px';
    tip.hidden = false;
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Daily Performance interactivity. Nearest-point
     hover: a dashed vertical guide + an enlarged dot + a tooltip positioned ABOVE
     the point (flips below near the top edge) so it never covers the dot/line. */
  function setupChartHover() {
    var chart = document.getElementById('mophChart');
    if (!chart || chart._hoverBound) return;
    chart._hoverBound = true;
    chart.addEventListener('mousemove', onChartHover);
    chart.addEventListener('mouseleave', hideChartHover);
  }
  function chartValFull(v) {
    if (chartIsPercent()) return (v * 100).toFixed(2) + '%';
    if (chartIsRatio()) return v.toFixed(2) + '×';
    if (chartIsCount()) return Math.round(v).toLocaleString('en-US');
    return (v < 0 ? '-$' : '$') + Math.abs(Math.round(v)).toLocaleString('en-US');
  }
  function chartTipSub(p) {
    function sub(t) { return '<div class="moph-chart__tip-sub">' + t + '</div>'; }
    function n(v) { return Math.round(Number(v) || 0).toLocaleString('en-US'); }
    var s = n(p.spend);
    switch (state.metric) {
      case 'cm':           return sub('$' + n(p.grossProfit) + ' gross profit − $' + s + ' spend');
      case 'adRoas':       return sub('cumulative: $' + n(p.cumAdRevenue) + ' ad rev ÷ $' + n(p.cumSpend) + ' spend');
      case 'totalRoas':    return sub('cumulative: $' + n(p.cumTotalRevenue) + ' total rev ÷ $' + n(p.cumSpend) + ' spend');
      case 'adRevenue':    return sub('$' + s + ' spend that day · ad-attributed');
      case 'totalRevenue': return sub('all Shopify sales that day');
      case 'conversions':  return sub('$' + s + ' spend that day · platform-claimed');
      case 'totalConversions': return sub('all Shopify sold orders that day · all channels');
      case 'ctr':          return sub(n(p.clicks) + ' clicks ÷ ' + n(p.impressions) + ' impressions that day');
      case 'cvr':          return sub('cumulative: ' + n(p.cumPaidOrders) + ' paid orders ÷ ' + n(p.cumClicks) + ' clicks');
      default:             return ''; // spend — no sub-line
    }
  }
  function onChartHover(e) {
    if (!_chart || !_chart.series.length) return;
    var chart = document.getElementById('mophChart');
    var rect = chart.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var svgX = (e.clientX - rect.left) / rect.width * 720;
    var xs = _chart.xs, i = 0, bestD = Infinity;
    for (var k = 0; k < xs.length; k++) { var dd = Math.abs(xs[k] - svgX); if (dd < bestD) { bestD = dd; i = k; } }
    // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — today's point isn't imported
    // yet; snap the hover dot to the dotted in-progress end (where the pulse is) rather
    // than to $0, and the tooltip shows a "pending" note instead of a fabricated value.
    var _pending = !!(_chart.todayIncomplete && i === _chart.lastI);
    var cy = _pending ? _chart.dotEndY : _chart.yFor(_chart.values[i]);
    // True-circle hover dot (#4): the SVG is stretched by preserveAspectRatio="none",
    // so a plain <circle> renders as an oval. Counter the x/y stretch with an ellipse.
    var hsx = rect.width / 720, hsy = rect.height / 240;
    var hrx = hsx > 0 ? 4.5 / hsx : 4.5, hry = hsy > 0 ? 4.5 / hsy : 4.5;
    document.getElementById('mophChartHover').innerHTML =
      '<line x1="' + xs[i] + '" y1="16" x2="' + xs[i] + '" y2="212" stroke="' + _chart.blueStroke + '" stroke-width="' + (hsx>0?(1/hsx):1).toFixed(2) + '" stroke-dasharray="3,3" opacity="0.45" />' +
      '<ellipse cx="' + xs[i].toFixed(1) + '" cy="' + cy.toFixed(1) + '" rx="' + hrx.toFixed(2) + '" ry="' + hry.toFixed(2) + '" fill="' + _chart.blueStroke + '" stroke="#fff" stroke-width="' + (hsy>0?(1.5/hsy):1.5).toFixed(2) + '" />';
    var p = _chart.series[i];
    var tip = document.getElementById('mophChartTip');
    if (_pending) {
      tip.innerHTML = '<div class="moph-chart__tip-date">' + escapeHtml(p.date) + '</div>' +
        '<div class="moph-chart__tip-sub">Data pending daily import</div>';
    } else {
      var _pv = (_chart.prevByOff && _chart.curOff) ? _chart.prevByOff[_chart.curOff[i]] : null;
      var _prevSub = (_pv != null)
        ? '<div class="moph-chart__tip-sub">' + escapeHtml(prevPeriodLabel()) + ': ' + chartValFull(_pv) + '</div>'
        : '';
      tip.innerHTML = '<div class="moph-chart__tip-date">' + escapeHtml(p.date) + '</div>' +
        '<div class="moph-chart__tip-val">' + chartValFull(_chart.values[i]) + '</div>' + chartTipSub(p) + _prevSub;
    }
    tip.hidden = false;
    var px = xs[i] / 720 * rect.width;
    var pyTop = cy / 240 * rect.height;
    tip.style.left = px + 'px';
    tip.style.top = pyTop + 'px';
    tip.style.transform = pyTop < 64 ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 12px))';
  }
  function hideChartHover() {
    var h = document.getElementById('mophChartHover'); if (h) h.innerHTML = '';
    var t = document.getElementById('mophChartTip'); if (t) t.hidden = true;
  }

  function formatChartTick(v, ratioInt) {
    if (chartIsPercent()) return (v * 100).toFixed(1) + '%';
    // Ratio: whole numbers at large scale (ratioInt); otherwise 1 decimal with
    // any trailing ".0" stripped (parseFloat) so 2.0× reads as 2× but 2.5× stays.
    if (chartIsRatio()) return (ratioInt ? Math.round(v) : parseFloat(v.toFixed(1))) + '×';
    if (chartIsCount()) return String(Math.round(v));
    var abs = Math.abs(v);
    if (abs >= 1000) return '$' + Math.round(v / 100) / 10 + 'k';
    return '$' + Math.round(v);
  }

  // ─── Event wiring ───────────────────────────────────────────────────────
  function bind() {
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — idempotency guard. init() runs on both
       the immediate/DOMContentLoaded path AND the 'rep-portal:authenticated'
       event, so bind() was attaching every listener twice. Double-bound click
       handlers fired the sort toggle TWICE per click — flipping asc→desc→asc
       and netting back to the original direction, so column sort appeared
       stuck in descending (could never reach ascending). Bind exactly once. */
    if (bind._done) return;
    bind._done = true;
    // Hover highlight via JS class — CSS :hover propagates from <label for=X>
    // to the associated <select id=X>, which incorrectly lit up the dropdown
    // when hovering its label text. Listening to mouseenter/mouseleave on the
    // select directly gives us precise hit-area control.
    document.querySelectorAll('.moph-toolbar__select').forEach(function(sel) {
      sel.addEventListener('mouseenter', function() { sel.classList.add('is-hover'); });
      sel.addEventListener('mouseleave', function() { sel.classList.remove('is-hover'); });
    });

    // Shared by the main toolbar select AND the Recent Sales card-header
    // select. Keeps the two in lockstep + handles custom-range visibility +
    // reloads every aggregate on the page so the whole dashboard reflects
    // the chosen window. Either picker drives the same path.
    function setRange(key) {
      state.rangeKey = key;
      var mainSel = document.getElementById('mophRange');
      var ordersSel = document.getElementById('mophOrdersRange');
      if (mainSel && mainSel.value !== key) mainSel.value = key;
      if (ordersSel && ordersSel.value !== key) ordersSel.value = key;
      var custom = document.getElementById('mophCustomRange');
      if (key === 'custom') {
        if (!state.customFrom || !state.customTo) {
          // F-023 — default the custom range to this account-TZ month, not UTC.
          var np = accountNowParts();
          state.customFrom = np.iso.slice(0, 8) + '01';
          state.customTo = np.iso;
        }
        document.getElementById('mophCustomFrom').value = state.customFrom;
        document.getElementById('mophCustomTo').value = state.customTo;
        custom.hidden = false;
        return; // wait for both dates to be set (existing pattern)
      }
      custom.hidden = true;
      loadAll();
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-19) — Refresh. The one way out of the hour-long
       caches; without it a stale view would have no remedy at all, since a browser reload re-asks
       the SHARED edge cache and gets the same payload back.
       Disabled while in flight so an impatient double-press can't start a second fan-out — the
       race guard would discard the first one's results, but it would still pay for the walk. */
    var _refreshBtn = document.getElementById('mophRefreshBtn');
    if (_refreshBtn) {
      _refreshBtn.addEventListener('click', async function() {
        if (_refreshBtn.disabled) return;
        _refreshBtn.disabled = true;
        _refreshBtn.classList.add('is-refreshing');
        try {
          await loadAll({ force: true });
        } finally {
          _refreshBtn.disabled = false;
          _refreshBtn.classList.remove('is-refreshing');
        }
      });
    }

    document.getElementById('mophRange').addEventListener('change', function(e) { setRange(e.target.value); });
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — Phase 2 Export Everywhere: wire CSV
       buttons on Campaigns + Recent Sales card headers. */
    var _csvCampaigns = document.getElementById('mophCsvCampaigns');
    if (_csvCampaigns) _csvCampaigns.addEventListener('click', downloadCampaignsCsv);
    var _csvOrders = document.getElementById('mophCsvOrders');
    if (_csvOrders) _csvOrders.addEventListener('click', downloadRecentSalesCsv);
    var _csvLeads = document.getElementById('mophCsvLeads');
    if (_csvLeads) _csvLeads.addEventListener('click', downloadLeadsCsv);
    var _csvChannels = document.getElementById('mophCsvChannels');
    if (_csvChannels) _csvChannels.addEventListener('click', downloadChannelsCsv);
    var _csvSeries = document.getElementById('mophCsvSeries');
    if (_csvSeries) _csvSeries.addEventListener('click', downloadSeriesCsv);
    /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#3) — collapsible cards. Clicking a
       collapsible card's header toggles its collapsed state — but NOT when the
       click lands on a header button/select (CSV, Plan toggle) so those keep
       working. Recommendations ships open; Campaigns/Recent Sales/Insights ship
       collapsed (the markup sets moph-card--collapsed). */
    var _collapseRoot = document.querySelector('.rep-quote-portal-content');
    if (_collapseRoot) _collapseRoot.addEventListener('click', function(e) {
      var hdr = e.target.closest('.moph-card__header');
      if (!hdr) return;
      var card = hdr.parentElement;
      if (!card || !card.classList.contains('moph-card--collapsible')) return;
      if (e.target.closest('button, select, a, input, .moph-switch, .moph-csv-btn')) return;
      var willCollapse = !card.classList.contains('moph-card--collapsed');
      var body = card.querySelector('.moph-card__body');
      if (body) {
        if (willCollapse) {
          // expanded → collapsed: fix the current height, reflow, then slide to 0
          body.style.maxHeight = body.scrollHeight + 'px';
          void body.offsetHeight;
          card.classList.add('moph-card--collapsed');
          body.style.maxHeight = '0px';
          body.style.opacity = '0';
        } else {
          // collapsed → expanded: slide 0 → measured content height, then release
          card.classList.remove('moph-card--collapsed');
          body.style.maxHeight = '0px'; body.style.opacity = '0';
          void body.offsetHeight;
          body.style.maxHeight = body.scrollHeight + 'px';
          body.style.opacity = '1';
          body.addEventListener('transitionend', function te(ev) {
            if (ev.target === body && ev.propertyName === 'max-height') {
              body.style.maxHeight = ''; // release so the content can grow freely (row-expand, show-all)
              body.removeEventListener('transitionend', te);
            }
          });
        }
      } else {
        card.classList.toggle('moph-card--collapsed');
      }
      var chev = hdr.querySelector('.moph-card__chevron');
      if (chev) {
        chev.textContent = willCollapse ? '+' : '−';
        chev.classList.remove('moph-card__chevron--spin');
        void chev.offsetWidth; // reflow so the spin re-triggers on rapid clicks
        chev.classList.add('moph-card__chevron--spin');
      }
    });
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (#5) — spin the +/− on each recommendation's
       "Show your work" disclosure to match the parent card chevron. The glyph
       itself is CSS-driven by [open]; this only replays the spin. The native
       <details> "toggle" event does NOT bubble, so listen in the CAPTURE phase on
       the stable root (survives the list's innerHTML re-renders). */
    if (_collapseRoot) _collapseRoot.addEventListener('toggle', function(e) {
      var d = e.target;
      if (!d || d.tagName !== 'DETAILS') return;
      if (!d.closest('.moph-reinvest-row') && !d.classList.contains('moph-cd-ag')) return;
      var sum = d.querySelector(':scope > summary');
      if (!sum) return;
      sum.classList.remove('moph-sum-spin');
      void sum.offsetWidth; // reflow so the spin replays on every toggle
      sum.classList.add('moph-sum-spin');
      // #7 — an ad-group expand/collapse changes the content height of the
      // campaign-detail slide panel; resize it so the new ads aren't clipped.
      if (d.classList.contains('moph-cd-ag')) _sizeDetailPanel(d);
    }, true);
    /* Phase C — delegated handlers for the campaign Changes card (add/save/cancel/
       delete). Attached once to the stable root; the cards are re-rendered constantly. */
    if (_collapseRoot) _collapseRoot.addEventListener('click', function(e) {
      var tgt = e.target;
      if (!tgt || !tgt.closest) return;
      var addBtn = tgt.closest('.moph-cd-note-add');
      if (addBtn) {
        var ch = addBtn.closest('.moph-cd-changes'); var f = ch && ch.querySelector('.moph-cd-note-form');
        if (f) {
          var willOpen = !f.classList.contains('moph-cd-note-form--open');
          f.classList.toggle('moph-cd-note-form--open', willOpen);
          _cdNestedAccordion(f, willOpen);
          if (willOpen) { var ti = f.querySelector('.moph-cd-note-text'); if (ti) ti.focus(); }
        }
        return;
      }
      var cancelBtn = tgt.closest('.moph-cd-note-cancel');
      if (cancelBtn) {
        var f2 = cancelBtn.closest('.moph-cd-note-form');
        if (f2) { f2.classList.remove('moph-cd-note-form--open'); _cdNestedAccordion(f2, false); var t2 = f2.querySelector('.moph-cd-note-text'); if (t2) t2.value = ''; }
        return;
      }
      var saveBtn = tgt.closest('.moph-cd-note-save');
      if (saveBtn) { _cdSaveNote(saveBtn); return; }
      var delBtn = tgt.closest('.moph-cd-note__del');
      if (delBtn) { _cdDeleteNote(delBtn); return; }
      // "Show N more" changes → reveal the collapsed extra rows + resize the panel.
      var showAll = tgt.closest('.moph-cd-note-showall');
      if (showAll) {
        var list = showAll.closest('.moph-cd-note-list');
        var expanded = list.classList.toggle('moph-cd-note-list--all');
        var en = showAll.getAttribute('data-extra') || '';
        showAll.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        showAll.textContent = expanded ? 'Show fewer' : ('Show ' + en + ' more change' + (en === '1' ? '' : 's'));
        _sizeDetailPanel(list);
        return;
      }
      // Click a change row → smoothly open/close its before/after impact strip.
      var noteRow = tgt.closest('.moph-cd-note__row');
      if (noteRow) {
        var noteEl = noteRow.closest('.moph-cd-note');
        if (noteEl && noteEl.classList.contains('moph-cd-note--has-impact')) {
          var willOpenImp = !noteEl.classList.contains('moph-cd-note--open');
          noteEl.classList.toggle('moph-cd-note--open', willOpenImp);
          _cdNestedAccordion(noteEl.querySelector('.moph-cd-note__impact'), willOpenImp);
        }
        return;
      }
      // Click a mini sparkline → accordion-open its big chart below the grid (swap/close).
      var spark = tgt.closest('.moph-cd-spark');
      if (spark && spark.getAttribute('data-metric')) {
        var key = spark.getAttribute('data-metric');
        var grid = spark.closest('.moph-cd-sparkgrid');
        var pad = spark.closest('.moph-camp-detail__pad');
        var zoom = pad && pad.querySelector('.moph-cd-zoom');
        if (!grid || !zoom) return;
        var wasActive = spark.classList.contains('moph-cd-spark--active');
        grid.querySelectorAll('.moph-cd-spark--active').forEach(function(s) { s.classList.remove('moph-cd-spark--active'); });
        if (wasActive) {
          /* Smooth slide-up close: the zoom is the last block in the panel, so instead of
             yanking it out instantly (which snapped), shrink the accordion's max-height by
             the zoom's footprint. The inner's overflow:hidden + max-height transition clips
             the chart from the bottom = it slides up. Only after the slide do we actually
             hide it + settle the height. */
          var innerEl = zoom.closest('.moph-camp-detail__inner');
          var zCS = window.getComputedStyle(zoom);
          var zFull = zoom.offsetHeight + (parseFloat(zCS.marginTop) || 0) + (parseFloat(zCS.marginBottom) || 0);
          if (innerEl) innerEl.style.maxHeight = Math.max(0, innerEl.scrollHeight - zFull) + 'px';
          window.setTimeout(function() {
            zoom.hidden = true;
            zoom.querySelectorAll('.moph-cd-zoom__panel').forEach(function(p) { p.style.display = 'none'; });
            _sizeDetailPanel(zoom);
          }, 400);
        } else {
          zoom.querySelectorAll('.moph-cd-zoom__panel').forEach(function(p) { p.style.display = 'none'; });
          var target = zoom.querySelector('.moph-cd-zoom__panel[data-metric="' + key + '"]');
          if (target) { zoom.hidden = false; target.style.display = 'block'; spark.classList.add('moph-cd-spark--active'); _setupZoomHover(target.querySelector('.moph-cd-zoom__chart')); }
          _sizeDetailPanel(zoom);
        }
        return;
      }
    });
    // Keep the change-form's Save button enabled only when category + description are set.
    function _onCdFormInput(e) {
      var form = e.target && e.target.closest && e.target.closest('.moph-cd-note-form');
      if (form) _cdSyncSaveBtn(form);
    }
    if (_collapseRoot) { _collapseRoot.addEventListener('input', _onCdFormInput); _collapseRoot.addEventListener('change', _onCdFormInput); }
    // Custom Range date inputs — refetch on either change (require both set)
    function onCustomDateChange() {
      var f = document.getElementById('mophCustomFrom').value;
      var t = document.getElementById('mophCustomTo').value;
      if (!f || !t) return;
      state.customFrom = f;
      state.customTo = t;
      loadAll();
    }
    document.getElementById('mophCustomFrom').addEventListener('change', onCustomDateChange);
    document.getElementById('mophCustomTo').addEventListener('change', onCustomDateChange);
    document.getElementById('mophPlatform').addEventListener('change', function(e) {
      state.platform = e.target.value;
      loadAll();
    });
    var attrSel = document.getElementById('mophAttribution');
    if (attrSel) {
      attrSel.value = state.attribution;
      attrSel.addEventListener('change', function(e) {
        state.attribution = e.target.value === 'lastPaid' ? 'lastPaid' : 'firstPaid';
        try { localStorage.setItem('moph_attribution', state.attribution); } catch(_) {}
        loadAll(); // server recomputes from stored journeys; KPI/channel/campaign all read state.cm
      });
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE — universal chart "?" → opens the right-side
       Show-Your-Work pane for the CURRENT metric. Most metrics map to an existing
       KPI explainer (cm→contributionMargin, adRevenue→revenue, adRoas→roas); the
       chart-only ones (ctr/cvr/inquiries/totalConversions) have their own panes. */
    var CHART_EXPLAIN_KEY = {
      cm: 'contributionMargin', spend: 'spend', adRevenue: 'revenue', totalRevenue: 'totalRevenue',
      adRoas: 'roas', totalRoas: 'totalRoas', ctr: 'ctr', cvr: 'cvr', inquiries: 'inquiries',
      conversions: 'conversions', totalConversions: 'totalConversions',
    };
    var _chartExplainBtn = document.getElementById('mophChartExplain');
    if (_chartExplainBtn) {
      _chartExplainBtn.setAttribute('title', 'How this chart is calculated — click for the formula & sources');
      _chartExplainBtn.addEventListener('click', function() {
        openExplainerDrawer(CHART_EXPLAIN_KEY[state.metric] || state.metric);
      });
    }
    var CHART_METRIC_ORDER = ['cm', 'spend', 'adRevenue', 'totalRevenue', 'adRoas', 'totalRoas', 'ctr', 'cvr', 'inquiries', 'conversions', 'totalConversions'];
    document.querySelectorAll('.moph-tab').forEach(function(tab) {
      tab.addEventListener('click', function() {
        var newMetric = tab.getAttribute('data-metric');
        if (newMetric === state.metric) return;
        var oldIdx = CHART_METRIC_ORDER.indexOf(state.metric);
        var newIdx = CHART_METRIC_ORDER.indexOf(newMetric);
        document.querySelectorAll('.moph-tab').forEach(function(t) { t.classList.remove('moph-tab--active'); });
        tab.classList.add('moph-tab--active');
        state.metric = newMetric;
        renderChart();
        /* Directional swipe (#21): moving forward in the tab order slides the
           new chart in from the right; moving backward, from the left. Animate
           the container — the SVG redraw already happened in renderChart(). */
        var chartEl = document.getElementById('mophChart');
        if (chartEl) {
          chartEl.classList.remove('moph-chart--anim-right', 'moph-chart--anim-left');
          void chartEl.offsetWidth; // reflow to restart the animation
          chartEl.classList.add(newIdx > oldIdx ? 'moph-chart--anim-right' : 'moph-chart--anim-left');
        }
      });
    });
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — Ad Spend Total / By-channel toggle.
    document.querySelectorAll('#mophSpendView [data-spendview]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var v = btn.getAttribute('data-spendview');
        if (v === state.spendView) return;
        state.spendView = v;
        renderChart();
      });
    });
    document.querySelectorAll('#mophCampaigns thead th[data-sort]').forEach(function(th) {
      th.addEventListener('click', function() {
        var k = th.getAttribute('data-sort');
        if (state.sort.key === k) state.sort.dir = state.sort.dir === 'asc' ? 'desc' : 'asc';
        else { state.sort.key = k; state.sort.dir = (k === 'name' || k === 'platform' || k === 'status') ? 'asc' : 'desc'; }
        renderCampaigns();
      });
    });
    /* #9 — delegated handler for the per-row rename (✎) button. Delegated
       because renderCampaigns() rebuilds the tbody on every sort/refresh. */
    var _campTbl = document.getElementById('mophCampaigns');
    if (_campTbl) _campTbl.addEventListener('click', function(e) {
      var btn = e.target.closest('.moph-camp-edit');
      if (btn) { e.stopPropagation(); startCampaignRename(btn); return; }
      // CUSTOM_MARKETINGOPS_BY_CLAUDE (campaign expand) — click anywhere else on
      // a campaign row toggles its detail panel (skip clicks inside an open panel).
      if (e.target.closest('.moph-camp-detail')) return;
      var row = e.target.closest('tr.moph-camp-row');
      if (row) toggleCampaignDetail(row);
    });
    // Recent Sales sort — independent state, mirrors the Orders page sort
    // pattern. Clicking same header toggles direction; clicking a new
    // header defaults string keys to asc, numeric keys to desc.
    document.querySelectorAll('#mophOrders thead th[data-sort]').forEach(function(th) {
      th.addEventListener('click', function() {
        var k = th.getAttribute('data-sort');
        var stringKeys = { dateSold: 1, ref: 1, customer: 1, sourceGroup: 1, channel: 1 };
        if (state.ordersSort.key === k) state.ordersSort.dir = state.ordersSort.dir === 'asc' ? 'desc' : 'asc';
        else { state.ordersSort.key = k; state.ordersSort.dir = stringKeys[k] ? 'asc' : 'desc'; }
        renderOrders();
      });
    });
    /* N14 — Recent Sales pagination buttons. CUSTOM_MARKETINGOPS_BY_CLAUDE — pager
       math runs over the FILTERED set so "Show all" / more / less honor the filter. */
    function _ordersTotal() { return getFilteredOrders().length; }
    var _oMore = document.getElementById('mophOrdersMore');
    if (_oMore) _oMore.addEventListener('click', function() { state.ordersShown = Math.min((state.ordersShown || 20) + 20, _ordersTotal()); renderOrders(); });
    var _oLess = document.getElementById('mophOrdersLess');
    if (_oLess) _oLess.addEventListener('click', function() { state.ordersShown = Math.max((state.ordersShown || 20) - 20, 20); renderOrders(); });
    var _oAll = document.getElementById('mophOrdersAll');
    if (_oAll) _oAll.addEventListener('click', function() { state.ordersShown = _ordersTotal(); renderOrders(); });

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-24) — Sales table Channel + Source
       filter wiring. Delegated once on the (stable) bar; menus are populated later by
       renderSalesFilters(). Toggling a box mutates state.ordersFilter in place, resets
       pagination, and re-renders. The menu stays open for multi-pick (only the button
       or an outside click closes it). */
    var _salesFilterBar = document.getElementById('mophSalesFilters');
    if (_salesFilterBar) {
      function _stateKeyOf(root) { return root.getAttribute('data-filter') === 'channel' ? 'channels' : 'sources'; }
      function _closeMsels(except) {
        _salesFilterBar.querySelectorAll('.moph-msel--open').forEach(function(r) {
          if (r === except) return;
          r.classList.remove('moph-msel--open');
          var b = r.querySelector('.moph-msel__btn'); if (b) b.setAttribute('aria-expanded', 'false');
        });
      }
      _salesFilterBar.addEventListener('click', function(e) {
        var clr = e.target.closest && e.target.closest('.moph-msel__clear');
        if (clr) {
          var croot = clr.closest('.moph-msel');
          state.ordersFilter[_stateKeyOf(croot)] = [];
          croot.querySelectorAll('.moph-msel__opts input[type="checkbox"]').forEach(function(b) { b.checked = false; });
          state.ordersShown = 20;
          _refreshMselVal(croot);
          renderOrders();
          return;
        }
        var btn = e.target.closest && e.target.closest('.moph-msel__btn');
        if (btn) {
          var broot = btn.closest('.moph-msel');
          var open = broot.classList.toggle('moph-msel--open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) _closeMsels(broot);
        }
      });
      _salesFilterBar.addEventListener('change', function(e) {
        var box = e.target.closest && e.target.closest('input[type="checkbox"]');
        if (!box) return;
        var root = box.closest('.moph-msel');
        var sel = state.ordersFilter[_stateKeyOf(root)] || (state.ordersFilter[_stateKeyOf(root)] = []);
        (box.getAttribute('data-keys') || '').split(',').filter(Boolean).forEach(function(k) {
          var idx = sel.indexOf(k);
          if (box.checked && idx === -1) sel.push(k);
          else if (!box.checked && idx !== -1) sel.splice(idx, 1);
        });
        state.ordersShown = 20;
        _refreshMselVal(root);
        renderOrders();
      });
      document.addEventListener('click', function(e) {
        if (_salesFilterBar.contains(e.target)) return;
        _closeMsels(null);
      });
    }
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Leads table) — sort headers + pagination, mirrors Sales. */
    document.querySelectorAll('#mophLeads thead th[data-sort]').forEach(function(th) {
      th.addEventListener('click', function() {
        var k = th.getAttribute('data-sort');
        var stringKeys = { date: 1, customer: 1, sourceGroup: 1, channel: 1, status: 1 };
        if (state.leadsSort.key === k) state.leadsSort.dir = state.leadsSort.dir === 'asc' ? 'desc' : 'asc';
        else { state.leadsSort.key = k; state.leadsSort.dir = stringKeys[k] ? 'asc' : 'desc'; }
        renderLeads();
      });
    });
    function _leadsTotal() { return ((state.leads && state.leads.leads) || []).length; }
    var _lMore = document.getElementById('mophLeadsMore');
    if (_lMore) _lMore.addEventListener('click', function() { state.leadsShown = Math.min((state.leadsShown || 20) + 20, _leadsTotal()); renderLeads(); });
    var _lLess = document.getElementById('mophLeadsLess');
    if (_lLess) _lLess.addEventListener('click', function() { state.leadsShown = Math.max((state.leadsShown || 20) - 20, 20); renderLeads(); });
    var _lAll = document.getElementById('mophLeadsAll');
    if (_lAll) _lAll.addEventListener('click', function() { state.leadsShown = _leadsTotal(); renderLeads(); });
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — Recent Sales row → per-order
       economics drawer (ported from the retired Orders page). Delegated on the
       tbody so it survives every re-render; finds the order by ref in the loaded
       set. Close on backdrop / × / Esc. */
    var _oTbody = document.querySelector('#mophOrders tbody');
    if (_oTbody) _oTbody.addEventListener('click', function(e) {
      var row = e.target.closest('tr.moph-orow');
      if (!row) return;
      var ref = row.getAttribute('data-ref');
      var list = (state.orders && state.orders.orders) || [];
      var o = null;
      for (var i = 0; i < list.length; i++) { if (list[i].ref === ref) { o = list[i]; break; } }
      if (o) openOrderDrawer(o);
    });
    document.querySelectorAll('#mophODrawer [data-oclose]').forEach(function(el) {
      el.addEventListener('click', closeOrderDrawer);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        var d = document.getElementById('mophODrawer');
        if (d && !d.hidden) closeOrderDrawer();
      }
    });
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — donut hover cross-highlight.
       Delegated per .moph-mix group (each has its own donut + legend) so hovering a
       segment OR its legend row lights up the pair. Containers are static markup;
       listeners survive every renderMix() innerHTML rebuild. */
    document.querySelectorAll('#mophMixWrap .moph-mix').forEach(function(mixEl) {
      mixEl.addEventListener('mouseover', function(e) {
        var el = e.target.closest && e.target.closest('[data-mix-key]');
        _mixHighlight(mixEl, el ? el.getAttribute('data-mix-key') : null);
      });
      mixEl.addEventListener('mouseleave', function() { _mixHighlight(mixEl, null); });
    });
    // Show Your Work — every (?) icon opens the explainer drawer for its
    // metric key. Catches .moph-kpi__explain (hero/KPI tiles), .moph-th__explain
    // (table column headers), AND .moph-hours-chip (Phase 6.5 Delight Layer).
    document.querySelectorAll('.moph-kpi__explain, .moph-th__explain, .moph-hours-chip').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var key = btn.getAttribute('data-explain');
        if (key) openExplainerDrawer(key);
      });
    });
    document.querySelectorAll('#mophExplain [data-close]').forEach(function(el) {
      el.addEventListener('click', closeExplainerDrawer);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !document.getElementById('mophExplain').hidden) closeExplainerDrawer();
    });

    // Plan overlay toggle — sets aria-pressed + data-plan-overlay (CSS uses
    // it to reveal the .moph-col--plan columns). Lazy-loads /budget/year
    // on first activation so the dashboard stays fast for users who never
    // touch the toggle.
    // Plan toggle now lives on BOTH the Channel Breakdown + Campaigns card
    // headers (the tables it affects), kept in sync — clicking either flips both.
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-08-03) — "All sales" toggle: reveal for admins only,
       restore the saved position, and reload everything on change (the flag is applied inside
       mwFetch, so a plain reload is enough — nothing else needs to know about it). */
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-09-06) — Phase-G MATCH WINDOW control. Everyone sees
       the number (the agency is paid under it); an admin can edit + Save. A save is followed by a
       forced, cache-bypassing reload — the attribution on every card changes with the window, and the
       worker's hour-long cache would otherwise keep showing the old credit for up to an hour. */
    (function initAttributionWindow() {
      var ctl = document.getElementById('mophWindowCtl');
      var input = document.getElementById('mophWindowDays');
      var save = document.getElementById('mophWindowSave');
      if (!ctl || !input || !save) return;
      var admin = isAdminSession();
      var current = null;
      function reflect() {
        var v = parseInt(input.value, 10);
        var dirty = admin && current != null && v >= 1 && v <= 365 && v !== current;
        ctl.classList.toggle('is-dirty', dirty);
        save.hidden = !dirty;
      }
      mwFetch('/dashboard/attribution-config').then(function (res) {
        var cfg = (res && res.config) || {};
        current = Number(cfg.windowDays) || 30;
        state.attributionWindowDays = current;
        input.value = String(current);
        input.disabled = !admin;
        ctl.hidden = false;
        if (cfg.updatedAt) ctl.title += ' Last set ' + new Date(cfg.updatedAt).toLocaleDateString() + (cfg.updatedBy ? ' by ' + cfg.updatedBy : '') + '.';
      }).catch(function (err) {
        console.warn('[marketing-ops] attribution window unavailable; drawer copy falls back to 30 days', err);
      });
      if (!admin) return;
      input.addEventListener('input', reflect);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter' && !save.hidden) save.click(); });
      save.addEventListener('click', async function () {
        var v = parseInt(input.value, 10);
        if (!(v >= 1 && v <= 365) || save.disabled) return;
        save.disabled = true; save.textContent = 'Saving…';
        try {
          var res = await mwPost('/dashboard/attribution-config', { windowDays: v });
          current = Number(res && res.config && res.config.windowDays) || v;
          state.attributionWindowDays = current;
          input.value = String(current);
          reflect();
          await loadAll({ force: true });
        } catch (err) {
          alert('Could not save the match window: ' + (err && err.message ? err.message : err));
        } finally {
          save.disabled = false; save.textContent = 'Save';
        }
      });
    })();

    var _nonMktBtn = document.getElementById('mophNonMktToggle');
    if (_nonMktBtn && isAdminSession()) {
      _nonMktBtn.hidden = false;
      _nonMktBtn.setAttribute('aria-checked', nonMktOn() ? 'true' : 'false');
      _nonMktBtn.addEventListener('click', function() {
        var next = !nonMktOn();
        setNonMktOn(next);
        _nonMktBtn.setAttribute('aria-checked', next ? 'true' : 'false');
        loadAll();
      });
    }

    var _pausedBtn = document.getElementById('mophPausedToggle');
    if (_pausedBtn) _pausedBtn.addEventListener('click', function() {
      state.showPaused = !state.showPaused;
      _pausedBtn.setAttribute('aria-checked', state.showPaused ? 'true' : 'false');
      renderCampaigns();
    });

    function syncPlanToggles() {
      document.querySelectorAll('.moph-plan-toggle').forEach(function(b) {
        b.setAttribute('aria-checked', state.showPlan ? 'true' : 'false');
      });
    }
    document.querySelectorAll('.moph-plan-toggle').forEach(function(planBtn) {
      planBtn.addEventListener('click', async function() {
        state.showPlan = !state.showPlan;
        syncPlanToggles();
        var wrap = document.querySelector('.rep-quote-portal-content');
        if (wrap) wrap.setAttribute('data-plan-overlay', state.showPlan ? 'on' : 'off');
        if (state.showPlan) await loadPlanIfNeeded();
        renderChannels();
        renderCampaigns();
      });
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function init() {
    if (!window.RepPortal || !window.RepPortal.Auth) {
      setTimeout(init, 100);
      return;
    }
    var session = window.RepPortal.Auth.getSession();
    if (!session) return;
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-23, Marketing Ops speed step 4) — once. init() is wired to
       BOTH the page start and 'rep-portal:authenticated'; if both ever reach here with a session, the second
       would restart the 13-request load. A guard only: measured live on 2026-10-23, the page loaded each card
       once even before this — the "every card ×2" in the request counts is the admin All-Sales twin prefetch
       (prefetchAltNonMkt), by design. The listener stays for a session that only arrives with that event. */
    if (init._done) return;
    init._done = true;
    bind();
    loadHealth();
    loadAll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('rep-portal:authenticated', init);
})();
